(() => {
  // node_modules/@11ty/is-land/is-land.js
  var Island = class _Island extends HTMLElement {
    static tagName = "is-land";
    static prefix = "is-land--";
    static attr = {
      template: "data-island",
      ready: "ready",
      defer: "defer-hydration"
    };
    static onceCache = /* @__PURE__ */ new Map();
    static onReady = /* @__PURE__ */ new Map();
    static fallback = {
      ":not(is-land,:defined,[defer-hydration])": (readyPromise, node, prefix2) => {
        let cloned = document.createElement(prefix2 + node.localName);
        for (let attr of node.getAttributeNames()) {
          cloned.setAttribute(attr, node.getAttribute(attr));
        }
        let shadowroot = node.shadowRoot;
        if (!shadowroot) {
          let tmpl = node.querySelector(":scope > template:is([shadowrootmode], [shadowroot])");
          if (tmpl) {
            let mode = tmpl.getAttribute("shadowrootmode") || tmpl.getAttribute("shadowroot") || "closed";
            shadowroot = node.attachShadow({ mode });
            shadowroot.appendChild(tmpl.content.cloneNode(true));
          }
        }
        if (shadowroot) {
          cloned.attachShadow({ mode: shadowroot.mode }).append(...shadowroot.childNodes);
        }
        cloned.append(...node.childNodes);
        node.replaceWith(cloned);
        return readyPromise.then(() => {
          if (cloned.shadowRoot) {
            node.shadowRoot.append(...cloned.shadowRoot.childNodes);
          }
          node.append(...cloned.childNodes);
          cloned.replaceWith(node);
        });
      }
    };
    constructor() {
      super();
      this.ready = new Promise((resolve) => {
        this.readyResolve = resolve;
      });
    }
    // any parents of `el` that are <is-land> (with conditions)
    static getParents(el, stopAt = false) {
      let nodes = [];
      while (el) {
        if (el.matches && el.matches(_Island.tagName)) {
          if (stopAt && el === stopAt) {
            break;
          }
          if (Conditions.hasConditions(el)) {
            nodes.push(el);
          }
        }
        el = el.parentNode;
      }
      return nodes;
    }
    static async ready(el, parents) {
      if (!parents) {
        parents = _Island.getParents(el);
      }
      if (parents.length === 0) {
        return;
      }
      let imports = await Promise.all(parents.map((p) => p.wait()));
      if (imports.length) {
        return imports[0];
      }
    }
    forceFallback() {
      if (window.Island) {
        Object.assign(_Island.fallback, window.Island.fallback);
      }
      for (let selector in _Island.fallback) {
        let components = Array.from(this.querySelectorAll(selector)).reverse();
        for (let node of components) {
          if (!node.isConnected) {
            continue;
          }
          let parents = _Island.getParents(node);
          if (parents.length === 1) {
            let p = _Island.ready(node, parents);
            _Island.fallback[selector](p, node, _Island.prefix);
          }
        }
      }
    }
    wait() {
      return this.ready;
    }
    async connectedCallback() {
      if (Conditions.hasConditions(this)) {
        this.forceFallback();
      }
      await this.hydrate();
    }
    getTemplates() {
      return this.querySelectorAll(`template[${_Island.attr.template}]`);
    }
    replaceTemplates(templates) {
      for (let node of templates) {
        if (_Island.getParents(node, this).length > 0) {
          continue;
        }
        let value = node.getAttribute(_Island.attr.template);
        if (value === "replace") {
          let children2 = Array.from(this.childNodes);
          for (let child2 of children2) {
            this.removeChild(child2);
          }
          this.appendChild(node.content);
          break;
        } else {
          let html = node.innerHTML;
          if (value === "once" && html) {
            if (_Island.onceCache.has(html)) {
              node.remove();
              return;
            }
            _Island.onceCache.set(html, true);
          }
          node.replaceWith(node.content);
        }
      }
    }
    async hydrate() {
      let conditions = [];
      if (this.parentNode) {
        conditions.push(_Island.ready(this.parentNode));
      }
      let attrs = Conditions.getConditions(this);
      for (let condition in attrs) {
        if (Conditions.map[condition]) {
          conditions.push(Conditions.map[condition](attrs[condition], this));
        }
      }
      await Promise.all(conditions);
      this.replaceTemplates(this.getTemplates());
      for (let fn of _Island.onReady.values()) {
        await fn.call(this, _Island);
      }
      this.readyResolve();
      this.setAttribute(_Island.attr.ready, "");
      this.querySelectorAll(`[${_Island.attr.defer}]`).forEach((node) => node.removeAttribute(_Island.attr.defer));
    }
  };
  var Conditions = class _Conditions {
    static map = {
      visible: _Conditions.visible,
      idle: _Conditions.idle,
      interaction: _Conditions.interaction,
      media: _Conditions.media,
      "save-data": _Conditions.saveData
    };
    static hasConditions(node) {
      return Object.keys(_Conditions.getConditions(node)).length > 0;
    }
    static getConditions(node) {
      let map = {};
      for (let key of Object.keys(_Conditions.map)) {
        if (node.hasAttribute(`on:${key}`)) {
          map[key] = node.getAttribute(`on:${key}`);
        }
      }
      return map;
    }
    static visible(noop3, el) {
      if (!("IntersectionObserver" in window)) {
        return;
      }
      return new Promise((resolve) => {
        let observer2 = new IntersectionObserver((entries2) => {
          let [entry] = entries2;
          if (entry.isIntersecting) {
            observer2.unobserve(entry.target);
            resolve();
          }
        });
        observer2.observe(el);
      });
    }
    // Warning: on:idle is not very useful with other conditions as it may resolve long before.
    static idle() {
      let onload = new Promise((resolve) => {
        if (document.readyState !== "complete") {
          window.addEventListener("load", () => resolve(), { once: true });
        } else {
          resolve();
        }
      });
      if (!("requestIdleCallback" in window)) {
        return onload;
      }
      return Promise.all([
        new Promise((resolve) => {
          requestIdleCallback(() => {
            resolve();
          });
        }),
        onload
      ]);
    }
    static interaction(eventOverrides, el) {
      let events = ["click", "touchstart"];
      if (eventOverrides) {
        events = (eventOverrides || "").split(",").map((entry) => entry.trim());
      }
      return new Promise((resolve) => {
        function resolveFn(e) {
          resolve();
          for (let name of events) {
            el.removeEventListener(name, resolveFn);
          }
        }
        for (let name of events) {
          el.addEventListener(name, resolveFn, { once: true });
        }
      });
    }
    static media(query2) {
      let mm = {
        matches: true
      };
      if (query2 && "matchMedia" in window) {
        mm = window.matchMedia(query2);
      }
      if (mm.matches) {
        return;
      }
      return new Promise((resolve) => {
        mm.addListener((e) => {
          if (e.matches) {
            resolve();
          }
        });
      });
    }
    static saveData(expects) {
      if (!("connection" in navigator) || navigator.connection.saveData === (expects !== "false")) {
        return;
      }
      return new Promise(() => {
      });
    }
  };
  if ("customElements" in window) {
    window.customElements.define(Island.tagName, Island);
    window.Island = Island;
  }
  var ready = Island.ready;

  // node_modules/alpinejs/dist/module.esm.js
  var flushPending = false;
  var flushing = false;
  var queue = [];
  var lastFlushedIndex = -1;
  var queueNeedsSort = false;
  var transactionActive = false;
  function scheduler(callback) {
    queueJob(callback);
  }
  function startTransaction() {
    transactionActive = true;
  }
  function commitTransaction() {
    transactionActive = false;
    queueFlush();
  }
  function queueJob(job) {
    if (!queue.includes(job)) {
      queue.push(job);
      if (job._x_schedulerPriority !== void 0)
        queueNeedsSort = true;
    }
    queueFlush();
  }
  function dequeueJob(job) {
    let index = queue.indexOf(job);
    if (index !== -1 && index > lastFlushedIndex)
      queue.splice(index, 1);
  }
  function queueFlush() {
    if (!flushing && !flushPending) {
      if (transactionActive)
        return;
      flushPending = true;
      queueMicrotask(flushJobs);
    }
  }
  function flushJobs() {
    flushPending = false;
    flushing = true;
    for (let i = 0; i < queue.length; i++) {
      if (queueNeedsSort)
        sortPendingJobs(i);
      queue[i]();
      lastFlushedIndex = i;
    }
    queue.length = 0;
    lastFlushedIndex = -1;
    queueNeedsSort = false;
    flushing = false;
  }
  function sortPendingJobs(start2) {
    let depths = /* @__PURE__ */ new Map();
    let sorted = queue.slice(start2).sort((a, b) => compareJobs(a, b, depths));
    for (let i = 0; i < sorted.length; i++) {
      queue[start2 + i] = sorted[i];
    }
    queueNeedsSort = false;
  }
  function compareJobs(a, b, depths) {
    if (!isStructural(a))
      return isStructural(b) ? 1 : 0;
    if (!isStructural(b))
      return -1;
    let depthDifference = getElementDepth(a._x_schedulerPriority.el, depths) - getElementDepth(b._x_schedulerPriority.el, depths);
    return depthDifference || a._x_schedulerPriority.order - b._x_schedulerPriority.order;
  }
  function isStructural(job) {
    return job._x_schedulerPriority !== void 0;
  }
  function getElementDepth(el, depths) {
    if (depths.has(el))
      return depths.get(el);
    let depth = 0;
    let owner = el;
    while (el) {
      depth++;
      if (el._x_teleportBack) {
        el = el._x_teleportBack;
      } else if (typeof ShadowRoot === "function" && el.parentNode instanceof ShadowRoot) {
        el = el.parentNode.host;
      } else {
        el = el.parentElement;
      }
    }
    depths.set(owner, depth);
    return depth;
  }
  var reactive;
  var effect;
  var release;
  var raw;
  var nextStructuralEffectOrder = 0;
  var shouldSchedule = true;
  function disableEffectScheduling(callback) {
    shouldSchedule = false;
    callback();
    shouldSchedule = true;
  }
  function setReactivityEngine(engine) {
    reactive = engine.reactive;
    release = engine.release;
    effect = (callback) => engine.effect(callback, { scheduler: (task) => {
      if (shouldSchedule) {
        scheduler(task);
      } else {
        task();
      }
    } });
    raw = engine.raw;
  }
  function overrideEffect(override) {
    effect = override;
  }
  function elementBoundEffect(el) {
    let cleanup = () => {
    };
    let wrappedEffect = (callback, options) => {
      let priority = options?.priority === "structural" ? nextStructuralEffectOrder++ : void 0;
      let effectReference = effect(callback);
      if (priority !== void 0 && effectReference !== void 0) {
        effectReference._x_schedulerPriority = { el, order: priority };
      }
      if (!el._x_effects) {
        el._x_effects = /* @__PURE__ */ new Set();
        el._x_runEffects = () => {
          el._x_effects.forEach((i) => i());
        };
      }
      el._x_effects.add(effectReference);
      cleanup = () => {
        if (effectReference === void 0)
          return;
        el._x_effects.delete(effectReference);
        release(effectReference);
      };
      return effectReference;
    };
    return [wrappedEffect, () => {
      cleanup();
    }];
  }
  function watch(getter, callback) {
    let firstTime = true;
    let oldValue;
    let oldValueJSON;
    let effectReference = effect(() => {
      let value = getter();
      let newJSON = JSON.stringify(value);
      if (!firstTime) {
        if (typeof value === "object" || value !== oldValue) {
          let previousValue = typeof oldValue === "object" ? JSON.parse(oldValueJSON) : oldValue;
          queueMicrotask(() => {
            callback(value, previousValue);
          });
        }
      }
      oldValue = value;
      oldValueJSON = newJSON;
      firstTime = false;
    });
    return () => {
      dequeueJob(effectReference);
      release(effectReference);
    };
  }
  async function transaction(callback) {
    startTransaction();
    try {
      await callback();
      await Promise.resolve();
    } finally {
      commitTransaction();
    }
  }
  var onAttributeAddeds = [];
  var onElRemoveds = [];
  var onElAddeds = [];
  function onElAdded(callback) {
    onElAddeds.push(callback);
  }
  function onElRemoved(el, callback) {
    if (typeof callback === "function") {
      if (!el._x_cleanups)
        el._x_cleanups = [];
      el._x_cleanups.push(callback);
    } else {
      callback = el;
      onElRemoveds.push(callback);
    }
  }
  function onAttributesAdded(callback) {
    onAttributeAddeds.push(callback);
  }
  function onAttributeRemoved(el, name, callback) {
    if (!el._x_attributeCleanups)
      el._x_attributeCleanups = {};
    if (!el._x_attributeCleanups[name])
      el._x_attributeCleanups[name] = [];
    el._x_attributeCleanups[name].push(callback);
  }
  function cleanupAttributes(el, names) {
    if (!el._x_attributeCleanups)
      return;
    Object.entries(el._x_attributeCleanups).forEach(([name, value]) => {
      if (names === void 0 || names.includes(name)) {
        value.forEach((i) => i());
        delete el._x_attributeCleanups[name];
      }
    });
  }
  function cleanupElement(el) {
    el._x_effects?.forEach(dequeueJob);
    while (el._x_cleanups?.length)
      el._x_cleanups.pop()();
  }
  var observer = new MutationObserver(onMutate);
  var currentlyObserving = false;
  function startObservingMutations() {
    observer.observe(document, { subtree: true, childList: true, attributes: true, attributeOldValue: true });
    currentlyObserving = true;
  }
  function stopObservingMutations() {
    flushObserver();
    observer.disconnect();
    currentlyObserving = false;
  }
  var queuedMutations = [];
  function flushObserver() {
    let records = observer.takeRecords();
    queuedMutations.push(() => records.length > 0 && onMutate(records));
    let queueLengthWhenTriggered = queuedMutations.length;
    queueMicrotask(() => {
      if (queuedMutations.length === queueLengthWhenTriggered) {
        while (queuedMutations.length > 0)
          queuedMutations.shift()();
      }
    });
  }
  function flushPendingMutations() {
    while (queuedMutations.length > 0)
      queuedMutations.shift()();
    let records = observer.takeRecords();
    if (records.length > 0)
      onMutate(records);
  }
  function mutateDom(callback) {
    if (!currentlyObserving)
      return callback();
    stopObservingMutations();
    try {
      return callback();
    } finally {
      startObservingMutations();
    }
  }
  var isCollecting = false;
  var deferredMutations = [];
  function deferMutations() {
    isCollecting = true;
  }
  function flushAndStopDeferringMutations() {
    isCollecting = false;
    onMutate(deferredMutations);
    deferredMutations = [];
  }
  function onMutate(mutations) {
    if (isCollecting) {
      deferredMutations = deferredMutations.concat(mutations);
      return;
    }
    let addedNodes = [];
    let removedNodes = /* @__PURE__ */ new Set();
    let addedAttributes = /* @__PURE__ */ new Map();
    let removedAttributes = /* @__PURE__ */ new Map();
    for (let i = 0; i < mutations.length; i++) {
      if (mutations[i].target._x_ignoreMutationObserver)
        continue;
      if (mutations[i].type === "childList") {
        mutations[i].removedNodes.forEach((node) => {
          if (node.nodeType !== 1)
            return;
          if (!node._x_marker)
            return;
          removedNodes.add(node);
        });
        mutations[i].addedNodes.forEach((node) => {
          if (node.nodeType !== 1)
            return;
          if (removedNodes.has(node)) {
            removedNodes.delete(node);
            return;
          }
          if (node._x_marker)
            return;
          addedNodes.push(node);
        });
      }
      if (mutations[i].type === "attributes") {
        let el = mutations[i].target;
        let name = mutations[i].attributeName;
        let oldValue = mutations[i].oldValue;
        let add = () => {
          if (!addedAttributes.has(el))
            addedAttributes.set(el, []);
          addedAttributes.get(el).push({ name, value: el.getAttribute(name) });
        };
        let remove2 = () => {
          if (!removedAttributes.has(el))
            removedAttributes.set(el, []);
          removedAttributes.get(el).push(name);
        };
        if (el.hasAttribute(name) && oldValue === null) {
          add();
        } else if (el.hasAttribute(name)) {
          remove2();
          add();
        } else {
          remove2();
        }
      }
    }
    removedAttributes.forEach((attrs, el) => {
      cleanupAttributes(el, attrs);
    });
    addedAttributes.forEach((attrs, el) => {
      onAttributeAddeds.forEach((i) => i(el, attrs));
    });
    for (let node of removedNodes) {
      if (addedNodes.some((i) => i.contains(node)))
        continue;
      onElRemoveds.forEach((i) => i(node));
    }
    for (let node of addedNodes) {
      if (!node.isConnected)
        continue;
      onElAddeds.forEach((i) => i(node));
    }
    addedNodes = null;
    removedNodes = null;
    addedAttributes = null;
    removedAttributes = null;
  }
  function scope(node) {
    return mergeProxies(closestDataStack(node));
  }
  function addScopeToNode(node, data2, referenceNode) {
    node._x_dataStack = [data2, ...closestDataStack(referenceNode || node)];
    return () => {
      node._x_dataStack = node._x_dataStack.filter((i) => i !== data2);
    };
  }
  function closestDataStack(node) {
    if (node._x_dataStack)
      return node._x_dataStack;
    if (typeof ShadowRoot === "function" && node instanceof ShadowRoot) {
      return closestDataStack(node.host);
    }
    if (!node.parentNode) {
      return [];
    }
    return closestDataStack(node.parentNode);
  }
  function mergeProxies(objects) {
    return new Proxy({ objects }, mergeProxyTrap);
  }
  function keyInPrototypeChain(obj, key) {
    if (obj === null || obj === Object.prototype)
      return null;
    if (Object.prototype.hasOwnProperty.call(obj, key))
      return obj;
    return keyInPrototypeChain(Object.getPrototypeOf(obj), key);
  }
  var mergeProxyTrap = {
    ownKeys({ objects }) {
      return Array.from(
        new Set(objects.flatMap((i) => Object.keys(i)))
      );
    },
    has({ objects }, name) {
      if (name == Symbol.unscopables)
        return false;
      return objects.some(
        (obj) => Object.prototype.hasOwnProperty.call(obj, name) || Reflect.has(obj, name)
      );
    },
    get({ objects }, name, thisProxy) {
      if (name == "toJSON")
        return collapseProxies;
      return Reflect.get(
        objects.find(
          (obj) => Reflect.has(obj, name)
        ) || {},
        name,
        thisProxy
      );
    },
    set({ objects }, name, value, thisProxy) {
      let target;
      for (const obj of objects) {
        target = keyInPrototypeChain(obj, name);
        if (target)
          break;
      }
      if (!target)
        target = objects[objects.length - 1];
      const descriptor = Object.getOwnPropertyDescriptor(target, name);
      if (descriptor?.set && descriptor?.get)
        return descriptor.set.call(thisProxy, value) || true;
      return Reflect.set(target, name, value);
    }
  };
  function collapseProxies() {
    let keys = Reflect.ownKeys(this);
    return keys.reduce((acc, key) => {
      acc[key] = Reflect.get(this, key);
      return acc;
    }, {});
  }
  function initInterceptors(data2, cleanup = () => {
  }) {
    let isObject32 = (val) => typeof val === "object" && !Array.isArray(val) && val !== null;
    let recurse = (obj, basePath = "") => {
      Object.entries(Object.getOwnPropertyDescriptors(obj)).forEach(([key, { value, enumerable }]) => {
        if (enumerable === false || value === void 0)
          return;
        if (typeof value === "object" && value !== null && value.__v_skip)
          return;
        let path = basePath === "" ? key : `${basePath}.${key}`;
        if (typeof value === "object" && value !== null && value._x_interceptor) {
          obj[key] = value.initialize(data2, path, key, cleanup);
        } else {
          if (isObject32(value) && value !== obj && !(value instanceof Element)) {
            recurse(value, path);
          }
        }
      });
    };
    return recurse(data2);
  }
  function interceptor(callback, mutateObj = () => {
  }) {
    let obj = {
      initialValue: void 0,
      _x_interceptor: true,
      initialize(data2, path, key, cleanup) {
        return callback(this.initialValue, () => get(data2, path), (value) => set(data2, path, value), path, key, cleanup);
      }
    };
    mutateObj(obj);
    return (initialValue) => {
      if (typeof initialValue === "object" && initialValue !== null && initialValue._x_interceptor) {
        let initialize = obj.initialize.bind(obj);
        obj.initialize = (data2, path, key, cleanup) => {
          let innerValue = initialValue.initialize(data2, path, key, cleanup);
          obj.initialValue = innerValue;
          return initialize(data2, path, key, cleanup);
        };
      } else {
        obj.initialValue = initialValue;
      }
      return obj;
    };
  }
  function get(obj, path) {
    return path.split(".").reduce((carry, segment) => carry[segment], obj);
  }
  function set(obj, path, value) {
    if (typeof path === "string")
      path = path.split(".");
    if (path.length === 1)
      obj[path[0]] = value;
    else if (path.length === 0)
      throw error;
    else {
      if (obj[path[0]])
        return set(obj[path[0]], path.slice(1), value);
      else {
        obj[path[0]] = {};
        return set(obj[path[0]], path.slice(1), value);
      }
    }
  }
  var magics = {};
  function magic(name, callback) {
    magics[name] = callback;
  }
  function injectMagics(obj, el) {
    let memoizedUtilities = getUtilities(el);
    Object.entries(magics).forEach(([name, callback]) => {
      Object.defineProperty(obj, `$${name}`, {
        get() {
          return callback(el, memoizedUtilities);
        },
        enumerable: false
      });
    });
    return obj;
  }
  function getUtilities(el) {
    let [utilities, cleanup] = getElementBoundUtilities(el);
    let utils = { interceptor, ...utilities };
    onElRemoved(el, cleanup);
    return utils;
  }
  function tryCatch(el, expression, callback, ...args) {
    try {
      return callback(...args);
    } catch (e) {
      handleError(e, el, expression);
    }
  }
  function handleError(...args) {
    return errorHandler(...args);
  }
  var errorHandler = normalErrorHandler;
  function setErrorHandler(handler4) {
    errorHandler = handler4;
  }
  function normalErrorHandler(error2, el, expression = void 0) {
    error2 = Object.assign(
      error2 ?? { message: "No error message given." },
      { el, expression }
    );
    console.warn(`Alpine Expression Error: ${error2.message}

${expression ? 'Expression: "' + expression + '"\n\n' : ""}`, el);
    setTimeout(() => {
      throw error2;
    }, 0);
  }
  var shouldAutoEvaluateFunctions = true;
  function dontAutoEvaluateFunctions(callback) {
    let cache = shouldAutoEvaluateFunctions;
    shouldAutoEvaluateFunctions = false;
    let result = callback();
    shouldAutoEvaluateFunctions = cache;
    return result;
  }
  function evaluate(el, expression, extras = {}) {
    let result;
    evaluateLater(el, expression)((value) => result = value, extras);
    return result;
  }
  function evaluateLater(...args) {
    return theEvaluatorFunction(...args);
  }
  var theEvaluatorFunction = () => {
  };
  function setEvaluator(newEvaluator) {
    theEvaluatorFunction = newEvaluator;
  }
  var theRawEvaluatorFunction;
  function setRawEvaluator(newEvaluator) {
    theRawEvaluatorFunction = newEvaluator;
  }
  function normalEvaluator(el, expression) {
    let overriddenMagics = {};
    injectMagics(overriddenMagics, el);
    let dataStack = [overriddenMagics, ...closestDataStack(el)];
    let evaluator = typeof expression === "function" ? generateEvaluatorFromFunction(dataStack, expression) : generateEvaluatorFromString(dataStack, expression, el);
    return tryCatch.bind(null, el, expression, evaluator);
  }
  function generateEvaluatorFromFunction(dataStack, func) {
    return (receiver = () => {
    }, { scope: scope2 = {}, params = [], context } = {}) => {
      if (!shouldAutoEvaluateFunctions) {
        runIfTypeOfFunction(receiver, func, mergeProxies([scope2, ...dataStack]), params);
        return;
      }
      let result = func.apply(mergeProxies([scope2, ...dataStack]), params);
      runIfTypeOfFunction(receiver, result);
    };
  }
  var evaluatorMemo = {};
  function generateFunctionFromString(expression, el) {
    if (evaluatorMemo[expression]) {
      return evaluatorMemo[expression];
    }
    let AsyncFunction = Object.getPrototypeOf(async function() {
    }).constructor;
    let rightSideSafeExpression = /^[\n\s]*if.*\(.*\)/.test(expression.trim()) || /^(let|const)\s/.test(expression.trim()) ? `(async()=>{ ${expression} })()` : expression;
    const safeAsyncFunction = () => {
      try {
        let func2 = new AsyncFunction(
          ["__self", "scope"],
          `with (scope) { __self.result = ${rightSideSafeExpression} }; __self.finished = true; return __self.result;`
        );
        Object.defineProperty(func2, "name", {
          value: `[Alpine] ${expression}`
        });
        return func2;
      } catch (error2) {
        handleError(error2, el, expression);
        return Promise.resolve();
      }
    };
    let func = safeAsyncFunction();
    evaluatorMemo[expression] = func;
    return func;
  }
  function generateEvaluatorFromString(dataStack, expression, el) {
    let func = generateFunctionFromString(expression, el);
    return (receiver = () => {
    }, { scope: scope2 = {}, params = [], context } = {}) => {
      func.result = void 0;
      func.finished = false;
      let completeScope = mergeProxies([scope2, ...dataStack]);
      if (typeof func === "function") {
        let promise = func.call(context, func, completeScope).catch((error2) => handleError(error2, el, expression));
        if (func.finished) {
          runIfTypeOfFunction(receiver, func.result, completeScope, params, el);
          func.result = void 0;
        } else {
          promise.then((result) => {
            runIfTypeOfFunction(receiver, result, completeScope, params, el);
          }).catch((error2) => handleError(error2, el, expression)).finally(() => func.result = void 0);
        }
      }
    };
  }
  function runIfTypeOfFunction(receiver, value, scope2, params, el) {
    if (shouldAutoEvaluateFunctions && typeof value === "function") {
      let result = value.apply(scope2, params);
      if (result instanceof Promise) {
        result.then((i) => runIfTypeOfFunction(receiver, i, scope2, params)).catch((error2) => handleError(error2, el, value));
      } else {
        receiver(result);
      }
    } else if (typeof value === "object" && value instanceof Promise) {
      value.then((i) => receiver(i));
    } else {
      receiver(value);
    }
  }
  function evaluateRaw(...args) {
    return theRawEvaluatorFunction(...args);
  }
  function normalRawEvaluator(el, expression, extras = {}) {
    let overriddenMagics = {};
    injectMagics(overriddenMagics, el);
    let dataStack = [overriddenMagics, ...closestDataStack(el)];
    let scope2 = mergeProxies([extras.scope ?? {}, ...dataStack]);
    let params = extras.params ?? [];
    if (expression.includes("await")) {
      let AsyncFunction = Object.getPrototypeOf(async function() {
      }).constructor;
      let rightSideSafeExpression = /^[\n\s]*if.*\(.*\)/.test(expression.trim()) || /^(let|const)\s/.test(expression.trim()) ? `(async()=>{ ${expression} })()` : expression;
      let func = new AsyncFunction(
        ["scope"],
        `with (scope) { let __result = ${rightSideSafeExpression}; return __result }`
      );
      let result = func.call(extras.context, scope2);
      return result;
    } else {
      let rightSideSafeExpression = /^[\n\s]*if.*\(.*\)/.test(expression.trim()) || /^(let|const)\s/.test(expression.trim()) ? `(()=>{ ${expression} })()` : expression;
      let func = new Function(
        ["scope"],
        `with (scope) { let __result = ${rightSideSafeExpression}; return __result }`
      );
      let result = func.call(extras.context, scope2);
      if (typeof result === "function" && shouldAutoEvaluateFunctions) {
        return result.apply(scope2, params);
      }
      return result;
    }
  }
  var prefixAsString = "x-";
  function prefix(subject = "") {
    return prefixAsString + subject;
  }
  function setPrefix(newPrefix) {
    prefixAsString = newPrefix;
  }
  var directiveHandlers = {};
  function directive(name, callback) {
    directiveHandlers[name] = callback;
    return {
      before(directive2) {
        if (!directiveHandlers[directive2]) {
          console.warn(String.raw`Cannot find directive \`${directive2}\`. \`${name}\` will use the default order of execution`);
          return;
        }
        const pos = directiveOrder.indexOf(directive2);
        directiveOrder.splice(pos >= 0 ? pos : directiveOrder.indexOf("DEFAULT"), 0, name);
      }
    };
  }
  function directiveExists(name) {
    return Object.keys(directiveHandlers).includes(name);
  }
  function directives(el, attributes, originalAttributeOverride) {
    attributes = Array.from(attributes);
    if (el._x_virtualDirectives) {
      let vAttributes = Object.entries(el._x_virtualDirectives).map(([name, value]) => ({ name, value }));
      let staticAttributes = attributesOnly(vAttributes);
      vAttributes = vAttributes.map((attribute) => {
        if (staticAttributes.find((attr) => attr.name === attribute.name)) {
          return {
            name: `x-bind:${attribute.name}`,
            value: `"${attribute.value}"`
          };
        }
        return attribute;
      });
      attributes = attributes.concat(vAttributes);
    }
    let transformedAttributeMap = {};
    let directives2 = attributes.map(toTransformedAttributes((newName, oldName) => transformedAttributeMap[newName] = oldName)).filter(outNonAlpineAttributes).map(toParsedDirectives(transformedAttributeMap, originalAttributeOverride)).sort(byPriority);
    return directives2.map((directive2) => {
      return getDirectiveHandler(el, directive2);
    });
  }
  function attributesOnly(attributes) {
    return Array.from(attributes).map(toTransformedAttributes()).filter((attr) => !outNonAlpineAttributes(attr));
  }
  var isDeferringHandlers = false;
  var directiveHandlerStacks = /* @__PURE__ */ new Map();
  var currentHandlerStackKey = Symbol();
  function deferHandlingDirectives(callback) {
    isDeferringHandlers = true;
    let key = Symbol();
    currentHandlerStackKey = key;
    directiveHandlerStacks.set(key, []);
    let flushHandlers = () => {
      while (directiveHandlerStacks.get(key).length)
        directiveHandlerStacks.get(key).shift()();
      directiveHandlerStacks.delete(key);
    };
    let stopDeferring = () => {
      isDeferringHandlers = false;
      flushHandlers();
    };
    callback(flushHandlers);
    stopDeferring();
  }
  function getElementBoundUtilities(el) {
    let cleanups = [];
    let cleanup = (callback) => cleanups.push(callback);
    let [effect3, cleanupEffect2] = elementBoundEffect(el);
    cleanups.push(cleanupEffect2);
    let utilities = {
      Alpine: alpine_default,
      effect: effect3,
      cleanup,
      evaluateLater: evaluateLater.bind(evaluateLater, el),
      evaluate: evaluate.bind(evaluate, el)
    };
    let doCleanup = () => cleanups.forEach((i) => i());
    return [utilities, doCleanup];
  }
  function getDirectiveHandler(el, directive2) {
    let noop3 = () => {
    };
    let handler4 = directiveHandlers[directive2.type] || noop3;
    let [utilities, cleanup] = getElementBoundUtilities(el);
    onAttributeRemoved(el, directive2.original, cleanup);
    let fullHandler = () => {
      if (el._x_ignore || el._x_ignoreSelf)
        return;
      handler4.inline && handler4.inline(el, directive2, utilities);
      handler4 = handler4.bind(handler4, el, directive2, utilities);
      isDeferringHandlers ? directiveHandlerStacks.get(currentHandlerStackKey).push(handler4) : handler4();
    };
    fullHandler.runCleanups = cleanup;
    return fullHandler;
  }
  var startingWith = (subject, replacement) => ({ name, value }) => {
    if (name.startsWith(subject))
      name = name.replace(subject, replacement);
    return { name, value };
  };
  var into = (i) => i;
  function toTransformedAttributes(callback = () => {
  }) {
    return ({ name, value }) => {
      let { name: newName, value: newValue } = attributeTransformers.reduce((carry, transform) => {
        return transform(carry);
      }, { name, value });
      if (newName !== name)
        callback(newName, name);
      return { name: newName, value: newValue };
    };
  }
  var attributeTransformers = [];
  function mapAttributes(callback) {
    attributeTransformers.push(callback);
  }
  function outNonAlpineAttributes({ name }) {
    return alpineAttributeRegex().test(name);
  }
  var alpineAttributeRegex = () => new RegExp(`^${prefixAsString}([^:^.]+)\\b`);
  function toParsedDirectives(transformedAttributeMap, originalAttributeOverride) {
    return ({ name, value }) => {
      if (name === value)
        value = "";
      let typeMatch = name.match(alpineAttributeRegex());
      let valueMatch = name.match(/:([a-zA-Z0-9\-_:]+)/);
      let modifiers = name.match(/\.[^.\]]+(?=[^\]]*$)/g) || [];
      let original = originalAttributeOverride || transformedAttributeMap[name] || name;
      return {
        type: typeMatch ? typeMatch[1] : null,
        value: valueMatch ? valueMatch[1] : null,
        modifiers: modifiers.map((i) => i.replace(".", "")),
        expression: value,
        original
      };
    };
  }
  var DEFAULT = "DEFAULT";
  var directiveOrder = [
    "ignore",
    "ref",
    "id",
    "data",
    "anchor",
    "bind",
    "init",
    "for",
    "model",
    "modelable",
    "transition",
    "show",
    "if",
    DEFAULT,
    "teleport"
  ];
  function byPriority(a, b) {
    let typeA = directiveOrder.indexOf(a.type) === -1 ? DEFAULT : a.type;
    let typeB = directiveOrder.indexOf(b.type) === -1 ? DEFAULT : b.type;
    return directiveOrder.indexOf(typeA) - directiveOrder.indexOf(typeB);
  }
  function walk(el, callback) {
    if (typeof ShadowRoot === "function" && el instanceof ShadowRoot) {
      Array.from(el.children).forEach((el2) => walk(el2, callback));
      return;
    }
    let skip = false;
    callback(el, () => skip = true);
    if (skip)
      return;
    let node = el.firstElementChild;
    while (node) {
      walk(node, callback, false);
      node = node.nextElementSibling;
    }
  }
  var isCloning = false;
  function skipDuringClone(callback, fallback = () => {
  }) {
    return (...args) => isCloning ? fallback(...args) : callback(...args);
  }
  function onlyDuringClone(callback) {
    return (...args) => isCloning && callback(...args);
  }
  var interceptors = [];
  function interceptClone(callback) {
    interceptors.push(callback);
  }
  function cloneNode(from, to) {
    interceptors.forEach((i) => i(from, to));
    isCloning = true;
    dontRegisterReactiveSideEffects(() => {
      initTree(to, (el, callback) => {
        callback(el, () => {
        });
      });
    });
    isCloning = false;
  }
  var isCloningLegacy = false;
  function clone(oldEl, newEl) {
    if (!newEl._x_dataStack)
      newEl._x_dataStack = oldEl._x_dataStack;
    isCloning = true;
    isCloningLegacy = true;
    dontRegisterReactiveSideEffects(() => {
      cloneTree(newEl);
    });
    isCloning = false;
    isCloningLegacy = false;
  }
  function cloneTree(el) {
    let hasRunThroughFirstEl = false;
    let shallowWalker = (el2, callback) => {
      walk(el2, (el3, skip) => {
        if (hasRunThroughFirstEl && isRoot(el3))
          return skip();
        hasRunThroughFirstEl = true;
        callback(el3, skip);
      });
    };
    initTree(el, shallowWalker);
  }
  function dontRegisterReactiveSideEffects(callback) {
    let cache = effect;
    overrideEffect((callback2, el) => {
      let storedEffect = cache(callback2);
      release(storedEffect);
      return () => {
      };
    });
    callback();
    overrideEffect(cache);
  }
  var activeDefers = 0;
  function deferInit(el, promise) {
    let record = el._x_deferInit;
    if (!record) {
      record = el._x_deferInit = {
        pending: 0,
        ownsIgnore: !el._x_ignore,
        queuedAttributes: /* @__PURE__ */ new Map()
      };
      if (record.ownsIgnore)
        el._x_ignore = true;
      activeDefers++;
    }
    record.pending++;
    Promise.resolve(promise).catch((error2) => {
      try {
        handleError(error2, el);
      } catch (error3) {
        setTimeout(() => {
          throw error3;
        }, 0);
      }
    }).then(() => settle(el, record));
  }
  function settle(el, record) {
    record.pending--;
    if (record.pending > 0)
      return;
    flushPendingMutations();
    if (record.pending > 0)
      return;
    if (el._x_deferInit !== record)
      return;
    delete el._x_deferInit;
    if (record.ownsIgnore)
      delete el._x_ignore;
    activeDefers--;
    if (!el.isConnected)
      return;
    replayQueuedAttributes(record);
    initTree(el);
  }
  function queueAttributesForDeferredTree(el, attrs) {
    if (activeDefers === 0)
      return false;
    let root = findClosest(el, (i) => i._x_deferInit);
    if (!root)
      return false;
    queueInto(root._x_deferInit, el, attrs.map(({ name }) => name));
    return true;
  }
  function queueInto(record, el, names) {
    let entry = record.queuedAttributes.get(el);
    if (!entry || entry.marker !== el._x_marker) {
      entry = { marker: el._x_marker, names: /* @__PURE__ */ new Set() };
      record.queuedAttributes.set(el, entry);
    }
    names.forEach((name) => entry.names.add(name));
  }
  function replayQueuedAttributes(record) {
    record.queuedAttributes.forEach((entry, el) => {
      if (!el.isConnected)
        return;
      if (!el._x_marker)
        return;
      if (el._x_marker !== entry.marker)
        return;
      let suspendedAncestor = findClosest(el, (i) => i._x_deferInit);
      if (suspendedAncestor) {
        queueInto(suspendedAncestor._x_deferInit, el, Array.from(entry.names));
        return;
      }
      let attrs = Array.from(entry.names).filter((name) => el.hasAttribute(name)).map((name) => ({ name, value: el.getAttribute(name) }));
      if (attrs.length === 0)
        return;
      directives(el, attrs).forEach((handle) => handle());
    });
  }
  interceptClone((from, to) => {
    if (activeDefers === 0)
      return;
    if (!from || from.nodeType !== 1 || !to || to.nodeType !== 1)
      return;
    if (findClosest(from, (i) => i._x_deferInit))
      to._x_ignore = true;
  });
  function dispatch(el, name, detail = {}, options = {}) {
    return el.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        // Allows events to pass the shadow DOM barrier.
        composed: true,
        cancelable: true,
        // Allows overriding the default event options.
        ...options
      })
    );
  }
  function warn(message, ...args) {
    console.warn(`Alpine Warning: ${message}`, ...args);
  }
  var started = false;
  function start() {
    if (started)
      warn("Alpine has already been initialized on this page. Calling Alpine.start() more than once can cause problems.");
    started = true;
    if (!document.body)
      warn("Unable to initialize. Trying to load Alpine before `<body>` is available. Did you forget to add `defer` in Alpine's `<script>` tag?");
    dispatch(document, "alpine:init");
    dispatch(document, "alpine:initializing");
    startObservingMutations();
    onElAdded((el) => initTree(el, walk));
    onElRemoved((el) => destroyTree(el));
    onAttributesAdded((el, attrs) => {
      if (queueAttributesForDeferredTree(el, attrs))
        return;
      directives(el, attrs).forEach((handle) => handle());
    });
    let outNestedComponents = (el) => !closestRoot(el.parentElement, true);
    Array.from(document.querySelectorAll(allSelectors().join(","))).filter(outNestedComponents).forEach((el) => {
      initTree(el);
    });
    dispatch(document, "alpine:initialized");
    setTimeout(() => {
      warnAboutMissingPlugins();
    });
  }
  var rootSelectorCallbacks = [];
  var initSelectorCallbacks = [];
  function rootSelectors() {
    return rootSelectorCallbacks.map((fn) => fn());
  }
  function allSelectors() {
    return rootSelectorCallbacks.concat(initSelectorCallbacks).map((fn) => fn());
  }
  function addRootSelector(selectorCallback) {
    rootSelectorCallbacks.push(selectorCallback);
  }
  function addInitSelector(selectorCallback) {
    initSelectorCallbacks.push(selectorCallback);
  }
  function closestRoot(el, includeInitSelectors = false) {
    return findClosest(el, (element) => {
      const selectors = includeInitSelectors ? allSelectors() : rootSelectors();
      if (selectors.some((selector) => element.matches(selector)))
        return true;
    });
  }
  function findClosest(el, callback) {
    if (!el)
      return;
    if (callback(el))
      return el;
    if (el._x_teleportBack)
      return findClosest(el._x_teleportBack, callback);
    if (el.parentNode instanceof ShadowRoot) {
      return findClosest(el.parentNode.host, callback);
    }
    if (!el.parentElement)
      return;
    return findClosest(el.parentElement, callback);
  }
  function isRoot(el) {
    return rootSelectors().some((selector) => el.matches(selector));
  }
  var initInterceptors2 = [];
  function interceptInit(callback) {
    initInterceptors2.push(callback);
  }
  var markerDispenser = 1;
  function initTree(el, walker = walk, intercept = () => {
  }) {
    if (findClosest(el, (i) => i._x_ignore))
      return;
    deferHandlingDirectives(() => {
      walker(el, (el2, skip) => {
        if (el2._x_marker)
          return;
        intercept(el2, skip);
        initInterceptors2.forEach((i) => i(el2, skip));
        directives(el2, el2.attributes).forEach((handle) => handle());
        if (!el2._x_ignore)
          el2._x_marker = markerDispenser++;
        el2._x_ignore && skip();
      });
    });
  }
  function destroyTree(root, walker = walk) {
    walker(root, (el) => {
      cleanupElement(el);
      cleanupAttributes(el);
      delete el._x_marker;
    });
  }
  function warnAboutMissingPlugins() {
    let pluginDirectives = [
      ["ui", "dialog", ["[x-dialog], [x-popover]"]],
      ["anchor", "anchor", ["[x-anchor]"]],
      ["sort", "sort", ["[x-sort]"]]
    ];
    pluginDirectives.forEach(([plugin2, directive2, selectors]) => {
      if (directiveExists(directive2))
        return;
      selectors.some((selector) => {
        if (document.querySelector(selector)) {
          warn(`found "${selector}", but missing ${plugin2} plugin`);
          return true;
        }
      });
    });
  }
  var tickStack = [];
  var isHolding = false;
  function nextTick(callback = () => {
  }) {
    queueMicrotask(() => {
      isHolding || setTimeout(() => {
        releaseNextTicks();
      });
    });
    return new Promise((res) => {
      tickStack.push(() => {
        callback();
        res();
      });
    });
  }
  function releaseNextTicks() {
    isHolding = false;
    while (tickStack.length)
      tickStack.shift()();
  }
  function holdNextTicks() {
    isHolding = true;
  }
  function setClasses(el, value) {
    if (Array.isArray(value)) {
      return setClassesFromString(el, value.join(" "));
    } else if (typeof value === "object" && value !== null) {
      return setClassesFromObject(el, value);
    } else if (typeof value === "function") {
      return setClasses(el, value());
    }
    return setClassesFromString(el, value);
  }
  function splitClasses(classString) {
    return classString.split(/\s/).filter(Boolean);
  }
  function setClassesFromString(el, classString) {
    let missingClasses = (classString2) => splitClasses(classString2).filter((i) => !el.classList.contains(i)).filter(Boolean);
    let addClassesAndReturnUndo = (classes) => {
      el.classList.add(...classes);
      return () => {
        el.classList.remove(...classes);
      };
    };
    classString = classString === true ? classString = "" : classString || "";
    return addClassesAndReturnUndo(missingClasses(classString));
  }
  function setClassesFromObject(el, classObject) {
    let forAdd = Object.entries(classObject).flatMap(([classString, bool]) => bool ? splitClasses(classString) : false).filter(Boolean);
    let forRemove = Object.entries(classObject).flatMap(([classString, bool]) => !bool ? splitClasses(classString) : false).filter(Boolean);
    let added = [];
    let removed = [];
    forRemove.forEach((i) => {
      if (el.classList.contains(i)) {
        el.classList.remove(i);
        removed.push(i);
      }
    });
    forAdd.forEach((i) => {
      if (!el.classList.contains(i)) {
        el.classList.add(i);
        added.push(i);
      }
    });
    return () => {
      removed.forEach((i) => el.classList.add(i));
      added.forEach((i) => el.classList.remove(i));
    };
  }
  function setStyles(el, value) {
    if (typeof value === "object" && value !== null) {
      return setStylesFromObject(el, value);
    }
    return setStylesFromString(el, value);
  }
  function setStylesFromObject(el, value) {
    let previousStyles = {};
    Object.entries(value).forEach(([key, value2]) => {
      previousStyles[key] = el.style[key];
      if (!key.startsWith("--")) {
        key = kebabCase(key);
      }
      el.style.setProperty(key, value2);
    });
    setTimeout(() => {
      if (el.style.length === 0) {
        el.removeAttribute("style");
      }
    });
    return () => {
      setStyles(el, previousStyles);
    };
  }
  function setStylesFromString(el, value) {
    let cache = el.getAttribute("style", value);
    el.setAttribute("style", value);
    return () => {
      el.setAttribute("style", cache || "");
    };
  }
  function kebabCase(subject) {
    return subject.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
  }
  function once(callback, fallback = () => {
  }) {
    let called = false;
    return function() {
      if (!called) {
        called = true;
        callback.apply(this, arguments);
      } else {
        fallback.apply(this, arguments);
      }
    };
  }
  directive("transition", (el, { value, modifiers, expression }, { evaluate: evaluate2 }) => {
    if (typeof expression === "function")
      expression = evaluate2(expression);
    if (expression === false)
      return;
    if (!expression || typeof expression === "boolean") {
      registerTransitionsFromHelper(el, modifiers, value);
    } else {
      registerTransitionsFromClassString(el, expression, value);
    }
  });
  function registerTransitionsFromClassString(el, classString, stage) {
    registerTransitionObject(el, setClasses, "");
    let directiveStorageMap = {
      "enter": (classes) => {
        el._x_transition.enter.during = classes;
      },
      "enter-start": (classes) => {
        el._x_transition.enter.start = classes;
      },
      "enter-end": (classes) => {
        el._x_transition.enter.end = classes;
      },
      "leave": (classes) => {
        el._x_transition.leave.during = classes;
      },
      "leave-start": (classes) => {
        el._x_transition.leave.start = classes;
      },
      "leave-end": (classes) => {
        el._x_transition.leave.end = classes;
      }
    };
    directiveStorageMap[stage](classString);
  }
  function registerTransitionsFromHelper(el, modifiers, stage) {
    registerTransitionObject(el, setStyles);
    let doesntSpecify = !modifiers.includes("in") && !modifiers.includes("out") && !stage;
    let transitioningIn = doesntSpecify || modifiers.includes("in") || ["enter"].includes(stage);
    let transitioningOut = doesntSpecify || modifiers.includes("out") || ["leave"].includes(stage);
    if (modifiers.includes("in") && !doesntSpecify) {
      modifiers = modifiers.filter((i, index) => index < modifiers.indexOf("out"));
    }
    if (modifiers.includes("out") && !doesntSpecify) {
      modifiers = modifiers.filter((i, index) => index > modifiers.indexOf("out"));
    }
    let wantsAll = !modifiers.includes("opacity") && !modifiers.includes("scale");
    let wantsOpacity = wantsAll || modifiers.includes("opacity");
    let wantsScale = wantsAll || modifiers.includes("scale");
    let opacityValue = wantsOpacity ? 0 : 1;
    let scaleValue = wantsScale ? modifierValue(modifiers, "scale", 95) / 100 : 1;
    let delay = modifierValue(modifiers, "delay", 0) / 1e3;
    let origin = modifierValue(modifiers, "origin", "center");
    let property = "opacity, transform";
    let durationIn = modifierValue(modifiers, "duration", 150) / 1e3;
    let durationOut = modifierValue(modifiers, "duration", 75) / 1e3;
    let easing = `cubic-bezier(0.4, 0.0, 0.2, 1)`;
    if (transitioningIn) {
      el._x_transition.enter.during = {
        transformOrigin: origin,
        transitionDelay: `${delay}s`,
        transitionProperty: property,
        transitionDuration: `${durationIn}s`,
        transitionTimingFunction: easing
      };
      el._x_transition.enter.start = {
        opacity: opacityValue,
        transform: `scale(${scaleValue})`
      };
      el._x_transition.enter.end = {
        opacity: 1,
        transform: `scale(1)`
      };
    }
    if (transitioningOut) {
      el._x_transition.leave.during = {
        transformOrigin: origin,
        transitionDelay: `${delay}s`,
        transitionProperty: property,
        transitionDuration: `${durationOut}s`,
        transitionTimingFunction: easing
      };
      el._x_transition.leave.start = {
        opacity: 1,
        transform: `scale(1)`
      };
      el._x_transition.leave.end = {
        opacity: opacityValue,
        transform: `scale(${scaleValue})`
      };
    }
  }
  function registerTransitionObject(el, setFunction, defaultValue = {}) {
    if (!el._x_transition)
      el._x_transition = {
        enter: { during: defaultValue, start: defaultValue, end: defaultValue },
        leave: { during: defaultValue, start: defaultValue, end: defaultValue },
        in(before2 = () => {
        }, after = () => {
        }) {
          transition(el, setFunction, {
            during: this.enter.during,
            start: this.enter.start,
            end: this.enter.end
          }, before2, after);
        },
        out(before2 = () => {
        }, after = () => {
        }) {
          transition(el, setFunction, {
            during: this.leave.during,
            start: this.leave.start,
            end: this.leave.end
          }, before2, after);
        }
      };
  }
  window.Element.prototype._x_toggleAndCascadeWithTransitions = function(el, value, show, hide) {
    const nextTick22 = document.visibilityState === "visible" ? requestAnimationFrame : setTimeout;
    let clickAwayCompatibleShow = () => nextTick22(show);
    if (value) {
      if (el._x_transition && (el._x_transition.enter || el._x_transition.leave)) {
        el._x_transition.enter && (Object.entries(el._x_transition.enter.during).length || Object.entries(el._x_transition.enter.start).length || Object.entries(el._x_transition.enter.end).length) ? el._x_transition.in(show) : clickAwayCompatibleShow();
      } else {
        el._x_transition ? el._x_transition.in(show) : clickAwayCompatibleShow();
      }
      return;
    }
    el._x_hidePromise = el._x_transition ? new Promise((resolve, reject) => {
      el._x_transition.out(() => {
      }, () => resolve(hide));
      el._x_transitioning && el._x_transitioning.beforeCancel(() => reject({ isFromCancelledTransition: true }));
    }) : Promise.resolve(hide);
    queueMicrotask(() => {
      let closest2 = closestHide(el);
      if (closest2) {
        if (!closest2._x_hideChildren)
          closest2._x_hideChildren = [];
        closest2._x_hideChildren.push(el);
      } else {
        nextTick22(() => {
          let hideAfterChildren = (el2) => {
            let carry = Promise.all([
              el2._x_hidePromise,
              ...(el2._x_hideChildren || []).map(hideAfterChildren)
            ]).then(([i]) => i?.());
            delete el2._x_hidePromise;
            delete el2._x_hideChildren;
            return carry;
          };
          hideAfterChildren(el).catch((e) => {
            if (!e.isFromCancelledTransition)
              throw e;
          });
        });
      }
    });
  };
  function closestHide(el) {
    let parent = el.parentNode;
    if (!parent)
      return;
    return parent._x_hidePromise ? parent : closestHide(parent);
  }
  function transition(el, setFunction, { during, start: start2, end } = {}, before2 = () => {
  }, after = () => {
  }) {
    if (el._x_transitioning)
      el._x_transitioning.cancel();
    if (Object.keys(during).length === 0 && Object.keys(start2).length === 0 && Object.keys(end).length === 0) {
      before2();
      after();
      return;
    }
    let undoStart, undoDuring, undoEnd;
    performTransition(el, {
      start() {
        undoStart = setFunction(el, start2);
      },
      during() {
        undoDuring = setFunction(el, during);
      },
      before: before2,
      end() {
        undoStart();
        undoEnd = setFunction(el, end);
      },
      after,
      cleanup() {
        undoDuring();
        undoEnd();
      }
    });
  }
  function performTransition(el, stages) {
    let interrupted, reachedBefore, reachedEnd;
    let finish = once(() => {
      mutateDom(() => {
        interrupted = true;
        if (!reachedBefore)
          stages.before();
        if (!reachedEnd) {
          stages.end();
          releaseNextTicks();
        }
        stages.after();
        if (el.isConnected)
          stages.cleanup();
        delete el._x_transitioning;
      });
    });
    el._x_transitioning = {
      beforeCancels: [],
      beforeCancel(callback) {
        this.beforeCancels.push(callback);
      },
      cancel: once(function() {
        while (this.beforeCancels.length) {
          this.beforeCancels.shift()();
        }
        ;
        finish();
      }),
      finish
    };
    mutateDom(() => {
      stages.start();
      stages.during();
    });
    holdNextTicks();
    requestAnimationFrame(() => {
      if (interrupted)
        return;
      let duration = Number(getComputedStyle(el).transitionDuration.replace(/,.*/, "").replace("s", "")) * 1e3;
      let delay = Number(getComputedStyle(el).transitionDelay.replace(/,.*/, "").replace("s", "")) * 1e3;
      if (duration === 0)
        duration = Number(getComputedStyle(el).animationDuration.replace("s", "")) * 1e3;
      mutateDom(() => {
        stages.before();
      });
      reachedBefore = true;
      requestAnimationFrame(() => {
        if (interrupted)
          return;
        mutateDom(() => {
          stages.end();
        });
        releaseNextTicks();
        setTimeout(el._x_transitioning.finish, duration + delay);
        reachedEnd = true;
      });
    });
  }
  function modifierValue(modifiers, key, fallback) {
    if (modifiers.indexOf(key) === -1)
      return fallback;
    const rawValue = modifiers[modifiers.indexOf(key) + 1];
    if (!rawValue)
      return fallback;
    if (key === "scale") {
      if (isNaN(rawValue))
        return fallback;
    }
    if (key === "duration" || key === "delay") {
      let match = rawValue.match(/([0-9]+)ms/);
      if (match)
        return match[1];
    }
    if (key === "origin") {
      if (["top", "right", "left", "center", "bottom"].includes(modifiers[modifiers.indexOf(key) + 2])) {
        return [rawValue, modifiers[modifiers.indexOf(key) + 2]].join(" ");
      }
    }
    return rawValue;
  }
  function bind(el, name, value, modifiers = []) {
    if (!el._x_bindings)
      el._x_bindings = reactive({});
    el._x_bindings[name] = value;
    name = modifiers.includes("camel") ? camelCase(name) : name;
    switch (name) {
      case "value":
        bindInputValue(el, value);
        break;
      case "style":
        bindStyles(el, value);
        break;
      case "class":
        bindClasses(el, value);
        break;
      case "selected":
      case "checked":
        bindAttributeAndProperty(el, name, value);
        break;
      default:
        bindAttribute(el, name, value);
        break;
    }
  }
  function bindInputValue(el, value) {
    if (isRadio(el)) {
      if (el.attributes.value === void 0) {
        el.value = value;
      }
    } else if (isCheckbox(el)) {
      if (Number.isInteger(value)) {
        el.value = value;
      } else if (!Array.isArray(value) && typeof value !== "boolean" && ![null, void 0].includes(value)) {
        el.value = String(value);
      } else {
        if (Array.isArray(value)) {
          el.checked = value.some((val) => checkedAttrLooseCompare(val, el.value));
        } else {
          el.checked = !!value;
        }
      }
    } else if (el.tagName === "SELECT") {
      updateSelect(el, value);
    } else if (el.tagName === "OPTION") {
      bindAttribute(el, "value", value);
    } else {
      if (el.value === value && (typeof value !== "object" || value === null))
        return;
      el.value = value === void 0 ? "" : value;
    }
  }
  function bindClasses(el, value) {
    if (el._x_undoAddedClasses)
      el._x_undoAddedClasses();
    el._x_undoAddedClasses = setClasses(el, value);
  }
  function bindStyles(el, value) {
    if (el._x_undoAddedStyles)
      el._x_undoAddedStyles();
    el._x_undoAddedStyles = setStyles(el, value);
  }
  function bindAttributeAndProperty(el, name, value) {
    bindAttribute(el, name, value);
    setPropertyIfChanged(el, name, value);
  }
  function bindAttribute(el, name, value) {
    if ([null, void 0, false].includes(value) && attributeShouldntBePreservedIfFalsy(name)) {
      el.removeAttribute(name);
    } else {
      if (isBooleanAttr(name))
        value = name;
      if (isObjectAttr(value))
        value = JSON.stringify(value);
      setIfChanged(el, name, value);
    }
  }
  function setIfChanged(el, attrName, value) {
    if (el.getAttribute(attrName) != value) {
      el.setAttribute(attrName, value);
    }
  }
  function setPropertyIfChanged(el, propName, value) {
    if (el[propName] !== value) {
      el[propName] = value;
    }
  }
  function updateSelect(el, value) {
    const arrayWrappedValue = [].concat(value).map((value2) => {
      return value2 + "";
    });
    Array.from(el.options).forEach((option) => {
      option.selected = arrayWrappedValue.includes(option.value);
    });
  }
  function camelCase(subject) {
    return subject.toLowerCase().replace(/-(\w)/g, (match, char) => char.toUpperCase());
  }
  function checkedAttrLooseCompare(valueA, valueB) {
    return valueA == valueB;
  }
  function safeParseBoolean(rawValue) {
    if ([1, "1", "true", "on", "yes", true].includes(rawValue)) {
      return true;
    }
    if ([0, "0", "false", "off", "no", false].includes(rawValue)) {
      return false;
    }
    return rawValue ? Boolean(rawValue) : null;
  }
  var booleanAttributes = /* @__PURE__ */ new Set([
    "allowfullscreen",
    "async",
    "autofocus",
    "autoplay",
    "checked",
    "controls",
    "default",
    "defer",
    "disabled",
    "formnovalidate",
    "inert",
    "ismap",
    "itemscope",
    "loop",
    "multiple",
    "muted",
    "nomodule",
    "novalidate",
    "open",
    "playsinline",
    "readonly",
    "required",
    "reversed",
    "selected",
    "shadowrootclonable",
    "shadowrootdelegatesfocus",
    "shadowrootserializable"
  ]);
  function isBooleanAttr(attrName) {
    return booleanAttributes.has(attrName);
  }
  function attributeShouldntBePreservedIfFalsy(name) {
    return !["aria-pressed", "aria-checked", "aria-expanded", "aria-selected"].includes(name);
  }
  function isObjectAttr(value) {
    return typeof value === "object" && value !== null;
  }
  function getBinding(el, name, fallback) {
    if (el._x_bindings && el._x_bindings[name] !== void 0)
      return el._x_bindings[name];
    return getAttributeBinding(el, name, fallback);
  }
  function extractProp(el, name, fallback, extract = true) {
    if (el._x_bindings && el._x_bindings[name] !== void 0)
      return el._x_bindings[name];
    if (el._x_inlineBindings && el._x_inlineBindings[name] !== void 0) {
      let binding = el._x_inlineBindings[name];
      binding.extract = extract;
      return dontAutoEvaluateFunctions(() => {
        return evaluate(el, binding.expression);
      });
    }
    return getAttributeBinding(el, name, fallback);
  }
  function getAttributeBinding(el, name, fallback) {
    let attr = el.getAttribute(name);
    if (attr === null)
      return typeof fallback === "function" ? fallback() : fallback;
    if (attr === "")
      return true;
    if (isBooleanAttr(name)) {
      return !![name, "true"].includes(attr);
    }
    return attr;
  }
  function isCheckbox(el) {
    return el.type === "checkbox" || el.localName === "ui-checkbox" || el.localName === "ui-switch";
  }
  function isRadio(el) {
    return el.type === "radio" || el.localName === "ui-radio";
  }
  function debounce(func, wait) {
    let timeout;
    return function() {
      const context = this, args = arguments;
      const later = function() {
        timeout = null;
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  function throttle(func, limit) {
    let inThrottle;
    return function() {
      let context = this, args = arguments;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
  function entangle({ get: outerGet, set: outerSet }, { get: innerGet, set: innerSet }) {
    let firstRun = true;
    let outerHash;
    let innerHash;
    let reference = effect(() => {
      let outer = outerGet();
      let inner = innerGet();
      if (firstRun) {
        innerSet(cloneIfObject(outer));
        firstRun = false;
      } else {
        let outerHashLatest = JSON.stringify(outer);
        let innerHashLatest = JSON.stringify(inner);
        if (outerHashLatest !== outerHash) {
          innerSet(cloneIfObject(outer));
        } else if (outerHashLatest !== innerHashLatest) {
          outerSet(cloneIfObject(inner));
        } else {
        }
      }
      outerHash = JSON.stringify(outerGet());
      innerHash = JSON.stringify(innerGet());
    });
    return () => {
      dequeueJob(reference);
      release(reference);
    };
  }
  function cloneIfObject(value) {
    return typeof value === "object" ? JSON.parse(JSON.stringify(value)) : value;
  }
  function plugin(callback) {
    let callbacks = Array.isArray(callback) ? callback : [callback];
    callbacks.forEach((i) => i(alpine_default));
  }
  var stores = {};
  var isReactive = false;
  function store(name, value) {
    if (!isReactive) {
      stores = reactive(stores);
      isReactive = true;
    }
    if (value === void 0) {
      return stores[name];
    }
    stores[name] = value;
    if (typeof value === "object" && value !== null && value._x_interceptor) {
      stores[name] = value.initialize(stores, name, name, () => {
      });
    } else {
      initInterceptors(stores[name]);
    }
    if (typeof value === "object" && value !== null && value.hasOwnProperty("init") && typeof value.init === "function") {
      stores[name].init();
    }
  }
  function getStores() {
    return stores;
  }
  var binds = {};
  function bind2(name, bindings) {
    let getBindings = typeof bindings !== "function" ? () => bindings : bindings;
    if (name instanceof Element) {
      return applyBindingsObject(name, getBindings());
    } else {
      binds[name] = getBindings;
    }
    return () => {
    };
  }
  function injectBindingProviders(obj) {
    Object.entries(binds).forEach(([name, callback]) => {
      Object.defineProperty(obj, name, {
        get() {
          return (...args) => {
            return callback(...args);
          };
        }
      });
    });
    return obj;
  }
  function applyBindingsObject(el, obj, original) {
    let cleanupRunners = [];
    while (cleanupRunners.length)
      cleanupRunners.pop()();
    let attributes = Object.entries(obj).map(([name, value]) => ({ name, value }));
    let staticAttributes = attributesOnly(attributes);
    attributes = attributes.map((attribute) => {
      if (staticAttributes.find((attr) => attr.name === attribute.name)) {
        return {
          name: `x-bind:${attribute.name}`,
          value: `"${attribute.value}"`
        };
      }
      return attribute;
    });
    directives(el, attributes, original).map((handle) => {
      cleanupRunners.push(handle.runCleanups);
      handle();
    });
    return () => {
      while (cleanupRunners.length)
        cleanupRunners.pop()();
    };
  }
  var datas = {};
  function data(name, callback) {
    datas[name] = callback;
  }
  function injectDataProviders(obj, context) {
    Object.entries(datas).forEach(([name, callback]) => {
      Object.defineProperty(obj, name, {
        get() {
          return (...args) => {
            return callback.bind(context)(...args);
          };
        },
        enumerable: false
      });
    });
    return obj;
  }
  var Alpine2 = {
    get reactive() {
      return reactive;
    },
    get release() {
      return release;
    },
    get effect() {
      return effect;
    },
    get raw() {
      return raw;
    },
    get transaction() {
      return transaction;
    },
    version: "3.17.4",
    flushAndStopDeferringMutations,
    dontAutoEvaluateFunctions,
    disableEffectScheduling,
    startObservingMutations,
    stopObservingMutations,
    setReactivityEngine,
    onAttributeRemoved,
    onAttributesAdded,
    closestDataStack,
    skipDuringClone,
    onlyDuringClone,
    addRootSelector,
    addInitSelector,
    setErrorHandler,
    interceptClone,
    addScopeToNode,
    deferMutations,
    mapAttributes,
    evaluateLater,
    interceptInit,
    initInterceptors,
    injectMagics,
    setEvaluator,
    setRawEvaluator,
    mergeProxies,
    extractProp,
    findClosest,
    onElRemoved,
    closestRoot,
    destroyTree,
    interceptor,
    // INTERNAL: not public API and is subject to change without major release.
    transition,
    // INTERNAL
    setStyles,
    // INTERNAL
    mutateDom,
    deferInit,
    directive,
    entangle,
    throttle,
    debounce,
    evaluate,
    evaluateRaw,
    initTree,
    nextTick,
    prefixed: prefix,
    prefix: setPrefix,
    plugin,
    magic,
    store,
    start,
    clone,
    // INTERNAL
    cloneNode,
    // INTERNAL
    bound: getBinding,
    $data: scope,
    watch,
    walk,
    data,
    bind: bind2
  };
  var alpine_default = Alpine2;
  function makeMap(str) {
    const map = /* @__PURE__ */ Object.create(null);
    for (const key of str.split(","))
      map[key] = 1;
    return (val) => val in map;
  }
  var EMPTY_OBJ = true ? Object.freeze({}) : {};
  var EMPTY_ARR = true ? Object.freeze([]) : [];
  var extend = Object.assign;
  var hasOwnProperty = Object.prototype.hasOwnProperty;
  var hasOwn = (val, key) => hasOwnProperty.call(val, key);
  var isArray = Array.isArray;
  var isMap = (val) => toTypeString(val) === "[object Map]";
  var isString = (val) => typeof val === "string";
  var isSymbol = (val) => typeof val === "symbol";
  var isObject = (val) => val !== null && typeof val === "object";
  var objectToString = Object.prototype.toString;
  var toTypeString = (value) => objectToString.call(value);
  var toRawType = (value) => {
    return toTypeString(value).slice(8, -1);
  };
  var isIntegerKey = (key) => isString(key) && key !== "NaN" && key[0] !== "-" && "" + parseInt(key, 10) === key;
  var cacheStringFunction = (fn) => {
    const cache = /* @__PURE__ */ Object.create(null);
    return (str) => {
      const hit = cache[str];
      return hit || (cache[str] = fn(str));
    };
  };
  var camelizeRE = /-\w/g;
  var camelize = cacheStringFunction(
    (str) => {
      return str.replace(camelizeRE, (c) => c.slice(1).toUpperCase());
    }
  );
  var hyphenateRE = /\B([A-Z])/g;
  var hyphenate = cacheStringFunction(
    (str) => str.replace(hyphenateRE, "-$1").toLowerCase()
  );
  var capitalize = cacheStringFunction((str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  });
  var toHandlerKey = cacheStringFunction(
    (str) => {
      const s = str ? `on${capitalize(str)}` : ``;
      return s;
    }
  );
  var hasChanged = (value, oldValue) => !Object.is(value, oldValue);
  var specialBooleanAttrs = `itemscope,allowfullscreen,formnovalidate,ismap,nomodule,novalidate,readonly`;
  var isBooleanAttr2 = /* @__PURE__ */ makeMap(
    specialBooleanAttrs + `,async,autofocus,autoplay,controls,default,defer,disabled,inert,loop,open,required,reversed,scoped,seamless,checked,muted,multiple,selected`
  );
  function warn2(msg, ...args) {
    console.warn(`[Vue warn] ${msg}`, ...args);
  }
  var activeEffectScope;
  var activeSub;
  var pausedQueueEffects = /* @__PURE__ */ new WeakSet();
  var ReactiveEffect = class {
    constructor(fn) {
      this.fn = fn;
      this.deps = void 0;
      this.depsTail = void 0;
      this.flags = 1 | 4;
      this.next = void 0;
      this.cleanup = void 0;
      this.scheduler = void 0;
      if (activeEffectScope) {
        if (activeEffectScope.active) {
          activeEffectScope.effects.push(this);
        } else {
          this.flags &= -2;
        }
      }
    }
    pause() {
      this.flags |= 64;
    }
    resume() {
      if (this.flags & 64) {
        this.flags &= -65;
        if (pausedQueueEffects.has(this)) {
          pausedQueueEffects.delete(this);
          this.trigger();
        }
      }
    }
    /**
     * @internal
     */
    notify() {
      if (this.flags & 2 && !(this.flags & 32)) {
        return;
      }
      if (!(this.flags & 8)) {
        batch(this);
      }
    }
    run() {
      if (!(this.flags & 1)) {
        return this.fn();
      }
      this.flags |= 2;
      cleanupEffect(this);
      prepareDeps(this);
      const prevEffect = activeSub;
      const prevShouldTrack = shouldTrack;
      activeSub = this;
      shouldTrack = true;
      try {
        return this.fn();
      } finally {
        if (activeSub !== this) {
          warn2(
            "Active effect was not restored correctly - this is likely a Vue internal bug."
          );
        }
        cleanupDeps(this);
        activeSub = prevEffect;
        shouldTrack = prevShouldTrack;
        this.flags &= -3;
      }
    }
    stop() {
      if (this.flags & 1) {
        for (let link = this.deps; link; link = link.nextDep) {
          removeSub(link);
        }
        this.deps = this.depsTail = void 0;
        cleanupEffect(this);
        this.onStop && this.onStop();
        this.flags &= -2;
      }
    }
    trigger() {
      if (this.flags & 64) {
        pausedQueueEffects.add(this);
      } else if (this.scheduler) {
        this.scheduler();
      } else {
        this.runIfDirty();
      }
    }
    /**
     * @internal
     */
    runIfDirty() {
      if (isDirty(this)) {
        this.run();
      }
    }
    get dirty() {
      return isDirty(this);
    }
  };
  var batchDepth = 0;
  var batchedSub;
  var batchedComputed;
  function batch(sub, isComputed = false) {
    sub.flags |= 8;
    if (isComputed) {
      sub.next = batchedComputed;
      batchedComputed = sub;
      return;
    }
    sub.next = batchedSub;
    batchedSub = sub;
  }
  function startBatch() {
    batchDepth++;
  }
  function endBatch() {
    if (--batchDepth > 0) {
      return;
    }
    if (batchedComputed) {
      let e = batchedComputed;
      batchedComputed = void 0;
      while (e) {
        const next = e.next;
        e.next = void 0;
        e.flags &= -9;
        e = next;
      }
    }
    let error2;
    while (batchedSub) {
      let e = batchedSub;
      batchedSub = void 0;
      while (e) {
        const next = e.next;
        e.next = void 0;
        e.flags &= -9;
        if (e.flags & 1) {
          try {
            ;
            e.trigger();
          } catch (err) {
            if (!error2)
              error2 = err;
          }
        }
        e = next;
      }
    }
    if (error2)
      throw error2;
  }
  function prepareDeps(sub) {
    for (let link = sub.deps; link; link = link.nextDep) {
      link.version = -1;
      link.prevActiveLink = link.dep.activeLink;
      link.dep.activeLink = link;
    }
  }
  function cleanupDeps(sub) {
    let head;
    let tail = sub.depsTail;
    let link = tail;
    while (link) {
      const prev = link.prevDep;
      if (link.version === -1) {
        if (link === tail)
          tail = prev;
        removeSub(link);
        removeDep(link);
      } else {
        head = link;
      }
      link.dep.activeLink = link.prevActiveLink;
      link.prevActiveLink = void 0;
      link = prev;
    }
    sub.deps = head;
    sub.depsTail = tail;
  }
  function isDirty(sub) {
    for (let link = sub.deps; link; link = link.nextDep) {
      if (link.dep.version !== link.version || link.dep.computed && (refreshComputed(link.dep.computed) || link.dep.version !== link.version)) {
        return true;
      }
    }
    if (sub._dirty) {
      return true;
    }
    return false;
  }
  function refreshComputed(computed) {
    if (computed.flags & 4 && !(computed.flags & 16)) {
      return;
    }
    computed.flags &= -17;
    if (computed.globalVersion === globalVersion) {
      return;
    }
    computed.globalVersion = globalVersion;
    if (!computed.isSSR && computed.flags & 128 && (!computed.deps && !computed._dirty || !isDirty(computed))) {
      return;
    }
    computed.flags |= 2;
    const dep = computed.dep;
    const prevSub = activeSub;
    const prevShouldTrack = shouldTrack;
    activeSub = computed;
    shouldTrack = true;
    try {
      prepareDeps(computed);
      const value = computed.fn(computed._value);
      if (dep.version === 0 || hasChanged(value, computed._value)) {
        computed.flags |= 128;
        computed._value = value;
        dep.version++;
      }
    } catch (err) {
      dep.version++;
      throw err;
    } finally {
      activeSub = prevSub;
      shouldTrack = prevShouldTrack;
      cleanupDeps(computed);
      computed.flags &= -3;
    }
  }
  function removeSub(link, soft = false) {
    const { dep, prevSub, nextSub } = link;
    if (prevSub) {
      prevSub.nextSub = nextSub;
      link.prevSub = void 0;
    }
    if (nextSub) {
      nextSub.prevSub = prevSub;
      link.nextSub = void 0;
    }
    if (dep.subsHead === link) {
      dep.subsHead = nextSub;
    }
    if (dep.subs === link) {
      dep.subs = prevSub;
      if (!prevSub && dep.computed) {
        dep.computed.flags &= -5;
        for (let l = dep.computed.deps; l; l = l.nextDep) {
          removeSub(l, true);
        }
      }
    }
    if (!soft && !--dep.sc && dep.map) {
      dep.map.delete(dep.key);
    }
  }
  function removeDep(link) {
    const { prevDep, nextDep } = link;
    if (prevDep) {
      prevDep.nextDep = nextDep;
      link.prevDep = void 0;
    }
    if (nextDep) {
      nextDep.prevDep = prevDep;
      link.nextDep = void 0;
    }
  }
  function effect2(fn, options) {
    if (fn.effect instanceof ReactiveEffect) {
      fn = fn.effect.fn;
    }
    const e = new ReactiveEffect(fn);
    if (options) {
      extend(e, options);
    }
    try {
      e.run();
    } catch (err) {
      e.stop();
      throw err;
    }
    const runner = e.run.bind(e);
    runner.effect = e;
    return runner;
  }
  function stop(runner) {
    runner.effect.stop();
  }
  var shouldTrack = true;
  var trackStack = [];
  function pauseTracking() {
    trackStack.push(shouldTrack);
    shouldTrack = false;
  }
  function resetTracking() {
    const last = trackStack.pop();
    shouldTrack = last === void 0 ? true : last;
  }
  function cleanupEffect(e) {
    const { cleanup } = e;
    e.cleanup = void 0;
    if (cleanup) {
      const prevSub = activeSub;
      activeSub = void 0;
      try {
        cleanup();
      } finally {
        activeSub = prevSub;
      }
    }
  }
  var globalVersion = 0;
  var Link = class {
    constructor(sub, dep) {
      this.sub = sub;
      this.dep = dep;
      this.version = dep.version;
      this.nextDep = this.prevDep = this.nextSub = this.prevSub = this.prevActiveLink = void 0;
    }
  };
  var Dep = class {
    // TODO isolatedDeclarations "__v_skip"
    constructor(computed) {
      this.computed = computed;
      this.version = 0;
      this.activeLink = void 0;
      this.subs = void 0;
      this.map = void 0;
      this.key = void 0;
      this.sc = 0;
      this.__v_skip = true;
      if (true) {
        this.subsHead = void 0;
      }
    }
    track(debugInfo) {
      if (!activeSub || !shouldTrack || activeSub === this.computed) {
        return;
      }
      let link = this.activeLink;
      if (link === void 0 || link.sub !== activeSub) {
        link = this.activeLink = new Link(activeSub, this);
        if (!activeSub.deps) {
          activeSub.deps = activeSub.depsTail = link;
        } else {
          link.prevDep = activeSub.depsTail;
          activeSub.depsTail.nextDep = link;
          activeSub.depsTail = link;
        }
        addSub(link);
      } else if (link.version === -1) {
        link.version = this.version;
        if (link.nextDep) {
          const next = link.nextDep;
          next.prevDep = link.prevDep;
          if (link.prevDep) {
            link.prevDep.nextDep = next;
          }
          link.prevDep = activeSub.depsTail;
          link.nextDep = void 0;
          activeSub.depsTail.nextDep = link;
          activeSub.depsTail = link;
          if (activeSub.deps === link) {
            activeSub.deps = next;
          }
        }
      }
      if (activeSub.onTrack) {
        activeSub.onTrack(
          extend(
            {
              effect: activeSub
            },
            debugInfo
          )
        );
      }
      return link;
    }
    trigger(debugInfo) {
      this.version++;
      globalVersion++;
      this.notify(debugInfo);
    }
    notify(debugInfo) {
      startBatch();
      try {
        if (true) {
          for (let head = this.subsHead; head; head = head.nextSub) {
            if (head.sub.onTrigger && !(head.sub.flags & 8)) {
              head.sub.onTrigger(
                extend(
                  {
                    effect: head.sub
                  },
                  debugInfo
                )
              );
            }
          }
        }
        for (let link = this.subs; link; link = link.prevSub) {
          if (link.sub.notify()) {
            ;
            link.sub.dep.notify();
          }
        }
      } finally {
        endBatch();
      }
    }
  };
  function addSub(link) {
    link.dep.sc++;
    if (link.sub.flags & 4) {
      const computed = link.dep.computed;
      if (computed && !link.dep.subs) {
        computed.flags |= 4 | 16;
        for (let l = computed.deps; l; l = l.nextDep) {
          addSub(l);
        }
      }
      const currentTail = link.dep.subs;
      if (currentTail !== link) {
        link.prevSub = currentTail;
        if (currentTail)
          currentTail.nextSub = link;
      }
      if (link.dep.subsHead === void 0) {
        link.dep.subsHead = link;
      }
      link.dep.subs = link;
    }
  }
  var targetMap = /* @__PURE__ */ new WeakMap();
  var ITERATE_KEY = /* @__PURE__ */ Symbol(
    true ? "Object iterate" : ""
  );
  var MAP_KEY_ITERATE_KEY = /* @__PURE__ */ Symbol(
    true ? "Map keys iterate" : ""
  );
  var ARRAY_ITERATE_KEY = /* @__PURE__ */ Symbol(
    true ? "Array iterate" : ""
  );
  function track(target, type, key) {
    if (shouldTrack && activeSub) {
      let depsMap = targetMap.get(target);
      if (!depsMap) {
        targetMap.set(target, depsMap = /* @__PURE__ */ new Map());
      }
      let dep = depsMap.get(key);
      if (!dep) {
        depsMap.set(key, dep = new Dep());
        dep.map = depsMap;
        dep.key = key;
      }
      if (true) {
        dep.track({
          target,
          type,
          key
        });
      } else {
        dep.track();
      }
    }
  }
  function trigger(target, type, key, newValue, oldValue, oldTarget) {
    const depsMap = targetMap.get(target);
    if (!depsMap) {
      globalVersion++;
      return;
    }
    const run = (dep) => {
      if (dep) {
        if (true) {
          dep.trigger({
            target,
            type,
            key,
            newValue,
            oldValue,
            oldTarget
          });
        } else {
          dep.trigger();
        }
      }
    };
    startBatch();
    if (type === "clear") {
      depsMap.forEach(run);
    } else {
      const targetIsArray = isArray(target);
      const isArrayIndex = targetIsArray && isIntegerKey(key);
      if (targetIsArray && key === "length") {
        const newLength = Number(newValue);
        depsMap.forEach((dep, key2) => {
          if (key2 === "length" || key2 === ARRAY_ITERATE_KEY || !isSymbol(key2) && key2 >= newLength) {
            run(dep);
          }
        });
      } else {
        if (key !== void 0 || depsMap.has(void 0)) {
          run(depsMap.get(key));
        }
        if (isArrayIndex) {
          run(depsMap.get(ARRAY_ITERATE_KEY));
        }
        switch (type) {
          case "add":
            if (!targetIsArray) {
              run(depsMap.get(ITERATE_KEY));
              if (isMap(target)) {
                run(depsMap.get(MAP_KEY_ITERATE_KEY));
              }
            } else if (isArrayIndex) {
              run(depsMap.get("length"));
            }
            break;
          case "delete":
            if (!targetIsArray) {
              run(depsMap.get(ITERATE_KEY));
              if (isMap(target)) {
                run(depsMap.get(MAP_KEY_ITERATE_KEY));
              }
            }
            break;
          case "set":
            if (isMap(target)) {
              run(depsMap.get(ITERATE_KEY));
            }
            break;
        }
      }
    }
    endBatch();
  }
  function reactiveReadArray(array) {
    const raw2 = toRaw(array);
    if (raw2 === array)
      return raw2;
    track(raw2, "iterate", ARRAY_ITERATE_KEY);
    return isShallow(array) ? raw2 : raw2.map(toReactive);
  }
  function shallowReadArray(arr) {
    track(arr = toRaw(arr), "iterate", ARRAY_ITERATE_KEY);
    return arr;
  }
  function toWrapped(target, item) {
    if (isReadonly(target)) {
      return isReactive2(target) ? toReadonly(toReactive(item)) : toReadonly(item);
    }
    return toReactive(item);
  }
  var arrayInstrumentations = {
    __proto__: null,
    [Symbol.iterator]() {
      return iterator(this, Symbol.iterator, (item) => toWrapped(this, item));
    },
    concat(...args) {
      return reactiveReadArray(this).concat(
        ...args.map((x) => isArray(x) ? reactiveReadArray(x) : x)
      );
    },
    entries() {
      return iterator(this, "entries", (value) => {
        value[1] = toWrapped(this, value[1]);
        return value;
      });
    },
    every(fn, thisArg) {
      return apply(this, "every", fn, thisArg, void 0, arguments);
    },
    filter(fn, thisArg) {
      return apply(
        this,
        "filter",
        fn,
        thisArg,
        (v) => v.map((item) => toWrapped(this, item)),
        arguments
      );
    },
    find(fn, thisArg) {
      return apply(
        this,
        "find",
        fn,
        thisArg,
        (item) => toWrapped(this, item),
        arguments
      );
    },
    findIndex(fn, thisArg) {
      return apply(this, "findIndex", fn, thisArg, void 0, arguments);
    },
    findLast(fn, thisArg) {
      return apply(
        this,
        "findLast",
        fn,
        thisArg,
        (item) => toWrapped(this, item),
        arguments
      );
    },
    findLastIndex(fn, thisArg) {
      return apply(this, "findLastIndex", fn, thisArg, void 0, arguments);
    },
    // flat, flatMap could benefit from ARRAY_ITERATE but are not straight-forward to implement
    forEach(fn, thisArg) {
      return apply(this, "forEach", fn, thisArg, void 0, arguments);
    },
    includes(...args) {
      return searchProxy(this, "includes", args);
    },
    indexOf(...args) {
      return searchProxy(this, "indexOf", args);
    },
    join(separator) {
      return reactiveReadArray(this).join(separator);
    },
    // keys() iterator only reads `length`, no optimization required
    lastIndexOf(...args) {
      return searchProxy(this, "lastIndexOf", args);
    },
    map(fn, thisArg) {
      return apply(this, "map", fn, thisArg, void 0, arguments);
    },
    pop() {
      return noTracking(this, "pop");
    },
    push(...args) {
      return noTracking(this, "push", args);
    },
    reduce(fn, ...args) {
      return reduce(this, "reduce", fn, args);
    },
    reduceRight(fn, ...args) {
      return reduce(this, "reduceRight", fn, args);
    },
    shift() {
      return noTracking(this, "shift");
    },
    // slice could use ARRAY_ITERATE but also seems to beg for range tracking
    some(fn, thisArg) {
      return apply(this, "some", fn, thisArg, void 0, arguments);
    },
    splice(...args) {
      return noTracking(this, "splice", args);
    },
    toReversed() {
      return reactiveReadArray(this).toReversed();
    },
    toSorted(comparer) {
      return reactiveReadArray(this).toSorted(comparer);
    },
    toSpliced(...args) {
      return reactiveReadArray(this).toSpliced(...args);
    },
    unshift(...args) {
      return noTracking(this, "unshift", args);
    },
    values() {
      return iterator(this, "values", (item) => toWrapped(this, item));
    }
  };
  function iterator(self2, method, wrapValue) {
    const arr = shallowReadArray(self2);
    const iter = arr[method]();
    if (arr !== self2 && !isShallow(self2)) {
      iter._next = iter.next;
      iter.next = () => {
        const result = iter._next();
        if (!result.done) {
          result.value = wrapValue(result.value);
        }
        return result;
      };
    }
    return iter;
  }
  var arrayProto = Array.prototype;
  function apply(self2, method, fn, thisArg, wrappedRetFn, args) {
    const arr = shallowReadArray(self2);
    const needsWrap = arr !== self2 && !isShallow(self2);
    const methodFn = arr[method];
    if (methodFn !== arrayProto[method]) {
      const result2 = methodFn.apply(self2, args);
      return needsWrap ? toReactive(result2) : result2;
    }
    let wrappedFn = fn;
    if (arr !== self2) {
      if (needsWrap) {
        wrappedFn = function(item, index) {
          return fn.call(this, toWrapped(self2, item), index, self2);
        };
      } else if (fn.length > 2) {
        wrappedFn = function(item, index) {
          return fn.call(this, item, index, self2);
        };
      }
    }
    const result = methodFn.call(arr, wrappedFn, thisArg);
    return needsWrap && wrappedRetFn ? wrappedRetFn(result) : result;
  }
  function reduce(self2, method, fn, args) {
    const arr = shallowReadArray(self2);
    const needsWrap = arr !== self2 && !isShallow(self2);
    let wrappedFn = fn;
    let wrapInitialAccumulator = false;
    if (arr !== self2) {
      if (needsWrap) {
        wrapInitialAccumulator = args.length === 0;
        wrappedFn = function(acc, item, index) {
          if (wrapInitialAccumulator) {
            wrapInitialAccumulator = false;
            acc = toWrapped(self2, acc);
          }
          return fn.call(this, acc, toWrapped(self2, item), index, self2);
        };
      } else if (fn.length > 3) {
        wrappedFn = function(acc, item, index) {
          return fn.call(this, acc, item, index, self2);
        };
      }
    }
    const result = arr[method](wrappedFn, ...args);
    return wrapInitialAccumulator ? toWrapped(self2, result) : result;
  }
  function searchProxy(self2, method, args) {
    const arr = toRaw(self2);
    track(arr, "iterate", ARRAY_ITERATE_KEY);
    const res = arr[method](...args);
    if ((res === -1 || res === false) && isProxy(args[0])) {
      args[0] = toRaw(args[0]);
      return arr[method](...args);
    }
    return res;
  }
  function noTracking(self2, method, args = []) {
    pauseTracking();
    startBatch();
    const res = toRaw(self2)[method].apply(self2, args);
    endBatch();
    resetTracking();
    return res;
  }
  var isNonTrackableKeys = /* @__PURE__ */ makeMap(`__proto__,__v_isRef,__isVue`);
  var builtInSymbols = new Set(
    /* @__PURE__ */ Object.getOwnPropertyNames(Symbol).filter((key) => key !== "arguments" && key !== "caller").map((key) => Symbol[key]).filter(isSymbol)
  );
  function hasOwnProperty2(key) {
    if (!isSymbol(key))
      key = String(key);
    const obj = toRaw(this);
    track(obj, "has", key);
    return obj.hasOwnProperty(key);
  }
  var BaseReactiveHandler = class {
    constructor(_isReadonly = false, _isShallow = false) {
      this._isReadonly = _isReadonly;
      this._isShallow = _isShallow;
    }
    get(target, key, receiver) {
      if (key === "__v_skip")
        return target["__v_skip"];
      const isReadonly2 = this._isReadonly, isShallow2 = this._isShallow;
      if (key === "__v_isReactive") {
        return !isReadonly2;
      } else if (key === "__v_isReadonly") {
        return isReadonly2;
      } else if (key === "__v_isShallow") {
        return isShallow2;
      } else if (key === "__v_raw") {
        if (receiver === (isReadonly2 ? isShallow2 ? shallowReadonlyMap : readonlyMap : isShallow2 ? shallowReactiveMap : reactiveMap).get(target) || // receiver is not the reactive proxy, but has the same prototype
        // this means the receiver is a user proxy of the reactive proxy
        Object.getPrototypeOf(target) === Object.getPrototypeOf(receiver)) {
          return target;
        }
        return;
      }
      const targetIsArray = isArray(target);
      if (!isReadonly2) {
        let fn;
        if (targetIsArray && (fn = arrayInstrumentations[key])) {
          return fn;
        }
        if (key === "hasOwnProperty") {
          return hasOwnProperty2;
        }
      }
      const res = Reflect.get(
        target,
        key,
        // if this is a proxy wrapping a ref, return methods using the raw ref
        // as receiver so that we don't have to call `toRaw` on the ref in all
        // its class methods
        isRef(target) ? target : receiver
      );
      if (isSymbol(key) ? builtInSymbols.has(key) : isNonTrackableKeys(key)) {
        return res;
      }
      if (!isReadonly2) {
        track(target, "get", key);
      }
      if (isShallow2) {
        return res;
      }
      if (isRef(res)) {
        const value = targetIsArray && isIntegerKey(key) ? res : res.value;
        return isReadonly2 && isObject(value) ? readonly(value) : value;
      }
      if (isObject(res)) {
        return isReadonly2 ? readonly(res) : reactive2(res);
      }
      return res;
    }
  };
  var MutableReactiveHandler = class extends BaseReactiveHandler {
    constructor(isShallow2 = false) {
      super(false, isShallow2);
    }
    set(target, key, value, receiver) {
      let oldValue = target[key];
      const isArrayWithIntegerKey = isArray(target) && isIntegerKey(key);
      if (!this._isShallow) {
        const isOldValueReadonly = isReadonly(oldValue);
        if (!isShallow(value) && !isReadonly(value)) {
          oldValue = toRaw(oldValue);
          value = toRaw(value);
        }
        if (!isArrayWithIntegerKey && isRef(oldValue) && !isRef(value)) {
          if (isOldValueReadonly) {
            if (true) {
              warn2(
                `Set operation on key "${String(key)}" failed: target is readonly.`,
                target[key]
              );
            }
            return true;
          } else {
            oldValue.value = value;
            return true;
          }
        }
      }
      const hadKey = isArrayWithIntegerKey ? Number(key) < target.length : hasOwn(target, key);
      const result = Reflect.set(
        target,
        key,
        value,
        isRef(target) ? target : receiver
      );
      if (target === toRaw(receiver) && result) {
        if (!hadKey) {
          trigger(target, "add", key, value);
        } else if (hasChanged(value, oldValue)) {
          trigger(target, "set", key, value, oldValue);
        }
      }
      return result;
    }
    deleteProperty(target, key) {
      const hadKey = hasOwn(target, key);
      const oldValue = target[key];
      const result = Reflect.deleteProperty(target, key);
      if (result && hadKey) {
        trigger(target, "delete", key, void 0, oldValue);
      }
      return result;
    }
    has(target, key) {
      const result = Reflect.has(target, key);
      if (!isSymbol(key) || !builtInSymbols.has(key)) {
        track(target, "has", key);
      }
      return result;
    }
    ownKeys(target) {
      track(
        target,
        "iterate",
        isArray(target) ? "length" : ITERATE_KEY
      );
      return Reflect.ownKeys(target);
    }
  };
  var ReadonlyReactiveHandler = class extends BaseReactiveHandler {
    constructor(isShallow2 = false) {
      super(true, isShallow2);
    }
    set(target, key) {
      if (true) {
        warn2(
          `Set operation on key "${String(key)}" failed: target is readonly.`,
          target
        );
      }
      return true;
    }
    deleteProperty(target, key) {
      if (true) {
        warn2(
          `Delete operation on key "${String(key)}" failed: target is readonly.`,
          target
        );
      }
      return true;
    }
  };
  var mutableHandlers = /* @__PURE__ */ new MutableReactiveHandler();
  var readonlyHandlers = /* @__PURE__ */ new ReadonlyReactiveHandler();
  var toShallow = (value) => value;
  var getProto = (v) => Reflect.getPrototypeOf(v);
  function createIterableMethod(method, isReadonly2, isShallow2) {
    return function(...args) {
      const target = this["__v_raw"];
      const rawTarget = toRaw(target);
      const targetIsMap = isMap(rawTarget);
      const isPair = method === "entries" || method === Symbol.iterator && targetIsMap;
      const isKeyOnly = method === "keys" && targetIsMap;
      const innerIterator = target[method](...args);
      const wrap = isShallow2 ? toShallow : isReadonly2 ? toReadonly : toReactive;
      !isReadonly2 && track(
        rawTarget,
        "iterate",
        isKeyOnly ? MAP_KEY_ITERATE_KEY : ITERATE_KEY
      );
      return extend(
        // inheriting all iterator properties
        Object.create(innerIterator),
        {
          // iterator protocol
          next() {
            const { value, done } = innerIterator.next();
            return done ? { value, done } : {
              value: isPair ? [wrap(value[0]), wrap(value[1])] : wrap(value),
              done
            };
          }
        }
      );
    };
  }
  function createReadonlyMethod(type) {
    return function(...args) {
      if (true) {
        const key = args[0] ? `on key "${args[0]}" ` : ``;
        warn2(
          `${capitalize(type)} operation ${key}failed: target is readonly.`,
          toRaw(this)
        );
      }
      return type === "delete" ? false : type === "clear" ? void 0 : this;
    };
  }
  function createInstrumentations(readonly2, shallow) {
    const instrumentations = {
      get(key) {
        const target = this["__v_raw"];
        const rawTarget = toRaw(target);
        const rawKey = toRaw(key);
        if (!readonly2) {
          if (hasChanged(key, rawKey)) {
            track(rawTarget, "get", key);
          }
          track(rawTarget, "get", rawKey);
        }
        const { has } = getProto(rawTarget);
        const wrap = shallow ? toShallow : readonly2 ? toReadonly : toReactive;
        if (has.call(rawTarget, key)) {
          return wrap(target.get(key));
        } else if (has.call(rawTarget, rawKey)) {
          return wrap(target.get(rawKey));
        } else if (target !== rawTarget) {
          target.get(key);
        }
      },
      get size() {
        const target = this["__v_raw"];
        !readonly2 && track(toRaw(target), "iterate", ITERATE_KEY);
        return target.size;
      },
      has(key) {
        const target = this["__v_raw"];
        const rawTarget = toRaw(target);
        const rawKey = toRaw(key);
        if (!readonly2) {
          if (hasChanged(key, rawKey)) {
            track(rawTarget, "has", key);
          }
          track(rawTarget, "has", rawKey);
        }
        return key === rawKey ? target.has(key) : target.has(key) || target.has(rawKey);
      },
      forEach(callback, thisArg) {
        const observed = this;
        const target = observed["__v_raw"];
        const rawTarget = toRaw(target);
        const wrap = shallow ? toShallow : readonly2 ? toReadonly : toReactive;
        !readonly2 && track(rawTarget, "iterate", ITERATE_KEY);
        return target.forEach((value, key) => {
          return callback.call(thisArg, wrap(value), wrap(key), observed);
        });
      }
    };
    extend(
      instrumentations,
      readonly2 ? {
        add: createReadonlyMethod("add"),
        set: createReadonlyMethod("set"),
        delete: createReadonlyMethod("delete"),
        clear: createReadonlyMethod("clear")
      } : {
        add(value) {
          const target = toRaw(this);
          const proto = getProto(target);
          const rawValue = toRaw(value);
          const valueToAdd = !shallow && !isShallow(value) && !isReadonly(value) ? rawValue : value;
          const hadKey = proto.has.call(target, valueToAdd) || hasChanged(value, valueToAdd) && proto.has.call(target, value) || hasChanged(rawValue, valueToAdd) && proto.has.call(target, rawValue);
          if (!hadKey) {
            target.add(valueToAdd);
            trigger(target, "add", valueToAdd, valueToAdd);
          }
          return this;
        },
        set(key, value) {
          if (!shallow && !isShallow(value) && !isReadonly(value)) {
            value = toRaw(value);
          }
          const target = toRaw(this);
          const { has, get: get2 } = getProto(target);
          let hadKey = has.call(target, key);
          if (!hadKey) {
            key = toRaw(key);
            hadKey = has.call(target, key);
          } else if (true) {
            checkIdentityKeys(target, has, key);
          }
          const oldValue = get2.call(target, key);
          target.set(key, value);
          if (!hadKey) {
            trigger(target, "add", key, value);
          } else if (hasChanged(value, oldValue)) {
            trigger(target, "set", key, value, oldValue);
          }
          return this;
        },
        delete(key) {
          const target = toRaw(this);
          const { has, get: get2 } = getProto(target);
          let hadKey = has.call(target, key);
          if (!hadKey) {
            key = toRaw(key);
            hadKey = has.call(target, key);
          } else if (true) {
            checkIdentityKeys(target, has, key);
          }
          const oldValue = get2 ? get2.call(target, key) : void 0;
          const result = target.delete(key);
          if (hadKey) {
            trigger(target, "delete", key, void 0, oldValue);
          }
          return result;
        },
        clear() {
          const target = toRaw(this);
          const hadItems = target.size !== 0;
          const oldTarget = true ? isMap(target) ? new Map(target) : new Set(target) : void 0;
          const result = target.clear();
          if (hadItems) {
            trigger(
              target,
              "clear",
              void 0,
              void 0,
              oldTarget
            );
          }
          return result;
        }
      }
    );
    const iteratorMethods = [
      "keys",
      "values",
      "entries",
      Symbol.iterator
    ];
    iteratorMethods.forEach((method) => {
      instrumentations[method] = createIterableMethod(method, readonly2, shallow);
    });
    return instrumentations;
  }
  function createInstrumentationGetter(isReadonly2, shallow) {
    const instrumentations = createInstrumentations(isReadonly2, shallow);
    return (target, key, receiver) => {
      if (key === "__v_isReactive") {
        return !isReadonly2;
      } else if (key === "__v_isReadonly") {
        return isReadonly2;
      } else if (key === "__v_raw") {
        return target;
      }
      return Reflect.get(
        hasOwn(instrumentations, key) && key in target ? instrumentations : target,
        key,
        receiver
      );
    };
  }
  var mutableCollectionHandlers = {
    get: /* @__PURE__ */ createInstrumentationGetter(false, false)
  };
  var readonlyCollectionHandlers = {
    get: /* @__PURE__ */ createInstrumentationGetter(true, false)
  };
  function checkIdentityKeys(target, has, key) {
    const rawKey = toRaw(key);
    if (rawKey !== key && has.call(target, rawKey)) {
      const type = toRawType(target);
      warn2(
        `Reactive ${type} contains both the raw and reactive versions of the same object${type === `Map` ? ` as keys` : ``}, which can lead to inconsistencies. Avoid differentiating between the raw and reactive versions of an object and only use the reactive version if possible.`
      );
    }
  }
  var reactiveMap = /* @__PURE__ */ new WeakMap();
  var shallowReactiveMap = /* @__PURE__ */ new WeakMap();
  var readonlyMap = /* @__PURE__ */ new WeakMap();
  var shallowReadonlyMap = /* @__PURE__ */ new WeakMap();
  function targetTypeMap(rawType) {
    switch (rawType) {
      case "Object":
      case "Array":
        return 1;
      case "Map":
      case "Set":
      case "WeakMap":
      case "WeakSet":
        return 2;
      default:
        return 0;
    }
  }
  function reactive2(target) {
    if (/* @__PURE__ */ isReadonly(target)) {
      return target;
    }
    return createReactiveObject(
      target,
      false,
      mutableHandlers,
      mutableCollectionHandlers,
      reactiveMap
    );
  }
  function readonly(target) {
    return createReactiveObject(
      target,
      true,
      readonlyHandlers,
      readonlyCollectionHandlers,
      readonlyMap
    );
  }
  function createReactiveObject(target, isReadonly2, baseHandlers, collectionHandlers, proxyMap) {
    if (!isObject(target)) {
      if (true) {
        warn2(
          `value cannot be made ${isReadonly2 ? "readonly" : "reactive"}: ${String(
            target
          )}`
        );
      }
      return target;
    }
    if (target["__v_raw"] && !(isReadonly2 && target["__v_isReactive"])) {
      return target;
    }
    if (target["__v_skip"] || !Object.isExtensible(target)) {
      return target;
    }
    const existingProxy = proxyMap.get(target);
    if (existingProxy) {
      return existingProxy;
    }
    const targetType = targetTypeMap(toRawType(target));
    if (targetType === 0) {
      return target;
    }
    const proxy = new Proxy(
      target,
      targetType === 2 ? collectionHandlers : baseHandlers
    );
    proxyMap.set(target, proxy);
    return proxy;
  }
  function isReactive2(value) {
    if (/* @__PURE__ */ isReadonly(value)) {
      return /* @__PURE__ */ isReactive2(value["__v_raw"]);
    }
    return !!(value && value["__v_isReactive"]);
  }
  function isReadonly(value) {
    return !!(value && value["__v_isReadonly"]);
  }
  function isShallow(value) {
    return !!(value && value["__v_isShallow"]);
  }
  function isProxy(value) {
    return value ? !!value["__v_raw"] : false;
  }
  function toRaw(observed) {
    const raw2 = observed && observed["__v_raw"];
    return raw2 ? /* @__PURE__ */ toRaw(raw2) : observed;
  }
  var toReactive = (value) => isObject(value) ? /* @__PURE__ */ reactive2(value) : value;
  var toReadonly = (value) => isObject(value) ? /* @__PURE__ */ readonly(value) : value;
  function isRef(r) {
    return r ? r["__v_isRef"] === true : false;
  }
  magic("nextTick", () => nextTick);
  magic("dispatch", (el) => dispatch.bind(dispatch, el));
  magic("watch", (el, { evaluateLater: evaluateLater2, cleanup }) => (key, callback) => {
    let evaluate2 = evaluateLater2(key);
    let getter = () => {
      let value;
      evaluate2((i) => value = i);
      return value;
    };
    let unwatch = watch(getter, callback);
    cleanup(unwatch);
  });
  magic("store", getStores);
  magic("data", (el) => scope(el));
  magic("root", (el) => closestRoot(el));
  magic("refs", (el) => {
    if (el._x_refs_proxy)
      return el._x_refs_proxy;
    el._x_refs_proxy = mergeProxies(getArrayOfRefObject(el));
    return el._x_refs_proxy;
  });
  function getArrayOfRefObject(el) {
    let refObjects = [];
    findClosest(el, (i) => {
      if (i._x_refs)
        refObjects.push(i._x_refs);
    });
    return refObjects;
  }
  var globalIdMemo = {};
  function findAndIncrementId(name) {
    if (!globalIdMemo[name])
      globalIdMemo[name] = 0;
    return ++globalIdMemo[name];
  }
  function closestIdRoot(el, name) {
    return findClosest(el, (element) => {
      if (element._x_ids && element._x_ids[name])
        return true;
    });
  }
  function setIdRoot(el, name) {
    if (!el._x_ids)
      el._x_ids = {};
    if (!el._x_ids[name])
      el._x_ids[name] = findAndIncrementId(name);
  }
  magic("id", (el, { cleanup }) => (name, key = null) => {
    let cacheKey = `${name}${key ? `-${key}` : ""}`;
    return cacheIdByNameOnElement(el, cacheKey, cleanup, () => {
      let root = closestIdRoot(el, name);
      let id = root ? root._x_ids[name] : findAndIncrementId(name);
      return key ? `${name}-${id}-${key}` : `${name}-${id}`;
    });
  });
  interceptClone((from, to) => {
    if (from._x_id) {
      to._x_id = from._x_id;
    }
  });
  function cacheIdByNameOnElement(el, cacheKey, cleanup, callback) {
    if (!el._x_id)
      el._x_id = {};
    if (el._x_id[cacheKey])
      return el._x_id[cacheKey];
    let output = callback();
    el._x_id[cacheKey] = output;
    cleanup(() => {
      delete el._x_id[cacheKey];
    });
    return output;
  }
  magic("el", (el) => el);
  warnMissingPluginMagic("Focus", "focus", "focus");
  warnMissingPluginMagic("Persist", "persist", "persist");
  function warnMissingPluginMagic(name, magicName, slug) {
    magic(magicName, (el) => warn(`You can't use [$${magicName}] without first installing the "${name}" plugin here: https://alpinejs.dev/plugins/${slug}`, el));
  }
  directive("modelable", (el, { expression }, { effect: effect3, evaluateLater: evaluateLater2, cleanup }) => {
    let func = evaluateLater2(expression);
    let innerGet = () => {
      let result;
      func((i) => result = i);
      return result;
    };
    let evaluateInnerSet = evaluateLater2(`${expression} = __placeholder`);
    let innerSet = (val) => evaluateInnerSet(() => {
    }, { scope: { "__placeholder": val } });
    let initialValue = innerGet();
    innerSet(initialValue);
    queueMicrotask(() => {
      if (!el._x_model)
        return;
      el._x_removeModelListeners["default"]();
      let outerGet = el._x_model.get;
      let outerSet = el._x_model.setWithModifiers;
      let releaseEntanglement = entangle(
        {
          get() {
            return outerGet();
          },
          set(value) {
            outerSet(value);
          }
        },
        {
          get() {
            return innerGet();
          },
          set(value) {
            innerSet(value);
          }
        }
      );
      cleanup(releaseEntanglement);
    });
  });
  directive("teleport", (el, { modifiers, expression }, { cleanup }) => {
    if (el.tagName.toLowerCase() !== "template")
      warn("x-teleport can only be used on a <template> tag", el);
    let target = getTarget(expression);
    let clone2 = el.content.cloneNode(true).firstElementChild;
    el._x_teleport = clone2;
    clone2._x_teleportBack = el;
    el.setAttribute("data-teleport-template", true);
    clone2.setAttribute("data-teleport-target", true);
    if (el._x_forwardEvents) {
      el._x_forwardEvents.forEach((eventName) => {
        clone2.addEventListener(eventName, (e) => {
          e.stopPropagation();
          el.dispatchEvent(new e.constructor(e.type, e));
        });
      });
    }
    addScopeToNode(clone2, {}, el);
    let placeInDom = (clone3, target2, modifiers2) => {
      if (modifiers2.includes("prepend")) {
        target2.parentNode.insertBefore(clone3, target2);
      } else if (modifiers2.includes("append")) {
        target2.parentNode.insertBefore(clone3, target2.nextSibling);
      } else {
        target2.appendChild(clone3);
      }
    };
    mutateDom(() => {
      skipDuringClone(() => {
        placeInDom(clone2, target, modifiers);
        initTree(clone2);
      })();
    });
    el._x_teleportPutBack = () => {
      let target2 = getTarget(expression);
      mutateDom(() => {
        placeInDom(el._x_teleport, target2, modifiers);
      });
    };
    cleanup(
      () => mutateDom(() => {
        clone2.remove();
        destroyTree(clone2);
      })
    );
  });
  var teleportContainerDuringClone = document.createElement("div");
  function getTarget(expression) {
    let target = skipDuringClone(() => {
      return document.querySelector(expression);
    }, () => {
      return teleportContainerDuringClone;
    })();
    if (!target)
      warn(`Cannot find x-teleport element for selector: "${expression}"`);
    return target;
  }
  var handler = () => {
  };
  handler.inline = (el, { modifiers }, { cleanup }) => {
    modifiers.includes("self") ? el._x_ignoreSelf = true : el._x_ignore = true;
    cleanup(() => {
      modifiers.includes("self") ? delete el._x_ignoreSelf : delete el._x_ignore;
    });
  };
  directive("ignore", handler);
  directive("effect", skipDuringClone((el, { expression }, { effect: effect3 }) => {
    effect3(evaluateLater(el, expression));
  }));
  function on(el, event, modifiers, callback) {
    let listenerTarget = el;
    let handler4 = (e) => callback(e);
    let options = {};
    let wrapHandler = (callback2, wrapper) => (e) => wrapper(callback2, e);
    if (modifiers.includes("dot"))
      event = dotSyntax(event);
    if (modifiers.includes("camel"))
      event = camelCase2(event);
    if (modifiers.includes("capture"))
      options.capture = true;
    if (modifiers.includes("window"))
      listenerTarget = window;
    if (modifiers.includes("document"))
      listenerTarget = document;
    if (modifiers.includes("passive")) {
      options.passive = modifiers[modifiers.indexOf("passive") + 1] !== "false";
    }
    handler4 = addDebounceOrThrottle(modifiers, handler4);
    if (modifiers.includes("prevent"))
      handler4 = wrapHandler(handler4, (next, e) => {
        e.preventDefault();
        next(e);
      });
    if (modifiers.includes("stop"))
      handler4 = wrapHandler(handler4, (next, e) => {
        e.stopPropagation();
        next(e);
      });
    if (modifiers.includes("once")) {
      handler4 = wrapHandler(handler4, (next, e) => {
        next(e);
        listenerTarget.removeEventListener(event, handler4, options);
      });
    }
    if (modifiers.includes("away") || modifiers.includes("outside")) {
      listenerTarget = document;
      handler4 = wrapHandler(handler4, (next, e) => {
        if (el.contains(e.target))
          return;
        if (e.target.isConnected === false)
          return;
        if (el.offsetWidth < 1 && el.offsetHeight < 1)
          return;
        if (el._x_isShown === false)
          return;
        next(e);
      });
    }
    if (modifiers.includes("self"))
      handler4 = wrapHandler(handler4, (next, e) => {
        e.target === el && next(e);
      });
    if (event === "submit") {
      handler4 = wrapHandler(handler4, (next, e) => {
        if (e.target._x_pendingModelUpdates) {
          e.target._x_pendingModelUpdates.forEach((fn) => fn());
        }
        next(e);
      });
    }
    if (isKeyEvent(event) || isClickEvent(event)) {
      handler4 = wrapHandler(handler4, (next, e) => {
        if (isListeningForASpecificKeyThatHasntBeenPressed(e, modifiers)) {
          return;
        }
        next(e);
      });
    }
    listenerTarget.addEventListener(event, handler4, options);
    return () => {
      listenerTarget.removeEventListener(event, handler4, options);
    };
  }
  function addDebounceOrThrottle(modifiers, handler4) {
    if (modifiers.includes("debounce")) {
      let nextModifier = modifiers[modifiers.indexOf("debounce") + 1] || "invalid-wait";
      let wait = isNumeric(nextModifier.split("ms")[0]) ? Number(nextModifier.split("ms")[0]) : 250;
      handler4 = debounce(handler4, wait);
    }
    if (modifiers.includes("throttle")) {
      let nextModifier = modifiers[modifiers.indexOf("throttle") + 1] || "invalid-wait";
      let wait = isNumeric(nextModifier.split("ms")[0]) ? Number(nextModifier.split("ms")[0]) : 250;
      handler4 = throttle(handler4, wait);
    }
    return handler4;
  }
  function dotSyntax(subject) {
    return subject.replace(/-/g, ".");
  }
  function camelCase2(subject) {
    return subject.toLowerCase().replace(/-(\w)/g, (match, char) => char.toUpperCase());
  }
  function isNumeric(subject) {
    return !Array.isArray(subject) && !isNaN(subject);
  }
  function kebabCase2(subject) {
    if ([" ", "_"].includes(
      subject
    ))
      return subject;
    return subject.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[_\s]/, "-").toLowerCase();
  }
  function isKeyEvent(event) {
    return ["keydown", "keyup"].includes(event);
  }
  function isClickEvent(event) {
    return ["contextmenu", "click", "mouse"].some((i) => event.includes(i));
  }
  var nonKeyModifiers = [
    // x-on's own modifiers:
    "window",
    "document",
    "prevent",
    "stop",
    "once",
    "capture",
    "self",
    "away",
    "outside",
    "passive",
    "dot",
    "camel",
    "preserve-scroll",
    // x-model's own modifiers:
    "blur",
    "change",
    "lazy",
    "number",
    "boolean",
    "trim",
    "fill",
    "unintrusive",
    "parent"
  ];
  function isListeningForASpecificKeyThatHasntBeenPressed(e, modifiers) {
    let keyModifiers = modifiers.filter((modifier, index) => {
      if (modifier === "false" && modifiers[index - 1] === "passive")
        return false;
      return !nonKeyModifiers.includes(modifier);
    });
    if (keyModifiers.includes("debounce")) {
      let debounceIndex = keyModifiers.indexOf("debounce");
      keyModifiers.splice(debounceIndex, isNumeric((keyModifiers[debounceIndex + 1] || "invalid-wait").split("ms")[0]) ? 2 : 1);
    }
    if (keyModifiers.includes("throttle")) {
      let debounceIndex = keyModifiers.indexOf("throttle");
      keyModifiers.splice(debounceIndex, isNumeric((keyModifiers[debounceIndex + 1] || "invalid-wait").split("ms")[0]) ? 2 : 1);
    }
    if (keyModifiers.length === 0)
      return false;
    if (keyModifiers.length === 1 && keyToModifiers(e.key).includes(keyModifiers[0]))
      return false;
    const systemKeyModifiers = ["ctrl", "shift", "alt", "meta", "cmd", "super"];
    const selectedSystemKeyModifiers = systemKeyModifiers.filter((modifier) => keyModifiers.includes(modifier));
    keyModifiers = keyModifiers.filter((i) => !selectedSystemKeyModifiers.includes(i));
    if (selectedSystemKeyModifiers.length > 0) {
      const activelyPressedKeyModifiers = selectedSystemKeyModifiers.filter((modifier) => {
        if (modifier === "cmd" || modifier === "super")
          modifier = "meta";
        return e[`${modifier}Key`];
      });
      if (activelyPressedKeyModifiers.length === selectedSystemKeyModifiers.length) {
        if (isClickEvent(e.type))
          return false;
        if (keyToModifiers(e.key).includes(keyModifiers[0]))
          return false;
      }
    }
    return true;
  }
  function keyToModifiers(key) {
    if (!key)
      return [];
    key = kebabCase2(key);
    let modifierToKeyMap = {
      "ctrl": "control",
      "slash": "/",
      "space": " ",
      "spacebar": " ",
      "cmd": "meta",
      "esc": "escape",
      "up": "arrow-up",
      "down": "arrow-down",
      "left": "arrow-left",
      "right": "arrow-right",
      "period": ".",
      "comma": ",",
      "equal": "=",
      "minus": "-",
      "underscore": "_"
    };
    modifierToKeyMap[key] = key;
    return Object.keys(modifierToKeyMap).map((modifier) => {
      if (modifierToKeyMap[modifier] === key)
        return modifier;
    }).filter((modifier) => modifier);
  }
  directive("model", (el, { modifiers, expression }, { effect: effect3, cleanup }) => {
    let scopeTarget = el;
    if (modifiers.includes("parent")) {
      scopeTarget = findClosest(el, (element) => element !== el);
    }
    let evaluateGet = evaluateLater(scopeTarget, expression);
    let evaluateSet;
    if (typeof expression === "string") {
      evaluateSet = evaluateLater(scopeTarget, `${expression} = __placeholder`);
    } else if (typeof expression === "function" && typeof expression() === "string") {
      evaluateSet = evaluateLater(scopeTarget, `${expression()} = __placeholder`);
    } else {
      evaluateSet = () => {
      };
    }
    let getValue = () => {
      let result;
      evaluateGet((value) => result = value);
      return isGetterSetter(result) ? result.get() : result;
    };
    let setValue = (value) => {
      let result;
      evaluateGet((value2) => result = value2);
      if (isGetterSetter(result)) {
        result.set(value);
      } else {
        evaluateSet(() => {
        }, {
          scope: { "__placeholder": value }
        });
      }
    };
    if (typeof expression === "string" && el.type === "radio") {
      mutateDom(() => {
        if (!el.hasAttribute("name"))
          el.setAttribute("name", expression);
      });
    }
    let hasChangeModifier = modifiers.includes("change") || modifiers.includes("lazy");
    let hasBlurModifier = modifiers.includes("blur");
    let hasEnterModifier = modifiers.includes("enter");
    let hasExplicitEventModifiers = hasChangeModifier || hasBlurModifier || hasEnterModifier;
    let removeListener;
    if (isCloning) {
      removeListener = () => {
      };
    } else if (hasExplicitEventModifiers) {
      let listeners = [];
      let syncValue = (e) => setValue(getInputValue(el, modifiers, e, getValue()));
      if (hasChangeModifier) {
        listeners.push(on(el, "change", modifiers, syncValue));
      }
      if (hasBlurModifier) {
        listeners.push(on(el, "blur", modifiers, syncValue));
        if (el.form) {
          let form = el.form;
          let syncCallback = () => syncValue({ target: el });
          if (!form._x_pendingModelUpdates)
            form._x_pendingModelUpdates = [];
          form._x_pendingModelUpdates.push(syncCallback);
          cleanup(() => {
            if (form._x_pendingModelUpdates) {
              form._x_pendingModelUpdates.splice(form._x_pendingModelUpdates.indexOf(syncCallback), 1);
            }
          });
        }
      }
      if (hasEnterModifier) {
        listeners.push(on(el, "keydown", modifiers, (e) => {
          if (e.key === "Enter")
            syncValue(e);
        }));
      }
      removeListener = () => listeners.forEach((remove2) => remove2());
    } else {
      let event = el.tagName.toLowerCase() === "select" || ["checkbox", "radio"].includes(el.type) ? "change" : "input";
      removeListener = on(el, event, modifiers, (e) => {
        setValue(getInputValue(el, modifiers, e, getValue()));
      });
    }
    if (modifiers.includes("fill")) {
      if ([void 0, null, ""].includes(getValue()) || isCheckbox(el) && Array.isArray(getValue()) || el.tagName.toLowerCase() === "select" && el.multiple) {
        setValue(
          getInputValue(el, modifiers, { target: el }, getValue())
        );
      }
    }
    if (!el._x_removeModelListeners)
      el._x_removeModelListeners = {};
    el._x_removeModelListeners["default"] = removeListener;
    cleanup(() => el._x_removeModelListeners["default"]());
    if (el.form) {
      let removeResetListener = on(el.form, "reset", [], (e) => {
        nextTick(() => el._x_model && el._x_model.set(getInputValue(el, modifiers, { target: el }, getValue())));
      });
      cleanup(() => removeResetListener());
    }
    el._x_model = {
      get() {
        return getValue();
      },
      set(value) {
        setValue(value);
      },
      setWithModifiers: addDebounceOrThrottle(modifiers, setValue)
    };
    el._x_forceModelUpdate = (value) => {
      if (value === void 0 && typeof expression === "string" && expression.match(/\./))
        value = "";
      mutateDom(() => {
        if (isCheckbox(el)) {
          if (Array.isArray(value)) {
            el.checked = value.some((val) => val == el.value);
          } else {
            el.checked = !!value;
          }
        } else if (isRadio(el)) {
          if (typeof value === "boolean") {
            el.checked = safeParseBoolean(el.value) === value;
          } else {
            el.checked = el.value == value;
          }
        } else {
          bind(el, "value", value);
        }
      });
    };
    if (el.tagName === "SELECT") {
      let observer2 = new MutationObserver(() => {
        el._x_forceModelUpdate(getValue());
      });
      observer2.observe(el, { childList: true });
      cleanup(() => observer2.disconnect());
    }
    effect3(() => {
      let value = getValue();
      if (modifiers.includes("unintrusive") && document.activeElement.isSameNode(el))
        return;
      el._x_forceModelUpdate(value);
    });
  });
  function getInputValue(el, modifiers, event, currentValue) {
    return mutateDom(() => {
      if (event instanceof CustomEvent && event.detail !== void 0)
        return event.detail !== null && event.detail !== void 0 ? event.detail : event.target.value;
      else if (isCheckbox(el)) {
        if (Array.isArray(currentValue)) {
          let newValue = null;
          if (modifiers.includes("number")) {
            newValue = safeParseNumber(event.target.value);
          } else if (modifiers.includes("boolean")) {
            newValue = safeParseBoolean(event.target.value);
          } else {
            newValue = event.target.value;
          }
          return event.target.checked ? currentValue.includes(newValue) ? currentValue : currentValue.concat([newValue]) : currentValue.filter((el2) => !checkedAttrLooseCompare2(el2, newValue));
        } else {
          return event.target.checked;
        }
      } else if (el.tagName.toLowerCase() === "select" && el.multiple) {
        if (modifiers.includes("number")) {
          return Array.from(event.target.selectedOptions).map((option) => {
            let rawValue = option.value || option.text;
            return safeParseNumber(rawValue);
          });
        } else if (modifiers.includes("boolean")) {
          return Array.from(event.target.selectedOptions).map((option) => {
            let rawValue = option.value || option.text;
            return safeParseBoolean(rawValue);
          });
        }
        return Array.from(event.target.selectedOptions).map((option) => {
          return option.value || option.text;
        });
      } else {
        let newValue;
        if (isRadio(el)) {
          if (event.target.checked) {
            newValue = event.target.value;
          } else {
            newValue = currentValue;
          }
        } else {
          newValue = event.target.value;
        }
        if (modifiers.includes("number")) {
          return safeParseNumber(newValue);
        } else if (modifiers.includes("boolean")) {
          return safeParseBoolean(newValue);
        } else if (modifiers.includes("trim")) {
          return newValue.trim();
        } else {
          return newValue;
        }
      }
    });
  }
  function safeParseNumber(rawValue) {
    let number = rawValue ? parseFloat(rawValue) : null;
    return isNumeric2(number) ? number : rawValue;
  }
  function checkedAttrLooseCompare2(valueA, valueB) {
    return valueA == valueB;
  }
  function isNumeric2(subject) {
    return !Array.isArray(subject) && !isNaN(subject);
  }
  function isGetterSetter(value) {
    return value !== null && typeof value === "object" && typeof value.get === "function" && typeof value.set === "function";
  }
  directive("cloak", (el) => queueMicrotask(() => mutateDom(() => el.removeAttribute(prefix("cloak")))));
  addInitSelector(() => `[${prefix("init")}]`);
  directive("init", skipDuringClone((el, { expression }, { evaluate: evaluate2 }) => {
    if (typeof expression === "string") {
      return !!expression.trim() && evaluate2(expression, {}, false);
    }
    return evaluate2(expression, {}, false);
  }));
  directive("text", (el, { expression }, { effect: effect3, evaluateLater: evaluateLater2 }) => {
    let evaluate2 = evaluateLater2(expression);
    effect3(() => {
      evaluate2((value) => {
        mutateDom(() => {
          el.textContent = value;
        });
      });
    });
  });
  directive("html", (el, { expression }, { effect: effect3, evaluateLater: evaluateLater2 }) => {
    let evaluate2 = evaluateLater2(expression);
    effect3(() => {
      evaluate2((value) => {
        mutateDom(() => {
          Array.from(el.children).forEach((child2) => destroyTree(child2));
          el.innerHTML = value ?? "";
          el._x_ignoreSelf = true;
          initTree(el);
          delete el._x_ignoreSelf;
        });
      });
    }, { priority: "structural" });
  });
  mapAttributes(startingWith(":", into(prefix("bind:"))));
  var handler2 = (el, { value, modifiers, expression, original }, { effect: effect3, cleanup }) => {
    if (!value) {
      let bindingProviders = {};
      injectBindingProviders(bindingProviders);
      let getBindings = evaluateLater(el, expression);
      getBindings((bindings) => {
        applyBindingsObject(el, bindings, original);
      }, { scope: bindingProviders });
      return;
    }
    if (value === "key")
      return storeKeyForXFor(el, expression);
    if (el._x_inlineBindings && el._x_inlineBindings[value] && el._x_inlineBindings[value].extract) {
      return;
    }
    let evaluate2 = evaluateLater(el, expression);
    effect3(() => evaluate2((result) => {
      if (result === void 0 && typeof expression === "string" && expression.match(/\./)) {
        result = "";
      }
      mutateDom(() => bind(el, value, result, modifiers));
    }));
    cleanup(() => {
      el._x_undoAddedClasses && el._x_undoAddedClasses();
      el._x_undoAddedStyles && el._x_undoAddedStyles();
    });
  };
  handler2.inline = (el, { value, modifiers, expression }) => {
    if (!value)
      return;
    if (!el._x_inlineBindings)
      el._x_inlineBindings = {};
    el._x_inlineBindings[value] = { expression, extract: false };
  };
  directive("bind", handler2);
  function storeKeyForXFor(el, expression) {
    el._x_keyExpression = expression;
  }
  addRootSelector(() => `[${prefix("data")}]`);
  var dataForReconciliation = Symbol();
  directive("data", (el, { expression }, { cleanup }) => {
    if (shouldSkipRegisteringDataDuringClone(el))
      return;
    let dataToReconcile = el[dataForReconciliation];
    if (dataToReconcile?.expression === expression)
      return;
    expression = expression === "" ? "{}" : expression;
    let magicContext = {};
    injectMagics(magicContext, el);
    let dataProviderContext = {};
    injectDataProviders(dataProviderContext, magicContext);
    let data2 = evaluate(el, expression, { scope: dataProviderContext });
    if (data2 === void 0 || data2 === true)
      data2 = {};
    injectMagics(data2, el);
    let reactiveData;
    if (dataToReconcile?.reactiveData) {
      reactiveData = dataToReconcile.reactiveData;
      reconcileData(reactiveData, data2);
      let initialized = { expression };
      el[dataForReconciliation] = initialized;
      queueMicrotask(() => {
        if (el[dataForReconciliation] === initialized) {
          delete el[dataForReconciliation];
        }
      });
    } else {
      reactiveData = reactive(data2);
    }
    initInterceptors(reactiveData, cleanup);
    let undo = addScopeToNode(el, reactiveData);
    skipDuringClone(() => {
      reactiveData["init"] && evaluate(el, reactiveData["init"]);
    })();
    cleanup(() => {
      reactiveData["destroy"] && evaluate(el, reactiveData["destroy"]);
      undo();
      let removed = { reactiveData };
      el[dataForReconciliation] = removed;
      queueMicrotask(() => {
        if (el[dataForReconciliation] === removed) {
          delete el[dataForReconciliation];
        }
      });
    });
  });
  function reconcileData(target, source) {
    Object.keys(source).forEach((key) => {
      let descriptor = Object.getOwnPropertyDescriptor(source, key);
      let existingDescriptor = Object.getOwnPropertyDescriptor(target, key);
      if (descriptor.get || descriptor.set || existingDescriptor?.get || existingDescriptor?.set) {
        if (existingDescriptor)
          delete target[key];
        if (!existingDescriptor)
          target[key] = void 0;
        descriptor.get || descriptor.set ? Object.defineProperty(target, key, descriptor) : target[key] = source[key];
      } else {
        target[key] = source[key];
      }
    });
    Object.keys(target).filter((key) => !Object.prototype.hasOwnProperty.call(source, key)).forEach((key) => delete target[key]);
  }
  interceptClone((from, to) => {
    if (from._x_dataStack) {
      to._x_dataStack = from._x_dataStack;
      to.setAttribute("data-has-alpine-state", true);
    }
  });
  function shouldSkipRegisteringDataDuringClone(el) {
    if (!isCloning)
      return false;
    if (isCloningLegacy)
      return true;
    return el.hasAttribute("data-has-alpine-state");
  }
  directive("show", (el, { modifiers, expression }, { effect: effect3 }) => {
    let evaluate2 = evaluateLater(el, expression);
    if (!el._x_doHide)
      el._x_doHide = () => {
        mutateDom(() => {
          el.style.setProperty("display", "none", modifiers.includes("important") ? "important" : void 0);
        });
      };
    if (!el._x_doShow)
      el._x_doShow = () => {
        mutateDom(() => {
          if (el.style.length === 1 && el.style.display === "none") {
            el.removeAttribute("style");
          } else {
            el.style.removeProperty("display");
          }
        });
      };
    let hide = () => {
      el._x_doHide();
      el._x_isShown = false;
    };
    let show = () => {
      el._x_doShow();
      el._x_isShown = true;
    };
    let clickAwayCompatibleShow = () => setTimeout(show);
    let toggle = once(
      (value) => value ? show() : hide(),
      (value) => {
        if (typeof el._x_toggleAndCascadeWithTransitions === "function") {
          el._x_toggleAndCascadeWithTransitions(el, value, show, hide);
        } else {
          value ? clickAwayCompatibleShow() : hide();
        }
      }
    );
    let oldValue;
    let firstTime = true;
    effect3(() => evaluate2((value) => {
      if (!firstTime && value === oldValue)
        return;
      if (modifiers.includes("immediate"))
        value ? clickAwayCompatibleShow() : hide();
      toggle(value);
      oldValue = value;
      firstTime = false;
    }));
  });
  directive("for", skipDuringClone((el, { expression }, { effect: effect3, cleanup }) => {
    let iteratorNames = parseForExpression(expression);
    let evaluateItems = evaluateLater(el, iteratorNames.items);
    let evaluateKey = evaluateLater(
      el,
      // the x-bind:key expression is stored for our use instead of evaluated.
      el._x_keyExpression || "index"
    );
    el._x_lookup = /* @__PURE__ */ new Map();
    effect3(() => loop(el, iteratorNames, evaluateItems, evaluateKey), { priority: "structural" });
    cleanup(() => {
      el._x_lookup.forEach(
        (el2) => mutateDom(() => {
          destroyTree(el2);
          el2.remove();
        })
      );
      delete el._x_lookup;
      delete el._x_lastRenderedEl;
    });
  }));
  function refreshScope(scope2) {
    return (newScope) => {
      Object.entries(newScope).forEach(([key, value]) => {
        scope2[key] = value;
      });
    };
  }
  function loop(templateEl, iteratorNames, evaluateItems, evaluateKey) {
    evaluateItems((items) => {
      if (isNumeric3(items))
        items = Array.from({ length: items }, (_, i) => i + 1);
      if (items === void 0 || items === null)
        items = [];
      if (items instanceof Set)
        items = Array.from(items);
      if (items instanceof Map)
        items = Array.from(items);
      let oldLookup = templateEl._x_lookup;
      let lookup = /* @__PURE__ */ new Map();
      templateEl._x_lookup = lookup;
      let hasStringKeys = isObject2(items);
      let scopeEntries = Object.entries(items).map(([index, item]) => {
        if (!hasStringKeys)
          index = parseInt(index);
        let scope2 = getIterationScopeVariables(iteratorNames, item, index, items);
        let key;
        evaluateKey((innerKey) => {
          if (typeof innerKey === "object")
            warn("x-for key cannot be an object, it must be a string or an integer", templateEl);
          if (oldLookup.has(innerKey)) {
            lookup.set(innerKey, oldLookup.get(innerKey));
            oldLookup.delete(innerKey);
          }
          key = innerKey;
        }, { scope: { index, ...scope2 } });
        return [key, scope2];
      });
      mutateDom(() => {
        oldLookup.forEach((el) => {
          destroyTree(el);
          el.remove();
        });
        let added = /* @__PURE__ */ new Set();
        let prev = templateEl;
        scopeEntries.forEach(([key, scope2]) => {
          if (lookup.has(key)) {
            let el = lookup.get(key);
            el._x_refreshXForScope(scope2);
            if (prev.nextElementSibling !== el) {
              if (prev.nextElementSibling)
                el.replaceWith(prev.nextElementSibling);
              prev.after(el);
            }
            prev = el;
            if (el._x_currentIfEl) {
              if (el.nextElementSibling !== el._x_currentIfEl)
                prev.after(el._x_currentIfEl);
              prev = el._x_currentIfEl;
            }
            return;
          }
          if (templateEl.content.children.length > 1)
            warn("x-for templates require a single root element, additional elements will be ignored.", templateEl);
          let clone2 = document.importNode(templateEl.content, true).firstElementChild;
          let reactiveScope = reactive(scope2);
          addScopeToNode(clone2, reactiveScope, templateEl);
          clone2._x_refreshXForScope = refreshScope(reactiveScope);
          lookup.set(key, clone2);
          added.add(clone2);
          prev.after(clone2);
          prev = clone2;
        });
        added.forEach((clone2) => initTree(clone2));
        if (prev !== templateEl) {
          templateEl._x_lastRenderedEl = prev;
        } else {
          delete templateEl._x_lastRenderedEl;
        }
      });
    });
  }
  function parseForExpression(expression) {
    let forIteratorRE = /,([^,\}\]]*)(?:,([^,\}\]]*))?$/;
    let stripParensRE = /^\s*\(|\)\s*$/g;
    let forAliasRE = /([\s\S]*?)\b(?:in|of)\b([\s\S]*)/;
    let inMatch = expression.match(forAliasRE);
    if (!inMatch)
      return;
    let res = {};
    res.items = inMatch[2].trim();
    let item = inMatch[1].replace(stripParensRE, "").trim();
    let iteratorMatch = item.match(forIteratorRE);
    if (iteratorMatch) {
      res.item = item.replace(forIteratorRE, "").trim();
      res.index = iteratorMatch[1].trim();
      if (iteratorMatch[2]) {
        res.collection = iteratorMatch[2].trim();
      }
    } else {
      res.item = item;
    }
    return res;
  }
  function getIterationScopeVariables(iteratorNames, item, index, items) {
    let scopeVariables = {};
    if (/^\[.*\]$/.test(iteratorNames.item) && Array.isArray(item)) {
      let names = iteratorNames.item.replace("[", "").replace("]", "").split(",").map((i) => i.trim());
      names.forEach((name, i) => {
        scopeVariables[name] = item[i];
      });
    } else if (/^\{.*\}$/.test(iteratorNames.item) && !Array.isArray(item) && typeof item === "object") {
      let names = iteratorNames.item.replace("{", "").replace("}", "").split(",").map((i) => i.trim());
      names.forEach((name) => {
        scopeVariables[name] = item[name];
      });
    } else {
      scopeVariables[iteratorNames.item] = item;
    }
    if (iteratorNames.index)
      scopeVariables[iteratorNames.index] = index;
    if (iteratorNames.collection)
      scopeVariables[iteratorNames.collection] = items;
    return scopeVariables;
  }
  function isNumeric3(subject) {
    return typeof subject !== "object" && !isNaN(subject);
  }
  function isObject2(subject) {
    return typeof subject === "object" && !Array.isArray(subject);
  }
  function handler3() {
  }
  handler3.inline = (el, { expression }, { cleanup }) => {
    let root = closestRoot(el);
    if (!root)
      return;
    if (!root._x_refs)
      root._x_refs = {};
    root._x_refs[expression] = el;
    cleanup(() => delete root._x_refs[expression]);
  };
  directive("ref", handler3);
  directive("if", skipDuringClone((el, { expression }, { effect: effect3, cleanup }) => {
    if (el.tagName.toLowerCase() !== "template")
      warn("x-if can only be used on a <template> tag", el);
    let evaluate2 = evaluateLater(el, expression);
    let show = () => {
      if (el._x_currentIfEl)
        return el._x_currentIfEl;
      let clone2 = el.content.cloneNode(true).firstElementChild;
      addScopeToNode(clone2, {}, el);
      mutateDom(() => {
        el.after(clone2);
        initTree(clone2);
      });
      el._x_currentIfEl = clone2;
      el._x_lastRenderedEl = clone2;
      el._x_undoIf = () => {
        mutateDom(() => {
          destroyTree(clone2);
          clone2.remove();
        });
        delete el._x_currentIfEl;
        delete el._x_lastRenderedEl;
      };
      return clone2;
    };
    let hide = () => {
      if (!el._x_undoIf)
        return;
      el._x_undoIf();
      delete el._x_undoIf;
    };
    effect3(() => evaluate2((value) => {
      value ? show() : hide();
    }), { priority: "structural" });
    cleanup(() => el._x_undoIf && el._x_undoIf());
  }));
  directive("id", (el, { expression }, { evaluate: evaluate2 }) => {
    let names = evaluate2(expression);
    names.forEach((name) => setIdRoot(el, name));
  });
  interceptClone((from, to) => {
    if (from._x_ids) {
      to._x_ids = from._x_ids;
    }
  });
  mapAttributes(startingWith("@", into(prefix("on:"))));
  directive("on", skipDuringClone((el, { value, modifiers, expression }, { cleanup }) => {
    let evaluate2 = expression ? evaluateLater(el, expression) : () => {
    };
    if (el.tagName.toLowerCase() === "template") {
      if (!el._x_forwardEvents)
        el._x_forwardEvents = [];
      if (!el._x_forwardEvents.includes(value))
        el._x_forwardEvents.push(value);
    }
    let removeListener = on(el, value, modifiers, (e) => {
      evaluate2(() => {
      }, { scope: { "$event": e }, params: [e] });
    });
    cleanup(() => removeListener());
  }));
  warnMissingPluginDirective("Collapse", "collapse", "collapse");
  warnMissingPluginDirective("Intersect", "intersect", "intersect");
  warnMissingPluginDirective("Focus", "trap", "focus");
  warnMissingPluginDirective("Mask", "mask", "mask");
  function warnMissingPluginDirective(name, directiveName, slug) {
    directive(directiveName, (el) => warn(`You can't use [x-${directiveName}] without first installing the "${name}" plugin here: https://alpinejs.dev/plugins/${slug}`, el));
  }
  alpine_default.setEvaluator(normalEvaluator);
  alpine_default.setRawEvaluator(normalRawEvaluator);
  alpine_default.setReactivityEngine({
    reactive: reactive2,
    // Since Vue 3.2, the scheduler is called with no arguments, so we wrap
    // the effect to hand Alpine's scheduler the runner it expects to queue.
    effect: (callback, options = {}) => {
      let runner;
      runner = effect2(callback, {
        scheduler: () => {
          if (!runner)
            return;
          options.scheduler ? options.scheduler(runner) : runner();
        }
      });
      return runner;
    },
    release: stop,
    raw: toRaw
  });
  var src_default = alpine_default;
  var module_default = src_default;

  // node_modules/@alpinejs/persist/dist/module.esm.js
  function src_default2(Alpine3) {
    let persist = () => {
      let alias;
      let storage;
      try {
        storage = localStorage;
      } catch (e) {
        console.error(e);
        console.warn("Alpine: $persist is using temporary storage since localStorage is unavailable.");
        let dummy = /* @__PURE__ */ new Map();
        storage = {
          getItem: (key) => dummy.has(key) ? dummy.get(key) : null,
          setItem: dummy.set.bind(dummy)
        };
      }
      return Alpine3.interceptor((initialValue, getter, setter, path, key, cleanup = () => {
      }) => {
        let lookup = alias || `_x_${path}`;
        let initial = storageHas(lookup, storage) ? storageGet(lookup, storage) : initialValue;
        setter(initial);
        let effect3 = Alpine3.effect(() => {
          let value = getter();
          storageSet(lookup, value, storage);
          setter(value);
        });
        cleanup(() => Alpine3.release(effect3));
        return initial;
      }, (func) => {
        func.as = (key) => {
          alias = key;
          return func;
        }, func.using = (target) => {
          storage = target;
          return func;
        };
      });
    };
    Object.defineProperty(Alpine3, "$persist", { get: () => persist() });
    Alpine3.magic("persist", persist);
    Alpine3.persist = (key, { get: get2, set: set2 }, storage = localStorage) => {
      let initial = storageHas(key, storage) ? storageGet(key, storage) : get2();
      set2(initial);
      Alpine3.effect(() => {
        let value = get2();
        storageSet(key, value, storage);
        set2(value);
      });
    };
  }
  function storageHas(key, storage) {
    let value = storage.getItem(key);
    return value !== null && value !== void 0;
  }
  function storageGet(key, storage) {
    let value = storage.getItem(key);
    if (value === void 0)
      return;
    return JSON.parse(value);
  }
  function storageSet(key, value, storage) {
    if (value === void 0) {
      storage.removeItem?.(key);
      return;
    }
    storage.setItem(key, JSON.stringify(value));
  }
  var module_default2 = src_default2;

  // src/scripts/utilities/store.js
  module_default.plugin(module_default2);
  module_default.store("lastViewed", module_default.$persist(false).as("lastViewed"));
  module_default.store("errors", {
    list: [],
    add(message) {
      this.list.push({
        id: Date.now(),
        message,
        visible: true
      });
      setTimeout(() => {
        this.removeById(this.list[0]?.id);
      }, 6e3);
    },
    removeById(id) {
      this.list = this.list.filter((err) => err.id !== id);
    }
  });

  // src/scripts/utilities/observe-element.js
  document.addEventListener("DOMContentLoaded", () => {
    const observedElements = document.querySelectorAll(".observed-element");
    observedElements.forEach((element) => {
      const variableName = element.getAttribute("data-variable");
      const observer2 = new IntersectionObserver((entries2) => {
        entries2.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementHeight = (entry.target.clientHeight || 0) + "px";
            const elementWidth = (entry.target.clientWidth || 0) + "px";
            document.documentElement.style.setProperty(
              `--${variableName}-height`,
              elementHeight
            );
            document.documentElement.style.setProperty(
              `--${variableName}-width`,
              elementWidth
            );
          }
        });
      });
      observer2.observe(element);
    });
  });

  // src/scripts/utilities/modal-inert.js
  var lastFocused = null;
  function modalInert(dialogEl, open) {
    const portal = dialogEl.closest(".component-modal__portal");
    if (!portal) return;
    const others = Array.from(document.body.children).filter(
      (node) => node !== portal
    );
    if (open) {
      others.forEach((node) => node.setAttribute("inert", ""));
      if (!portal.dataset.trapActive) {
        lastFocused = document.activeElement;
        portal.dataset.trapActive = "true";
        requestAnimationFrame(() => {
          const target = dialogEl.querySelector(".close") || dialogEl;
          target.focus();
        });
      }
    } else {
      others.forEach((node) => node.removeAttribute("inert"));
      if (portal.dataset.trapActive) {
        delete portal.dataset.trapActive;
        if (lastFocused && typeof lastFocused.focus === "function") {
          lastFocused.focus();
        }
        lastFocused = null;
      }
    }
  }
  window.modalInert = modalInert;

  // src/scripts/utilities/form-validation.js
  function isEmptyOrWhitespace(value) {
    return !value || !value.trim();
  }
  function validateRequiredTextFields(form) {
    const requiredTextFields = form.querySelectorAll(
      'input[type="text"][required], input[type="email"][required], textarea[required]'
    );
    requiredTextFields.forEach((input) => {
      const errorContainer = document.getElementById(`${input.id}-error`);
      input.addEventListener("blur", () => {
        if (!errorContainer) return;
        let isValid = !isEmptyOrWhitespace(input.value);
        if (isValid && input.type === "email") {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          isValid = emailPattern.test(input.value);
        }
        errorContainer.hidden = isValid;
        input.setAttribute("aria-invalid", isValid ? "false" : "true");
        const msg = errorContainer.querySelector(".form__message");
        if (msg) {
          msg.textContent = isValid ? "" : input.dataset.validationMessage || "This field is required.";
        }
      });
    });
  }
  function sanitizePhoneInput(input) {
    input.addEventListener("input", () => {
      input.value = input.value.replace(/[^0-9+\-\s().]/g, "");
    });
  }
  function validatePhoneField(input, errorContainer) {
    const pattern = /^[0-9+\-\s().]{7,}$/;
    input.addEventListener("blur", () => {
      const isValid = pattern.test(input.value);
      errorContainer.hidden = isValid;
      input.setAttribute("aria-invalid", isValid ? "false" : "true");
    });
  }
  function markRequiredFields(form) {
    const requiredFields = form.querySelectorAll("[required]");
    requiredFields.forEach((field) => {
      const label = form.querySelector(`label[for="${field.id}"]`);
      if (label && !label.innerHTML.includes("*")) {
        label.innerHTML += ' <span aria-hidden="true">*</span>';
      }
    });
  }
  function setupFormValidation(form) {
    const phoneInput = form.querySelector('input[type="tel"]');
    const phoneError = form.querySelector("#ContactFormPhone-error");
    if (phoneInput && phoneError) {
      sanitizePhoneInput(phoneInput);
      validatePhoneField(phoneInput, phoneError);
    }
    markRequiredFields(form);
    validateRequiredTextFields(form);
  }
  function focusFormSuccess(form) {
    const success = form.querySelector(".form-status--success");
    if (!success) return;
    const bringIntoView = () => {
      success.scrollIntoView({ behavior: "auto", block: "center" });
      success.focus({ preventScroll: true });
    };
    if (document.readyState === "complete") {
      bringIntoView();
    } else {
      window.addEventListener("load", bringIntoView, { once: true });
    }
  }
  function initForms() {
    const forms = document.querySelectorAll("form.form--validate");
    forms.forEach((form) => {
      setupFormValidation(form);
      focusFormSuccess(form);
    });
  }
  document.addEventListener("DOMContentLoaded", initForms);

  // src/scripts/utilities/video-player.js
  window.VideoPlayerManager = {
    pauseWithin(root, except) {
      if (!root) return;
      root.querySelectorAll(".js-youtube").forEach((iframe) => {
        if (iframe === except) return;
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
      });
      root.querySelectorAll(".js-vimeo").forEach((iframe) => {
        if (iframe === except) return;
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      });
      root.querySelectorAll("video").forEach((video) => {
        if (video === except) return;
        video.pause();
      });
    },
    pauseAll(except) {
      this.pauseWithin(document, except);
    }
  };

  // src/scripts/utilities/video-facade.js
  var FACADE = "[data-video-facade]";
  var READY = "component-video--facade-ready";
  function dispatchPlaying(el) {
    el.dispatchEvent(
      new CustomEvent("video-playing", {
        bubbles: true,
        detail: { playing: true }
      })
    );
  }
  function prepare(container) {
    if (container.classList.contains(READY)) return;
    container.classList.add(READY);
    const video = container.querySelector("video");
    if (video && video.hasAttribute("controls")) {
      video.removeAttribute("controls");
      video.dataset.restoreControls = "true";
    }
  }
  function revealControls(video) {
    if (video && video.dataset.restoreControls === "true") {
      video.controls = true;
      delete video.dataset.restoreControls;
    }
  }
  function retireFacade(facade) {
    facade.hidden = true;
  }
  function launchNative(facade, { autoplay = false, muted = false } = {}) {
    const container = facade.closest(".component-video");
    const video = container && container.querySelector("video");
    if (!video) return;
    window.VideoPlayerManager?.pauseAll(video);
    revealControls(video);
    retireFacade(facade);
    if (autoplay) video.muted = muted;
    const started2 = video.play();
    if (started2 && typeof started2.catch === "function") {
      started2.catch((error2) => {
        if (!autoplay || video.muted || error2?.name !== "NotAllowedError") {
          throw error2;
        }
        video.muted = true;
        return video.play();
      }).catch(() => {
        facade.hidden = false;
      });
    }
  }
  function post(iframe, payload) {
    iframe.contentWindow?.postMessage(JSON.stringify(payload), "*");
  }
  function youtubeCommand(iframe, func, args = []) {
    post(iframe, { event: "command", func, args });
  }
  var YT_UNSTARTED = -1;
  var YT_PLAYING = 1;
  var YT_BUFFERING = 3;
  function mutedSrc(src, muteParam) {
    try {
      const url = new URL(src);
      url.searchParams.set(muteParam, "1");
      url.searchParams.set("autoplay", "1");
      return url.toString();
    } catch {
      return src;
    }
  }
  function driveYouTube(iframe, isRetry = false) {
    let answered = false;
    let playing = false;
    let lastState = YT_UNSTARTED;
    const onMessage = (event) => {
      if (event.source !== iframe.contentWindow) return;
      let data2 = event.data;
      if (typeof data2 === "string") {
        try {
          data2 = JSON.parse(data2);
        } catch {
          return;
        }
      }
      if (!data2 || typeof data2 !== "object") return;
      answered = true;
      const state = data2.info?.playerState;
      if (typeof state === "number") {
        lastState = state;
        if (state === YT_PLAYING) playing = true;
      }
      if (data2.event === "onReady") youtubeCommand(iframe, "playVideo");
    };
    window.addEventListener("message", onMessage);
    const handshake = () => {
      post(iframe, { event: "listening" });
      youtubeCommand(iframe, "playVideo");
    };
    const handshakes = setInterval(handshake, 250);
    handshake();
    const stop2 = () => {
      clearInterval(handshakes);
      window.removeEventListener("message", onMessage);
    };
    const reloadMuted = () => {
      if (playing || !answered) return stop2();
      stop2();
      iframe.addEventListener("load", () => driveYouTube(iframe, true), {
        once: true
      });
      iframe.src = mutedSrc(iframe.src, "mute");
    };
    setTimeout(() => {
      clearInterval(handshakes);
      if (playing || isRetry) return stop2();
      setTimeout(reloadMuted, lastState === YT_BUFFERING ? 1500 : 0);
    }, 2e3);
  }
  function driveVimeo(iframe, isRetry = false) {
    let answered = false;
    let playing = false;
    const onMessage = (event) => {
      if (event.source !== iframe.contentWindow) return;
      let data2 = event.data;
      if (typeof data2 === "string") {
        try {
          data2 = JSON.parse(data2);
        } catch {
          return;
        }
      }
      if (!data2 || typeof data2 !== "object") return;
      answered = true;
      if (data2.event === "play" || data2.event === "playing") playing = true;
      if (data2.method === "getPaused") playing = data2.value === false;
    };
    window.addEventListener("message", onMessage);
    const play = () => post(iframe, { method: "play" });
    const attempts = setInterval(play, 250);
    play();
    const stop2 = () => {
      clearInterval(attempts);
      window.removeEventListener("message", onMessage);
    };
    setTimeout(() => {
      clearInterval(attempts);
      if (isRetry) return stop2();
      post(iframe, { method: "getPaused" });
      setTimeout(() => {
        if (playing || !answered) return stop2();
        stop2();
        iframe.addEventListener("load", () => driveVimeo(iframe, true), {
          once: true
        });
        iframe.src = mutedSrc(iframe.src, "muted");
      }, 500);
    }, 2e3);
  }
  function launchEmbed(facade, { muted = false } = {}) {
    const container = facade.closest(".component-video");
    const src = facade.dataset.embedSrc;
    if (!container || !src) return;
    window.VideoPlayerManager?.pauseAll();
    const platform = ["js-youtube", "js-vimeo"].find(
      (name) => container.classList.contains(name)
    );
    const iframe = document.createElement("iframe");
    iframe.src = muted ? mutedSrc(src, platform === "js-vimeo" ? "muted" : "mute") : src;
    iframe.className = platform ? `component-video__media ${platform}` : "component-video__media";
    iframe.title = facade.dataset.embedTitle || "";
    iframe.allow = "autoplay; encrypted-media; fullscreen; picture-in-picture";
    iframe.setAttribute("frameborder", "0");
    iframe.allowFullscreen = true;
    facade.replaceWith(iframe);
    container.classList.add("component-video--embed-loaded");
    dispatchPlaying(iframe);
    iframe.addEventListener(
      "load",
      () => {
        if (platform === "js-youtube") driveYouTube(iframe);
        if (platform === "js-vimeo") driveVimeo(iframe);
      },
      { once: true }
    );
  }
  function prepareAll(root = document) {
    root.querySelectorAll(FACADE).forEach((facade) => {
      const container = facade.closest(".component-video");
      if (container) prepare(container);
    });
  }
  document.addEventListener("click", (event) => {
    const facade = event.target.closest?.(FACADE);
    if (!facade) return;
    event.preventDefault();
    if (facade.dataset.videoFacade === "embed") {
      launchEmbed(facade);
    } else {
      launchNative(facade);
    }
  });
  document.addEventListener("video-facade:autoplay", (event) => {
    const container = event.target.closest?.("[data-autoplay-on-active]");
    if (!container) return;
    const muted = container.dataset.autoplayOnActive !== "sound";
    const facade = container.querySelector(`${FACADE}:not([hidden])`);
    if (facade) {
      if (facade.dataset.videoFacade === "embed") {
        launchEmbed(facade, { muted });
      } else {
        launchNative(facade, { autoplay: true, muted });
      }
      return;
    }
    const iframe = container.querySelector("iframe.js-youtube, iframe.js-vimeo");
    if (!iframe) return;
    window.VideoPlayerManager?.pauseAll(iframe);
    if (iframe.classList.contains("js-youtube")) {
      youtubeCommand(iframe, "playVideo");
    } else {
      post(iframe, { method: "play" });
    }
  });
  document.addEventListener(
    "play",
    (event) => {
      const container = event.target.closest?.(".component-video");
      if (!container) return;
      revealControls(event.target);
      const facade = container.querySelector(FACADE);
      if (facade) retireFacade(facade);
    },
    true
  );
  document.addEventListener(
    "error",
    (event) => {
      const img = event.target;
      if (!(img instanceof HTMLImageElement) || !img.closest(FACADE)) return;
      if (!img.src.includes("/maxresdefault.jpg")) return;
      img.src = img.src.replace("/maxresdefault.jpg", "/hqdefault.jpg");
    },
    true
  );
  prepareAll();
  document.addEventListener("DOMContentLoaded", () => prepareAll());
  document.addEventListener(
    "shopify:section:load",
    (event) => prepareAll(event.target)
  );
  new MutationObserver((records) => {
    const arrived = records.some(
      (record) => [...record.addedNodes].some(
        (node) => node.nodeType === Node.ELEMENT_NODE && (node.matches(FACADE) || node.querySelector(FACADE))
      )
    );
    if (arrived) prepareAll();
  }).observe(document.documentElement, { childList: true, subtree: true });

  // node_modules/@alpinejs/collapse/dist/module.esm.js
  function src_default3(Alpine3) {
    Alpine3.directive("collapse", collapse);
    collapse.inline = (el, { modifiers }) => {
      if (!modifiers.includes("min"))
        return;
      el._x_doShow = () => {
      };
      el._x_doHide = () => {
      };
    };
    function collapse(el, { modifiers }) {
      let duration = modifierValue2(modifiers, "duration", 250) / 1e3;
      let floor3 = modifierValue2(modifiers, "min", 0);
      let fullyHide = !modifiers.includes("min");
      if (!el._x_isShown)
        el.style.height = `${floor3}px`;
      if (!el._x_isShown && fullyHide)
        el.hidden = true;
      if (!el._x_isShown)
        el.style.overflow = "hidden";
      let setFunction = (el2, styles) => {
        let revertFunction = Alpine3.setStyles(el2, styles);
        return styles.height ? () => {
        } : revertFunction;
      };
      let transitionStyles = {
        transitionProperty: "height",
        transitionDuration: `${duration}s`,
        transitionTimingFunction: "cubic-bezier(0.4, 0.0, 0.2, 1)"
      };
      el._x_transition = {
        in(before2 = () => {
        }, after = () => {
        }) {
          if (fullyHide)
            el.hidden = false;
          if (fullyHide)
            el.style.display = null;
          let current = el.getBoundingClientRect().height;
          el.style.height = "auto";
          let full = el.getBoundingClientRect().height;
          if (current === full) {
            current = floor3;
          }
          Alpine3.transition(el, Alpine3.setStyles, {
            during: transitionStyles,
            start: { height: current + "px" },
            end: { height: full + "px" }
          }, () => el._x_isShown = true, () => {
            if (Math.abs(el.getBoundingClientRect().height - full) < 1) {
              el.style.overflow = null;
            }
          });
        },
        out(before2 = () => {
        }, after = () => {
        }) {
          let full = el.getBoundingClientRect().height;
          Alpine3.transition(el, setFunction, {
            during: transitionStyles,
            start: { height: full + "px" },
            end: { height: floor3 + "px" }
          }, () => el.style.overflow = "hidden", () => {
            el._x_isShown = false;
            if (el.style.height == `${floor3}px` && fullyHide) {
              el.style.display = "none";
              el.hidden = true;
            }
          });
        }
      };
    }
  }
  function modifierValue2(modifiers, key, fallback) {
    if (modifiers.indexOf(key) === -1)
      return fallback;
    const rawValue = modifiers[modifiers.indexOf(key) + 1];
    if (!rawValue)
      return fallback;
    if (key === "duration") {
      let match = rawValue.match(/([0-9]+)ms/);
      if (match)
        return match[1];
    }
    if (key === "min") {
      let match = rawValue.match(/([0-9]+)px/);
      if (match)
        return match[1];
    }
    return rawValue;
  }
  var module_default3 = src_default3;

  // node_modules/@alpinejs/intersect/dist/module.esm.js
  function src_default4(Alpine3) {
    Alpine3.directive("intersect", Alpine3.skipDuringClone((el, { value, expression, modifiers }, { evaluateLater: evaluateLater2, cleanup }) => {
      let evaluate2 = evaluateLater2(expression);
      let threshold = getThreshold(modifiers);
      let options = {
        rootMargin: getRootMargin(modifiers),
        threshold,
        root: modifiers.includes("parent") ? el.parentElement : null
      };
      if (modifiers.includes("dwell")) {
        observeForDwell(el, value, modifiers, evaluate2, cleanup, options, threshold);
        return;
      }
      let observer2 = new IntersectionObserver((entries2) => {
        entries2.forEach((entry) => {
          if (entry.isIntersecting === (value === "leave"))
            return;
          evaluate2();
          modifiers.includes("once") && observer2.disconnect();
        });
      }, options);
      observer2.observe(el);
      cleanup(() => {
        observer2.disconnect();
      });
    }));
  }
  function observeForDwell(el, value, modifiers, evaluate2, cleanup, options, threshold) {
    let dwellTimeout;
    let lastEntry;
    let hasEvaluated = false;
    let isConfirmingDwell = false;
    let duration = getDwellDuration(modifiers);
    let observer2;
    let clearDwellTimeout = () => {
      clearTimeout(dwellTimeout);
      dwellTimeout = void 0;
    };
    let completeDwell = () => {
      isConfirmingDwell = false;
      if (!lastEntry || document.hidden || !meetsDwellThreshold(lastEntry, value, threshold))
        return;
      hasEvaluated = true;
      evaluate2();
      if (modifiers.includes("once")) {
        observer2.disconnect();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
    let observe = () => {
      observer2 && observer2.disconnect();
      observer2 = new IntersectionObserver(handleEntries, options);
      observer2.observe(el);
    };
    let confirmDwell = () => {
      dwellTimeout = void 0;
      if (document.hidden)
        return;
      let pendingEntries = observer2.takeRecords();
      if (pendingEntries.some((entry) => !meetsDwellThreshold(entry, value, threshold))) {
        handleEntries(pendingEntries);
        return;
      }
      isConfirmingDwell = true;
      lastEntry = void 0;
      observe();
    };
    let beginDwell = () => {
      if (dwellTimeout !== void 0 || hasEvaluated || document.hidden || !lastEntry)
        return;
      if (!meetsDwellThreshold(lastEntry, value, threshold))
        return;
      dwellTimeout = setTimeout(confirmDwell, duration);
    };
    let handleEntries = (entries2) => {
      entries2.forEach((entry) => {
        lastEntry = entry;
        if (!meetsDwellThreshold(entry, value, threshold)) {
          clearDwellTimeout();
          hasEvaluated = false;
          isConfirmingDwell = false;
          return;
        }
        if (isConfirmingDwell) {
          completeDwell();
          return;
        }
        beginDwell();
      });
    };
    let handleVisibilityChange = () => {
      if (document.hidden) {
        clearDwellTimeout();
        lastEntry = void 0;
        hasEvaluated = false;
        isConfirmingDwell = false;
        observer2.disconnect();
        return;
      }
      observe();
    };
    observe();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    cleanup(() => {
      clearDwellTimeout();
      observer2.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    });
  }
  function meetsDwellThreshold(entry, value, threshold) {
    let meetsThreshold = threshold === 0 ? entry.isIntersecting : entry.isIntersecting && entry.intersectionRatio >= threshold;
    return value === "leave" ? !meetsThreshold : meetsThreshold;
  }
  function getDwellDuration(modifiers) {
    let rawDuration = modifiers[modifiers.indexOf("dwell") + 1] || "";
    let match = rawDuration.match(/^([0-9]+)(ms)?$/);
    return match ? Number(match[1]) : 250;
  }
  function getThreshold(modifiers) {
    if (modifiers.includes("full"))
      return 0.99;
    if (modifiers.includes("half"))
      return 0.5;
    if (!modifiers.includes("threshold"))
      return 0;
    let threshold = modifiers[modifiers.indexOf("threshold") + 1];
    if (threshold === "100")
      return 1;
    if (threshold === "0")
      return 0;
    return Number(`.${threshold}`);
  }
  function getLengthValue(rawValue) {
    let match = rawValue.match(/^(-?[0-9]+)(px|%)?$/);
    return match ? match[1] + (match[2] || "px") : void 0;
  }
  function getRootMargin(modifiers) {
    const key = "margin";
    const fallback = "0px 0px 0px 0px";
    const index = modifiers.indexOf(key);
    if (index === -1)
      return fallback;
    let values = [];
    for (let i = 1; i < 5; i++) {
      values.push(getLengthValue(modifiers[index + i] || ""));
    }
    values = values.filter((v) => v !== void 0);
    return values.length ? values.join(" ").trim() : fallback;
  }
  var module_default4 = src_default4;

  // node_modules/@fylgja/alpinejs-dialog/dist/index.mjs
  function dialog_default(Alpine3) {
    Alpine3.directive("dialog", fylgjaAlpineDialog);
    function fylgjaAlpineDialog(el, { expression, modifiers }, { evaluateLater: evaluateLater2, cleanup }) {
      const evaluate2 = expression.length ? evaluateLater2(expression) : () => {
      };
      const lockPageScroll = modifiers.includes("noscroll");
      el.style.display = null;
      el.hidden = false;
      el.style.length === 0 && el.removeAttribute("style");
      function scrollLock(use = true) {
        document.body.style.overflow = use ? "hidden" : "";
      }
      function escapeDialog(event) {
        if (event.key !== "Escape")
          return;
        event.preventDefault();
        evaluate2();
      }
      function backdropDialog(event) {
        const rect2 = el.getBoundingClientRect();
        const isInDialog = rect2.top <= event.clientY && event.clientY <= rect2.top + rect2.height && rect2.left <= event.clientX && event.clientX <= rect2.left + rect2.width;
        if (isInDialog)
          return;
        evaluate2();
      }
      el._x_doShow = () => {
        if (el.hasAttribute("open"))
          return;
        el.showModal();
        document.addEventListener("keydown", escapeDialog);
        el.addEventListener("click", backdropDialog);
        scrollLock(lockPageScroll);
      };
      el._x_doHide = () => {
        if (!el.hasAttribute("open"))
          return;
        el.close();
        document.removeEventListener("keydown", escapeDialog);
        el.removeEventListener("click", backdropDialog);
        scrollLock(false);
      };
      cleanup(() => {
        document.removeEventListener("keydown", escapeDialog);
        el.removeEventListener("click", backdropDialog);
        scrollLock(false);
      });
    }
  }
  var module_default5 = dialog_default;

  // node_modules/@alpinejs/morph/dist/module.esm.js
  function morph(from, toHtml, options) {
    monkeyPatchDomSetAttributeToAllowAtSymbols();
    let context = createMorphContext(options);
    let toEl = typeof toHtml === "string" ? createElement(toHtml) : toHtml;
    if (window.Alpine && window.Alpine.closestDataStack && !from._x_dataStack) {
      toEl._x_dataStack = window.Alpine.closestDataStack(from);
      toEl._x_dataStack && window.Alpine.cloneNode(from, toEl);
    }
    context.patch(from, toEl);
    return from;
  }
  function morphBetween(startMarker, endMarker, toHtml, options = {}) {
    monkeyPatchDomSetAttributeToAllowAtSymbols();
    let context = createMorphContext(options);
    let fromContainer = startMarker.parentNode;
    let fromBlock = new Block(startMarker, endMarker);
    let toContainer = typeof toHtml === "string" ? (() => {
      let container = document.createElement("div");
      container.insertAdjacentHTML("beforeend", toHtml);
      return container;
    })() : toHtml;
    let toStartMarker = document.createComment("[morph-start]");
    let toEndMarker = document.createComment("[morph-end]");
    toContainer.insertBefore(toStartMarker, toContainer.firstChild);
    toContainer.appendChild(toEndMarker);
    let toBlock = new Block(toStartMarker, toEndMarker);
    if (window.Alpine && window.Alpine.closestDataStack) {
      toContainer._x_dataStack = window.Alpine.closestDataStack(fromContainer);
      toContainer._x_dataStack && window.Alpine.cloneNode(fromContainer, toContainer);
    }
    context.patchChildren(fromBlock, toBlock);
  }
  function createMorphContext(options = {}) {
    let defaultGetKey = (el) => el.getAttribute("key");
    let noop3 = () => {
    };
    let context = {
      key: options.key || defaultGetKey,
      lookahead: options.lookahead || false,
      updating: options.updating || noop3,
      updated: options.updated || noop3,
      removing: options.removing || noop3,
      removed: options.removed || noop3,
      adding: options.adding || noop3,
      added: options.added || noop3
    };
    context.patch = function(from, to) {
      if (context.differentElementNamesTypesOrKeys(from, to)) {
        return context.swapElements(from, to);
      }
      let updateChildrenOnly = false;
      let skipChildren = false;
      let skipUntil = (predicate) => context.skipUntilCondition = predicate;
      if (shouldSkipChildren(context.updating, () => skipChildren = true, skipUntil, from, to, () => updateChildrenOnly = true))
        return;
      if (from.nodeType === 1 && window.Alpine) {
        window.Alpine.cloneNode(from, to);
        if (from._x_teleport && to._x_teleport) {
          context.patch(from._x_teleport, to._x_teleport);
        }
      }
      if (textOrComment(to)) {
        context.patchNodeValue(from, to);
        context.updated(from, to);
        return;
      }
      if (!updateChildrenOnly) {
        context.patchAttributes(from, to);
      }
      context.updated(from, to);
      if (!skipChildren) {
        context.patchChildren(from, to);
      }
    };
    context.differentElementNamesTypesOrKeys = function(from, to) {
      return from.nodeType != to.nodeType || from.nodeName != to.nodeName || context.getKey(from) != context.getKey(to);
    };
    context.swapElements = function(from, to) {
      if (shouldSkip(context.removing, from))
        return;
      let toCloned = to.cloneNode(true);
      if (shouldSkip(context.adding, toCloned))
        return;
      from.replaceWith(toCloned);
      context.removed(from);
      context.added(toCloned);
    };
    context.patchNodeValue = function(from, to) {
      let value = to.nodeValue;
      if (from.nodeValue !== value) {
        from.nodeValue = value;
      }
    };
    context.patchAttributes = function(from, to) {
      if (from._x_transitioning)
        return;
      if (from._x_isShown && !to._x_isShown) {
        return;
      }
      if (!from._x_isShown && to._x_isShown) {
        return;
      }
      let domAttributes = Array.from(from.attributes);
      let toAttributes = Array.from(to.attributes);
      for (let i = domAttributes.length - 1; i >= 0; i--) {
        let name = domAttributes[i].name;
        if (!to.hasAttribute(name)) {
          if (name === "open" && from.nodeName === "DIALOG" && from.open) {
            from.close();
          } else {
            from.removeAttribute(name);
          }
        }
      }
      for (let i = toAttributes.length - 1; i >= 0; i--) {
        let name = toAttributes[i].name;
        let value = toAttributes[i].value;
        if (from.getAttribute(name) !== value) {
          from.setAttribute(name, value);
        }
      }
    };
    context.patchChildren = function(from, to) {
      let fromKeys = context.keyToMap(from.children);
      let fromKeyHoldovers = {};
      let currentTo = getFirstNode(to);
      let currentFrom = getFirstNode(from);
      while (currentTo) {
        seedingMatchingId(currentTo, currentFrom);
        let toKey = context.getKey(currentTo);
        let fromKey = context.getKey(currentFrom);
        if (context.skipUntilCondition) {
          let fromDone = !currentFrom || context.skipUntilCondition(currentFrom);
          let toDone = !currentTo || context.skipUntilCondition(currentTo);
          if (fromDone && toDone) {
            context.skipUntilCondition = null;
          } else {
            if (!fromDone)
              currentFrom = currentFrom && getNextSibling(from, currentFrom);
            if (!toDone)
              currentTo = currentTo && getNextSibling(to, currentTo);
            continue;
          }
        }
        if (!currentFrom) {
          if (toKey && fromKeyHoldovers[toKey]) {
            let holdover = fromKeyHoldovers[toKey];
            from.appendChild(holdover);
            currentFrom = holdover;
            fromKey = context.getKey(currentFrom);
          } else {
            if (!shouldSkip(context.adding, currentTo)) {
              let clone2 = currentTo.cloneNode(true);
              from.appendChild(clone2);
              context.added(clone2);
            }
            currentTo = getNextSibling(to, currentTo);
            continue;
          }
        }
        let isIf = (node) => node && node.nodeType === 8 && node.textContent === "[if BLOCK]><![endif]";
        let isEnd = (node) => node && node.nodeType === 8 && node.textContent === "[if ENDBLOCK]><![endif]";
        if (isIf(currentTo) && isIf(currentFrom)) {
          let nestedIfCount = 0;
          let fromBlockStart = currentFrom;
          while (currentFrom) {
            let next = getNextSibling(from, currentFrom);
            if (isIf(next)) {
              nestedIfCount++;
            } else if (isEnd(next) && nestedIfCount > 0) {
              nestedIfCount--;
            } else if (isEnd(next) && nestedIfCount === 0) {
              currentFrom = next;
              break;
            }
            currentFrom = next;
          }
          let fromBlockEnd = currentFrom;
          nestedIfCount = 0;
          let toBlockStart = currentTo;
          while (currentTo) {
            let next = getNextSibling(to, currentTo);
            if (isIf(next)) {
              nestedIfCount++;
            } else if (isEnd(next) && nestedIfCount > 0) {
              nestedIfCount--;
            } else if (isEnd(next) && nestedIfCount === 0) {
              currentTo = next;
              break;
            }
            currentTo = next;
          }
          let toBlockEnd = currentTo;
          let fromBlock = new Block(fromBlockStart, fromBlockEnd);
          let toBlock = new Block(toBlockStart, toBlockEnd);
          context.patchChildren(fromBlock, toBlock);
          continue;
        }
        if (currentFrom.nodeType === 1 && context.lookahead && !currentFrom.isEqualNode(currentTo)) {
          let nextToElementSibling = getNextSibling(to, currentTo);
          let found = false;
          while (!found && nextToElementSibling) {
            if (nextToElementSibling.nodeType === 1 && currentFrom.isEqualNode(nextToElementSibling)) {
              found = true;
              currentFrom = context.addNodeBefore(from, currentTo, currentFrom);
              fromKey = context.getKey(currentFrom);
            }
            nextToElementSibling = getNextSibling(to, nextToElementSibling);
          }
        }
        if (toKey !== fromKey) {
          if (!toKey && fromKey) {
            fromKeyHoldovers[fromKey] = currentFrom;
            currentFrom = context.addNodeBefore(from, currentTo, currentFrom);
            fromKeyHoldovers[fromKey].remove();
            currentFrom = getNextSibling(from, currentFrom);
            currentTo = getNextSibling(to, currentTo);
            continue;
          }
          if (toKey && !fromKey) {
            if (fromKeys[toKey]) {
              currentFrom.replaceWith(fromKeys[toKey]);
              currentFrom = fromKeys[toKey];
              fromKey = context.getKey(currentFrom);
            }
          }
          if (toKey && fromKey) {
            let fromKeyNode = fromKeys[toKey];
            if (fromKeyNode) {
              fromKeyHoldovers[fromKey] = currentFrom;
              currentFrom.replaceWith(fromKeyNode);
              currentFrom = fromKeyNode;
              fromKey = context.getKey(currentFrom);
            } else {
              fromKeyHoldovers[fromKey] = currentFrom;
              currentFrom = context.addNodeBefore(from, currentTo, currentFrom);
              fromKeyHoldovers[fromKey].remove();
              currentFrom = getNextSibling(from, currentFrom);
              currentTo = getNextSibling(to, currentTo);
              continue;
            }
          }
        }
        let currentFromNext = currentFrom && getNextSibling(from, currentFrom);
        context.patch(currentFrom, currentTo);
        if (currentFrom._x_lastRenderedEl) {
          currentFromNext = getNextSibling(from, currentFrom._x_lastRenderedEl);
        }
        currentTo = currentTo && getNextSibling(to, currentTo);
        currentFrom = currentFromNext;
      }
      let removals = [];
      while (currentFrom) {
        if (!shouldSkip(context.removing, currentFrom))
          removals.push(currentFrom);
        currentFrom = getNextSibling(from, currentFrom);
      }
      while (removals.length) {
        let domForRemoval = removals.shift();
        domForRemoval.remove();
        context.removed(domForRemoval);
      }
    };
    context.getKey = function(el) {
      return el && el.nodeType === 1 && context.key(el);
    };
    context.keyToMap = function(els) {
      let map = {};
      for (let el of els) {
        let theKey = context.getKey(el);
        if (theKey) {
          map[theKey] = el;
        }
      }
      return map;
    };
    context.addNodeBefore = function(parent, node, beforeMe) {
      if (!shouldSkip(context.adding, node)) {
        let clone2 = node.cloneNode(true);
        parent.insertBefore(clone2, beforeMe);
        context.added(clone2);
        return clone2;
      }
      return node;
    };
    return context;
  }
  morph.step = () => {
  };
  morph.log = () => {
  };
  function shouldSkip(hook, ...args) {
    let skip = false;
    hook(...args, () => skip = true);
    return skip;
  }
  function shouldSkipChildren(hook, skipChildren, skipUntil, ...args) {
    let skip = false;
    hook(...args, () => skip = true, skipChildren, skipUntil);
    return skip;
  }
  var patched = false;
  function createElement(html) {
    const template = document.createElement("template");
    template.innerHTML = html;
    return template.content.firstElementChild;
  }
  function textOrComment(el) {
    return el.nodeType === 3 || el.nodeType === 8;
  }
  var Block = class {
    constructor(start2, end) {
      this.startComment = start2;
      this.endComment = end;
    }
    get children() {
      let children2 = [];
      let currentNode = this.startComment.nextSibling;
      while (currentNode && currentNode !== this.endComment) {
        children2.push(currentNode);
        currentNode = currentNode.nextSibling;
      }
      return children2;
    }
    appendChild(child2) {
      this.endComment.before(child2);
    }
    get firstChild() {
      let first = this.startComment.nextSibling;
      if (first === this.endComment)
        return;
      return first;
    }
    nextNode(reference) {
      let next = reference.nextSibling;
      if (next === this.endComment)
        return;
      return next;
    }
    insertBefore(newNode, reference) {
      reference.before(newNode);
      return newNode;
    }
  };
  function getFirstNode(parent) {
    return parent.firstChild;
  }
  function getNextSibling(parent, reference) {
    let next;
    if (parent instanceof Block) {
      next = parent.nextNode(reference);
    } else {
      next = reference.nextSibling;
    }
    return next;
  }
  function monkeyPatchDomSetAttributeToAllowAtSymbols() {
    if (patched)
      return;
    patched = true;
    let original = Element.prototype.setAttribute;
    let hostDiv = document.createElement("div");
    Element.prototype.setAttribute = function newSetAttribute(name, value) {
      if (!name.includes("@")) {
        return original.call(this, name, value);
      }
      let escapedValue = value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
      hostDiv.innerHTML = `<span ${name}="${escapedValue}"></span>`;
      let attr = hostDiv.firstElementChild.getAttributeNode(name);
      hostDiv.firstElementChild.removeAttributeNode(attr);
      this.setAttributeNode(attr);
    };
  }
  function seedingMatchingId(to, from) {
    let fromId = from && from._x_bindings && from._x_bindings.id;
    if (!fromId)
      return;
    if (!to.setAttribute)
      return;
    to.setAttribute("id", fromId);
    to.id = fromId;
  }
  function src_default5(Alpine3) {
    Alpine3.morph = morph;
    Alpine3.morphBetween = morphBetween;
  }
  var module_default6 = src_default5;

  // node_modules/@ryangjchandler/alpine-clipboard/src/index.js
  var onCopy = () => {
  };
  var copy = (target, mimeType = void 0) => {
    if (typeof target === "function") {
      target = target();
    }
    if (typeof target === "object") {
      target = JSON.stringify(target);
    }
    if (mimeType !== void 0) {
      return window.navigator.clipboard.write([
        new ClipboardItem({
          [mimeType]: new Blob([target], {
            type: mimeType
          })
        })
      ]).then(onCopy);
    }
    return window.navigator.clipboard.writeText(target).then(onCopy);
  };
  function Clipboard(Alpine3) {
    Alpine3.magic("clipboard", () => {
      return copy;
    });
    Alpine3.directive("clipboard", (el, { modifiers, expression }, { evaluateLater: evaluateLater2, cleanup }) => {
      const getCopyContent = modifiers.includes("raw") ? (c) => c(expression) : evaluateLater2(expression);
      const clickHandler = () => getCopyContent(copy);
      el.addEventListener("click", clickHandler);
      cleanup(() => {
        el.removeEventListener("click", clickHandler);
      });
    });
  }
  Clipboard.configure = (config) => {
    if (config.hasOwnProperty("onCopy") && typeof config.onCopy === "function") {
      onCopy = config.onCopy;
    }
    return Clipboard;
  };
  var src_default6 = Clipboard;

  // src/scripts/utilities/get-section.js
  async function getSection({ sectionId, uri, pageAware, view }) {
    pageAware = pageAware || false;
    view = view || false;
    let root_url;
    view ? view = `&view=${view}` : view = "";
    pageAware ? root_url = window.location.pathname : root_url = window.Shopify.routes.root;
    uri.length > 0 ? uri = `${uri}&` : "";
    const response = await fetch(`${root_url}${uri}sections=${sectionId}${view}`);
    const responseJson = await response.json();
    return responseJson[sectionId];
  }
  function getSectionDOM({ html, selector }) {
    selector = selector || ".shopify-section";
    return new DOMParser().parseFromString(html, "text/html");
  }
  function reRenderSectionComponent({
    selector,
    section,
    newHTML,
    append: append2,
    removeEmpty
  }) {
    selector = selector || ".shopify-section";
    append2 = append2 || false;
    removeEmpty = removeEmpty || false;
    if (!section || !newHTML) return;
    const target = section.querySelector(selector);
    if (!target) return;
    const source = newHTML.querySelector(selector);
    if (!source) {
      if (removeEmpty) {
        target.innerHTML = "";
      }
      return;
    }
    const html = source.innerHTML;
    if (append2) {
      target.insertAdjacentHTML("beforeend", html);
    } else {
      target.innerHTML = html;
    }
  }
  var get_section_default = getSection;

  // src/scripts/components/cart.js
  var debug = false;
  var BUNDLE_PROPERTIES = [
    "_bundle_upgrades",
    "_bundle_render",
    "_bundle_upgrade_images",
    "_bundle_cleared"
  ];
  var Cart2 = {
    getSectionsToRender() {
      function getSectionId(selector) {
        return document.querySelector(selector)?.id?.replace("shopify-section-", "");
      }
      const mainCartId = getSectionId(".shopify-section--main-cart");
      const cartDrawerId = getSectionId(".shopify-section--cart-drawer");
      const headerCartId = getSectionId(".shopify-section-header");
      const sectionsToUpdate = [
        {
          id: mainCartId,
          selector: ".cart-page-inner"
        },
        {
          id: cartDrawerId,
          selector: ".cart-drawer-inner"
        },
        {
          id: headerCartId,
          selector: ".header-cart-bubble"
        }
      ];
      if (debug) {
        sectionsToUpdate.forEach((section) => {
          const element = document.querySelector(section.selector);
          if (element) {
            console.log(
              `HTML for section with ID "${section.id}" and selector "${section.selector}":`
            );
            console.log(element.outerHTML);
          } else {
            console.warn(`Element not found for selector "${section.selector}"`);
          }
        });
      }
      return sectionsToUpdate;
    },
    renderCartSections(sectionsHTML, morph2 = true) {
      const sectionsToUpdate = Cart2.getSectionsToRender();
      if (debug) console.log(this.renderCartSections);
      sectionsToUpdate?.map((section) => {
        const section_id = document.getElementById(
          `shopify-section-${section.id}`
        );
        if (!section_id || !sectionsHTML) return;
        let sectionSelector = section_id.querySelector(section.selector);
        let updatedContentDOM = getSectionDOM({
          html: sectionsHTML[section.id],
          selector: section.selector
        });
        let updatedContentElement = updatedContentDOM.querySelector(
          section.selector
        );
        let updatedContent = updatedContentElement ? updatedContentElement.outerHTML : null;
        if (morph2) {
          Alpine.morph(sectionSelector, updatedContent, {
            key: Cart2.getMorphKey,
            updating(el, toEl, childrenOnly, skip) {
              if (el.nodeName === "SHOPIFY-ACCELERATED-CHECKOUT-CART") skip();
            }
          });
        } else {
          sectionSelector.outerHTML = updatedContent;
        }
      });
      Cart2.ensureRecommendations();
    },
    getMorphKey(el) {
      if (el.nodeName !== "SHOPIFY-ACCELERATED-CHECKOUT-CART") {
        return el.getAttribute("key");
      }
      let wallets = "";
      try {
        wallets = JSON.parse(el.getAttribute("wallet-configs") || "[]").map((wallet) => wallet.name).join(",");
      } catch (_error) {
        wallets = "";
      }
      return [
        "cart-id",
        "access-token",
        "shop-id",
        "buyer-country",
        "buyer-locale",
        "buyer-currency",
        "requires-shipping",
        "enabled-flags"
      ].map((name) => el.getAttribute(name)).concat(wallets).join("|");
    },
    // The drawer's recommendation slider is server-rendered empty and filled by
    // islands/product-recommendations.js over a second request. That module is
    // loaded by an <is-land> script inside the drawer markup — but markup that
    // arrives through a section swap is parsed as a fragment, and a script parsed
    // that way is inert: it never executes, however many times the drawer is
    // re-rendered. So on a page that had not already loaded the module for its own
    // recommendations (and on the first add, when an empty cart meant the drawer
    // had no slider at all), nothing ever filled it. Load it here, from a script
    // that is always present, and call it directly once it is available.
    async ensureRecommendations() {
      const wrapper = document.querySelector(
        ".cart-drawer .product-recommendations__wrapper"
      );
      if (!wrapper) return;
      if (typeof window.renderProductRecommendations === "function") {
        window.renderProductRecommendations(
          null,
          wrapper.closest(".cart-drawer")
        );
        return;
      }
      const islandScript = wrapper.closest("is-land")?.querySelector("template[data-island]")?.content?.querySelector('script[type="module"]');
      const src = islandScript?.textContent?.match(
        /import\s+['"]([^'"]+)['"]/
      )?.[1];
      if (!src) return;
      try {
        await import(src);
      } catch (error2) {
        console.error("Error loading product recommendations:", error2);
      }
    },
    async updateQuantity(line, qty) {
      const sectionsToUpdate = Cart2.getSectionsToRender();
      const req = {
        quantity: qty,
        line,
        sections: Array.from(sectionsToUpdate, (section) => section.id)
      };
      if (debug) console.log(this.updateQuantity);
      try {
        const response = await fetch(
          window.Shopify.routes.root + "cart/change.js",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(req)
          }
        );
        const data2 = await response.json();
        if (!response.ok) {
          if (response.status == 422) {
            Alpine.store("errors").add(data2.message);
            throw new Error(`${data2.description}: ${data2.message}`);
          }
        }
        Cart2.renderCartSections(data2.sections);
      } catch (error2) {
        console.error("Error:", error2);
      }
      window.dispatchEvent(new Event("cart-updated"));
    },
    bundleSignature(properties, withoutVariantId) {
      const props = properties || {};
      const own = Object.keys(props).filter(
        (key) => !BUNDLE_PROPERTIES.includes(key) && props[key] !== "" && props[key] != null
      ).map((key) => `${key}=${props[key]}`).sort().join("\xA6");
      const upgrades = String(props._bundle_render || "").split(",").map((record) => record.split(":")[0]).filter((id) => id && id !== String(withoutVariantId)).sort().join(",");
      return `${own}#${upgrades}`;
    },
    upgradeProperties(properties, variantId, quantity) {
      const props = { ...properties || {} };
      let upgrades = [];
      try {
        upgrades = JSON.parse(props._bundle_upgrades || "[]");
      } catch (_error) {
        upgrades = [];
      }
      const gid = `gid://shopify/ProductVariant/${variantId}`;
      upgrades = upgrades.map((u) => u.id === gid ? { ...u, quantity } : u).filter((u) => Number(u.quantity) > 0);
      const render = (props._bundle_render || "").split(",").map((record) => record.split(":")).filter((parts) => parts[0] && parts[1]).map(([vid, handle]) => {
        const match = upgrades.find(
          (u) => u.id === `gid://shopify/ProductVariant/${vid}`
        );
        return match ? `${vid}:${handle}:${match.quantity}` : null;
      }).filter(Boolean).join(",");
      if (upgrades.length) {
        props._bundle_upgrades = JSON.stringify(upgrades);
        props._bundle_render = render;
      } else {
        delete props._bundle_upgrades;
        delete props._bundle_render;
        delete props._bundle_upgrade_images;
        const hasValue = Object.keys(props).some(
          (key) => props[key] !== "" && props[key] != null
        );
        if (!hasValue) props._bundle_cleared = "1";
      }
      return props;
    },
    async updateUpgradeQuantity(parentLine, parentKey, variantId, qty) {
      const sectionsToUpdate = Cart2.getSectionsToRender();
      const sections = Array.from(sectionsToUpdate, (section) => section.id);
      try {
        const cart = await fetch(window.Shopify.routes.root + "cart.js").then(
          (r) => r.json()
        );
        const parent = cart.items[parentLine - 1]?.key === parentKey ? cart.items[parentLine - 1] : cart.items.find((item) => item.key === parentKey) || cart.items[parentLine - 1];
        const hasUpgrade = String(parent?.properties?._bundle_render || "").split(",").some((record) => record.split(":")[0] === String(variantId));
        if (!parent || !hasUpgrade) {
          await Cart2.updateCart(false);
          return;
        }
        const signature = Cart2.bundleSignature(parent.properties, variantId);
        const spares = cart.items.filter(
          (item) => item.key !== parent.key && item.variant_id === parent.variant_id && Cart2.bundleSignature(item.properties) === signature
        );
        const spareCount = spares.reduce((sum, item) => sum + item.quantity, 0);
        const total = Math.min(
          Math.max(0, Math.floor(Number(qty) || 0)),
          parent.quantity + spareCount
        );
        const requests = [];
        const spare = spares[0];
        if (total === 0 && spare) {
          requests.push([
            "update",
            {
              updates: {
                [parent.key]: 0,
                [spare.key]: spare.quantity + parent.quantity
              }
            }
          ]);
        } else if (total === 0) {
          requests.push([
            "change",
            {
              id: parent.key,
              quantity: parent.quantity,
              properties: Cart2.upgradeProperties(parent.properties, variantId, 0)
            }
          ]);
        } else {
          if (total > parent.quantity) {
            let needed = total - parent.quantity;
            const updates = {};
            spares.forEach((line) => {
              const taken = Math.min(needed, line.quantity);
              if (!taken) return;
              updates[line.key] = line.quantity - taken;
              needed -= taken;
            });
            requests.push(["update", { updates }]);
          }
          requests.push([
            "change",
            {
              id: parent.key,
              quantity: total,
              properties: Cart2.upgradeProperties(parent.properties, variantId, 1)
            }
          ]);
          if (total < parent.quantity && spare) {
            requests.push([
              "update",
              {
                updates: {
                  [spare.key]: spare.quantity + parent.quantity - total
                }
              }
            ]);
          } else if (total < parent.quantity) {
            const { _bundle_cleared, ...properties } = Cart2.upgradeProperties(
              parent.properties,
              variantId,
              0
            );
            requests.push([
              "add",
              {
                items: [
                  {
                    id: parent.variant_id,
                    quantity: parent.quantity - total,
                    ...Object.keys(properties).length ? { properties } : {}
                  }
                ]
              }
            ]);
          }
        }
        let data2;
        for (const [index, [endpoint, body]] of requests.entries()) {
          const response = await fetch(
            window.Shopify.routes.root + `cart/${endpoint}.js`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(
                index === requests.length - 1 ? { ...body, sections } : body
              )
            }
          );
          data2 = await response.json();
          if (!response.ok) {
            if (response.status == 422) {
              Alpine.store("errors").add(data2.message);
            }
            await Cart2.updateCart(false);
            return;
          }
        }
        Cart2.renderCartSections(data2.sections);
      } catch (error2) {
        console.error("Error:", error2);
        await Cart2.updateCart(false);
      }
      window.dispatchEvent(new Event("cart-updated"));
    },
    async updateCart(toggleCart = true) {
      const sectionsToUpdate = Cart2.getSectionsToRender();
      if (debug) console.log(this.updateCart);
      try {
        const response = await fetch(
          window.Shopify.routes.root + `?sections=${Array.from(sectionsToUpdate, (section) => section.id)}`
        );
        const html = await response.text();
        const sectionsHTML = JSON.parse(html);
        Cart2.renderCartSections(sectionsHTML);
        window.dispatchEvent(new Event("cart-updated"));
        toggleCart && window.dispatchEvent(new Event("cart-toggle"));
      } catch (error2) {
        console.error("Error:", error2);
      }
    },
    async clearCart() {
      try {
        await fetch(window.Shopify.routes.root + "cart/clear.js", {
          method: "POST"
        });
        window.dispatchEvent(new Event("cart-updated"));
      } catch (error2) {
        console.error("Error:", error2);
      }
    },
    async addToCart(data2, { openDrawer = true } = {}) {
      if (debug) console.log(this.addToCart);
      const sectionsToUpdate = Cart2.getSectionsToRender();
      const headers = () => {
        return {
          Accept: `application/json`,
          "X-Requested-With": "XMLHttpRequest"
        };
      };
      data2.set(
        "sections",
        Array.from(sectionsToUpdate, (section) => section.id)
      );
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: "POST",
          headers: headers(),
          body: data2
        });
        const responseData = await response.json();
        const multiItem = Array.isArray(responseData.items);
        const added = response.ok && (responseData.id != void 0 || multiItem);
        if (!added) {
          if (responseData.id == void 0 && !multiItem && !responseData?.errors) {
            if (data2.has("requires_selling_plan") && data2.get("requires_selling_plan") == "true" && !data2.get("selling_plan")) {
              Alpine.store("errors").add(window.cartStrings.selling_plan_error);
            } else {
              Alpine.store("errors").add(window.cartStrings.error);
            }
            return false;
          } else {
            Alpine.store("errors").add(responseData.errors);
            if (!responseData.sections) {
              const responseSections = await fetch(
                window.routes.cart_url + `?sections=${Array.from(
                  sectionsToUpdate,
                  (section) => section.id
                )}`
              );
              responseData.sections = await responseSections.json();
            }
          }
        }
        if (added && !responseData.sections) {
          const responseSections = await fetch(
            window.routes.cart_url + `?sections=${Array.from(sectionsToUpdate, (section) => section.id)}`
          );
          responseData.sections = await responseSections.json();
        }
        if (responseData.sections) {
          Cart2.renderCartSections(responseData.sections, false);
          window.dispatchEvent(new Event("cart-updated"));
          if (openDrawer) {
            const drawer = document.querySelector("dialog.cart-drawer");
            const isDrawerOpen = drawer?.hasAttribute("open");
            if (!isDrawerOpen) {
              window.dispatchEvent(new Event("cart-toggle"));
            }
          }
        }
        return added;
      } catch (error2) {
        console.error("Error:", error2);
        return false;
      }
    }
  };
  window.Cart = Cart2;

  // node_modules/dialog-polyfill/dist/dialog-polyfill.esm.js
  var supportCustomEvent = window.CustomEvent;
  if (!supportCustomEvent || typeof supportCustomEvent === "object") {
    supportCustomEvent = function CustomEvent2(event, x) {
      x = x || {};
      var ev = document.createEvent("CustomEvent");
      ev.initCustomEvent(event, !!x.bubbles, !!x.cancelable, x.detail || null);
      return ev;
    };
    supportCustomEvent.prototype = window.Event.prototype;
  }
  function safeDispatchEvent(target, event) {
    var check = "on" + event.type.toLowerCase();
    if (typeof target[check] === "function") {
      target[check](event);
    }
    return target.dispatchEvent(event);
  }
  function createsStackingContext(el) {
    while (el && el !== document.body) {
      var s = window.getComputedStyle(el);
      var invalid = function(k, ok) {
        return !(s[k] === void 0 || s[k] === ok);
      };
      if (s.opacity < 1 || invalid("zIndex", "auto") || invalid("transform", "none") || invalid("mixBlendMode", "normal") || invalid("filter", "none") || invalid("perspective", "none") || s["isolation"] === "isolate" || s.position === "fixed" || s.webkitOverflowScrolling === "touch") {
        return true;
      }
      el = el.parentElement;
    }
    return false;
  }
  function findNearestDialog(el) {
    while (el) {
      if (el.localName === "dialog") {
        return (
          /** @type {HTMLDialogElement} */
          el
        );
      }
      if (el.parentElement) {
        el = el.parentElement;
      } else if (el.parentNode) {
        el = el.parentNode.host;
      } else {
        el = null;
      }
    }
    return null;
  }
  function safeBlur(el) {
    while (el && el.shadowRoot && el.shadowRoot.activeElement) {
      el = el.shadowRoot.activeElement;
    }
    if (el && el.blur && el !== document.body) {
      el.blur();
    }
  }
  function inNodeList(nodeList, node) {
    for (var i = 0; i < nodeList.length; ++i) {
      if (nodeList[i] === node) {
        return true;
      }
    }
    return false;
  }
  function isFormMethodDialog(el) {
    if (!el || !el.hasAttribute("method")) {
      return false;
    }
    return el.getAttribute("method").toLowerCase() === "dialog";
  }
  function findFocusableElementWithin(hostElement) {
    var opts = ["button", "input", "keygen", "select", "textarea"];
    var query2 = opts.map(function(el) {
      return el + ":not([disabled])";
    });
    query2.push('[tabindex]:not([disabled]):not([tabindex=""])');
    var target = hostElement.querySelector(query2.join(", "));
    if (!target && "attachShadow" in Element.prototype) {
      var elems = hostElement.querySelectorAll("*");
      for (var i = 0; i < elems.length; i++) {
        if (elems[i].tagName && elems[i].shadowRoot) {
          target = findFocusableElementWithin(elems[i].shadowRoot);
          if (target) {
            break;
          }
        }
      }
    }
    return target;
  }
  function isConnected(element) {
    return element.isConnected || document.body.contains(element);
  }
  function findFormSubmitter(event) {
    if (event.submitter) {
      return event.submitter;
    }
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) {
      return null;
    }
    var submitter = dialogPolyfill.formSubmitter;
    if (!submitter) {
      var target = event.target;
      var root = "getRootNode" in target && target.getRootNode() || document;
      submitter = root.activeElement;
    }
    if (!submitter || submitter.form !== form) {
      return null;
    }
    return submitter;
  }
  function maybeHandleSubmit(event) {
    if (event.defaultPrevented) {
      return;
    }
    var form = (
      /** @type {!HTMLFormElement} */
      event.target
    );
    var value = dialogPolyfill.imagemapUseValue;
    var submitter = findFormSubmitter(event);
    if (value === null && submitter) {
      value = submitter.value;
    }
    var dialog2 = findNearestDialog(form);
    if (!dialog2) {
      return;
    }
    var formmethod = submitter && submitter.getAttribute("formmethod") || form.getAttribute("method");
    if (formmethod !== "dialog") {
      return;
    }
    event.preventDefault();
    if (value != null) {
      dialog2.close(value);
    } else {
      dialog2.close();
    }
  }
  function dialogPolyfillInfo(dialog2) {
    this.dialog_ = dialog2;
    this.replacedStyleTop_ = false;
    this.openAsModal_ = false;
    if (!dialog2.hasAttribute("role")) {
      dialog2.setAttribute("role", "dialog");
    }
    dialog2.show = this.show.bind(this);
    dialog2.showModal = this.showModal.bind(this);
    dialog2.close = this.close.bind(this);
    dialog2.addEventListener("submit", maybeHandleSubmit, false);
    if (!("returnValue" in dialog2)) {
      dialog2.returnValue = "";
    }
    if ("MutationObserver" in window) {
      var mo = new MutationObserver(this.maybeHideModal.bind(this));
      mo.observe(dialog2, { attributes: true, attributeFilter: ["open"] });
    } else {
      var removed = false;
      var cb = function() {
        removed ? this.downgradeModal() : this.maybeHideModal();
        removed = false;
      }.bind(this);
      var timeout;
      var delayModel = function(ev) {
        if (ev.target !== dialog2) {
          return;
        }
        var cand = "DOMNodeRemoved";
        removed |= ev.type.substr(0, cand.length) === cand;
        window.clearTimeout(timeout);
        timeout = window.setTimeout(cb, 0);
      };
      ["DOMAttrModified", "DOMNodeRemoved", "DOMNodeRemovedFromDocument"].forEach(function(name) {
        dialog2.addEventListener(name, delayModel);
      });
    }
    Object.defineProperty(dialog2, "open", {
      set: this.setOpen.bind(this),
      get: dialog2.hasAttribute.bind(dialog2, "open")
    });
    this.backdrop_ = document.createElement("div");
    this.backdrop_.className = "backdrop";
    this.backdrop_.addEventListener("mouseup", this.backdropMouseEvent_.bind(this));
    this.backdrop_.addEventListener("mousedown", this.backdropMouseEvent_.bind(this));
    this.backdrop_.addEventListener("click", this.backdropMouseEvent_.bind(this));
  }
  dialogPolyfillInfo.prototype = /** @type {HTMLDialogElement.prototype} */
  {
    get dialog() {
      return this.dialog_;
    },
    /**
     * Maybe remove this dialog from the modal top layer. This is called when
     * a modal dialog may no longer be tenable, e.g., when the dialog is no
     * longer open or is no longer part of the DOM.
     */
    maybeHideModal: function() {
      if (this.dialog_.hasAttribute("open") && isConnected(this.dialog_)) {
        return;
      }
      this.downgradeModal();
    },
    /**
     * Remove this dialog from the modal top layer, leaving it as a non-modal.
     */
    downgradeModal: function() {
      if (!this.openAsModal_) {
        return;
      }
      this.openAsModal_ = false;
      this.dialog_.style.zIndex = "";
      if (this.replacedStyleTop_) {
        this.dialog_.style.top = "";
        this.replacedStyleTop_ = false;
      }
      this.backdrop_.parentNode && this.backdrop_.parentNode.removeChild(this.backdrop_);
      dialogPolyfill.dm.removeDialog(this);
    },
    /**
     * @param {boolean} value whether to open or close this dialog
     */
    setOpen: function(value) {
      if (value) {
        this.dialog_.hasAttribute("open") || this.dialog_.setAttribute("open", "");
      } else {
        this.dialog_.removeAttribute("open");
        this.maybeHideModal();
      }
    },
    /**
     * Handles mouse events ('mouseup', 'mousedown', 'click') on the fake .backdrop element, redirecting them as if
     * they were on the dialog itself.
     *
     * @param {!Event} e to redirect
     */
    backdropMouseEvent_: function(e) {
      if (!this.dialog_.hasAttribute("tabindex")) {
        var fake = document.createElement("div");
        this.dialog_.insertBefore(fake, this.dialog_.firstChild);
        fake.tabIndex = -1;
        fake.focus();
        this.dialog_.removeChild(fake);
      } else {
        this.dialog_.focus();
      }
      var redirectedEvent = document.createEvent("MouseEvents");
      redirectedEvent.initMouseEvent(
        e.type,
        e.bubbles,
        e.cancelable,
        window,
        e.detail,
        e.screenX,
        e.screenY,
        e.clientX,
        e.clientY,
        e.ctrlKey,
        e.altKey,
        e.shiftKey,
        e.metaKey,
        e.button,
        e.relatedTarget
      );
      this.dialog_.dispatchEvent(redirectedEvent);
      e.stopPropagation();
    },
    /**
     * Focuses on the first focusable element within the dialog. This will always blur the current
     * focus, even if nothing within the dialog is found.
     */
    focus_: function() {
      var target = this.dialog_.querySelector("[autofocus]:not([disabled])");
      if (!target && this.dialog_.tabIndex >= 0) {
        target = this.dialog_;
      }
      if (!target) {
        target = findFocusableElementWithin(this.dialog_);
      }
      safeBlur(document.activeElement);
      target && target.focus();
    },
    /**
     * Sets the zIndex for the backdrop and dialog.
     *
     * @param {number} dialogZ
     * @param {number} backdropZ
     */
    updateZIndex: function(dialogZ, backdropZ) {
      if (dialogZ < backdropZ) {
        throw new Error("dialogZ should never be < backdropZ");
      }
      this.dialog_.style.zIndex = dialogZ;
      this.backdrop_.style.zIndex = backdropZ;
    },
    /**
     * Shows the dialog. If the dialog is already open, this does nothing.
     */
    show: function() {
      if (!this.dialog_.open) {
        this.setOpen(true);
        this.focus_();
      }
    },
    /**
     * Show this dialog modally.
     */
    showModal: function() {
      if (this.dialog_.hasAttribute("open")) {
        throw new Error("Failed to execute 'showModal' on dialog: The element is already open, and therefore cannot be opened modally.");
      }
      if (!isConnected(this.dialog_)) {
        throw new Error("Failed to execute 'showModal' on dialog: The element is not in a Document.");
      }
      if (!dialogPolyfill.dm.pushDialog(this)) {
        throw new Error("Failed to execute 'showModal' on dialog: There are too many open modal dialogs.");
      }
      if (createsStackingContext(this.dialog_.parentElement)) {
        console.warn("A dialog is being shown inside a stacking context. This may cause it to be unusable. For more information, see this link: https://github.com/GoogleChrome/dialog-polyfill/#stacking-context");
      }
      this.setOpen(true);
      this.openAsModal_ = true;
      if (dialogPolyfill.needsCentering(this.dialog_)) {
        dialogPolyfill.reposition(this.dialog_);
        this.replacedStyleTop_ = true;
      } else {
        this.replacedStyleTop_ = false;
      }
      this.dialog_.parentNode.insertBefore(this.backdrop_, this.dialog_.nextSibling);
      this.focus_();
    },
    /**
     * Closes this HTMLDialogElement. This is optional vs clearing the open
     * attribute, however this fires a 'close' event.
     *
     * @param {string=} opt_returnValue to use as the returnValue
     */
    close: function(opt_returnValue) {
      if (!this.dialog_.hasAttribute("open")) {
        throw new Error("Failed to execute 'close' on dialog: The element does not have an 'open' attribute, and therefore cannot be closed.");
      }
      this.setOpen(false);
      if (opt_returnValue !== void 0) {
        this.dialog_.returnValue = opt_returnValue;
      }
      var closeEvent = new supportCustomEvent("close", {
        bubbles: false,
        cancelable: false
      });
      safeDispatchEvent(this.dialog_, closeEvent);
    }
  };
  var dialogPolyfill = {};
  dialogPolyfill.reposition = function(element) {
    var scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
    var topValue = scrollTop + (window.innerHeight - element.offsetHeight) / 2;
    element.style.top = Math.max(scrollTop, topValue) + "px";
  };
  dialogPolyfill.isInlinePositionSetByStylesheet = function(element) {
    for (var i = 0; i < document.styleSheets.length; ++i) {
      var styleSheet = document.styleSheets[i];
      var cssRules = null;
      try {
        cssRules = styleSheet.cssRules;
      } catch (e) {
      }
      if (!cssRules) {
        continue;
      }
      for (var j = 0; j < cssRules.length; ++j) {
        var rule = cssRules[j];
        var selectedNodes = null;
        try {
          selectedNodes = document.querySelectorAll(rule.selectorText);
        } catch (e) {
        }
        if (!selectedNodes || !inNodeList(selectedNodes, element)) {
          continue;
        }
        var cssTop = rule.style.getPropertyValue("top");
        var cssBottom = rule.style.getPropertyValue("bottom");
        if (cssTop && cssTop !== "auto" || cssBottom && cssBottom !== "auto") {
          return true;
        }
      }
    }
    return false;
  };
  dialogPolyfill.needsCentering = function(dialog2) {
    var computedStyle = window.getComputedStyle(dialog2);
    if (computedStyle.position !== "absolute") {
      return false;
    }
    if (dialog2.style.top !== "auto" && dialog2.style.top !== "" || dialog2.style.bottom !== "auto" && dialog2.style.bottom !== "") {
      return false;
    }
    return !dialogPolyfill.isInlinePositionSetByStylesheet(dialog2);
  };
  dialogPolyfill.forceRegisterDialog = function(element) {
    if (window.HTMLDialogElement || element.showModal) {
      console.warn("This browser already supports <dialog>, the polyfill may not work correctly", element);
    }
    if (element.localName !== "dialog") {
      throw new Error("Failed to register dialog: The element is not a dialog.");
    }
    new dialogPolyfillInfo(
      /** @type {!HTMLDialogElement} */
      element
    );
  };
  dialogPolyfill.registerDialog = function(element) {
    if (!element.showModal) {
      dialogPolyfill.forceRegisterDialog(element);
    }
  };
  dialogPolyfill.DialogManager = function() {
    this.pendingDialogStack = [];
    var checkDOM = this.checkDOM_.bind(this);
    this.overlay = document.createElement("div");
    this.overlay.className = "_dialog_overlay";
    this.overlay.addEventListener("click", function(e) {
      this.forwardTab_ = void 0;
      e.stopPropagation();
      checkDOM([]);
    }.bind(this));
    this.handleKey_ = this.handleKey_.bind(this);
    this.handleFocus_ = this.handleFocus_.bind(this);
    this.zIndexLow_ = 1e5;
    this.zIndexHigh_ = 1e5 + 150;
    this.forwardTab_ = void 0;
    if ("MutationObserver" in window) {
      this.mo_ = new MutationObserver(function(records) {
        var removed = [];
        records.forEach(function(rec) {
          for (var i = 0, c; c = rec.removedNodes[i]; ++i) {
            if (!(c instanceof Element)) {
              continue;
            } else if (c.localName === "dialog") {
              removed.push(c);
            }
            removed = removed.concat(c.querySelectorAll("dialog"));
          }
        });
        removed.length && checkDOM(removed);
      });
    }
  };
  dialogPolyfill.DialogManager.prototype.blockDocument = function() {
    document.documentElement.addEventListener("focus", this.handleFocus_, true);
    document.addEventListener("keydown", this.handleKey_);
    this.mo_ && this.mo_.observe(document, { childList: true, subtree: true });
  };
  dialogPolyfill.DialogManager.prototype.unblockDocument = function() {
    document.documentElement.removeEventListener("focus", this.handleFocus_, true);
    document.removeEventListener("keydown", this.handleKey_);
    this.mo_ && this.mo_.disconnect();
  };
  dialogPolyfill.DialogManager.prototype.updateStacking = function() {
    var zIndex = this.zIndexHigh_;
    for (var i = 0, dpi; dpi = this.pendingDialogStack[i]; ++i) {
      dpi.updateZIndex(--zIndex, --zIndex);
      if (i === 0) {
        this.overlay.style.zIndex = --zIndex;
      }
    }
    var last = this.pendingDialogStack[0];
    if (last) {
      var p = last.dialog.parentNode || document.body;
      p.appendChild(this.overlay);
    } else if (this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
  };
  dialogPolyfill.DialogManager.prototype.containedByTopDialog_ = function(candidate) {
    while (candidate = findNearestDialog(candidate)) {
      for (var i = 0, dpi; dpi = this.pendingDialogStack[i]; ++i) {
        if (dpi.dialog === candidate) {
          return i === 0;
        }
      }
      candidate = candidate.parentElement;
    }
    return false;
  };
  dialogPolyfill.DialogManager.prototype.handleFocus_ = function(event) {
    var target = event.composedPath ? event.composedPath()[0] : event.target;
    if (this.containedByTopDialog_(target)) {
      return;
    }
    if (document.activeElement === document.documentElement) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    safeBlur(
      /** @type {Element} */
      target
    );
    if (this.forwardTab_ === void 0) {
      return;
    }
    var dpi = this.pendingDialogStack[0];
    var dialog2 = dpi.dialog;
    var position = dialog2.compareDocumentPosition(target);
    if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      if (this.forwardTab_) {
        dpi.focus_();
      } else if (target !== document.documentElement) {
        document.documentElement.focus();
      }
    }
    return false;
  };
  dialogPolyfill.DialogManager.prototype.handleKey_ = function(event) {
    this.forwardTab_ = void 0;
    if (event.keyCode === 27) {
      event.preventDefault();
      event.stopPropagation();
      var cancelEvent = new supportCustomEvent("cancel", {
        bubbles: false,
        cancelable: true
      });
      var dpi = this.pendingDialogStack[0];
      if (dpi && safeDispatchEvent(dpi.dialog, cancelEvent)) {
        dpi.dialog.close();
      }
    } else if (event.keyCode === 9) {
      this.forwardTab_ = !event.shiftKey;
    }
  };
  dialogPolyfill.DialogManager.prototype.checkDOM_ = function(removed) {
    var clone2 = this.pendingDialogStack.slice();
    clone2.forEach(function(dpi) {
      if (removed.indexOf(dpi.dialog) !== -1) {
        dpi.downgradeModal();
      } else {
        dpi.maybeHideModal();
      }
    });
  };
  dialogPolyfill.DialogManager.prototype.pushDialog = function(dpi) {
    var allowed = (this.zIndexHigh_ - this.zIndexLow_) / 2 - 1;
    if (this.pendingDialogStack.length >= allowed) {
      return false;
    }
    if (this.pendingDialogStack.unshift(dpi) === 1) {
      this.blockDocument();
    }
    this.updateStacking();
    return true;
  };
  dialogPolyfill.DialogManager.prototype.removeDialog = function(dpi) {
    var index = this.pendingDialogStack.indexOf(dpi);
    if (index === -1) {
      return;
    }
    this.pendingDialogStack.splice(index, 1);
    if (this.pendingDialogStack.length === 0) {
      this.unblockDocument();
    }
    this.updateStacking();
  };
  dialogPolyfill.dm = new dialogPolyfill.DialogManager();
  dialogPolyfill.formSubmitter = null;
  dialogPolyfill.imagemapUseValue = null;
  if (window.HTMLDialogElement === void 0) {
    testForm = document.createElement("form");
    testForm.setAttribute("method", "dialog");
    if (testForm.method !== "dialog") {
      methodDescriptor = Object.getOwnPropertyDescriptor(HTMLFormElement.prototype, "method");
      if (methodDescriptor) {
        realGet = methodDescriptor.get;
        methodDescriptor.get = function() {
          if (isFormMethodDialog(this)) {
            return "dialog";
          }
          return realGet.call(this);
        };
        realSet = methodDescriptor.set;
        methodDescriptor.set = function(v) {
          if (typeof v === "string" && v.toLowerCase() === "dialog") {
            return this.setAttribute("method", v);
          }
          return realSet.call(this, v);
        };
        Object.defineProperty(HTMLFormElement.prototype, "method", methodDescriptor);
      }
    }
    document.addEventListener("click", function(ev) {
      dialogPolyfill.formSubmitter = null;
      dialogPolyfill.imagemapUseValue = null;
      if (ev.defaultPrevented) {
        return;
      }
      var target = (
        /** @type {Element} */
        ev.target
      );
      if ("composedPath" in ev) {
        var path = ev.composedPath();
        target = path.shift() || target;
      }
      if (!target || !isFormMethodDialog(target.form)) {
        return;
      }
      var valid = target.type === "submit" && ["button", "input"].indexOf(target.localName) > -1;
      if (!valid) {
        if (!(target.localName === "input" && target.type === "image")) {
          return;
        }
        dialogPolyfill.imagemapUseValue = ev.offsetX + "," + ev.offsetY;
      }
      var dialog2 = findNearestDialog(target);
      if (!dialog2) {
        return;
      }
      dialogPolyfill.formSubmitter = target;
    }, false);
    document.addEventListener("submit", function(ev) {
      var form = ev.target;
      var dialog2 = findNearestDialog(form);
      if (dialog2) {
        return;
      }
      var submitter = findFormSubmitter(ev);
      var formmethod = submitter && submitter.getAttribute("formmethod") || form.getAttribute("method");
      if (formmethod === "dialog") {
        ev.preventDefault();
      }
    });
    nativeFormSubmit = HTMLFormElement.prototype.submit;
    replacementFormSubmit = function() {
      if (!isFormMethodDialog(this)) {
        return nativeFormSubmit.call(this);
      }
      var dialog2 = findNearestDialog(this);
      dialog2 && dialog2.close();
    };
    HTMLFormElement.prototype.submit = replacementFormSubmit;
  }
  var testForm;
  var methodDescriptor;
  var realGet;
  var realSet;
  var nativeFormSubmit;
  var replacementFormSubmit;
  var dialog_polyfill_esm_default = dialogPolyfill;

  // src/scripts/utilities/browsing-context.js
  var getBrowsingContext = async () => await fetch(
    window.Shopify.routes.root + `browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${window.Shopify.country}&language[enabled]=true&language[exclude]=${window.Shopify.language}`
  ).then((data2) => data2.json());
  var browsing_context_default = getBrowsingContext;

  // src/scripts/components/localisation.js
  function setBrowsingContext(data2) {
    const dialog2 = document.getElementById("localisation-popup");
    if (!dialog2) return;
    const detectedCountry = data2.detected_values.country.handle;
    const language = data2.suggestions[0].parts.language.handle;
    const form = dialog2?.querySelector("form");
    const submit = document.getElementById("submit-localisation");
    const languageInput = dialog2.querySelector('input[name="language_code"]');
    const countryInput = dialog2.querySelector('input[name="country_code"]');
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const queryStringCountry = urlParams.get("country");
    submit.autofocus = false;
    function setLocalisationDefaults() {
      const countryList = document.getElementById("CountryList");
      const languageList = document.getElementById("LanguageList");
      const languageListButton = languageList.closest(".disclosure").querySelector(".disclosure__button_text");
      const countryListButton = countryList.closest(".disclosure").querySelector(".disclosure__button_text");
      let availableCountry = queryStringCountry || detectedCountry;
      const languageListItem = languageList.querySelector(`[lang="${language}"]`);
      const countryListItem = countryList.querySelector(
        `[country="${availableCountry}"]`
      );
      const flag = dialog2.querySelectorAll(".localisation__country_flag img")[0];
      const country_tag = dialog2.querySelectorAll(".country_tag")[0];
      if (languageListItem) {
        languageInput.value = language;
        languageListButton.innerHTML = languageList.querySelector(
          `[lang="${language}"]`
        ).text;
      }
      if (countryListItem) {
        const countryName = countryListItem.getAttribute("country_name");
        countryInput.value = availableCountry;
        countryListButton.innerHTML = countryListItem.text;
        country_tag.innerHTML = countryName;
        flag.setAttribute(
          "src",
          `http://cdn.shopify.com/static/images/flags/${availableCountry.toLowerCase()}.svg?width=80`
        );
        flag.setAttribute(
          "srcset",
          `http://cdn.shopify.com/static/images/flags/${availableCountry.toLowerCase()}.svg?width=80`
        );
        flag.setAttribute("alt", countryName);
      }
    }
    function setLocalisationPreferences(country, language2) {
      localStorage.setItem(
        "localisationPreferences",
        JSON.stringify({
          country,
          language: language2
        })
      );
    }
    const localisationPreferences = JSON.parse(
      localStorage.getItem("localisationPreferences")
    );
    if (!localisationPreferences) {
      setLocalisationDefaults();
      dialog2.showModal();
    } else {
      if (queryStringCountry)
        setLocalisationPreferences(queryStringCountry, languageInput.value);
    }
    function submitLocalisationForm() {
      setLocalisationPreferences(countryInput.value, languageInput.value);
      if (form) form.submit();
    }
    function localisationPopup() {
      const close = dialog2.querySelector(".close");
      close.addEventListener("click", () => {
        dialog2.close();
        submitLocalisationForm();
      });
      dialog2.addEventListener("cancel", () => {
        dialog2.close();
        submitLocalisationForm();
      });
      submit.addEventListener("click", (e) => {
        e.preventDefault();
        submitLocalisationForm();
      });
    }
    localisationPopup();
  }
  browsing_context_default().then((data2) => setBrowsingContext(data2));
  var openDialog = document.getElementById("openDialog");
  var dialog = document.getElementById("localisation-popup");
  if (dialog) dialog_polyfill_esm_default.registerDialog(dialog);
  openDialog?.addEventListener("click", () => {
    dialog?.showModal();
  });

  // src/scripts/components/fake-variant-progress.js
  var CHANGE_EVENT = "fake-variant:changed";
  var FakeVariantProgress = class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute("section-id") || null;
      this.optionNames = [];
      this.selected = {};
      this.touched = /* @__PURE__ */ new Set();
      this._onChange = this.handleChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this._onChange);
    }
    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }
    handleChange(event) {
      const detail = event.detail || {};
      if (this.sectionId && detail.sectionId && String(detail.sectionId) !== String(this.sectionId)) {
        return;
      }
      const selected = detail.selected || {};
      const names = Object.keys(selected);
      if (!detail.initial) {
        names.forEach((name) => {
          if (this.selected[name] === selected[name]) return;
          if (selected[name] == null) this.touched.delete(name);
          else this.touched.add(name);
        });
      }
      this.optionNames = names;
      this.selected = { ...selected };
      this.render();
    }
    render() {
      this.replaceChildren();
      if (!this.optionNames.length) return;
      const total = this.optionNames.length;
      const done = this.optionNames.filter(
        (name) => this.touched.has(name)
      ).length;
      const percent = total ? Math.round(done / total * 100) : 0;
      const bar = document.createElement("div");
      bar.className = "fv-progress__bar";
      const fill = document.createElement("span");
      fill.className = "fv-progress__bar-fill";
      fill.style.width = `${percent}%`;
      bar.appendChild(fill);
      const count = document.createElement("p");
      count.className = "fv-progress__count";
      count.textContent = `${done} of ${total} selected`;
      const steps = document.createElement("ol");
      steps.className = "fv-progress__steps";
      this.optionNames.forEach((name, index) => {
        const isDone = this.touched.has(name);
        const step = document.createElement("li");
        step.className = `fv-progress__step${isDone ? " is-done" : ""}`;
        const marker = document.createElement("span");
        marker.className = "fv-progress__marker";
        marker.textContent = isDone ? "\u2713" : String(index + 1);
        const label = document.createElement("span");
        label.className = "fv-progress__label";
        const nameEl = document.createElement("span");
        nameEl.className = "fv-progress__name";
        nameEl.textContent = name;
        const valueEl = document.createElement("span");
        valueEl.className = "fv-progress__value";
        valueEl.textContent = this.selected[name] ?? "";
        label.append(nameEl, valueEl);
        step.append(marker, label);
        steps.appendChild(step);
      });
      this.append(bar, count, steps);
    }
  };
  if (!customElements.get("fake-variant-progress")) {
    customElements.define("fake-variant-progress", FakeVariantProgress);
  }

  // src/scripts/components/configurator-media.js
  var CHANGE_EVENT2 = "configurator:media-changed";
  var RANK = { LIFTING: 0, FAKE: 10, COLOUR: 50, UPGRADES: 60 };
  var entries = /* @__PURE__ */ new Map();
  var lastChanged = null;
  var cssEscape = (value) => window.CSS?.escape ? CSS.escape(String(value)) : String(value);
  var imageSrc = (img) => img ? img.getAttribute("src") || img.dataset?.src || null : null;
  var RESOLVED_EVENT = "configurator:resolved-image";
  var eqi = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  var parsedRules = { text: null, rules: [] };
  function readRules() {
    const el = document.querySelector("[data-configurator-image-rules]");
    if (!el) {
      if (window.configuratorMedia?.logging) {
        console.warn(
          "[configurator-media] no [data-configurator-image-rules] on the page \u2014 the configurator_image_rules metafield is empty/unset, under a different key, or the theme running here doesn't emit it yet."
        );
      }
      return [];
    }
    const text = (el.textContent || "").trim();
    if (!text) return [];
    if (parsedRules.text === text) return parsedRules.rules;
    try {
      let data2 = JSON.parse(text);
      if (typeof data2 === "string") data2 = JSON.parse(data2);
      const list2 = Array.isArray(data2) ? data2 : data2.rules || [];
      const rules = list2.filter((rule) => rule && rule.when && rule.image);
      parsedRules = { text, rules };
      return rules;
    } catch (error2) {
      if (window.configuratorMedia?.logging) {
        console.warn("[configurator-media] could not parse image rules:", error2);
      }
      return [];
    }
  }
  function readContext() {
    const el = document.querySelector("[data-configurator-context]");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || "{}") || {};
    } catch {
      return {};
    }
  }
  function currentProductId() {
    const el = document.querySelector('[data-product-json="selected-variant"]');
    if (!el) return "";
    try {
      return String(JSON.parse(el.textContent).product_id ?? "");
    } catch {
      return "";
    }
  }
  function currentState() {
    const state = { generic: {} };
    const context = readContext();
    Object.keys(context).forEach((name) => {
      const key = name.toLowerCase();
      state.generic[key] = context[name];
      if (key.includes("orient")) state.orientation = context[name];
      else if (key.includes("size")) state.size = context[name];
      else if (key.includes("cabinet")) state.cabinet = context[name];
      else if (key.includes("platform")) state.platform = context[name];
    });
    entries.forEach((entry) => {
      const step = (entry.step || "").toLowerCase();
      state.generic[step] = entry.value;
      if (entry.kind === "fake") {
        if (step.includes("orient")) state.orientation = entry.value;
        else if (step.includes("size")) state.size = entry.value;
      } else if (entry.kind === "colour") {
        if (step.includes("cabinet")) state.cabinet = entry.value;
        else if (step.includes("platform")) state.platform = entry.value;
      } else if (entry.kind === "lifting") {
        state.lifting = entry.value;
      }
    });
    state.upgrades = selectedUpgrades();
    return state;
  }
  function selectedUpgrades() {
    const list2 = [];
    document.querySelectorAll(
      "[data-product-upgrades] input[data-upgrade-product-id]:checked"
    ).forEach((input) => {
      const id = (input.dataset.upgradeProductId || "").toString();
      if (!id || id === "0") return;
      const variant = document.querySelector(
        `[data-product-upgrades] input[data-upgrade-variant][data-upgrade-of="${cssEscape(
          id
        )}"]:checked`
      );
      list2.push({
        id,
        handle: input.dataset.upgradeHandle || "",
        title: upgradeTitle(id) || "",
        colour: variant?.dataset.upgradeColor || "",
        size: variant?.dataset.upgradeSize || "",
        variantId: variant?.value ? String(variant.value) : ""
      });
    });
    return list2;
  }
  function parseUpgradeToken(raw2) {
    const parts = String(raw2).split(":");
    return {
      token: (parts[0] || "").trim(),
      colour: parts.length > 1 ? (parts[1] || "").trim() : null,
      size: parts.length > 2 ? parts.slice(2).join(":").trim() : null
    };
  }
  function upgradeReqMatches(req, selected) {
    const { token, colour, size } = parseUpgradeToken(req);
    return selected.some((upgrade) => {
      const tokenHit = token === "" || eqi(upgrade.id, token) || eqi(upgrade.handle, token) || eqi(upgrade.title, token);
      if (!tokenHit) return false;
      if (colour && !(eqi(upgrade.colour, colour) || eqi(upgrade.variantId, colour)))
        return false;
      if (size && !eqi(upgrade.size, size)) return false;
      return true;
    });
  }
  function ruleMatches(when, state) {
    const selected = state.upgrades || [];
    return Object.keys(when).every((rawKey) => {
      const key = rawKey.toLowerCase();
      const cond = when[rawKey];
      if (key === "upgrade") return upgradeReqMatches(cond, selected);
      if (key === "upgrades") {
        const reqs = Array.isArray(cond) ? cond : [cond];
        return reqs.every((req) => upgradeReqMatches(req, selected));
      }
      let actual;
      if (key === "size") actual = state.size;
      else if (key === "orientation") actual = state.orientation;
      else if (key === "cabinet") actual = state.cabinet;
      else if (key === "platform") actual = state.platform;
      else if (key === "lifting" || key === "lifting method")
        actual = state.lifting;
      else actual = state.generic[key];
      return actual != null && eqi(actual, cond);
    });
  }
  function ruleWeight(when) {
    let weight = 0;
    Object.keys(when).forEach((rawKey) => {
      const key = rawKey.toLowerCase();
      const value = when[rawKey];
      if (key === "upgrades") {
        const reqs = Array.isArray(value) ? value : [value];
        reqs.forEach((req) => {
          weight += String(req).split(":").length;
        });
      } else if (key === "upgrade") {
        weight += String(value).split(":").length;
      } else {
        weight += 1;
      }
    });
    return weight;
  }
  function findBestRule(rules, state) {
    let best = null;
    let bestScore = -1;
    let bestPriority = -Infinity;
    rules.forEach((rule) => {
      if (!ruleMatches(rule.when, state)) return;
      const score = ruleWeight(rule.when);
      const priority = Number(rule.priority) || 0;
      if (score > bestScore || score === bestScore && priority >= bestPriority) {
        best = rule;
        bestScore = score;
        bestPriority = priority;
      }
    });
    return best;
  }
  function imageKey(url) {
    if (!url) return "";
    const name = String(url).split("?")[0].split("#")[0].split("/").pop() || "";
    try {
      return decodeURIComponent(name).toLowerCase();
    } catch {
      return name.toLowerCase();
    }
  }
  function imageKeyOf(img) {
    return imageKey(img?.getAttribute?.("src") || img?.dataset?.src);
  }
  function variantIdsOf(img) {
    const raw2 = img?.dataset?.variants;
    if (!raw2) return [];
    try {
      const parsed = JSON.parse(raw2);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return String(raw2).match(/\d+/g) || [];
    }
  }
  function selectedVariantId(scope2) {
    const root = scope2 || document.querySelector(".product") || document;
    const select = root.querySelector('select[name="id"]');
    if (select?.value) return String(select.value);
    const checked = root.querySelector('input[name="id"]:checked');
    return checked?.value ? String(checked.value) : "";
  }
  var COLOUR_OPTION_RE = /colou?r|cabinet|platform|fabric/;
  var NON_WORD_RE = /[^a-z0-9]+/;
  function altWords(text) {
    return String(text).toLowerCase().split(NON_WORD_RE).filter(Boolean);
  }
  function colourOptionGroups(scope2) {
    const root = scope2 || document.querySelector(".product") || document;
    const accordion = root.querySelector(".fv-color-accordion");
    if (!accordion) return [];
    const groups = [];
    accordion.querySelectorAll("fieldset[data-option-name]").forEach((fieldset) => {
      const name = (fieldset.dataset.optionName || "").toLowerCase();
      if (!COLOUR_OPTION_RE.test(name)) return;
      const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
      if (!inputs.length) return;
      groups.push({
        name: fieldset.dataset.optionName || "",
        position: parseInt(fieldset.dataset.optionPosition, 10) || groups.length + 1,
        values: inputs.map((input) => input.value),
        selected: inputs.find((input) => input.checked)?.value ?? null
      });
    });
    return groups.sort((a, b) => a.position - b.position);
  }
  function colourVocabulary(groups) {
    const vocab = /* @__PURE__ */ new Set();
    groups.forEach((group) => {
      const sets = group.values.map((value) => new Set(altWords(value)));
      if (sets.length < 2) return;
      const counts = /* @__PURE__ */ new Map();
      sets.forEach(
        (set2) => set2.forEach((word) => counts.set(word, (counts.get(word) || 0) + 1))
      );
      counts.forEach((count, word) => {
        if (count < sets.length) vocab.add(word);
      });
    });
    return vocab;
  }
  function selectedColourCode(groups, vocab) {
    const code = [];
    for (let index = 0; index < groups.length; index += 1) {
      const group = groups[index];
      if (group.selected == null) return null;
      altWords(group.selected).forEach((word) => {
        if (vocab.has(word)) code.push(word);
      });
    }
    return code.length ? code : null;
  }
  function altColourRuns(alt, vocab) {
    const runs = [];
    let run = [];
    altWords(alt).forEach((word) => {
      if (vocab.has(word)) {
        run.push(word);
        return;
      }
      if (run.length) runs.push(run);
      run = [];
    });
    if (run.length) runs.push(run);
    return runs;
  }
  function colourCode(scope2) {
    const groups = colourOptionGroups(scope2);
    const vocabulary = colourVocabulary(groups);
    return {
      options: groups,
      vocabulary: [...vocabulary],
      code: selectedColourCode(groups, vocabulary)
    };
  }
  function colourCodeFilter(scope2) {
    const { vocabulary, code } = colourCode(scope2);
    if (!code) return null;
    const vocab = new Set(vocabulary);
    const expected = code.join(" ");
    return (img) => {
      const alt = img?.dataset?.mediaAlt ?? img?.getAttribute?.("alt");
      if (!alt) return null;
      const runs = altColourRuns(alt, vocab).filter(
        (run) => run.length === code.length
      );
      if (!runs.length) return null;
      return runs.some((run) => run.join(" ") === expected);
    };
  }
  function selectionFilter(scope2) {
    const state = currentState();
    const variantId = selectedVariantId(scope2);
    const colourCoded = colourCodeFilter(scope2);
    const ruled = /* @__PURE__ */ new Set();
    const matched = /* @__PURE__ */ new Set();
    readRules().forEach((rule) => {
      const key = imageKey(rule.image);
      if (!key) return;
      ruled.add(key);
      if (ruleMatches(rule.when, state)) matched.add(key);
    });
    const rulesApply = matched.size > 0;
    return {
      state,
      variantId,
      key: imageKeyOf,
      matches(img) {
        if (!img) return true;
        const coded = colourCoded ? colourCoded(img) : null;
        if (coded !== null) return coded;
        const variants = variantIdsOf(img);
        if (variants.length) return !variantId || variants.includes(variantId);
        if (!rulesApply) return true;
        const key = imageKeyOf(img);
        return !key || !ruled.has(key) || matched.has(key);
      }
    };
  }
  function runResolver() {
    const rules = readRules();
    if (!rules.length) return;
    const state = currentState();
    const rule = findBestRule(rules, state);
    if (!rule) return;
    if (window.configuratorMedia?.logging) {
      console.log(
        `[configurator-media] rule matched (weight ${ruleWeight(
          rule.when
        )}) \u2192 gallery override:`,
        rule.image,
        rule.when
      );
    }
    document.dispatchEvent(
      new CustomEvent(RESOLVED_EVENT, {
        detail: {
          productId: currentProductId(),
          src: rule.image,
          alt: rule.alt || null
        }
      })
    );
  }
  function imageForUpgrade(baseState, upgrade) {
    const rules = readRules();
    if (!rules.length) return null;
    const rule = findBestRule(rules, { ...baseState, upgrades: [upgrade] });
    return rule ? rule.image : null;
  }
  function upgradeMeta(scope2, id) {
    const input = scope2.querySelector(
      `input[data-upgrade-product-id="${cssEscape(id)}"]`
    );
    return {
      id: String(id),
      handle: input?.dataset.upgradeHandle || "",
      title: upgradeTitle(id) || "",
      variantId: ""
    };
  }
  function paintSwatch(swatch, image) {
    if (swatch.dataset.baseStyle === void 0) {
      swatch.dataset.baseStyle = swatch.getAttribute("style") || "";
    }
    if (!image) {
      swatch.setAttribute("style", swatch.dataset.baseStyle);
      return;
    }
    swatch.style.backgroundImage = `url("${configuratorMedia.resize(
      image,
      160
    )}")`;
    swatch.style.backgroundSize = "cover";
    swatch.style.backgroundPosition = "center";
    swatch.style.backgroundRepeat = "no-repeat";
  }
  function paintUpgradeSwatches() {
    const scope2 = document.querySelector("[data-product-upgrades]");
    if (!scope2) return;
    const rules = readRules();
    if (!rules.length) return;
    const base = currentState();
    scope2.querySelectorAll(".fv-upgrade-variant").forEach((label) => {
      const input = label.querySelector("input[data-upgrade-variant]");
      const swatch = label.querySelector(".fv-upgrade-variant__swatch");
      if (!input || !swatch) return;
      const meta = upgradeMeta(scope2, input.dataset.upgradeOf);
      const image = imageForUpgrade(base, {
        ...meta,
        colour: input.dataset.upgradeColor || ""
      });
      paintSwatch(swatch, image);
    });
  }
  function seedCheckedColours() {
    document.querySelectorAll(".fv-color-accordion .radio__input:checked").forEach((input) => {
      const label = input.closest(".radio__label");
      if (label) setColour(colourEntryFrom(label));
    });
  }
  function runAll() {
    seedCheckedColours();
    runResolver();
    paintUpgradeSwatches();
  }
  function scheduleResolve() {
    queueMicrotask(runAll);
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => runAll());
    } else {
      setTimeout(runAll, 50);
    }
  }
  function list() {
    return [...entries.values()].sort((a, b) => a.rank - b.rank).map(({ step, value, image, color }) => ({
      step,
      value,
      image: image ?? null,
      color: color ?? null,
      mediaType: image ? "image" : color ? "color" : "none"
    }));
  }
  function images() {
    return list().map((entry) => entry.image).filter(Boolean);
  }
  function dropKind(kind) {
    entries.forEach((entry, key) => {
      if (entry.kind === kind) entries.delete(key);
    });
  }
  function publish() {
    const all = list();
    const current = lastChanged ? all.find((entry) => entry.step === lastChanged) ?? null : null;
    const detail = {
      entries: all,
      images: all.map((entry) => entry.image).filter(Boolean),
      current
    };
    if (window.configuratorMedia?.logging) {
      if (current) {
        console.log(
          `[configurator-media] gallery should show \u2192 ${current.step}: "${current.value}" \u2014`,
          current.image || (current.color ? `(no image \u2014 swatch colour ${current.color})` : "(no image \u2014 keep the current/default image)")
        );
      }
      console.log("[configurator-media]", all.slice().reverse());
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT2, { detail }));
    scheduleResolve();
  }
  function onLiftingChanged(event) {
    const detail = event.detail || {};
    const step = detail.step || "Lifting Method";
    dropKind("lifting");
    entries.set(step, {
      step,
      value: detail.title || detail.handle || "",
      image: detail.image || null,
      rank: RANK.LIFTING,
      kind: "lifting"
    });
    lastChanged = step;
    publish();
  }
  function onFakeVariantChanged(event) {
    const detail = event.detail || {};
    const selected = detail.selected || {};
    const media = detail.media || {};
    if (detail.initial) dropKind("fake");
    Object.keys(selected).forEach((name, index) => {
      if (selected[name] == null) {
        entries.delete(name);
        return;
      }
      const previous = entries.get(name);
      if (!detail.initial && previous?.value !== selected[name]) {
        lastChanged = name;
      }
      entries.set(name, {
        step: name,
        value: selected[name],
        image: media[name] ?? entries.get(name)?.image ?? null,
        rank: RANK.FAKE + index,
        kind: "fake"
      });
    });
    if (detail.initial || detail.swapped !== false) {
      dropKind("colour");
      dropKind("upgrades");
    }
    publish();
  }
  function colourEntryFrom(label) {
    const accordion = label.closest(".fv-color-accordion");
    const input = label.querySelector('input[type="radio"]');
    if (!accordion || !input) return null;
    const fieldset = label.closest("fieldset[data-option-name]");
    const step = fieldset?.dataset.optionName || accordion.dataset.optionName || "Colour";
    const position = parseInt(fieldset?.dataset.optionPosition, 10) || 1;
    const value = input.value;
    const scope2 = label.closest(".product") || document;
    const slide = scope2.querySelector(
      `[data-color-swatch-slide="${cssEscape(value)}"] img`
    );
    let image = imageSrc(slide);
    let color = null;
    const chip = label.querySelector(".radio__value--color");
    if (!image && chip) {
      const match = (chip.style.backgroundImage || "").match(
        /url\(["']?([^"')]+)["']?\)/
      );
      if (match) image = match[1];
      else if (chip.style.backgroundColor) color = chip.style.backgroundColor;
    }
    return { step, value, image, color, position };
  }
  function setColour(data2) {
    if (!data2) return;
    const current = entries.get(data2.step);
    if (current && current.kind === "colour" && current.value === data2.value && current.image === data2.image) {
      return;
    }
    entries.set(data2.step, {
      ...data2,
      rank: RANK.COLOUR + (data2.position - 1),
      kind: "colour"
    });
    lastChanged = data2.step;
    publish();
  }
  function onColourClick(event) {
    const label = event.target?.closest?.(".fv-color-accordion .radio__label");
    if (!label) return;
    setColour(colourEntryFrom(label));
  }
  function onColourChange(event) {
    const label = event.target?.closest?.(".fv-color-accordion .radio__label");
    if (!label) return;
    setColour(colourEntryFrom(label));
  }
  function upgradeImage(upgradeId, variantId) {
    if (variantId) {
      const variantTemplate = document.querySelector(
        `template[data-upgrade-variant-gallery="${cssEscape(upgradeId)}"]`
      );
      const match = variantTemplate ? [...variantTemplate.content.querySelectorAll("img")].find(
        (img) => (img.dataset.variants || "").includes(variantId)
      ) : null;
      if (match) return imageSrc(match);
    }
    const template = document.querySelector(
      `template[data-upgrade-gallery="${cssEscape(upgradeId)}"]`
    );
    const imgs = template?.content?.querySelectorAll("img");
    if (!imgs || !imgs.length) return null;
    if (variantId) {
      const match = [...imgs].find(
        (img) => (img.dataset.variants || "").includes(variantId)
      );
      if (match) return imageSrc(match);
    }
    return imageSrc(imgs[0]);
  }
  function upgradeTitle(id) {
    return document.querySelector(
      `[data-product-upgrades] input[data-upgrade-product-id="${cssEscape(
        id
      )}"]`
    )?.closest(".fv-upgrade")?.querySelector(".fv-upgrade__label")?.textContent.trim();
  }
  function onUpgradeChanged(event) {
    const id = (event.detail?.upgradeProductId ?? "0").toString();
    const selectedIds = Array.isArray(event.detail?.selectedUpgradeIds) ? event.detail.selectedUpgradeIds.map(String) : id === "0" ? [] : [id];
    const input = document.querySelector(
      `[data-product-upgrades] input[data-upgrade-product-id="${cssEscape(id)}"]`
    );
    const step = input?.closest("[data-option-name]")?.dataset.optionName || "Upgrades";
    dropKind("upgrades");
    if (!selectedIds.length) {
      const noneLabel = upgradeTitle("0") || "No upgrades";
      entries.set(step, {
        step,
        value: noneLabel,
        image: null,
        rank: RANK.UPGRADES,
        kind: "upgrades",
        meta: { upgradeId: null, baseValue: noneLabel }
      });
    } else {
      const value = selectedIds.map((upgradeId) => upgradeTitle(upgradeId) || upgradeId).join(" + ");
      const imageId = id === "0" ? selectedIds[selectedIds.length - 1] : id;
      const defaultVariantId = (event.detail?.variantId ?? "").toString() || document.querySelector(
        `[data-product-upgrades] input[data-upgrade-product-id="${cssEscape(
          imageId
        )}"]`
      )?.dataset.upgradeVariantId || null;
      entries.set(step, {
        step,
        value,
        image: upgradeImage(imageId, defaultVariantId),
        rank: RANK.UPGRADES,
        kind: "upgrades",
        meta: { upgradeId: imageId, baseValue: value }
      });
    }
    lastChanged = step;
    publish();
  }
  function onUpgradeVariantChanged(event) {
    const variantId = (event.detail?.variantId ?? "").toString();
    const upgradeId = (event.detail?.upgradeProductId ?? "").toString();
    const entry = [...entries.values()].find((e) => e.kind === "upgrades");
    if (!entry || !entry.meta?.upgradeId || !variantId) return;
    if (upgradeId && upgradeId !== entry.meta.upgradeId) return;
    const label = document.querySelector(
      `[data-product-upgrades] input[data-upgrade-variant-id="${cssEscape(
        variantId
      )}"]`
    )?.closest(".fv-upgrade-variant")?.querySelector(".fv-upgrade-variant__label")?.textContent.trim();
    entry.image = upgradeImage(entry.meta.upgradeId, variantId) || entry.image;
    entry.value = label ? `${entry.meta.baseValue}, ${label}` : entry.value;
    lastChanged = entry.step;
    publish();
  }
  var configuratorMedia = {
    EVENT: CHANGE_EVENT2,
    RESOLVED_EVENT,
    logging: false,
    entries: list,
    reversed: () => list().reverse(),
    images,
    get: (step) => list().find((entry) => entry.step === step),
    state: currentState,
    rules: readRules,
    context: readContext,
    resolve: runResolver,
    // The conditional image for the current configurator state (selected options +
    // upgrades) from sw_configurator_image_rules, or null when no rule matches.
    resolvedImage: () => {
      const rules = readRules();
      if (!rules.length) return null;
      const rule = findBestRule(rules, currentState());
      return rule ? rule.image : null;
    },
    // The conditional image resolved for ONE specific upgrade within the current
    // configurator state, reusing the existing rule logic (imageForUpgrade). Used
    // to persist each upgrade's conditional image onto its cart line. `upgrade` is
    // { id, handle, colour, size, variantId }. Returns null when no rule matches.
    conditionalImageForUpgrade: (upgrade) => upgrade ? imageForUpgrade(currentState(), {
      title: upgradeTitle(upgrade.id) || "",
      ...upgrade
    }) : null,
    paintSwatches: paintUpgradeSwatches,
    imageKey,
    selectedVariantId,
    selectionFilter,
    colourCode,
    debug() {
      const rulesEl = document.querySelector("[data-configurator-image-rules]");
      const ctxEl = document.querySelector("[data-configurator-context]");
      console.log(
        "[configurator-media] rules script on page:",
        !!rulesEl,
        rulesEl ? `(${(rulesEl.textContent || "").trim().length} chars)` : ""
      );
      console.log(
        "[configurator-media] context script on page:",
        !!ctxEl,
        ctxEl ? (ctxEl.textContent || "").trim() : ""
      );
      console.log("[configurator-media] parsed rules:", readRules());
      console.log("[configurator-media] state:", currentState());
      console.log("[configurator-media] colour code:", colourCode());
      console.log(
        "[configurator-media] selected variant id:",
        selectedVariantId() || "(none)"
      );
    },
    resize(url, width) {
      if (!url) return url;
      try {
        const resized = new URL(url, window.location.origin);
        resized.searchParams.set("width", String(width));
        return resized.toString();
      } catch {
        return url;
      }
    }
  };
  window.configuratorMedia = configuratorMedia;
  var configuratorMediaHandlers = [
    ["lifting-method:changed", onLiftingChanged],
    ["fake-variant:changed", onFakeVariantChanged],
    ["click", onColourClick],
    ["change", onColourChange],
    ["swanky:upgrade-changed", onUpgradeChanged],
    ["swanky:upgrade-variant-changed", onUpgradeVariantChanged],
    ["configurator:request-resolve", scheduleResolve],
    ["swanky:upgrade-changed", scheduleResolve],
    ["swanky:upgrade-variant-changed", scheduleResolve],
    ["change", scheduleResolve],
    ["component:hydrated", scheduleResolve]
  ];
  if (window.__configuratorMediaHandlers) {
    window.__configuratorMediaHandlers.forEach(
      ([event, handler4]) => document.removeEventListener(event, handler4)
    );
  }
  configuratorMediaHandlers.forEach(
    ([event, handler4]) => document.addEventListener(event, handler4)
  );
  window.__configuratorMediaHandlers = configuratorMediaHandlers;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleResolve);
  } else {
    scheduleResolve();
  }

  // node_modules/@splidejs/splide/dist/js/splide.esm.js
  function _defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }
  function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    Object.defineProperty(Constructor, "prototype", { writable: false });
    return Constructor;
  }
  var MEDIA_PREFERS_REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
  var CREATED = 1;
  var MOUNTED = 2;
  var IDLE = 3;
  var MOVING = 4;
  var SCROLLING = 5;
  var DRAGGING = 6;
  var DESTROYED = 7;
  var STATES = {
    CREATED,
    MOUNTED,
    IDLE,
    MOVING,
    SCROLLING,
    DRAGGING,
    DESTROYED
  };
  function empty(array) {
    array.length = 0;
  }
  function slice(arrayLike, start2, end) {
    return Array.prototype.slice.call(arrayLike, start2, end);
  }
  function apply2(func) {
    return func.bind.apply(func, [null].concat(slice(arguments, 1)));
  }
  var nextTick2 = setTimeout;
  var noop = function noop2() {
  };
  function raf(func) {
    return requestAnimationFrame(func);
  }
  function typeOf(type, subject) {
    return typeof subject === type;
  }
  function isObject3(subject) {
    return !isNull(subject) && typeOf("object", subject);
  }
  var isArray2 = Array.isArray;
  var isFunction = apply2(typeOf, "function");
  var isString2 = apply2(typeOf, "string");
  var isUndefined = apply2(typeOf, "undefined");
  function isNull(subject) {
    return subject === null;
  }
  function isHTMLElement(subject) {
    try {
      return subject instanceof (subject.ownerDocument.defaultView || window).HTMLElement;
    } catch (e) {
      return false;
    }
  }
  function toArray(value) {
    return isArray2(value) ? value : [value];
  }
  function forEach(values, iteratee) {
    toArray(values).forEach(iteratee);
  }
  function includes(array, value) {
    return array.indexOf(value) > -1;
  }
  function push(array, items) {
    array.push.apply(array, toArray(items));
    return array;
  }
  function toggleClass(elm, classes, add) {
    if (elm) {
      forEach(classes, function(name) {
        if (name) {
          elm.classList[add ? "add" : "remove"](name);
        }
      });
    }
  }
  function addClass(elm, classes) {
    toggleClass(elm, isString2(classes) ? classes.split(" ") : classes, true);
  }
  function append(parent, children2) {
    forEach(children2, parent.appendChild.bind(parent));
  }
  function before(nodes, ref) {
    forEach(nodes, function(node) {
      var parent = (ref || node).parentNode;
      if (parent) {
        parent.insertBefore(node, ref);
      }
    });
  }
  function matches(elm, selector) {
    return isHTMLElement(elm) && (elm["msMatchesSelector"] || elm.matches).call(elm, selector);
  }
  function children(parent, selector) {
    var children2 = parent ? slice(parent.children) : [];
    return selector ? children2.filter(function(child2) {
      return matches(child2, selector);
    }) : children2;
  }
  function child(parent, selector) {
    return selector ? children(parent, selector)[0] : parent.firstElementChild;
  }
  var ownKeys = Object.keys;
  function forOwn(object, iteratee, right) {
    if (object) {
      (right ? ownKeys(object).reverse() : ownKeys(object)).forEach(function(key) {
        key !== "__proto__" && iteratee(object[key], key);
      });
    }
    return object;
  }
  function assign(object) {
    slice(arguments, 1).forEach(function(source) {
      forOwn(source, function(value, key) {
        object[key] = source[key];
      });
    });
    return object;
  }
  function merge(object) {
    slice(arguments, 1).forEach(function(source) {
      forOwn(source, function(value, key) {
        if (isArray2(value)) {
          object[key] = value.slice();
        } else if (isObject3(value)) {
          object[key] = merge({}, isObject3(object[key]) ? object[key] : {}, value);
        } else {
          object[key] = value;
        }
      });
    });
    return object;
  }
  function omit(object, keys) {
    forEach(keys || ownKeys(object), function(key) {
      delete object[key];
    });
  }
  function removeAttribute(elms, attrs) {
    forEach(elms, function(elm) {
      forEach(attrs, function(attr) {
        elm && elm.removeAttribute(attr);
      });
    });
  }
  function setAttribute(elms, attrs, value) {
    if (isObject3(attrs)) {
      forOwn(attrs, function(value2, name) {
        setAttribute(elms, name, value2);
      });
    } else {
      forEach(elms, function(elm) {
        isNull(value) || value === "" ? removeAttribute(elm, attrs) : elm.setAttribute(attrs, String(value));
      });
    }
  }
  function create(tag, attrs, parent) {
    var elm = document.createElement(tag);
    if (attrs) {
      isString2(attrs) ? addClass(elm, attrs) : setAttribute(elm, attrs);
    }
    parent && append(parent, elm);
    return elm;
  }
  function style(elm, prop, value) {
    if (isUndefined(value)) {
      return getComputedStyle(elm)[prop];
    }
    if (!isNull(value)) {
      elm.style[prop] = "" + value;
    }
  }
  function display(elm, display2) {
    style(elm, "display", display2);
  }
  function focus(elm) {
    elm["setActive"] && elm["setActive"]() || elm.focus({
      preventScroll: true
    });
  }
  function getAttribute(elm, attr) {
    return elm.getAttribute(attr);
  }
  function hasClass(elm, className) {
    return elm && elm.classList.contains(className);
  }
  function rect(target) {
    return target.getBoundingClientRect();
  }
  function remove(nodes) {
    forEach(nodes, function(node) {
      if (node && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
  }
  function parseHtml(html) {
    return child(new DOMParser().parseFromString(html, "text/html").body);
  }
  function prevent(e, stopPropagation) {
    e.preventDefault();
    if (stopPropagation) {
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  }
  function query(parent, selector) {
    return parent && parent.querySelector(selector);
  }
  function queryAll(parent, selector) {
    return selector ? slice(parent.querySelectorAll(selector)) : [];
  }
  function removeClass(elm, classes) {
    toggleClass(elm, classes, false);
  }
  function timeOf(e) {
    return e.timeStamp;
  }
  function unit(value) {
    return isString2(value) ? value : value ? value + "px" : "";
  }
  var PROJECT_CODE = "splide";
  var DATA_ATTRIBUTE = "data-" + PROJECT_CODE;
  function assert(condition, message) {
    if (!condition) {
      throw new Error("[" + PROJECT_CODE + "] " + (message || ""));
    }
  }
  var min = Math.min;
  var max = Math.max;
  var floor = Math.floor;
  var ceil = Math.ceil;
  var abs = Math.abs;
  function approximatelyEqual(x, y, epsilon) {
    return abs(x - y) < epsilon;
  }
  function between(number, x, y, exclusive) {
    var minimum = min(x, y);
    var maximum = max(x, y);
    return exclusive ? minimum < number && number < maximum : minimum <= number && number <= maximum;
  }
  function clamp(number, x, y) {
    var minimum = min(x, y);
    var maximum = max(x, y);
    return min(max(minimum, number), maximum);
  }
  function sign(x) {
    return +(x > 0) - +(x < 0);
  }
  function format(string, replacements) {
    forEach(replacements, function(replacement) {
      string = string.replace("%s", "" + replacement);
    });
    return string;
  }
  function pad(number) {
    return number < 10 ? "0" + number : "" + number;
  }
  var ids = {};
  function uniqueId(prefix2) {
    return "" + prefix2 + pad(ids[prefix2] = (ids[prefix2] || 0) + 1);
  }
  function EventBinder() {
    var listeners = [];
    function bind3(targets, events, callback, options) {
      forEachEvent(targets, events, function(target, event, namespace) {
        var isEventTarget = "addEventListener" in target;
        var remover = isEventTarget ? target.removeEventListener.bind(target, event, callback, options) : target["removeListener"].bind(target, callback);
        isEventTarget ? target.addEventListener(event, callback, options) : target["addListener"](callback);
        listeners.push([target, event, namespace, callback, remover]);
      });
    }
    function unbind(targets, events, callback) {
      forEachEvent(targets, events, function(target, event, namespace) {
        listeners = listeners.filter(function(listener) {
          if (listener[0] === target && listener[1] === event && listener[2] === namespace && (!callback || listener[3] === callback)) {
            listener[4]();
            return false;
          }
          return true;
        });
      });
    }
    function dispatch2(target, type, detail) {
      var e;
      var bubbles = true;
      if (typeof CustomEvent === "function") {
        e = new CustomEvent(type, {
          bubbles,
          detail
        });
      } else {
        e = document.createEvent("CustomEvent");
        e.initCustomEvent(type, bubbles, false, detail);
      }
      target.dispatchEvent(e);
      return e;
    }
    function forEachEvent(targets, events, iteratee) {
      forEach(targets, function(target) {
        target && forEach(events, function(events2) {
          events2.split(" ").forEach(function(eventNS) {
            var fragment = eventNS.split(".");
            iteratee(target, fragment[0], fragment[1]);
          });
        });
      });
    }
    function destroy() {
      listeners.forEach(function(data2) {
        data2[4]();
      });
      empty(listeners);
    }
    return {
      bind: bind3,
      unbind,
      dispatch: dispatch2,
      destroy
    };
  }
  var EVENT_MOUNTED = "mounted";
  var EVENT_READY = "ready";
  var EVENT_MOVE = "move";
  var EVENT_MOVED = "moved";
  var EVENT_CLICK = "click";
  var EVENT_ACTIVE = "active";
  var EVENT_INACTIVE = "inactive";
  var EVENT_VISIBLE = "visible";
  var EVENT_HIDDEN = "hidden";
  var EVENT_REFRESH = "refresh";
  var EVENT_UPDATED = "updated";
  var EVENT_RESIZE = "resize";
  var EVENT_RESIZED = "resized";
  var EVENT_DRAG = "drag";
  var EVENT_DRAGGING = "dragging";
  var EVENT_DRAGGED = "dragged";
  var EVENT_SCROLL = "scroll";
  var EVENT_SCROLLED = "scrolled";
  var EVENT_OVERFLOW = "overflow";
  var EVENT_DESTROY = "destroy";
  var EVENT_ARROWS_MOUNTED = "arrows:mounted";
  var EVENT_ARROWS_UPDATED = "arrows:updated";
  var EVENT_PAGINATION_MOUNTED = "pagination:mounted";
  var EVENT_PAGINATION_UPDATED = "pagination:updated";
  var EVENT_NAVIGATION_MOUNTED = "navigation:mounted";
  var EVENT_AUTOPLAY_PLAY = "autoplay:play";
  var EVENT_AUTOPLAY_PLAYING = "autoplay:playing";
  var EVENT_AUTOPLAY_PAUSE = "autoplay:pause";
  var EVENT_LAZYLOAD_LOADED = "lazyload:loaded";
  var EVENT_SLIDE_KEYDOWN = "sk";
  var EVENT_SHIFTED = "sh";
  var EVENT_END_INDEX_CHANGED = "ei";
  function EventInterface(Splide2) {
    var bus = Splide2 ? Splide2.event.bus : document.createDocumentFragment();
    var binder = EventBinder();
    function on2(events, callback) {
      binder.bind(bus, toArray(events).join(" "), function(e) {
        callback.apply(callback, isArray2(e.detail) ? e.detail : []);
      });
    }
    function emit(event) {
      binder.dispatch(bus, event, slice(arguments, 1));
    }
    if (Splide2) {
      Splide2.event.on(EVENT_DESTROY, binder.destroy);
    }
    return assign(binder, {
      bus,
      on: on2,
      off: apply2(binder.unbind, bus),
      emit
    });
  }
  function RequestInterval(interval, onInterval, onUpdate, limit) {
    var now = Date.now;
    var startTime;
    var rate = 0;
    var id;
    var paused = true;
    var count = 0;
    function update() {
      if (!paused) {
        rate = interval ? min((now() - startTime) / interval, 1) : 1;
        onUpdate && onUpdate(rate);
        if (rate >= 1) {
          onInterval();
          startTime = now();
          if (limit && ++count >= limit) {
            return pause();
          }
        }
        id = raf(update);
      }
    }
    function start2(resume) {
      resume || cancel();
      startTime = now() - (resume ? rate * interval : 0);
      paused = false;
      id = raf(update);
    }
    function pause() {
      paused = true;
    }
    function rewind() {
      startTime = now();
      rate = 0;
      if (onUpdate) {
        onUpdate(rate);
      }
    }
    function cancel() {
      id && cancelAnimationFrame(id);
      rate = 0;
      id = 0;
      paused = true;
    }
    function set2(time) {
      interval = time;
    }
    function isPaused() {
      return paused;
    }
    return {
      start: start2,
      rewind,
      pause,
      cancel,
      set: set2,
      isPaused
    };
  }
  function State(initialState) {
    var state = initialState;
    function set2(value) {
      state = value;
    }
    function is(states) {
      return includes(toArray(states), state);
    }
    return {
      set: set2,
      is
    };
  }
  function Throttle(func, duration) {
    var interval = RequestInterval(duration || 0, func, null, 1);
    return function() {
      interval.isPaused() && interval.start();
    };
  }
  function Media(Splide2, Components2, options) {
    var state = Splide2.state;
    var breakpoints = options.breakpoints || {};
    var reducedMotion = options.reducedMotion || {};
    var binder = EventBinder();
    var queries = [];
    function setup() {
      var isMin = options.mediaQuery === "min";
      ownKeys(breakpoints).sort(function(n, m) {
        return isMin ? +n - +m : +m - +n;
      }).forEach(function(key) {
        register(breakpoints[key], "(" + (isMin ? "min" : "max") + "-width:" + key + "px)");
      });
      register(reducedMotion, MEDIA_PREFERS_REDUCED_MOTION);
      update();
    }
    function destroy(completely) {
      if (completely) {
        binder.destroy();
      }
    }
    function register(options2, query2) {
      var queryList = matchMedia(query2);
      binder.bind(queryList, "change", update);
      queries.push([options2, queryList]);
    }
    function update() {
      var destroyed = state.is(DESTROYED);
      var direction = options.direction;
      var merged = queries.reduce(function(merged2, entry) {
        return merge(merged2, entry[1].matches ? entry[0] : {});
      }, {});
      omit(options);
      set2(merged);
      if (options.destroy) {
        Splide2.destroy(options.destroy === "completely");
      } else if (destroyed) {
        destroy(true);
        Splide2.mount();
      } else {
        direction !== options.direction && Splide2.refresh();
      }
    }
    function reduce2(enable) {
      if (matchMedia(MEDIA_PREFERS_REDUCED_MOTION).matches) {
        enable ? merge(options, reducedMotion) : omit(options, ownKeys(reducedMotion));
      }
    }
    function set2(opts, base, notify) {
      merge(options, opts);
      base && merge(Object.getPrototypeOf(options), opts);
      if (notify || !state.is(CREATED)) {
        Splide2.emit(EVENT_UPDATED, options);
      }
    }
    return {
      setup,
      destroy,
      reduce: reduce2,
      set: set2
    };
  }
  var ARROW = "Arrow";
  var ARROW_LEFT = ARROW + "Left";
  var ARROW_RIGHT = ARROW + "Right";
  var ARROW_UP = ARROW + "Up";
  var ARROW_DOWN = ARROW + "Down";
  var RTL = "rtl";
  var TTB = "ttb";
  var ORIENTATION_MAP = {
    width: ["height"],
    left: ["top", "right"],
    right: ["bottom", "left"],
    x: ["y"],
    X: ["Y"],
    Y: ["X"],
    ArrowLeft: [ARROW_UP, ARROW_RIGHT],
    ArrowRight: [ARROW_DOWN, ARROW_LEFT]
  };
  function Direction(Splide2, Components2, options) {
    function resolve(prop, axisOnly, direction) {
      direction = direction || options.direction;
      var index = direction === RTL && !axisOnly ? 1 : direction === TTB ? 0 : -1;
      return ORIENTATION_MAP[prop] && ORIENTATION_MAP[prop][index] || prop.replace(/width|left|right/i, function(match, offset) {
        var replacement = ORIENTATION_MAP[match.toLowerCase()][index] || match;
        return offset > 0 ? replacement.charAt(0).toUpperCase() + replacement.slice(1) : replacement;
      });
    }
    function orient(value) {
      return value * (options.direction === RTL ? 1 : -1);
    }
    return {
      resolve,
      orient
    };
  }
  var ROLE = "role";
  var TAB_INDEX = "tabindex";
  var DISABLED = "disabled";
  var ARIA_PREFIX = "aria-";
  var ARIA_CONTROLS = ARIA_PREFIX + "controls";
  var ARIA_CURRENT = ARIA_PREFIX + "current";
  var ARIA_SELECTED = ARIA_PREFIX + "selected";
  var ARIA_LABEL = ARIA_PREFIX + "label";
  var ARIA_LABELLEDBY = ARIA_PREFIX + "labelledby";
  var ARIA_HIDDEN = ARIA_PREFIX + "hidden";
  var ARIA_ORIENTATION = ARIA_PREFIX + "orientation";
  var ARIA_ROLEDESCRIPTION = ARIA_PREFIX + "roledescription";
  var ARIA_LIVE = ARIA_PREFIX + "live";
  var ARIA_BUSY = ARIA_PREFIX + "busy";
  var ARIA_ATOMIC = ARIA_PREFIX + "atomic";
  var ALL_ATTRIBUTES = [ROLE, TAB_INDEX, DISABLED, ARIA_CONTROLS, ARIA_CURRENT, ARIA_LABEL, ARIA_LABELLEDBY, ARIA_HIDDEN, ARIA_ORIENTATION, ARIA_ROLEDESCRIPTION];
  var CLASS_PREFIX = PROJECT_CODE + "__";
  var STATUS_CLASS_PREFIX = "is-";
  var CLASS_ROOT = PROJECT_CODE;
  var CLASS_TRACK = CLASS_PREFIX + "track";
  var CLASS_LIST = CLASS_PREFIX + "list";
  var CLASS_SLIDE = CLASS_PREFIX + "slide";
  var CLASS_CLONE = CLASS_SLIDE + "--clone";
  var CLASS_CONTAINER = CLASS_SLIDE + "__container";
  var CLASS_ARROWS = CLASS_PREFIX + "arrows";
  var CLASS_ARROW = CLASS_PREFIX + "arrow";
  var CLASS_ARROW_PREV = CLASS_ARROW + "--prev";
  var CLASS_ARROW_NEXT = CLASS_ARROW + "--next";
  var CLASS_PAGINATION = CLASS_PREFIX + "pagination";
  var CLASS_PAGINATION_PAGE = CLASS_PAGINATION + "__page";
  var CLASS_PROGRESS = CLASS_PREFIX + "progress";
  var CLASS_PROGRESS_BAR = CLASS_PROGRESS + "__bar";
  var CLASS_TOGGLE = CLASS_PREFIX + "toggle";
  var CLASS_TOGGLE_PLAY = CLASS_TOGGLE + "__play";
  var CLASS_TOGGLE_PAUSE = CLASS_TOGGLE + "__pause";
  var CLASS_SPINNER = CLASS_PREFIX + "spinner";
  var CLASS_SR = CLASS_PREFIX + "sr";
  var CLASS_INITIALIZED = STATUS_CLASS_PREFIX + "initialized";
  var CLASS_ACTIVE = STATUS_CLASS_PREFIX + "active";
  var CLASS_PREV = STATUS_CLASS_PREFIX + "prev";
  var CLASS_NEXT = STATUS_CLASS_PREFIX + "next";
  var CLASS_VISIBLE = STATUS_CLASS_PREFIX + "visible";
  var CLASS_LOADING = STATUS_CLASS_PREFIX + "loading";
  var CLASS_FOCUS_IN = STATUS_CLASS_PREFIX + "focus-in";
  var CLASS_OVERFLOW = STATUS_CLASS_PREFIX + "overflow";
  var STATUS_CLASSES = [CLASS_ACTIVE, CLASS_VISIBLE, CLASS_PREV, CLASS_NEXT, CLASS_LOADING, CLASS_FOCUS_IN, CLASS_OVERFLOW];
  var CLASSES = {
    slide: CLASS_SLIDE,
    clone: CLASS_CLONE,
    arrows: CLASS_ARROWS,
    arrow: CLASS_ARROW,
    prev: CLASS_ARROW_PREV,
    next: CLASS_ARROW_NEXT,
    pagination: CLASS_PAGINATION,
    page: CLASS_PAGINATION_PAGE,
    spinner: CLASS_SPINNER
  };
  function closest(from, selector) {
    if (isFunction(from.closest)) {
      return from.closest(selector);
    }
    var elm = from;
    while (elm && elm.nodeType === 1) {
      if (matches(elm, selector)) {
        break;
      }
      elm = elm.parentElement;
    }
    return elm;
  }
  var FRICTION = 5;
  var LOG_INTERVAL = 200;
  var POINTER_DOWN_EVENTS = "touchstart mousedown";
  var POINTER_MOVE_EVENTS = "touchmove mousemove";
  var POINTER_UP_EVENTS = "touchend touchcancel mouseup click";
  function Elements(Splide2, Components2, options) {
    var _EventInterface = EventInterface(Splide2), on2 = _EventInterface.on, bind3 = _EventInterface.bind;
    var root = Splide2.root;
    var i18n = options.i18n;
    var elements = {};
    var slides = [];
    var rootClasses = [];
    var trackClasses = [];
    var track2;
    var list2;
    var isUsingKey;
    function setup() {
      collect();
      init();
      update();
    }
    function mount() {
      on2(EVENT_REFRESH, destroy);
      on2(EVENT_REFRESH, setup);
      on2(EVENT_UPDATED, update);
      bind3(document, POINTER_DOWN_EVENTS + " keydown", function(e) {
        isUsingKey = e.type === "keydown";
      }, {
        capture: true
      });
      bind3(root, "focusin", function() {
        toggleClass(root, CLASS_FOCUS_IN, !!isUsingKey);
      });
    }
    function destroy(completely) {
      var attrs = ALL_ATTRIBUTES.concat("style");
      empty(slides);
      removeClass(root, rootClasses);
      removeClass(track2, trackClasses);
      removeAttribute([track2, list2], attrs);
      removeAttribute(root, completely ? attrs : ["style", ARIA_ROLEDESCRIPTION]);
    }
    function update() {
      removeClass(root, rootClasses);
      removeClass(track2, trackClasses);
      rootClasses = getClasses(CLASS_ROOT);
      trackClasses = getClasses(CLASS_TRACK);
      addClass(root, rootClasses);
      addClass(track2, trackClasses);
      setAttribute(root, ARIA_LABEL, options.label);
      setAttribute(root, ARIA_LABELLEDBY, options.labelledby);
    }
    function collect() {
      track2 = find("." + CLASS_TRACK);
      list2 = child(track2, "." + CLASS_LIST);
      assert(track2 && list2, "A track/list element is missing.");
      push(slides, children(list2, "." + CLASS_SLIDE + ":not(." + CLASS_CLONE + ")"));
      forOwn({
        arrows: CLASS_ARROWS,
        pagination: CLASS_PAGINATION,
        prev: CLASS_ARROW_PREV,
        next: CLASS_ARROW_NEXT,
        bar: CLASS_PROGRESS_BAR,
        toggle: CLASS_TOGGLE
      }, function(className, key) {
        elements[key] = find("." + className);
      });
      assign(elements, {
        root,
        track: track2,
        list: list2,
        slides
      });
    }
    function init() {
      var id = root.id || uniqueId(PROJECT_CODE);
      var role = options.role;
      root.id = id;
      track2.id = track2.id || id + "-track";
      list2.id = list2.id || id + "-list";
      if (!getAttribute(root, ROLE) && root.tagName !== "SECTION" && role) {
        setAttribute(root, ROLE, role);
      }
      setAttribute(root, ARIA_ROLEDESCRIPTION, i18n.carousel);
      setAttribute(list2, ROLE, "presentation");
    }
    function find(selector) {
      var elm = query(root, selector);
      return elm && closest(elm, "." + CLASS_ROOT) === root ? elm : void 0;
    }
    function getClasses(base) {
      return [base + "--" + options.type, base + "--" + options.direction, options.drag && base + "--draggable", options.isNavigation && base + "--nav", base === CLASS_ROOT && CLASS_ACTIVE];
    }
    return assign(elements, {
      setup,
      mount,
      destroy
    });
  }
  var SLIDE = "slide";
  var LOOP = "loop";
  var FADE = "fade";
  function Slide$1(Splide2, index, slideIndex, slide) {
    var event = EventInterface(Splide2);
    var on2 = event.on, emit = event.emit, bind3 = event.bind;
    var Components = Splide2.Components, root = Splide2.root, options = Splide2.options;
    var isNavigation = options.isNavigation, updateOnMove = options.updateOnMove, i18n = options.i18n, pagination = options.pagination, slideFocus = options.slideFocus;
    var resolve = Components.Direction.resolve;
    var styles = getAttribute(slide, "style");
    var label = getAttribute(slide, ARIA_LABEL);
    var isClone = slideIndex > -1;
    var container = child(slide, "." + CLASS_CONTAINER);
    var destroyed;
    function mount() {
      if (!isClone) {
        slide.id = root.id + "-slide" + pad(index + 1);
        setAttribute(slide, ROLE, pagination ? "tabpanel" : "group");
        setAttribute(slide, ARIA_ROLEDESCRIPTION, i18n.slide);
        setAttribute(slide, ARIA_LABEL, label || format(i18n.slideLabel, [index + 1, Splide2.length]));
      }
      listen();
    }
    function listen() {
      bind3(slide, "click", apply2(emit, EVENT_CLICK, self));
      bind3(slide, "keydown", apply2(emit, EVENT_SLIDE_KEYDOWN, self));
      on2([EVENT_MOVED, EVENT_SHIFTED, EVENT_SCROLLED], update);
      on2(EVENT_NAVIGATION_MOUNTED, initNavigation);
      if (updateOnMove) {
        on2(EVENT_MOVE, onMove);
      }
    }
    function destroy() {
      destroyed = true;
      event.destroy();
      removeClass(slide, STATUS_CLASSES);
      removeAttribute(slide, ALL_ATTRIBUTES);
      setAttribute(slide, "style", styles);
      setAttribute(slide, ARIA_LABEL, label || "");
    }
    function initNavigation() {
      var controls = Splide2.splides.map(function(target) {
        var Slide2 = target.splide.Components.Slides.getAt(index);
        return Slide2 ? Slide2.slide.id : "";
      }).join(" ");
      setAttribute(slide, ARIA_LABEL, format(i18n.slideX, (isClone ? slideIndex : index) + 1));
      setAttribute(slide, ARIA_CONTROLS, controls);
      setAttribute(slide, ROLE, slideFocus ? "button" : "");
      slideFocus && removeAttribute(slide, ARIA_ROLEDESCRIPTION);
    }
    function onMove() {
      if (!destroyed) {
        update();
      }
    }
    function update() {
      if (!destroyed) {
        var curr = Splide2.index;
        updateActivity();
        updateVisibility();
        toggleClass(slide, CLASS_PREV, index === curr - 1);
        toggleClass(slide, CLASS_NEXT, index === curr + 1);
      }
    }
    function updateActivity() {
      var active = isActive();
      if (active !== hasClass(slide, CLASS_ACTIVE)) {
        toggleClass(slide, CLASS_ACTIVE, active);
        setAttribute(slide, ARIA_CURRENT, isNavigation && active || "");
        emit(active ? EVENT_ACTIVE : EVENT_INACTIVE, self);
      }
    }
    function updateVisibility() {
      var visible = isVisible();
      var hidden = !visible && (!isActive() || isClone);
      if (!Splide2.state.is([MOVING, SCROLLING])) {
        setAttribute(slide, ARIA_HIDDEN, hidden || "");
      }
      setAttribute(queryAll(slide, options.focusableNodes || ""), TAB_INDEX, hidden ? -1 : "");
      if (slideFocus) {
        setAttribute(slide, TAB_INDEX, hidden ? -1 : 0);
      }
      if (visible !== hasClass(slide, CLASS_VISIBLE)) {
        toggleClass(slide, CLASS_VISIBLE, visible);
        emit(visible ? EVENT_VISIBLE : EVENT_HIDDEN, self);
      }
      if (!visible && document.activeElement === slide) {
        var Slide2 = Components.Slides.getAt(Splide2.index);
        Slide2 && focus(Slide2.slide);
      }
    }
    function style$1(prop, value, useContainer) {
      style(useContainer && container || slide, prop, value);
    }
    function isActive() {
      var curr = Splide2.index;
      return curr === index || options.cloneStatus && curr === slideIndex;
    }
    function isVisible() {
      if (Splide2.is(FADE)) {
        return isActive();
      }
      var trackRect = rect(Components.Elements.track);
      var slideRect = rect(slide);
      var left = resolve("left", true);
      var right = resolve("right", true);
      return floor(trackRect[left]) <= ceil(slideRect[left]) && floor(slideRect[right]) <= ceil(trackRect[right]);
    }
    function isWithin(from, distance) {
      var diff = abs(from - index);
      if (!isClone && (options.rewind || Splide2.is(LOOP))) {
        diff = min(diff, Splide2.length - diff);
      }
      return diff <= distance;
    }
    var self = {
      index,
      slideIndex,
      slide,
      container,
      isClone,
      mount,
      destroy,
      update,
      style: style$1,
      isWithin
    };
    return self;
  }
  function Slides(Splide2, Components2, options) {
    var _EventInterface2 = EventInterface(Splide2), on2 = _EventInterface2.on, emit = _EventInterface2.emit, bind3 = _EventInterface2.bind;
    var _Components2$Elements = Components2.Elements, slides = _Components2$Elements.slides, list2 = _Components2$Elements.list;
    var Slides2 = [];
    function mount() {
      init();
      on2(EVENT_REFRESH, destroy);
      on2(EVENT_REFRESH, init);
    }
    function init() {
      slides.forEach(function(slide, index) {
        register(slide, index, -1);
      });
    }
    function destroy() {
      forEach$12(function(Slide2) {
        Slide2.destroy();
      });
      empty(Slides2);
    }
    function update() {
      forEach$12(function(Slide2) {
        Slide2.update();
      });
    }
    function register(slide, index, slideIndex) {
      var object = Slide$1(Splide2, index, slideIndex, slide);
      object.mount();
      Slides2.push(object);
      Slides2.sort(function(Slide1, Slide2) {
        return Slide1.index - Slide2.index;
      });
    }
    function get2(excludeClones) {
      return excludeClones ? filter(function(Slide2) {
        return !Slide2.isClone;
      }) : Slides2;
    }
    function getIn(page) {
      var Controller2 = Components2.Controller;
      var index = Controller2.toIndex(page);
      var max3 = Controller2.hasFocus() ? 1 : options.perPage;
      return filter(function(Slide2) {
        return between(Slide2.index, index, index + max3 - 1);
      });
    }
    function getAt(index) {
      return filter(index)[0];
    }
    function add(items, index) {
      forEach(items, function(slide) {
        if (isString2(slide)) {
          slide = parseHtml(slide);
        }
        if (isHTMLElement(slide)) {
          var ref = slides[index];
          ref ? before(slide, ref) : append(list2, slide);
          addClass(slide, options.classes.slide);
          observeImages(slide, apply2(emit, EVENT_RESIZE));
        }
      });
      emit(EVENT_REFRESH);
    }
    function remove$1(matcher) {
      remove(filter(matcher).map(function(Slide2) {
        return Slide2.slide;
      }));
      emit(EVENT_REFRESH);
    }
    function forEach$12(iteratee, excludeClones) {
      get2(excludeClones).forEach(iteratee);
    }
    function filter(matcher) {
      return Slides2.filter(isFunction(matcher) ? matcher : function(Slide2) {
        return isString2(matcher) ? matches(Slide2.slide, matcher) : includes(toArray(matcher), Slide2.index);
      });
    }
    function style2(prop, value, useContainer) {
      forEach$12(function(Slide2) {
        Slide2.style(prop, value, useContainer);
      });
    }
    function observeImages(elm, callback) {
      var images2 = queryAll(elm, "img");
      var length = images2.length;
      if (length) {
        images2.forEach(function(img) {
          bind3(img, "load error", function() {
            if (!--length) {
              callback();
            }
          });
        });
      } else {
        callback();
      }
    }
    function getLength(excludeClones) {
      return excludeClones ? slides.length : Slides2.length;
    }
    function isEnough() {
      return Slides2.length > options.perPage;
    }
    return {
      mount,
      destroy,
      update,
      register,
      get: get2,
      getIn,
      getAt,
      add,
      remove: remove$1,
      forEach: forEach$12,
      filter,
      style: style2,
      getLength,
      isEnough
    };
  }
  function Layout(Splide2, Components2, options) {
    var _EventInterface3 = EventInterface(Splide2), on2 = _EventInterface3.on, bind3 = _EventInterface3.bind, emit = _EventInterface3.emit;
    var Slides2 = Components2.Slides;
    var resolve = Components2.Direction.resolve;
    var _Components2$Elements2 = Components2.Elements, root = _Components2$Elements2.root, track2 = _Components2$Elements2.track, list2 = _Components2$Elements2.list;
    var getAt = Slides2.getAt, styleSlides = Slides2.style;
    var vertical;
    var rootRect;
    var overflow;
    function mount() {
      init();
      bind3(window, "resize load", Throttle(apply2(emit, EVENT_RESIZE)));
      on2([EVENT_UPDATED, EVENT_REFRESH], init);
      on2(EVENT_RESIZE, resize);
    }
    function init() {
      vertical = options.direction === TTB;
      style(root, "maxWidth", unit(options.width));
      style(track2, resolve("paddingLeft"), cssPadding(false));
      style(track2, resolve("paddingRight"), cssPadding(true));
      resize(true);
    }
    function resize(force) {
      var newRect = rect(root);
      if (force || rootRect.width !== newRect.width || rootRect.height !== newRect.height) {
        style(track2, "height", cssTrackHeight());
        styleSlides(resolve("marginRight"), unit(options.gap));
        styleSlides("width", cssSlideWidth());
        styleSlides("height", cssSlideHeight(), true);
        rootRect = newRect;
        emit(EVENT_RESIZED);
        if (overflow !== (overflow = isOverflow())) {
          toggleClass(root, CLASS_OVERFLOW, overflow);
          emit(EVENT_OVERFLOW, overflow);
        }
      }
    }
    function cssPadding(right) {
      var padding = options.padding;
      var prop = resolve(right ? "right" : "left");
      return padding && unit(padding[prop] || (isObject3(padding) ? 0 : padding)) || "0px";
    }
    function cssTrackHeight() {
      var height = "";
      if (vertical) {
        height = cssHeight();
        assert(height, "height or heightRatio is missing.");
        height = "calc(" + height + " - " + cssPadding(false) + " - " + cssPadding(true) + ")";
      }
      return height;
    }
    function cssHeight() {
      return unit(options.height || rect(list2).width * options.heightRatio);
    }
    function cssSlideWidth() {
      return options.autoWidth ? null : unit(options.fixedWidth) || (vertical ? "" : cssSlideSize());
    }
    function cssSlideHeight() {
      return unit(options.fixedHeight) || (vertical ? options.autoHeight ? null : cssSlideSize() : cssHeight());
    }
    function cssSlideSize() {
      var gap = unit(options.gap);
      return "calc((100%" + (gap && " + " + gap) + ")/" + (options.perPage || 1) + (gap && " - " + gap) + ")";
    }
    function listSize() {
      return rect(list2)[resolve("width")];
    }
    function slideSize(index, withoutGap) {
      var Slide2 = getAt(index || 0);
      return Slide2 ? rect(Slide2.slide)[resolve("width")] + (withoutGap ? 0 : getGap()) : 0;
    }
    function totalSize(index, withoutGap) {
      var Slide2 = getAt(index);
      if (Slide2) {
        var right = rect(Slide2.slide)[resolve("right")];
        var left = rect(list2)[resolve("left")];
        return abs(right - left) + (withoutGap ? 0 : getGap());
      }
      return 0;
    }
    function sliderSize(withoutGap) {
      return totalSize(Splide2.length - 1) - totalSize(0) + slideSize(0, withoutGap);
    }
    function getGap() {
      var Slide2 = getAt(0);
      return Slide2 && parseFloat(style(Slide2.slide, resolve("marginRight"))) || 0;
    }
    function getPadding(right) {
      return parseFloat(style(track2, resolve("padding" + (right ? "Right" : "Left")))) || 0;
    }
    function isOverflow() {
      return Splide2.is(FADE) || sliderSize(true) > listSize();
    }
    return {
      mount,
      resize,
      listSize,
      slideSize,
      sliderSize,
      totalSize,
      getPadding,
      isOverflow
    };
  }
  var MULTIPLIER = 2;
  function Clones(Splide2, Components2, options) {
    var event = EventInterface(Splide2);
    var on2 = event.on;
    var Elements2 = Components2.Elements, Slides2 = Components2.Slides;
    var resolve = Components2.Direction.resolve;
    var clones = [];
    var cloneCount;
    function mount() {
      on2(EVENT_REFRESH, remount);
      on2([EVENT_UPDATED, EVENT_RESIZE], observe);
      if (cloneCount = computeCloneCount()) {
        generate(cloneCount);
        Components2.Layout.resize(true);
      }
    }
    function remount() {
      destroy();
      mount();
    }
    function destroy() {
      remove(clones);
      empty(clones);
      event.destroy();
    }
    function observe() {
      var count = computeCloneCount();
      if (cloneCount !== count) {
        if (cloneCount < count || !count) {
          event.emit(EVENT_REFRESH);
        }
      }
    }
    function generate(count) {
      var slides = Slides2.get().slice();
      var length = slides.length;
      if (length) {
        while (slides.length < count) {
          push(slides, slides);
        }
        push(slides.slice(-count), slides.slice(0, count)).forEach(function(Slide2, index) {
          var isHead = index < count;
          var clone2 = cloneDeep(Slide2.slide, index);
          isHead ? before(clone2, slides[0].slide) : append(Elements2.list, clone2);
          push(clones, clone2);
          Slides2.register(clone2, index - count + (isHead ? 0 : length), Slide2.index);
        });
      }
    }
    function cloneDeep(elm, index) {
      var clone2 = elm.cloneNode(true);
      addClass(clone2, options.classes.clone);
      clone2.id = Splide2.root.id + "-clone" + pad(index + 1);
      return clone2;
    }
    function computeCloneCount() {
      var clones2 = options.clones;
      if (!Splide2.is(LOOP)) {
        clones2 = 0;
      } else if (isUndefined(clones2)) {
        var fixedSize = options[resolve("fixedWidth")] && Components2.Layout.slideSize(0);
        var fixedCount = fixedSize && ceil(rect(Elements2.track)[resolve("width")] / fixedSize);
        clones2 = fixedCount || options[resolve("autoWidth")] && Splide2.length || options.perPage * MULTIPLIER;
      }
      return clones2;
    }
    return {
      mount,
      destroy
    };
  }
  function Move(Splide2, Components2, options) {
    var _EventInterface4 = EventInterface(Splide2), on2 = _EventInterface4.on, emit = _EventInterface4.emit;
    var set2 = Splide2.state.set;
    var _Components2$Layout = Components2.Layout, slideSize = _Components2$Layout.slideSize, getPadding = _Components2$Layout.getPadding, totalSize = _Components2$Layout.totalSize, listSize = _Components2$Layout.listSize, sliderSize = _Components2$Layout.sliderSize;
    var _Components2$Directio = Components2.Direction, resolve = _Components2$Directio.resolve, orient = _Components2$Directio.orient;
    var _Components2$Elements3 = Components2.Elements, list2 = _Components2$Elements3.list, track2 = _Components2$Elements3.track;
    var Transition;
    function mount() {
      Transition = Components2.Transition;
      on2([EVENT_MOUNTED, EVENT_RESIZED, EVENT_UPDATED, EVENT_REFRESH], reposition);
    }
    function reposition() {
      if (!Components2.Controller.isBusy()) {
        Components2.Scroll.cancel();
        jump(Splide2.index);
        Components2.Slides.update();
      }
    }
    function move(dest, index, prev, callback) {
      if (dest !== index && canShift(dest > prev)) {
        cancel();
        translate(shift(getPosition(), dest > prev), true);
      }
      set2(MOVING);
      emit(EVENT_MOVE, index, prev, dest);
      Transition.start(index, function() {
        set2(IDLE);
        emit(EVENT_MOVED, index, prev, dest);
        callback && callback();
      });
    }
    function jump(index) {
      translate(toPosition(index, true));
    }
    function translate(position, preventLoop) {
      if (!Splide2.is(FADE)) {
        var destination = preventLoop ? position : loop2(position);
        style(list2, "transform", "translate" + resolve("X") + "(" + destination + "px)");
        position !== destination && emit(EVENT_SHIFTED);
      }
    }
    function loop2(position) {
      if (Splide2.is(LOOP)) {
        var index = toIndex(position);
        var exceededMax = index > Components2.Controller.getEnd();
        var exceededMin = index < 0;
        if (exceededMin || exceededMax) {
          position = shift(position, exceededMax);
        }
      }
      return position;
    }
    function shift(position, backwards) {
      var excess = position - getLimit(backwards);
      var size = sliderSize();
      position -= orient(size * (ceil(abs(excess) / size) || 1)) * (backwards ? 1 : -1);
      return position;
    }
    function cancel() {
      translate(getPosition(), true);
      Transition.cancel();
    }
    function toIndex(position) {
      var Slides2 = Components2.Slides.get();
      var index = 0;
      var minDistance = Infinity;
      for (var i = 0; i < Slides2.length; i++) {
        var slideIndex = Slides2[i].index;
        var distance = abs(toPosition(slideIndex, true) - position);
        if (distance <= minDistance) {
          minDistance = distance;
          index = slideIndex;
        } else {
          break;
        }
      }
      return index;
    }
    function toPosition(index, trimming) {
      var position = orient(totalSize(index - 1) - offset(index));
      return trimming ? trim(position) : position;
    }
    function getPosition() {
      var left = resolve("left");
      return rect(list2)[left] - rect(track2)[left] + orient(getPadding(false));
    }
    function trim(position) {
      if (options.trimSpace && Splide2.is(SLIDE)) {
        position = clamp(position, 0, orient(sliderSize(true) - listSize()));
      }
      return position;
    }
    function offset(index) {
      var focus2 = options.focus;
      return focus2 === "center" ? (listSize() - slideSize(index, true)) / 2 : +focus2 * slideSize(index) || 0;
    }
    function getLimit(max3) {
      return toPosition(max3 ? Components2.Controller.getEnd() : 0, !!options.trimSpace);
    }
    function canShift(backwards) {
      var shifted = orient(shift(getPosition(), backwards));
      return backwards ? shifted >= 0 : shifted <= list2[resolve("scrollWidth")] - rect(track2)[resolve("width")];
    }
    function exceededLimit(max3, position) {
      position = isUndefined(position) ? getPosition() : position;
      var exceededMin = max3 !== true && orient(position) < orient(getLimit(false));
      var exceededMax = max3 !== false && orient(position) > orient(getLimit(true));
      return exceededMin || exceededMax;
    }
    return {
      mount,
      move,
      jump,
      translate,
      shift,
      cancel,
      toIndex,
      toPosition,
      getPosition,
      getLimit,
      exceededLimit,
      reposition
    };
  }
  function Controller(Splide2, Components2, options) {
    var _EventInterface5 = EventInterface(Splide2), on2 = _EventInterface5.on, emit = _EventInterface5.emit;
    var Move2 = Components2.Move;
    var getPosition = Move2.getPosition, getLimit = Move2.getLimit, toPosition = Move2.toPosition;
    var _Components2$Slides = Components2.Slides, isEnough = _Components2$Slides.isEnough, getLength = _Components2$Slides.getLength;
    var omitEnd = options.omitEnd;
    var isLoop = Splide2.is(LOOP);
    var isSlide = Splide2.is(SLIDE);
    var getNext = apply2(getAdjacent, false);
    var getPrev = apply2(getAdjacent, true);
    var currIndex = options.start || 0;
    var endIndex;
    var prevIndex = currIndex;
    var slideCount;
    var perMove;
    var perPage;
    function mount() {
      init();
      on2([EVENT_UPDATED, EVENT_REFRESH, EVENT_END_INDEX_CHANGED], init);
      on2(EVENT_RESIZED, onResized);
    }
    function init() {
      slideCount = getLength(true);
      perMove = options.perMove;
      perPage = options.perPage;
      endIndex = getEnd();
      var index = clamp(currIndex, 0, omitEnd ? endIndex : slideCount - 1);
      if (index !== currIndex) {
        currIndex = index;
        Move2.reposition();
      }
    }
    function onResized() {
      if (endIndex !== getEnd()) {
        emit(EVENT_END_INDEX_CHANGED);
      }
    }
    function go(control, allowSameIndex, callback) {
      if (!isBusy()) {
        var dest = parse(control);
        var index = loop2(dest);
        if (index > -1 && (allowSameIndex || index !== currIndex)) {
          setIndex(index);
          Move2.move(dest, index, prevIndex, callback);
        }
      }
    }
    function scroll(destination, duration, snap, callback) {
      Components2.Scroll.scroll(destination, duration, snap, function() {
        var index = loop2(Move2.toIndex(getPosition()));
        setIndex(omitEnd ? min(index, endIndex) : index);
        callback && callback();
      });
    }
    function parse(control) {
      var index = currIndex;
      if (isString2(control)) {
        var _ref = control.match(/([+\-<>])(\d+)?/) || [], indicator = _ref[1], number = _ref[2];
        if (indicator === "+" || indicator === "-") {
          index = computeDestIndex(currIndex + +("" + indicator + (+number || 1)), currIndex);
        } else if (indicator === ">") {
          index = number ? toIndex(+number) : getNext(true);
        } else if (indicator === "<") {
          index = getPrev(true);
        }
      } else {
        index = isLoop ? control : clamp(control, 0, endIndex);
      }
      return index;
    }
    function getAdjacent(prev, destination) {
      var number = perMove || (hasFocus() ? 1 : perPage);
      var dest = computeDestIndex(currIndex + number * (prev ? -1 : 1), currIndex, !(perMove || hasFocus()));
      if (dest === -1 && isSlide) {
        if (!approximatelyEqual(getPosition(), getLimit(!prev), 1)) {
          return prev ? 0 : endIndex;
        }
      }
      return destination ? dest : loop2(dest);
    }
    function computeDestIndex(dest, from, snapPage) {
      if (isEnough() || hasFocus()) {
        var index = computeMovableDestIndex(dest);
        if (index !== dest) {
          from = dest;
          dest = index;
          snapPage = false;
        }
        if (dest < 0 || dest > endIndex) {
          if (!perMove && (between(0, dest, from, true) || between(endIndex, from, dest, true))) {
            dest = toIndex(toPage(dest));
          } else {
            if (isLoop) {
              dest = snapPage ? dest < 0 ? -(slideCount % perPage || perPage) : slideCount : dest;
            } else if (options.rewind) {
              dest = dest < 0 ? endIndex : 0;
            } else {
              dest = -1;
            }
          }
        } else {
          if (snapPage && dest !== from) {
            dest = toIndex(toPage(from) + (dest < from ? -1 : 1));
          }
        }
      } else {
        dest = -1;
      }
      return dest;
    }
    function computeMovableDestIndex(dest) {
      if (isSlide && options.trimSpace === "move" && dest !== currIndex) {
        var position = getPosition();
        while (position === toPosition(dest, true) && between(dest, 0, Splide2.length - 1, !options.rewind)) {
          dest < currIndex ? --dest : ++dest;
        }
      }
      return dest;
    }
    function loop2(index) {
      return isLoop ? (index + slideCount) % slideCount || 0 : index;
    }
    function getEnd() {
      var end = slideCount - (hasFocus() || isLoop && perMove ? 1 : perPage);
      while (omitEnd && end-- > 0) {
        if (toPosition(slideCount - 1, true) !== toPosition(end, true)) {
          end++;
          break;
        }
      }
      return clamp(end, 0, slideCount - 1);
    }
    function toIndex(page) {
      return clamp(hasFocus() ? page : perPage * page, 0, endIndex);
    }
    function toPage(index) {
      return hasFocus() ? min(index, endIndex) : floor((index >= endIndex ? slideCount - 1 : index) / perPage);
    }
    function toDest(destination) {
      var closest2 = Move2.toIndex(destination);
      return isSlide ? clamp(closest2, 0, endIndex) : closest2;
    }
    function setIndex(index) {
      if (index !== currIndex) {
        prevIndex = currIndex;
        currIndex = index;
      }
    }
    function getIndex(prev) {
      return prev ? prevIndex : currIndex;
    }
    function hasFocus() {
      return !isUndefined(options.focus) || options.isNavigation;
    }
    function isBusy() {
      return Splide2.state.is([MOVING, SCROLLING]) && !!options.waitForTransition;
    }
    return {
      mount,
      go,
      scroll,
      getNext,
      getPrev,
      getAdjacent,
      getEnd,
      setIndex,
      getIndex,
      toIndex,
      toPage,
      toDest,
      hasFocus,
      isBusy
    };
  }
  var XML_NAME_SPACE = "http://www.w3.org/2000/svg";
  var PATH = "m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z";
  var SIZE = 40;
  function Arrows(Splide2, Components2, options) {
    var event = EventInterface(Splide2);
    var on2 = event.on, bind3 = event.bind, emit = event.emit;
    var classes = options.classes, i18n = options.i18n;
    var Elements2 = Components2.Elements, Controller2 = Components2.Controller;
    var placeholder = Elements2.arrows, track2 = Elements2.track;
    var wrapper = placeholder;
    var prev = Elements2.prev;
    var next = Elements2.next;
    var created;
    var wrapperClasses;
    var arrows = {};
    function mount() {
      init();
      on2(EVENT_UPDATED, remount);
    }
    function remount() {
      destroy();
      mount();
    }
    function init() {
      var enabled = options.arrows;
      if (enabled && !(prev && next)) {
        createArrows();
      }
      if (prev && next) {
        assign(arrows, {
          prev,
          next
        });
        display(wrapper, enabled ? "" : "none");
        addClass(wrapper, wrapperClasses = CLASS_ARROWS + "--" + options.direction);
        if (enabled) {
          listen();
          update();
          setAttribute([prev, next], ARIA_CONTROLS, track2.id);
          emit(EVENT_ARROWS_MOUNTED, prev, next);
        }
      }
    }
    function destroy() {
      event.destroy();
      removeClass(wrapper, wrapperClasses);
      if (created) {
        remove(placeholder ? [prev, next] : wrapper);
        prev = next = null;
      } else {
        removeAttribute([prev, next], ALL_ATTRIBUTES);
      }
    }
    function listen() {
      on2([EVENT_MOUNTED, EVENT_MOVED, EVENT_REFRESH, EVENT_SCROLLED, EVENT_END_INDEX_CHANGED], update);
      bind3(next, "click", apply2(go, ">"));
      bind3(prev, "click", apply2(go, "<"));
    }
    function go(control) {
      Controller2.go(control, true);
    }
    function createArrows() {
      wrapper = placeholder || create("div", classes.arrows);
      prev = createArrow(true);
      next = createArrow(false);
      created = true;
      append(wrapper, [prev, next]);
      !placeholder && before(wrapper, track2);
    }
    function createArrow(prev2) {
      var arrow = '<button class="' + classes.arrow + " " + (prev2 ? classes.prev : classes.next) + '" type="button"><svg xmlns="' + XML_NAME_SPACE + '" viewBox="0 0 ' + SIZE + " " + SIZE + '" width="' + SIZE + '" height="' + SIZE + '" focusable="false"><path d="' + (options.arrowPath || PATH) + '" />';
      return parseHtml(arrow);
    }
    function update() {
      if (prev && next) {
        var index = Splide2.index;
        var prevIndex = Controller2.getPrev();
        var nextIndex = Controller2.getNext();
        var prevLabel = prevIndex > -1 && index < prevIndex ? i18n.last : i18n.prev;
        var nextLabel = nextIndex > -1 && index > nextIndex ? i18n.first : i18n.next;
        prev.disabled = prevIndex < 0;
        next.disabled = nextIndex < 0;
        setAttribute(prev, ARIA_LABEL, prevLabel);
        setAttribute(next, ARIA_LABEL, nextLabel);
        emit(EVENT_ARROWS_UPDATED, prev, next, prevIndex, nextIndex);
      }
    }
    return {
      arrows,
      mount,
      destroy,
      update
    };
  }
  var INTERVAL_DATA_ATTRIBUTE = DATA_ATTRIBUTE + "-interval";
  function Autoplay(Splide2, Components2, options) {
    var _EventInterface6 = EventInterface(Splide2), on2 = _EventInterface6.on, bind3 = _EventInterface6.bind, emit = _EventInterface6.emit;
    var interval = RequestInterval(options.interval, Splide2.go.bind(Splide2, ">"), onAnimationFrame);
    var isPaused = interval.isPaused;
    var Elements2 = Components2.Elements, _Components2$Elements4 = Components2.Elements, root = _Components2$Elements4.root, toggle = _Components2$Elements4.toggle;
    var autoplay = options.autoplay;
    var hovered;
    var focused;
    var stopped = autoplay === "pause";
    function mount() {
      if (autoplay) {
        listen();
        toggle && setAttribute(toggle, ARIA_CONTROLS, Elements2.track.id);
        stopped || play();
        update();
      }
    }
    function listen() {
      if (options.pauseOnHover) {
        bind3(root, "mouseenter mouseleave", function(e) {
          hovered = e.type === "mouseenter";
          autoToggle();
        });
      }
      if (options.pauseOnFocus) {
        bind3(root, "focusin focusout", function(e) {
          focused = e.type === "focusin";
          autoToggle();
        });
      }
      if (toggle) {
        bind3(toggle, "click", function() {
          stopped ? play() : pause(true);
        });
      }
      on2([EVENT_MOVE, EVENT_SCROLL, EVENT_REFRESH], interval.rewind);
      on2(EVENT_MOVE, onMove);
    }
    function play() {
      if (isPaused() && Components2.Slides.isEnough()) {
        interval.start(!options.resetProgress);
        focused = hovered = stopped = false;
        update();
        emit(EVENT_AUTOPLAY_PLAY);
      }
    }
    function pause(stop2) {
      if (stop2 === void 0) {
        stop2 = true;
      }
      stopped = !!stop2;
      update();
      if (!isPaused()) {
        interval.pause();
        emit(EVENT_AUTOPLAY_PAUSE);
      }
    }
    function autoToggle() {
      if (!stopped) {
        hovered || focused ? pause(false) : play();
      }
    }
    function update() {
      if (toggle) {
        toggleClass(toggle, CLASS_ACTIVE, !stopped);
        setAttribute(toggle, ARIA_LABEL, options.i18n[stopped ? "play" : "pause"]);
      }
    }
    function onAnimationFrame(rate) {
      var bar = Elements2.bar;
      bar && style(bar, "width", rate * 100 + "%");
      emit(EVENT_AUTOPLAY_PLAYING, rate);
    }
    function onMove(index) {
      var Slide2 = Components2.Slides.getAt(index);
      interval.set(Slide2 && +getAttribute(Slide2.slide, INTERVAL_DATA_ATTRIBUTE) || options.interval);
    }
    return {
      mount,
      destroy: interval.cancel,
      play,
      pause,
      isPaused
    };
  }
  function Cover(Splide2, Components2, options) {
    var _EventInterface7 = EventInterface(Splide2), on2 = _EventInterface7.on;
    function mount() {
      if (options.cover) {
        on2(EVENT_LAZYLOAD_LOADED, apply2(toggle, true));
        on2([EVENT_MOUNTED, EVENT_UPDATED, EVENT_REFRESH], apply2(cover, true));
      }
    }
    function cover(cover2) {
      Components2.Slides.forEach(function(Slide2) {
        var img = child(Slide2.container || Slide2.slide, "img");
        if (img && img.src) {
          toggle(cover2, img, Slide2);
        }
      });
    }
    function toggle(cover2, img, Slide2) {
      Slide2.style("background", cover2 ? 'center/cover no-repeat url("' + img.src + '")' : "", true);
      display(img, cover2 ? "none" : "");
    }
    return {
      mount,
      destroy: apply2(cover, false)
    };
  }
  var BOUNCE_DIFF_THRESHOLD = 10;
  var BOUNCE_DURATION = 600;
  var FRICTION_FACTOR = 0.6;
  var BASE_VELOCITY = 1.5;
  var MIN_DURATION = 800;
  function Scroll(Splide2, Components2, options) {
    var _EventInterface8 = EventInterface(Splide2), on2 = _EventInterface8.on, emit = _EventInterface8.emit;
    var set2 = Splide2.state.set;
    var Move2 = Components2.Move;
    var getPosition = Move2.getPosition, getLimit = Move2.getLimit, exceededLimit = Move2.exceededLimit, translate = Move2.translate;
    var isSlide = Splide2.is(SLIDE);
    var interval;
    var callback;
    var friction = 1;
    function mount() {
      on2(EVENT_MOVE, clear);
      on2([EVENT_UPDATED, EVENT_REFRESH], cancel);
    }
    function scroll(destination, duration, snap, onScrolled, noConstrain) {
      var from = getPosition();
      clear();
      if (snap && (!isSlide || !exceededLimit())) {
        var size = Components2.Layout.sliderSize();
        var offset = sign(destination) * size * floor(abs(destination) / size) || 0;
        destination = Move2.toPosition(Components2.Controller.toDest(destination % size)) + offset;
      }
      var noDistance = approximatelyEqual(from, destination, 1);
      friction = 1;
      duration = noDistance ? 0 : duration || max(abs(destination - from) / BASE_VELOCITY, MIN_DURATION);
      callback = onScrolled;
      interval = RequestInterval(duration, onEnd, apply2(update, from, destination, noConstrain), 1);
      set2(SCROLLING);
      emit(EVENT_SCROLL);
      interval.start();
    }
    function onEnd() {
      set2(IDLE);
      callback && callback();
      emit(EVENT_SCROLLED);
    }
    function update(from, to, noConstrain, rate) {
      var position = getPosition();
      var target = from + (to - from) * easing(rate);
      var diff = (target - position) * friction;
      translate(position + diff);
      if (isSlide && !noConstrain && exceededLimit()) {
        friction *= FRICTION_FACTOR;
        if (abs(diff) < BOUNCE_DIFF_THRESHOLD) {
          scroll(getLimit(exceededLimit(true)), BOUNCE_DURATION, false, callback, true);
        }
      }
    }
    function clear() {
      if (interval) {
        interval.cancel();
      }
    }
    function cancel() {
      if (interval && !interval.isPaused()) {
        clear();
        onEnd();
      }
    }
    function easing(t) {
      var easingFunc = options.easingFunc;
      return easingFunc ? easingFunc(t) : 1 - Math.pow(1 - t, 4);
    }
    return {
      mount,
      destroy: clear,
      scroll,
      cancel
    };
  }
  var SCROLL_LISTENER_OPTIONS = {
    passive: false,
    capture: true
  };
  function Drag(Splide2, Components2, options) {
    var _EventInterface9 = EventInterface(Splide2), on2 = _EventInterface9.on, emit = _EventInterface9.emit, bind3 = _EventInterface9.bind, unbind = _EventInterface9.unbind;
    var state = Splide2.state;
    var Move2 = Components2.Move, Scroll2 = Components2.Scroll, Controller2 = Components2.Controller, track2 = Components2.Elements.track, reduce2 = Components2.Media.reduce;
    var _Components2$Directio2 = Components2.Direction, resolve = _Components2$Directio2.resolve, orient = _Components2$Directio2.orient;
    var getPosition = Move2.getPosition, exceededLimit = Move2.exceededLimit;
    var basePosition;
    var baseEvent;
    var prevBaseEvent;
    var isFree;
    var dragging;
    var exceeded = false;
    var clickPrevented;
    var disabled;
    var target;
    function mount() {
      bind3(track2, POINTER_MOVE_EVENTS, noop, SCROLL_LISTENER_OPTIONS);
      bind3(track2, POINTER_UP_EVENTS, noop, SCROLL_LISTENER_OPTIONS);
      bind3(track2, POINTER_DOWN_EVENTS, onPointerDown, SCROLL_LISTENER_OPTIONS);
      bind3(track2, "click", onClick, {
        capture: true
      });
      bind3(track2, "dragstart", prevent);
      on2([EVENT_MOUNTED, EVENT_UPDATED], init);
    }
    function init() {
      var drag = options.drag;
      disable(!drag);
      isFree = drag === "free";
    }
    function onPointerDown(e) {
      clickPrevented = false;
      if (!disabled) {
        var isTouch = isTouchEvent(e);
        if (isDraggable(e.target) && (isTouch || !e.button)) {
          if (!Controller2.isBusy()) {
            target = isTouch ? track2 : window;
            dragging = state.is([MOVING, SCROLLING]);
            prevBaseEvent = null;
            bind3(target, POINTER_MOVE_EVENTS, onPointerMove, SCROLL_LISTENER_OPTIONS);
            bind3(target, POINTER_UP_EVENTS, onPointerUp, SCROLL_LISTENER_OPTIONS);
            Move2.cancel();
            Scroll2.cancel();
            save(e);
          } else {
            prevent(e, true);
          }
        }
      }
    }
    function onPointerMove(e) {
      if (!state.is(DRAGGING)) {
        state.set(DRAGGING);
        emit(EVENT_DRAG);
      }
      if (e.cancelable) {
        if (dragging) {
          Move2.translate(basePosition + constrain(diffCoord(e)));
          var expired = diffTime(e) > LOG_INTERVAL;
          var hasExceeded = exceeded !== (exceeded = exceededLimit());
          if (expired || hasExceeded) {
            save(e);
          }
          clickPrevented = true;
          emit(EVENT_DRAGGING);
          prevent(e);
        } else if (isSliderDirection(e)) {
          dragging = shouldStart(e);
          prevent(e);
        }
      }
    }
    function onPointerUp(e) {
      if (state.is(DRAGGING)) {
        state.set(IDLE);
        emit(EVENT_DRAGGED);
      }
      if (dragging) {
        move(e);
        prevent(e);
      }
      unbind(target, POINTER_MOVE_EVENTS, onPointerMove);
      unbind(target, POINTER_UP_EVENTS, onPointerUp);
      dragging = false;
    }
    function onClick(e) {
      if (!disabled && clickPrevented) {
        prevent(e, true);
      }
    }
    function save(e) {
      prevBaseEvent = baseEvent;
      baseEvent = e;
      basePosition = getPosition();
    }
    function move(e) {
      var velocity = computeVelocity(e);
      var destination = computeDestination(velocity);
      var rewind = options.rewind && options.rewindByDrag;
      reduce2(false);
      if (isFree) {
        Controller2.scroll(destination, 0, options.snap);
      } else if (Splide2.is(FADE)) {
        Controller2.go(orient(sign(velocity)) < 0 ? rewind ? "<" : "-" : rewind ? ">" : "+");
      } else if (Splide2.is(SLIDE) && exceeded && rewind) {
        Controller2.go(exceededLimit(true) ? ">" : "<");
      } else {
        Controller2.go(Controller2.toDest(destination), true);
      }
      reduce2(true);
    }
    function shouldStart(e) {
      var thresholds = options.dragMinThreshold;
      var isObj = isObject3(thresholds);
      var mouse = isObj && thresholds.mouse || 0;
      var touch = (isObj ? thresholds.touch : +thresholds) || 10;
      return abs(diffCoord(e)) > (isTouchEvent(e) ? touch : mouse);
    }
    function isSliderDirection(e) {
      return abs(diffCoord(e)) > abs(diffCoord(e, true));
    }
    function computeVelocity(e) {
      if (Splide2.is(LOOP) || !exceeded) {
        var time = diffTime(e);
        if (time && time < LOG_INTERVAL) {
          return diffCoord(e) / time;
        }
      }
      return 0;
    }
    function computeDestination(velocity) {
      return getPosition() + sign(velocity) * min(abs(velocity) * (options.flickPower || 600), isFree ? Infinity : Components2.Layout.listSize() * (options.flickMaxPages || 1));
    }
    function diffCoord(e, orthogonal) {
      return coordOf(e, orthogonal) - coordOf(getBaseEvent(e), orthogonal);
    }
    function diffTime(e) {
      return timeOf(e) - timeOf(getBaseEvent(e));
    }
    function getBaseEvent(e) {
      return baseEvent === e && prevBaseEvent || baseEvent;
    }
    function coordOf(e, orthogonal) {
      return (isTouchEvent(e) ? e.changedTouches[0] : e)["page" + resolve(orthogonal ? "Y" : "X")];
    }
    function constrain(diff) {
      return diff / (exceeded && Splide2.is(SLIDE) ? FRICTION : 1);
    }
    function isDraggable(target2) {
      var noDrag = options.noDrag;
      return !matches(target2, "." + CLASS_PAGINATION_PAGE + ", ." + CLASS_ARROW) && (!noDrag || !matches(target2, noDrag));
    }
    function isTouchEvent(e) {
      return typeof TouchEvent !== "undefined" && e instanceof TouchEvent;
    }
    function isDragging() {
      return dragging;
    }
    function disable(value) {
      disabled = value;
    }
    return {
      mount,
      disable,
      isDragging
    };
  }
  var NORMALIZATION_MAP = {
    Spacebar: " ",
    Right: ARROW_RIGHT,
    Left: ARROW_LEFT,
    Up: ARROW_UP,
    Down: ARROW_DOWN
  };
  function normalizeKey(key) {
    key = isString2(key) ? key : key.key;
    return NORMALIZATION_MAP[key] || key;
  }
  var KEYBOARD_EVENT = "keydown";
  function Keyboard(Splide2, Components2, options) {
    var _EventInterface10 = EventInterface(Splide2), on2 = _EventInterface10.on, bind3 = _EventInterface10.bind, unbind = _EventInterface10.unbind;
    var root = Splide2.root;
    var resolve = Components2.Direction.resolve;
    var target;
    var disabled;
    function mount() {
      init();
      on2(EVENT_UPDATED, destroy);
      on2(EVENT_UPDATED, init);
      on2(EVENT_MOVE, onMove);
    }
    function init() {
      var keyboard = options.keyboard;
      if (keyboard) {
        target = keyboard === "global" ? window : root;
        bind3(target, KEYBOARD_EVENT, onKeydown);
      }
    }
    function destroy() {
      unbind(target, KEYBOARD_EVENT);
    }
    function disable(value) {
      disabled = value;
    }
    function onMove() {
      var _disabled = disabled;
      disabled = true;
      nextTick2(function() {
        disabled = _disabled;
      });
    }
    function onKeydown(e) {
      if (!disabled) {
        var key = normalizeKey(e);
        if (key === resolve(ARROW_LEFT)) {
          Splide2.go("<");
        } else if (key === resolve(ARROW_RIGHT)) {
          Splide2.go(">");
        }
      }
    }
    return {
      mount,
      destroy,
      disable
    };
  }
  var SRC_DATA_ATTRIBUTE = DATA_ATTRIBUTE + "-lazy";
  var SRCSET_DATA_ATTRIBUTE = SRC_DATA_ATTRIBUTE + "-srcset";
  var IMAGE_SELECTOR = "[" + SRC_DATA_ATTRIBUTE + "], [" + SRCSET_DATA_ATTRIBUTE + "]";
  function LazyLoad(Splide2, Components2, options) {
    var _EventInterface11 = EventInterface(Splide2), on2 = _EventInterface11.on, off = _EventInterface11.off, bind3 = _EventInterface11.bind, emit = _EventInterface11.emit;
    var isSequential = options.lazyLoad === "sequential";
    var events = [EVENT_MOVED, EVENT_SCROLLED];
    var entries2 = [];
    function mount() {
      if (options.lazyLoad) {
        init();
        on2(EVENT_REFRESH, init);
      }
    }
    function init() {
      empty(entries2);
      register();
      if (isSequential) {
        loadNext();
      } else {
        off(events);
        on2(events, check);
        check();
      }
    }
    function register() {
      Components2.Slides.forEach(function(Slide2) {
        queryAll(Slide2.slide, IMAGE_SELECTOR).forEach(function(img) {
          var src = getAttribute(img, SRC_DATA_ATTRIBUTE);
          var srcset = getAttribute(img, SRCSET_DATA_ATTRIBUTE);
          if (src !== img.src || srcset !== img.srcset) {
            var className = options.classes.spinner;
            var parent = img.parentElement;
            var spinner = child(parent, "." + className) || create("span", className, parent);
            entries2.push([img, Slide2, spinner]);
            img.src || display(img, "none");
          }
        });
      });
    }
    function check() {
      entries2 = entries2.filter(function(data2) {
        var distance = options.perPage * ((options.preloadPages || 1) + 1) - 1;
        return data2[1].isWithin(Splide2.index, distance) ? load(data2) : true;
      });
      entries2.length || off(events);
    }
    function load(data2) {
      var img = data2[0];
      addClass(data2[1].slide, CLASS_LOADING);
      bind3(img, "load error", apply2(onLoad, data2));
      setAttribute(img, "src", getAttribute(img, SRC_DATA_ATTRIBUTE));
      setAttribute(img, "srcset", getAttribute(img, SRCSET_DATA_ATTRIBUTE));
      removeAttribute(img, SRC_DATA_ATTRIBUTE);
      removeAttribute(img, SRCSET_DATA_ATTRIBUTE);
    }
    function onLoad(data2, e) {
      var img = data2[0], Slide2 = data2[1];
      removeClass(Slide2.slide, CLASS_LOADING);
      if (e.type !== "error") {
        remove(data2[2]);
        display(img, "");
        emit(EVENT_LAZYLOAD_LOADED, img, Slide2);
        emit(EVENT_RESIZE);
      }
      isSequential && loadNext();
    }
    function loadNext() {
      entries2.length && load(entries2.shift());
    }
    return {
      mount,
      destroy: apply2(empty, entries2),
      check
    };
  }
  function Pagination(Splide2, Components2, options) {
    var event = EventInterface(Splide2);
    var on2 = event.on, emit = event.emit, bind3 = event.bind;
    var Slides2 = Components2.Slides, Elements2 = Components2.Elements, Controller2 = Components2.Controller;
    var hasFocus = Controller2.hasFocus, getIndex = Controller2.getIndex, go = Controller2.go;
    var resolve = Components2.Direction.resolve;
    var placeholder = Elements2.pagination;
    var items = [];
    var list2;
    var paginationClasses;
    function mount() {
      destroy();
      on2([EVENT_UPDATED, EVENT_REFRESH, EVENT_END_INDEX_CHANGED], mount);
      var enabled = options.pagination;
      placeholder && display(placeholder, enabled ? "" : "none");
      if (enabled) {
        on2([EVENT_MOVE, EVENT_SCROLL, EVENT_SCROLLED], update);
        createPagination();
        update();
        emit(EVENT_PAGINATION_MOUNTED, {
          list: list2,
          items
        }, getAt(Splide2.index));
      }
    }
    function destroy() {
      if (list2) {
        remove(placeholder ? slice(list2.children) : list2);
        removeClass(list2, paginationClasses);
        empty(items);
        list2 = null;
      }
      event.destroy();
    }
    function createPagination() {
      var length = Splide2.length;
      var classes = options.classes, i18n = options.i18n, perPage = options.perPage;
      var max3 = hasFocus() ? Controller2.getEnd() + 1 : ceil(length / perPage);
      list2 = placeholder || create("ul", classes.pagination, Elements2.track.parentElement);
      addClass(list2, paginationClasses = CLASS_PAGINATION + "--" + getDirection());
      setAttribute(list2, ROLE, "tablist");
      setAttribute(list2, ARIA_LABEL, i18n.select);
      setAttribute(list2, ARIA_ORIENTATION, getDirection() === TTB ? "vertical" : "");
      for (var i = 0; i < max3; i++) {
        var li = create("li", null, list2);
        var button = create("button", {
          class: classes.page,
          type: "button"
        }, li);
        var controls = Slides2.getIn(i).map(function(Slide2) {
          return Slide2.slide.id;
        });
        var text = !hasFocus() && perPage > 1 ? i18n.pageX : i18n.slideX;
        bind3(button, "click", apply2(onClick, i));
        if (options.paginationKeyboard) {
          bind3(button, "keydown", apply2(onKeydown, i));
        }
        setAttribute(li, ROLE, "presentation");
        setAttribute(button, ROLE, "tab");
        setAttribute(button, ARIA_CONTROLS, controls.join(" "));
        setAttribute(button, ARIA_LABEL, format(text, i + 1));
        setAttribute(button, TAB_INDEX, -1);
        items.push({
          li,
          button,
          page: i
        });
      }
    }
    function onClick(page) {
      go(">" + page, true);
    }
    function onKeydown(page, e) {
      var length = items.length;
      var key = normalizeKey(e);
      var dir = getDirection();
      var nextPage = -1;
      if (key === resolve(ARROW_RIGHT, false, dir)) {
        nextPage = ++page % length;
      } else if (key === resolve(ARROW_LEFT, false, dir)) {
        nextPage = (--page + length) % length;
      } else if (key === "Home") {
        nextPage = 0;
      } else if (key === "End") {
        nextPage = length - 1;
      }
      var item = items[nextPage];
      if (item) {
        focus(item.button);
        go(">" + nextPage);
        prevent(e, true);
      }
    }
    function getDirection() {
      return options.paginationDirection || options.direction;
    }
    function getAt(index) {
      return items[Controller2.toPage(index)];
    }
    function update() {
      var prev = getAt(getIndex(true));
      var curr = getAt(getIndex());
      if (prev) {
        var button = prev.button;
        removeClass(button, CLASS_ACTIVE);
        removeAttribute(button, ARIA_SELECTED);
        setAttribute(button, TAB_INDEX, -1);
      }
      if (curr) {
        var _button = curr.button;
        addClass(_button, CLASS_ACTIVE);
        setAttribute(_button, ARIA_SELECTED, true);
        setAttribute(_button, TAB_INDEX, "");
      }
      emit(EVENT_PAGINATION_UPDATED, {
        list: list2,
        items
      }, prev, curr);
    }
    return {
      items,
      mount,
      destroy,
      getAt,
      update
    };
  }
  var TRIGGER_KEYS = [" ", "Enter"];
  function Sync(Splide2, Components2, options) {
    var isNavigation = options.isNavigation, slideFocus = options.slideFocus;
    var events = [];
    function mount() {
      Splide2.splides.forEach(function(target) {
        if (!target.isParent) {
          sync(Splide2, target.splide);
          sync(target.splide, Splide2);
        }
      });
      if (isNavigation) {
        navigate();
      }
    }
    function destroy() {
      events.forEach(function(event) {
        event.destroy();
      });
      empty(events);
    }
    function remount() {
      destroy();
      mount();
    }
    function sync(splide, target) {
      var event = EventInterface(splide);
      event.on(EVENT_MOVE, function(index, prev, dest) {
        target.go(target.is(LOOP) ? dest : index);
      });
      events.push(event);
    }
    function navigate() {
      var event = EventInterface(Splide2);
      var on2 = event.on;
      on2(EVENT_CLICK, onClick);
      on2(EVENT_SLIDE_KEYDOWN, onKeydown);
      on2([EVENT_MOUNTED, EVENT_UPDATED], update);
      events.push(event);
      event.emit(EVENT_NAVIGATION_MOUNTED, Splide2.splides);
    }
    function update() {
      setAttribute(Components2.Elements.list, ARIA_ORIENTATION, options.direction === TTB ? "vertical" : "");
    }
    function onClick(Slide2) {
      Splide2.go(Slide2.index);
    }
    function onKeydown(Slide2, e) {
      if (includes(TRIGGER_KEYS, normalizeKey(e))) {
        onClick(Slide2);
        prevent(e);
      }
    }
    return {
      setup: apply2(Components2.Media.set, {
        slideFocus: isUndefined(slideFocus) ? isNavigation : slideFocus
      }, true),
      mount,
      destroy,
      remount
    };
  }
  function Wheel(Splide2, Components2, options) {
    var _EventInterface12 = EventInterface(Splide2), bind3 = _EventInterface12.bind;
    var lastTime = 0;
    function mount() {
      if (options.wheel) {
        bind3(Components2.Elements.track, "wheel", onWheel, SCROLL_LISTENER_OPTIONS);
      }
    }
    function onWheel(e) {
      if (e.cancelable) {
        var deltaY = e.deltaY;
        var backwards = deltaY < 0;
        var timeStamp = timeOf(e);
        var _min = options.wheelMinThreshold || 0;
        var sleep = options.wheelSleep || 0;
        if (abs(deltaY) > _min && timeStamp - lastTime > sleep) {
          Splide2.go(backwards ? "<" : ">");
          lastTime = timeStamp;
        }
        shouldPrevent(backwards) && prevent(e);
      }
    }
    function shouldPrevent(backwards) {
      return !options.releaseWheel || Splide2.state.is(MOVING) || Components2.Controller.getAdjacent(backwards) !== -1;
    }
    return {
      mount
    };
  }
  var SR_REMOVAL_DELAY = 90;
  function Live(Splide2, Components2, options) {
    var _EventInterface13 = EventInterface(Splide2), on2 = _EventInterface13.on;
    var track2 = Components2.Elements.track;
    var enabled = options.live && !options.isNavigation;
    var sr = create("span", CLASS_SR);
    var interval = RequestInterval(SR_REMOVAL_DELAY, apply2(toggle, false));
    function mount() {
      if (enabled) {
        disable(!Components2.Autoplay.isPaused());
        setAttribute(track2, ARIA_ATOMIC, true);
        sr.textContent = "\u2026";
        on2(EVENT_AUTOPLAY_PLAY, apply2(disable, true));
        on2(EVENT_AUTOPLAY_PAUSE, apply2(disable, false));
        on2([EVENT_MOVED, EVENT_SCROLLED], apply2(toggle, true));
      }
    }
    function toggle(active) {
      setAttribute(track2, ARIA_BUSY, active);
      if (active) {
        append(track2, sr);
        interval.start();
      } else {
        remove(sr);
        interval.cancel();
      }
    }
    function destroy() {
      removeAttribute(track2, [ARIA_LIVE, ARIA_ATOMIC, ARIA_BUSY]);
      remove(sr);
    }
    function disable(disabled) {
      if (enabled) {
        setAttribute(track2, ARIA_LIVE, disabled ? "off" : "polite");
      }
    }
    return {
      mount,
      disable,
      destroy
    };
  }
  var ComponentConstructors = /* @__PURE__ */ Object.freeze({
    __proto__: null,
    Media,
    Direction,
    Elements,
    Slides,
    Layout,
    Clones,
    Move,
    Controller,
    Arrows,
    Autoplay,
    Cover,
    Scroll,
    Drag,
    Keyboard,
    LazyLoad,
    Pagination,
    Sync,
    Wheel,
    Live
  });
  var I18N = {
    prev: "Previous slide",
    next: "Next slide",
    first: "Go to first slide",
    last: "Go to last slide",
    slideX: "Go to slide %s",
    pageX: "Go to page %s",
    play: "Start autoplay",
    pause: "Pause autoplay",
    carousel: "carousel",
    slide: "slide",
    select: "Select a slide to show",
    slideLabel: "%s of %s"
  };
  var DEFAULTS = {
    type: "slide",
    role: "region",
    speed: 400,
    perPage: 1,
    cloneStatus: true,
    arrows: true,
    pagination: true,
    paginationKeyboard: true,
    interval: 5e3,
    pauseOnHover: true,
    pauseOnFocus: true,
    resetProgress: true,
    easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    drag: true,
    direction: "ltr",
    trimSpace: true,
    focusableNodes: "a, button, textarea, input, select, iframe",
    live: true,
    classes: CLASSES,
    i18n: I18N,
    reducedMotion: {
      speed: 0,
      rewindSpeed: 0,
      autoplay: "pause"
    }
  };
  function Fade(Splide2, Components2, options) {
    var Slides2 = Components2.Slides;
    function mount() {
      EventInterface(Splide2).on([EVENT_MOUNTED, EVENT_REFRESH], init);
    }
    function init() {
      Slides2.forEach(function(Slide2) {
        Slide2.style("transform", "translateX(-" + 100 * Slide2.index + "%)");
      });
    }
    function start2(index, done) {
      Slides2.style("transition", "opacity " + options.speed + "ms " + options.easing);
      nextTick2(done);
    }
    return {
      mount,
      start: start2,
      cancel: noop
    };
  }
  function Slide(Splide2, Components2, options) {
    var Move2 = Components2.Move, Controller2 = Components2.Controller, Scroll2 = Components2.Scroll;
    var list2 = Components2.Elements.list;
    var transition2 = apply2(style, list2, "transition");
    var endCallback;
    function mount() {
      EventInterface(Splide2).bind(list2, "transitionend", function(e) {
        if (e.target === list2 && endCallback) {
          cancel();
          endCallback();
        }
      });
    }
    function start2(index, done) {
      var destination = Move2.toPosition(index, true);
      var position = Move2.getPosition();
      var speed = getSpeed(index);
      if (abs(destination - position) >= 1 && speed >= 1) {
        if (options.useScroll) {
          Scroll2.scroll(destination, speed, false, done);
        } else {
          transition2("transform " + speed + "ms " + options.easing);
          Move2.translate(destination, true);
          endCallback = done;
        }
      } else {
        Move2.jump(index);
        done();
      }
    }
    function cancel() {
      transition2("");
      Scroll2.cancel();
    }
    function getSpeed(index) {
      var rewindSpeed = options.rewindSpeed;
      if (Splide2.is(SLIDE) && rewindSpeed) {
        var prev = Controller2.getIndex(true);
        var end = Controller2.getEnd();
        if (prev === 0 && index >= end || prev >= end && index === 0) {
          return rewindSpeed;
        }
      }
      return options.speed;
    }
    return {
      mount,
      start: start2,
      cancel
    };
  }
  var _Splide = /* @__PURE__ */ function() {
    function _Splide2(target, options) {
      this.event = EventInterface();
      this.Components = {};
      this.state = State(CREATED);
      this.splides = [];
      this._o = {};
      this._E = {};
      var root = isString2(target) ? query(document, target) : target;
      assert(root, root + " is invalid.");
      this.root = root;
      options = merge({
        label: getAttribute(root, ARIA_LABEL) || "",
        labelledby: getAttribute(root, ARIA_LABELLEDBY) || ""
      }, DEFAULTS, _Splide2.defaults, options || {});
      try {
        merge(options, JSON.parse(getAttribute(root, DATA_ATTRIBUTE)));
      } catch (e) {
        assert(false, "Invalid JSON");
      }
      this._o = Object.create(merge({}, options));
    }
    var _proto = _Splide2.prototype;
    _proto.mount = function mount(Extensions, Transition) {
      var _this = this;
      var state = this.state, Components2 = this.Components;
      assert(state.is([CREATED, DESTROYED]), "Already mounted!");
      state.set(CREATED);
      this._C = Components2;
      this._T = Transition || this._T || (this.is(FADE) ? Fade : Slide);
      this._E = Extensions || this._E;
      var Constructors = assign({}, ComponentConstructors, this._E, {
        Transition: this._T
      });
      forOwn(Constructors, function(Component, key) {
        var component = Component(_this, Components2, _this._o);
        Components2[key] = component;
        component.setup && component.setup();
      });
      forOwn(Components2, function(component) {
        component.mount && component.mount();
      });
      this.emit(EVENT_MOUNTED);
      addClass(this.root, CLASS_INITIALIZED);
      state.set(IDLE);
      this.emit(EVENT_READY);
      return this;
    };
    _proto.sync = function sync(splide) {
      this.splides.push({
        splide
      });
      splide.splides.push({
        splide: this,
        isParent: true
      });
      if (this.state.is(IDLE)) {
        this._C.Sync.remount();
        splide.Components.Sync.remount();
      }
      return this;
    };
    _proto.go = function go(control) {
      this._C.Controller.go(control);
      return this;
    };
    _proto.on = function on2(events, callback) {
      this.event.on(events, callback);
      return this;
    };
    _proto.off = function off(events) {
      this.event.off(events);
      return this;
    };
    _proto.emit = function emit(event) {
      var _this$event;
      (_this$event = this.event).emit.apply(_this$event, [event].concat(slice(arguments, 1)));
      return this;
    };
    _proto.add = function add(slides, index) {
      this._C.Slides.add(slides, index);
      return this;
    };
    _proto.remove = function remove2(matcher) {
      this._C.Slides.remove(matcher);
      return this;
    };
    _proto.is = function is(type) {
      return this._o.type === type;
    };
    _proto.refresh = function refresh() {
      this.emit(EVENT_REFRESH);
      return this;
    };
    _proto.destroy = function destroy(completely) {
      if (completely === void 0) {
        completely = true;
      }
      var event = this.event, state = this.state;
      if (state.is(CREATED)) {
        EventInterface(this).on(EVENT_READY, this.destroy.bind(this, completely));
      } else {
        forOwn(this._C, function(component) {
          component.destroy && component.destroy(completely);
        }, true);
        event.emit(EVENT_DESTROY);
        event.destroy();
        completely && empty(this.splides);
        state.set(DESTROYED);
      }
      return this;
    };
    _createClass(_Splide2, [{
      key: "options",
      get: function get2() {
        return this._o;
      },
      set: function set2(options) {
        this._C.Media.set(options, true, true);
      }
    }, {
      key: "length",
      get: function get2() {
        return this._C.Slides.getLength(true);
      }
    }, {
      key: "index",
      get: function get2() {
        return this._C.Controller.getIndex();
      }
    }]);
    return _Splide2;
  }();
  var Splide = _Splide;
  Splide.defaults = {};
  Splide.STATES = STATES;

  // node_modules/@splidejs/splide-extension-auto-scroll/dist/js/splide-extension-auto-scroll.esm.js
  function empty2(array) {
    array.length = 0;
  }
  function slice$1(arrayLike, start2, end) {
    return Array.prototype.slice.call(arrayLike, start2, end);
  }
  function apply$1(func) {
    return func.bind.apply(func, [null].concat(slice$1(arguments, 1)));
  }
  function raf2(func) {
    return requestAnimationFrame(func);
  }
  function typeOf$1(type, subject) {
    return typeof subject === type;
  }
  var isArray$1 = Array.isArray;
  apply$1(typeOf$1, "function");
  apply$1(typeOf$1, "string");
  apply$1(typeOf$1, "undefined");
  function toArray$1(value) {
    return isArray$1(value) ? value : [value];
  }
  function forEach$1(values, iteratee) {
    toArray$1(values).forEach(iteratee);
  }
  var ownKeys$1 = Object.keys;
  function forOwn$1(object, iteratee, right) {
    if (object) {
      var keys = ownKeys$1(object);
      keys = right ? keys.reverse() : keys;
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        if (key !== "__proto__") {
          if (iteratee(object[key], key) === false) {
            break;
          }
        }
      }
    }
    return object;
  }
  function assign$1(object) {
    slice$1(arguments, 1).forEach(function(source) {
      forOwn$1(source, function(value, key) {
        object[key] = source[key];
      });
    });
    return object;
  }
  var min$1 = Math.min;
  function EventBinder2() {
    var listeners = [];
    function bind3(targets, events, callback, options) {
      forEachEvent(targets, events, function(target, event, namespace) {
        var isEventTarget = "addEventListener" in target;
        var remover = isEventTarget ? target.removeEventListener.bind(target, event, callback, options) : target["removeListener"].bind(target, callback);
        isEventTarget ? target.addEventListener(event, callback, options) : target["addListener"](callback);
        listeners.push([target, event, namespace, callback, remover]);
      });
    }
    function unbind(targets, events, callback) {
      forEachEvent(targets, events, function(target, event, namespace) {
        listeners = listeners.filter(function(listener) {
          if (listener[0] === target && listener[1] === event && listener[2] === namespace && (!callback || listener[3] === callback)) {
            listener[4]();
            return false;
          }
          return true;
        });
      });
    }
    function dispatch2(target, type, detail) {
      var e;
      var bubbles = true;
      if (typeof CustomEvent === "function") {
        e = new CustomEvent(type, {
          bubbles,
          detail
        });
      } else {
        e = document.createEvent("CustomEvent");
        e.initCustomEvent(type, bubbles, false, detail);
      }
      target.dispatchEvent(e);
      return e;
    }
    function forEachEvent(targets, events, iteratee) {
      forEach$1(targets, function(target) {
        target && forEach$1(events, function(events2) {
          events2.split(" ").forEach(function(eventNS) {
            var fragment = eventNS.split(".");
            iteratee(target, fragment[0], fragment[1]);
          });
        });
      });
    }
    function destroy() {
      listeners.forEach(function(data2) {
        data2[4]();
      });
      empty2(listeners);
    }
    return {
      bind: bind3,
      unbind,
      dispatch: dispatch2,
      destroy
    };
  }
  var EVENT_MOVE2 = "move";
  var EVENT_MOVED2 = "moved";
  var EVENT_UPDATED2 = "updated";
  var EVENT_DRAG2 = "drag";
  var EVENT_DRAGGED2 = "dragged";
  var EVENT_SCROLL2 = "scroll";
  var EVENT_SCROLLED2 = "scrolled";
  var EVENT_DESTROY2 = "destroy";
  function EventInterface2(Splide2) {
    var bus = Splide2 ? Splide2.event.bus : document.createDocumentFragment();
    var binder = EventBinder2();
    function on2(events, callback) {
      binder.bind(bus, toArray$1(events).join(" "), function(e) {
        callback.apply(callback, isArray$1(e.detail) ? e.detail : []);
      });
    }
    function emit(event) {
      binder.dispatch(bus, event, slice$1(arguments, 1));
    }
    if (Splide2) {
      Splide2.event.on(EVENT_DESTROY2, binder.destroy);
    }
    return assign$1(binder, {
      bus,
      on: on2,
      off: apply$1(binder.unbind, bus),
      emit
    });
  }
  function RequestInterval2(interval, onInterval, onUpdate, limit) {
    var now = Date.now;
    var startTime;
    var rate = 0;
    var id;
    var paused = true;
    var count = 0;
    function update() {
      if (!paused) {
        rate = interval ? min$1((now() - startTime) / interval, 1) : 1;
        onUpdate && onUpdate(rate);
        if (rate >= 1) {
          onInterval();
          startTime = now();
          if (limit && ++count >= limit) {
            return pause();
          }
        }
        raf2(update);
      }
    }
    function start2(resume) {
      !resume && cancel();
      startTime = now() - (resume ? rate * interval : 0);
      paused = false;
      raf2(update);
    }
    function pause() {
      paused = true;
    }
    function rewind() {
      startTime = now();
      rate = 0;
      if (onUpdate) {
        onUpdate(rate);
      }
    }
    function cancel() {
      id && cancelAnimationFrame(id);
      rate = 0;
      id = 0;
      paused = true;
    }
    function set2(time) {
      interval = time;
    }
    function isPaused() {
      return paused;
    }
    return {
      start: start2,
      rewind,
      pause,
      cancel,
      set: set2,
      isPaused
    };
  }
  function Throttle2(func, duration) {
    var interval;
    function throttled() {
      if (!interval) {
        interval = RequestInterval2(duration || 0, function() {
          func();
          interval = null;
        }, null, 1);
        interval.start();
      }
    }
    return throttled;
  }
  var CLASS_ACTIVE2 = "is-active";
  var SLIDE2 = "slide";
  var FADE2 = "fade";
  function slice2(arrayLike, start2, end) {
    return Array.prototype.slice.call(arrayLike, start2, end);
  }
  function apply3(func) {
    return func.bind(null, ...slice2(arguments, 1));
  }
  function typeOf2(type, subject) {
    return typeof subject === type;
  }
  function isObject4(subject) {
    return !isNull2(subject) && typeOf2("object", subject);
  }
  var isArray3 = Array.isArray;
  apply3(typeOf2, "function");
  apply3(typeOf2, "string");
  var isUndefined2 = apply3(typeOf2, "undefined");
  function isNull2(subject) {
    return subject === null;
  }
  function toArray2(value) {
    return isArray3(value) ? value : [value];
  }
  function forEach2(values, iteratee) {
    toArray2(values).forEach(iteratee);
  }
  function toggleClass2(elm, classes, add) {
    if (elm) {
      forEach2(classes, (name) => {
        if (name) {
          elm.classList[add ? "add" : "remove"](name);
        }
      });
    }
  }
  var ownKeys2 = Object.keys;
  function forOwn2(object, iteratee, right) {
    if (object) {
      let keys = ownKeys2(object);
      keys = right ? keys.reverse() : keys;
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (key !== "__proto__") {
          if (iteratee(object[key], key) === false) {
            break;
          }
        }
      }
    }
    return object;
  }
  function assign2(object) {
    slice2(arguments, 1).forEach((source) => {
      forOwn2(source, (value, key) => {
        object[key] = source[key];
      });
    });
    return object;
  }
  function removeAttribute2(elms, attrs) {
    forEach2(elms, (elm) => {
      forEach2(attrs, (attr) => {
        elm && elm.removeAttribute(attr);
      });
    });
  }
  function setAttribute2(elms, attrs, value) {
    if (isObject4(attrs)) {
      forOwn2(attrs, (value2, name) => {
        setAttribute2(elms, name, value2);
      });
    } else {
      forEach2(elms, (elm) => {
        isNull2(value) || value === "" ? removeAttribute2(elm, attrs) : elm.setAttribute(attrs, String(value));
      });
    }
  }
  var { min: min2, max: max2, floor: floor2, ceil: ceil2, abs: abs2 } = Math;
  function clamp2(number, x, y) {
    const minimum = min2(x, y);
    const maximum = max2(x, y);
    return min2(max2(minimum, number), maximum);
  }
  var DEFAULTS2 = {
    speed: 1,
    autoStart: true,
    pauseOnHover: true,
    pauseOnFocus: true
  };
  var I18N2 = {
    startScroll: "Start auto scroll",
    pauseScroll: "Pause auto scroll"
  };
  function AutoScroll(Splide2, Components2, options) {
    const { on: on2, off, bind: bind3, unbind } = EventInterface2(Splide2);
    const { translate, getPosition, toIndex, getLimit } = Components2.Move;
    const { setIndex, getIndex } = Components2.Controller;
    const { orient } = Components2.Direction;
    const { toggle } = Components2.Elements;
    const { Live: Live2 } = Components2;
    const { root } = Splide2;
    const throttledUpdateArrows = Throttle2(Components2.Arrows.update, 500);
    let autoScrollOptions = {};
    let interval;
    let stopped;
    let hovered;
    let focused;
    let busy;
    let currPosition;
    function setup() {
      const { autoScroll } = options;
      autoScrollOptions = assign2({}, DEFAULTS2, isObject4(autoScroll) ? autoScroll : {});
    }
    function mount() {
      if (!Splide2.is(FADE2)) {
        if (!interval && options.autoScroll !== false) {
          interval = RequestInterval2(0, move);
          listen();
          autoStart();
        }
      }
    }
    function destroy() {
      if (interval) {
        interval.cancel();
        interval = null;
        currPosition = void 0;
        off([EVENT_MOVE2, EVENT_DRAG2, EVENT_SCROLL2, EVENT_MOVED2, EVENT_SCROLLED2]);
        unbind(root, "mouseenter mouseleave focusin focusout");
        unbind(toggle, "click");
      }
    }
    function listen() {
      if (autoScrollOptions.pauseOnHover) {
        bind3(root, "mouseenter mouseleave", (e) => {
          hovered = e.type === "mouseenter";
          autoToggle();
        });
      }
      if (autoScrollOptions.pauseOnFocus) {
        bind3(root, "focusin focusout", (e) => {
          focused = e.type === "focusin";
          autoToggle();
        });
      }
      if (autoScrollOptions.useToggleButton) {
        bind3(toggle, "click", () => {
          stopped ? play() : pause();
        });
      }
      on2(EVENT_UPDATED2, update);
      on2([EVENT_MOVE2, EVENT_DRAG2, EVENT_SCROLL2], () => {
        busy = true;
        pause(false);
      });
      on2([EVENT_MOVED2, EVENT_DRAGGED2, EVENT_SCROLLED2], () => {
        busy = false;
        autoToggle();
      });
    }
    function update() {
      const { autoScroll } = options;
      if (autoScroll !== false) {
        autoScrollOptions = assign2({}, autoScrollOptions, isObject4(autoScroll) ? autoScroll : {});
        mount();
      } else {
        destroy();
      }
      if (interval && !isUndefined2(currPosition)) {
        translate(currPosition);
      }
    }
    function autoStart() {
      if (autoScrollOptions.autoStart) {
        if (document.readyState === "complete") {
          play();
        } else {
          bind3(window, "load", play);
        }
      }
    }
    function play() {
      if (isPaused()) {
        interval.start(true);
        Live2.disable(true);
        focused = hovered = stopped = false;
        updateButton();
      }
    }
    function pause(stop2 = true) {
      if (!stopped) {
        stopped = stop2;
        updateButton();
        if (!isPaused()) {
          interval.pause();
          Live2.disable(false);
        }
      }
    }
    function autoToggle() {
      if (!stopped) {
        hovered || focused || busy ? pause(false) : play();
      }
    }
    function move() {
      const position = getPosition();
      const destination = computeDestination(position);
      if (position !== destination) {
        translate(destination);
        updateIndex(currPosition = getPosition());
      } else {
        pause(false);
        if (autoScrollOptions.rewind) {
          Splide2.go(autoScrollOptions.speed > 0 ? 0 : Components2.Controller.getEnd());
        }
      }
      throttledUpdateArrows();
    }
    function computeDestination(position) {
      const speed = autoScrollOptions.speed || 1;
      position += orient(speed);
      if (Splide2.is(SLIDE2)) {
        position = clamp2(position, getLimit(false), getLimit(true));
      }
      return position;
    }
    function updateIndex(position) {
      const { length } = Splide2;
      const index = (toIndex(position) + length) % length;
      if (index !== getIndex()) {
        setIndex(index);
        Components2.Slides.update();
        Components2.Pagination.update();
        options.lazyLoad === "nearby" && Components2.LazyLoad.check();
      }
    }
    function updateButton() {
      if (toggle) {
        const key = stopped ? "startScroll" : "pauseScroll";
        toggleClass2(toggle, CLASS_ACTIVE2, !stopped);
        setAttribute2(toggle, "aria-label", options.i18n[key] || I18N2[key]);
      }
    }
    function isPaused() {
      return !interval || interval.isPaused();
    }
    return {
      setup,
      mount,
      destroy,
      play,
      pause,
      isPaused
    };
  }

  // src/scripts/components/splide-default.js
  var DefaultSplide = class {
    params = {
      classes: {
        arrows: "splide__arrows slider-arrows",
        arrow: "splide__arrow slider-arrow",
        prev: "splide__arrow--prev slider-arrow-prev",
        next: "splide__arrow--next slider-arrow-next",
        pagination: "splide__pagination slider-pagination",
        page: "splide__pagination__page slider-page"
      },
      direction: "ltr",
      arrows: true,
      pagination: true,
      type: "loop",
      drag: true,
      snap: true,
      perPage: 1,
      perMove: 1,
      label: "Slider",
      rewind: true,
      rewindSpeed: 800,
      speed: 400,
      width: "100%",
      height: "auto",
      start: 0,
      gap: "0em",
      padding: "0em",
      autoplay: false,
      interval: 5e3,
      lazyLoad: "nearby",
      omitEnd: false,
      mediaQuery: "min",
      hasProgress: false,
      keyboard: true,
      allowTouchMove: true,
      flickPower: 200,
      flickMaxPages: 1,
      trackOverflow: "hidden",
      breakpoints: {
        767: {
          flickPower: 500
        }
      }
    };
    override;
    splide_element;
    splide_instance;
    id;
    navigation;
    constructor({
      desktop_slide_count,
      mobile_slide_count,
      direction,
      elm,
      override
    }) {
      this.override = override || {};
      this.desktop_slide_count = desktop_slide_count || 3;
      this.mobile_slide_count = mobile_slide_count || 1;
      this.direction = direction || "ltr";
      this.splide_element = elm;
      this.id = this.splide_element.dataset.id || this.splide_element.id || "splide";
      this.setParams(override || {});
      if (this.override.sync) {
        this.navigation = this.override.sync;
      }
      if (this.splide_element.dataset.scroll_speed) {
        this.params.autoScroll = {
          ...this.params.autoScroll || {},
          speed: this.splide_element.dataset.scroll_speed
        };
      }
    }
    setParams = (params) => {
      if (this.splide_element.dataset.desktop_slide_count && this.splide_element.dataset.mobile_slide_count) {
        this.desktop_slide_count = this.splide_element.dataset.desktop_slide_count;
        this.mobile_slide_count = this.splide_element.dataset.mobile_slide_count;
      }
      if (this.splide_element.dataset.direction) {
        this.direction = this.splide_element.dataset.direction;
      }
      ["mobile", "desktop"].forEach((device) => {
        const capitalizedDevice = device.charAt(0).toUpperCase() + device.slice(1);
        let amount = this.splide_element.dataset[`paddingRight${capitalizedDevice}`] || 0;
        this[`${device}_padding`] = {
          right: amount
        };
      });
      this.params.perPage = this.mobile_slide_count;
      this.params.padding = this.mobile_padding;
      this.params.breakpoints = {
        768: {
          effect: "slide",
          allowTouchMove: false,
          navigation: false,
          perPage: this.desktop_slide_count,
          padding: this.desktop_padding
        }
      };
      this.params.direction = this.direction;
      if (params.breakpoints) {
        this.setBreakpoints(params.breakpoints);
        delete params.breakpoints;
      }
      this.params = { ...this.params, ...params };
    };
    setEventListeners = () => {
      this.splide_instance.on("mounted", this.afterLoad);
      this.splide_instance.on("resize", this.onResize);
      this.splide_instance.on("moved", this.onMoved);
      this.splide_instance.on("scrolled", this.onScrolled);
      this.splide_instance.on("destroy", this.onDestroy);
    };
    setBreakpoints = (breakpoints) => {
      for (const [key, value] of Object.entries(breakpoints)) {
        this.params.breakpoints[key] = {
          ...this.params.breakpoints[key],
          ...value
        };
      }
    };
    createSplide = () => {
      this.splide_instance = new Splide(this.splide_element, this.params);
      if (this.navigation) {
        this.splide_instance.sync(this.navigation.splide_instance);
      }
      this.setEventListeners();
      this.mount();
      const event = new CustomEvent(`swanky:splide:initialized${this.id}`, {
        detail: {
          splide_element: this.splide_element,
          splide_instance: this.splide_instance
        }
      });
      window.dispatchEvent(event);
    };
    mount = () => {
      const mountOptions = this.params.autoScroll ? { AutoScroll } : void 0;
      if (this.params.isNavigation !== true) {
        this.splide_instance.mount(mountOptions);
      }
      if (this.navigation) {
        this.navigation.splide_instance.mount(mountOptions);
      }
      const event = new CustomEvent(`swanky:splide:mounted${this.id}`, {
        bubbles: true,
        detail: {
          element: this.splide_element
        }
      });
      this.splide_element.dispatchEvent(event);
    };
    onMoved = (index) => {
      this.toggleMedia(index);
      const event = new CustomEvent(`swanky:splide:moved${this.id}`, {
        bubbles: true,
        detail: {
          element: this.splide_element
        }
      });
      this.splide_element.dispatchEvent(event);
    };
    onScrolled = (index) => {
      this.toggleMedia(index);
      const event = new CustomEvent(`swanky:splide:moved${this.id}`, {
        bubbles: true,
        detail: {
          element: this.splide_element
        }
      });
      this.splide_element.dispatchEvent(event);
    };
    onResize = () => {
    };
    afterLoad = () => {
      const track2 = this.splide_element.querySelector(".splide__track");
      if (track2 && this.override.trackOverflow) {
        track2.style.overflow = this.override.trackOverflow;
        if (this.override.trackOverflow === "visible") {
          const section = track2.closest("section");
          if (section) {
            section.style.overflow = "hidden";
          }
        }
      }
    };
    getSlide = (index) => {
      return this.splide_instance.Components.Slides.getAt(index);
    };
    onMove = () => {
    };
    goTo = (index) => {
      this.splide_instance.go(index);
    };
    addSlide = (slide) => {
      this.splide_instance.add(slide);
    };
    removeSlide = (index) => {
      this.splide_instance.remove(index);
    };
    refresh = () => {
      this.splide_instance.refresh();
    };
    onDestroy = () => {
      this.splide_instance.destroy();
      const event = new CustomEvent(`swanky:splide:destroyed${this.id}`, {
        detail: {
          splide_element: this.splide_element,
          splide_instance: this.splide_instance
        }
      });
      window.dispatchEvent(event);
    };
    toggleMedia = (index) => {
      const slides = this.splide_instance.Components.Slides.get();
      const currentSlideInstance = this.getSlide(index);
      slides.forEach((slideInstance) => {
        const slide = slideInstance.slide;
        const video = slide.querySelector("video");
        const model = slide.querySelector("model-media");
        if (slideInstance !== currentSlideInstance) {
          window.VideoPlayerManager?.pauseWithin(slide);
          if (model) model.pause();
          return;
        }
        const facade = slide.querySelector("[data-video-facade]:not([hidden])");
        if (video && !facade) {
          const started2 = video.play();
          if (started2 && typeof started2.catch === "function") {
            started2.catch(() => {
            });
          }
        }
        if (model) model.play();
      });
    };
    init = () => {
      this.createSplide();
    };
  };

  // src/scripts/islands/header.js
  var DESKTOP_QUERY = "(min-width: 1280px)";
  var header = (data2 = {
    isBurgerMenuOpen: false
    // Set the default value for isBurgerMenuOpen
  }) => ({
    isBurgerMenuOpen: data2.isBurgerMenuOpen,
    isSearchExpanded: data2.isSearchExpanded,
    init() {
      this.setEventListeners();
      this.bodyClass();
      this.positionStickyHeader();
      this.updateHeaderHeights();
      this.positionFloatingHeader();
      this.observeHeaderSize();
      this.updateStuckState();
    },
    observeHeaderSize() {
      if (typeof ResizeObserver === "undefined") return;
      const observer2 = new ResizeObserver(() => {
        this.positionStickyHeader();
        this.updateHeaderHeights();
      });
      [
        document.querySelector(".header__wrapper"),
        document.querySelector(".announcement-bar")
      ].forEach((element) => {
        if (element) observer2.observe(element);
      });
    },
    setEventListeners() {
      window.addEventListener("resize", () => {
        this.closeBurgerMenu();
        this.positionStickyHeader();
        this.updateHeaderHeights();
        this.updateStuckState();
      });
      window.addEventListener("scroll", () => {
        this.updateStuckState();
        this.updateHeaderHeights();
      });
      window.addEventListener("megamenu-active", () => {
        this.toggleDesktopMenu(true);
      });
      window.addEventListener("megamenu-inactive", () => {
        this.toggleDesktopMenu(false);
      });
      window.addEventListener("load", () => {
        this.bodyClass();
      });
    },
    positionFloatingHeader() {
      const headerWrapper = document.querySelector(".header__wrapper.floating");
      if (!headerWrapper) return;
      const headerHeight = getComputedStyle(document.documentElement).getPropertyValue("--header-height").trim();
      if (headerHeight && headerHeight !== "0px") {
        requestAnimationFrame(() => {
          headerWrapper.style.position = "absolute";
        });
      }
    },
    floatingStickOffset() {
      const wrapper = document.querySelector(".header__wrapper.floating");
      if (!wrapper || !window.matchMedia(DESKTOP_QUERY).matches) return 0;
      return parseFloat(getComputedStyle(wrapper).marginTop) || 0;
    },
    updateStuckState() {
      const header2 = document.querySelector(".header");
      const section = document.querySelector("header");
      if (!header2 || !section) return;
      if (header2.dataset.headerType !== "floating") return;
      if (!header2.classList.contains("sticky")) return;
      const stickyTop = parseFloat(getComputedStyle(section).top) || 0;
      const isStuck = window.scrollY > 0 && section.getBoundingClientRect().top <= stickyTop + 0.5;
      header2.classList.toggle("header--stuck", isStuck);
    },
    positionStickyHeader() {
      const header2 = document.querySelector("header");
      const headerIsSticky = document.querySelector(".header").classList.contains("sticky");
      let headerTopAmount = 0;
      const stickOffset = this.floatingStickOffset();
      const announcementBar = document.querySelector(".announcement-bar");
      const announcementBarHeight = announcementBar?.offsetHeight;
      const announcementBarIsSticky = document.querySelector(".announcement-bar__wrapper")?.classList.contains("sticky");
      let headerIsBeforeAnnouncementBar = header2.compareDocumentPosition(announcementBar);
      if (headerIsBeforeAnnouncementBar === 2) {
        headerIsBeforeAnnouncementBar = false;
      } else if (headerIsBeforeAnnouncementBar === 4) {
        headerIsBeforeAnnouncementBar = true;
      }
      if (headerIsBeforeAnnouncementBar == false) {
        if (announcementBarIsSticky) {
          announcementBar.style.top = "0px";
          headerTopAmount += announcementBarHeight;
        }
        if (headerIsSticky) {
          header2.style.top = `${headerTopAmount - stickOffset}px`;
        }
      } else {
        if (headerIsSticky) {
          header2.style.top = `${-stickOffset}px`;
        }
        if (announcementBarIsSticky) {
          announcementBar.style.top = `${header2.offsetHeight}px`;
        }
      }
    },
    updateHeaderHeights() {
      const header2 = document.querySelector(".header__wrapper");
      const announcementBar = document.querySelector(".announcement-bar");
      const announcementWrapper = document.querySelector(
        ".announcement-bar__wrapper"
      );
      const headerElement = document.querySelector(".header");
      if (!header2) return;
      const announcementBarHeight = announcementBar?.offsetHeight || 0;
      const announcementBarIsSticky = announcementWrapper?.classList.contains("sticky");
      const headerMarginTop = parseInt(getComputedStyle(header2).marginTop) || 0;
      const headerIsFloating = header2.classList.contains("floating");
      const headerIsSticky = headerElement?.classList.contains("sticky");
      const headerHeight = header2.offsetHeight + (headerIsFloating ? headerMarginTop : 0);
      const stickOffset = headerIsSticky && window.scrollY > 0 ? this.floatingStickOffset() : 0;
      let totalHeaderHeight = 0;
      if (announcementBarIsSticky && headerIsSticky) {
        totalHeaderHeight = announcementBarHeight + headerHeight - stickOffset;
      } else if (announcementBarIsSticky && !headerIsSticky) {
        totalHeaderHeight = announcementBarHeight;
      } else if (!announcementBarIsSticky && headerIsSticky) {
        totalHeaderHeight = window.scrollY === 0 ? announcementBarHeight + headerHeight : headerHeight - stickOffset;
      } else {
        totalHeaderHeight = headerHeight + announcementBarHeight;
      }
      const scrolledHeaderHeight = headerIsSticky ? (announcementBarIsSticky ? announcementBarHeight : 0) + headerHeight - this.floatingStickOffset() : totalHeaderHeight;
      document.documentElement.style.setProperty(
        "--header-group-height-dynamic",
        `${Math.max(0, totalHeaderHeight - 2)}px`
      );
      document.documentElement.style.setProperty(
        "--header-group-height-scrolled",
        `${Math.max(0, scrolledHeaderHeight - 2)}px`
      );
      document.documentElement.style.setProperty(
        "--header-height",
        `${headerHeight}px`
      );
      document.documentElement.style.setProperty(
        "--announcement-bar-height",
        `${announcementBarHeight}px`
      );
    },
    closeBurgerMenu() {
      this.isBurgerMenuOpen = false;
    },
    toggleIsBurgerMenu() {
      this.isBurgerMenuOpen = !this.isBurgerMenuOpen;
      const header2 = document.querySelector(".header");
      if (this.isBurgerMenuOpen) {
        document.body.classList.add("overflow-hidden");
        header2.classList.add("mobile-menu-open");
      } else {
        document.body.classList.remove("overflow-hidden");
        header2.classList.remove("mobile-menu-open");
      }
    },
    toggleDesktopMenu(isActive) {
      const header2 = document.querySelector(".header");
      setTimeout(() => {
        header2.classList.toggle("megamenu-active", isActive);
      }, 40);
    },
    bodyClass() {
      const header2 = document.querySelector(".header");
      const headerType = header2?.dataset.headerType;
      if (headerType) {
        document.body.classList.add(`header_type--${headerType}`);
      }
    }
  });
  var header_default = header;

  // src/scripts/islands/navigation.js
  var navigationIsland = (data2 = {
    isMegamenuAccordionOpen: {},
    // Set the default value for isMegamenuAccordionOpen
    isMegamenuActive: false
    // Set the default value for isMegamenuActive
  }) => ({
    isMegamenuAccordionOpen: data2.isMegamenuAccordionOpen,
    isMegamenuActive: data2.isMegamenuActive,
    init() {
      this.megamenuAccordionState();
      this.setEventListeners();
    },
    setEventListeners() {
    },
    megamenuAccordionState() {
      for (let key in this.isMegamenuAccordionOpen) {
        this.isMegamenuAccordionOpen[key] = false;
      }
    },
    toggleMegamenuAccordion(title) {
      for (let key in this.isMegamenuAccordionOpen) {
        if (key !== title) {
          this.isMegamenuAccordionOpen[key] = false;
        }
      }
      this.isMegamenuAccordionOpen[title] = !this.isMegamenuAccordionOpen[title];
    }
  });
  var navigation_default = navigationIsland;

  // src/scripts/components/product-card.js
  var productCard = {
    getProductCard: async function(productHandle, variantId = null) {
      const variantString = variantId ? `&variant=${variantId}` : "";
      const rawProductCard = await get_section_default({
        sectionId: "product-card",
        uri: `products/${productHandle}?view=card${variantString}`,
        pageAware: false
      });
      const productCard2 = getSectionDOM({
        html: rawProductCard,
        selector: ".product-card"
      });
      return productCard2;
    }
  };
  var product_card_default = productCard;

  // src/scripts/utilities/format-money.js
  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  function formatAmount(cents, precision, thousands = ",", decimal = ".") {
    const [whole, fraction] = (cents / 100).toFixed(precision).split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }
  var FORMATTERS = {
    amount: (cents) => formatAmount(cents, 2),
    amount_no_decimals: (cents) => formatAmount(cents, 0),
    amount_with_comma_separator: (cents) => formatAmount(cents, 2, ".", ","),
    amount_no_decimals_with_comma_separator: (cents) => formatAmount(cents, 0, "."),
    amount_with_apostrophe_separator: (cents) => formatAmount(cents, 2, "'"),
    amount_no_decimals_with_space_separator: (cents) => formatAmount(cents, 0, " "),
    amount_with_space_separator: (cents) => formatAmount(cents, 2, " ", ","),
    amount_with_period_and_space_separator: (cents) => formatAmount(cents, 2, " ", ".")
  };
  function formatMoney(cents, format2 = "{{amount}}", centsClass = "") {
    const value = Math.round(Number(cents) || 0);
    const placeholder = String(format2).match(PLACEHOLDER)?.[1];
    const formatter = FORMATTERS[placeholder] || FORMATTERS.amount;
    let amount = formatter(value);
    if (centsClass) {
      amount = amount.replace(
        /[.,]\d{2}$/,
        (fraction) => `<span class="${centsClass}">${fraction}</span>`
      );
    }
    return String(format2).replace(PLACEHOLDER, amount);
  }

  // src/scripts/components/regios-tile-price.js
  var API_EVENT = "regios-dopp:api-initialized";
  var apiPromise = null;
  function regiosApi() {
    if (!apiPromise) {
      apiPromise = new Promise((resolve) => {
        const api = window.RegiosDOPP?.api?.v0;
        if (api) {
          resolve(api);
          return;
        }
        window.addEventListener(
          API_EVENT,
          (event) => resolve(event.detail?.api || window.RegiosDOPP?.api?.v0),
          { once: true }
        );
      });
    }
    return apiPromise;
  }
  async function discountFor(product2, variantId, quantity = 1) {
    const variant = (product2.variants || []).find(
      (entry) => String(entry.id) === variantId
    );
    if (!variant) return null;
    const api = await regiosApi();
    if (!api) return null;
    let result = null;
    try {
      result = await api.calculateDiscountedPrices({
        ...product2,
        variantId: variant.id,
        regularPriceInCents: variant.priceInCents,
        compareAtPriceInCents: variant.compareAtPriceInCents,
        quantity
      });
    } catch {
      return null;
    }
    if (!result || !(result.salePrice?.amount < result.regularPrice?.amount)) {
      return null;
    }
    const saleCents = Math.round(result.salePrice.amount * 100);
    return {
      sale: result.salePrice.formatted,
      regular: result.regularPrice.formatted,
      saleCents,
      saleTrueCents: Math.floor(result.salePrice.amount * 100),
      regularCents: Math.round(result.regularPrice.amount * 100),
      message: result.description?.html?.trim() || "",
      badge: result.badge?.html?.trim() || "",
      saving: variant.priceInCents * quantity - saleCents
    };
  }
  var regiosTilePrice = (product2 = {}, format2 = "") => ({
    sale: "",
    saleTrueCents: null,
    regular: "",
    percent: "",
    message: "",
    badge: "",
    request: "",
    async update(variantId, quantity = 1) {
      const id = String(variantId || "");
      const request = `${id}:${quantity}`;
      this.request = request;
      const discount = await discountFor(product2, id, quantity);
      if (this.request !== request) return;
      const cents = Boolean(format2 && discount);
      this.sale = cents ? formatMoney(discount.saleCents, format2, "cart-money__cents") : discount?.sale || "";
      this.regular = cents && discount.regularCents % 100 ? formatMoney(discount.regularCents, format2, "cart-money__cents") : discount?.regular || "";
      this.percent = cents && discount.regularCents > 0 ? Math.round((1 - discount.saleCents / discount.regularCents) * 1e3) / 10 : "";
      this.saleTrueCents = discount?.saleTrueCents && Math.floor(discount.saleTrueCents) || null;
      this.message = discount?.message || "";
      this.badge = discount?.badge || "";
      const event = new CustomEvent("regiosTilePrice:updated", {
        detail: {
          variantId,
          product: product2,
          sale: this.sale,
          saleTrueCents: this.saleTrueCents,
          regular: this.regular,
          message: this.message
        }
      });
      document.dispatchEvent(event);
    }
  });
  var regios_tile_price_default = regiosTilePrice;

  // src/scripts/components/product-quickbuy.js
  var regiosRequest = 0;
  var productQuickbuy = {
    loadQuickBuy: async function(productHandle, variantId = null) {
      const productQuickbuy2 = await this.getProductQuickbuy(
        productHandle,
        variantId
      );
      const quickbuyModal = document.getElementById("quickbuy-content");
      quickbuyModal.innerHTML = productQuickbuy2.body.innerHTML;
      this.applyRegiosPrices(quickbuyModal, variantId);
    },
    getProductQuickbuy: async function(productHandle, variantId = null) {
      const variantString = variantId ? `&variant=${variantId}` : "";
      const rawProductQuickbuy = await get_section_default({
        sectionId: "product-quickbuy",
        uri: `products/${productHandle}?view=quickbuy${variantString}`,
        pageAware: false
      });
      const productQuickbuy2 = getSectionDOM({
        html: rawProductQuickbuy,
        selector: ".product-quickbuy"
      });
      return productQuickbuy2;
    },
    applyRegiosPrices: async function(container, variantId = null) {
      const quickbuy = container?.querySelector(".quickbuy");
      const script = quickbuy?.querySelector("[data-regios-product]");
      if (!script) return;
      let product2;
      let selectedId = Number(variantId);
      try {
        product2 = JSON.parse(script.textContent);
        if (!selectedId) {
          selectedId = JSON.parse(
            quickbuy.querySelector('[data-product-json="selected-variant"]').textContent
          ).id;
        }
      } catch {
        return;
      }
      const variant = product2.variants?.find((entry) => entry.id === selectedId);
      if (!variant) return;
      const request = ++regiosRequest;
      const api = await regiosApi();
      if (!api || request !== regiosRequest) return;
      const args = {
        ...product2,
        variantId: variant.id,
        regularPriceInCents: variant.priceInCents,
        compareAtPriceInCents: variant.compareAtPriceInCents
      };
      api.themeInterop?.updateOnPagePrices?.(quickbuy, "PRODUCT_RECOMMENDATIONS", args).catch(() => {
      });
      const badge = quickbuy.querySelector(".regios-dopp-generic-badge");
      if (!badge) return;
      let result = null;
      try {
        result = await api.calculateDiscountedPrices(args);
      } catch {
        result = null;
      }
      if (request !== regiosRequest) return;
      const onSale = result?.salePrice?.amount < result?.regularPrice?.amount;
      badge.innerHTML = onSale ? result.badge?.html?.trim() || "" : "";
    }
  };
  var product_quickbuy_default = productQuickbuy;

  // src/scripts/utilities/dispatch-hydration.js
  var debug2 = false;
  function dispatchHydrationEvent(componentInstance, eventName = "component:hydrated") {
    componentInstance.$nextTick(() => {
      const eventDetail = {
        element: componentInstance.$el
        // The root element of the component
      };
      const event = new CustomEvent(eventName, {
        bubbles: true,
        detail: eventDetail
      });
      if (debug2) {
        console.log("Dispatching event:", event);
      }
      componentInstance.$el.dispatchEvent(event);
    });
  }

  // src/scripts/utilities/product-page-seo-data-update.js
  var seperator = String.fromCharCode(8211);
  var storeName;
  function updatePageSEOData({ title = "" }) {
    if (typeof title === "string" && title) {
      storeName ??= document.title.split(seperator).slice(-1)[0]?.trim() || Shopify.shop;
      document.title = `${title} ${seperator} ${storeName}`;
    }
    return;
  }

  // src/scripts/islands/progressive-steps.js
  var SELECTABLE = 'input:not([disabled]):not([type="hidden"]), select:not([disabled])';
  var GATE_ATTRIBUTE = "data-step-requires-previous";
  var REQUIRED_NOTE_CLASS = "fv-accordion__required-note";
  var REQUIRED_NOTE_TEXT = "Required selection";
  var REQUIRED_NOTE_MS = 5e3;
  var ADVANCE_DELAY_MS = 3e3;
  var REVEAL_FALLBACK_MS = 700;
  var COLOUR_SELECTOR = ".fv-color-accordion";
  var GROUP_SELECTORS = [
    COLOUR_SELECTOR,
    ".fv-upgrades-accordion",
    ".fv-additional-mattress-accordion"
  ];
  var VALIDATE_EVENT = "swanky:configurator-validate";
  var progressiveSteps = (data2 = {}) => ({
    groupOpen: data2.groupOpen === true,
    _batchOpening: false,
    _suppressNotify: null,
    _releaseRaf: null,
    _onValidate: null,
    _raf: null,
    _onSwapped: null,
    _onChange: null,
    _onHeaderClick: null,
    _requiredStep: null,
    _requiredTimer: null,
    _advanceStep: null,
    _advanceTarget: null,
    _advanceTimer: null,
    _cancelReveal: null,
    init() {
      this._onChange = (event) => this.onSelectionChange(event);
      this.$el.addEventListener("change", this._onChange);
      this._onHeaderClick = (event) => this.onHeaderClick(event);
      this.$el.addEventListener("click", this._onHeaderClick, true);
      this._onSwapped = () => {
        if (!document.contains(this.$el)) return;
        this.cancelAdvance();
        this.settle(() => this.openFirstIncomplete());
      };
      document.addEventListener("swanky:section-swapped", this._onSwapped);
      this._onValidate = (event) => this.onValidateRequest(event);
      this.$el.addEventListener(VALIDATE_EVENT, this._onValidate);
      this.settle(() => this.openFirstIncomplete());
    },
    destroy() {
      if (this._onChange) this.$el.removeEventListener("change", this._onChange);
      if (this._onHeaderClick) {
        this.$el.removeEventListener("click", this._onHeaderClick, true);
      }
      if (this._onSwapped) {
        document.removeEventListener("swanky:section-swapped", this._onSwapped);
      }
      if (this._onValidate) {
        this.$el.removeEventListener(VALIDATE_EVENT, this._onValidate);
      }
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._releaseRaf) cancelAnimationFrame(this._releaseRaf);
      this.releaseBatch();
      this.clearRequired();
      this.cancelAdvance();
      this.cancelReveal();
    },
    steps() {
      return Array.from(this.$el.querySelectorAll(".fv-accordion")).filter(
        (el) => el.closest(".fv-accordion") === el
      );
    },
    button(step) {
      return step.querySelector(".fv-accordion__button");
    },
    isComplete(step) {
      return step.classList.contains("is-selected");
    },
    isOpen(step) {
      return this.button(step)?.getAttribute("aria-expanded") === "true";
    },
    needsInput(step) {
      return !this.isComplete(step) && step.querySelector(SELECTABLE) != null;
    },
    // The step above a gated one (data-step-requires-previous, i.e. Upgrades) that
    // still wants an answer, so upgrades can't be configured against a half-built
    // bed. Reusing needsInput means steps with no form controls of their own —
    // Lifting Method — never hold the gate closed.
    blockingStep(step) {
      if (!step.hasAttribute(GATE_ATTRIBUTE)) return null;
      const steps = this.steps();
      const index = steps.indexOf(step);
      if (index < 1) return null;
      return steps.slice(0, index).find((earlier) => this.needsInput(earlier)) || null;
    },
    // Clicking a gated step sends the customer to the selection it depends on
    // instead of opening it. The header itself stays live and unstyled — the
    // redirect plus the flagged step is the whole explanation.
    onHeaderClick(event) {
      const btn = event.target?.closest?.(".fv-accordion__button");
      if (!btn || !this.$el.contains(btn)) return;
      const step = btn.closest(".fv-accordion");
      if (!step) return;
      this.cancelAdvance();
      if (this.isOpen(step)) return;
      if (this._batchOpening || this.groupOpen) return;
      const blocker = this.blockingStep(step);
      if (!blocker) return;
      event.preventDefault();
      event.stopPropagation();
      this.flagRequired(blocker);
      this.openStep(blocker, true);
      this.button(blocker)?.focus({ preventScroll: true });
    },
    // Transient "Required selection" flag on the step being asked for. Injected
    // rather than authored per step because every step type — Liquid-rendered and
    // the JS-built fake-variant ones — shares the same trail container.
    flagRequired(step, placement = "trail") {
      this.clearRequired();
      const lead = placement === "lead" ? step.querySelector(".fv-accordion__lead") : null;
      const host = lead || step.querySelector(".fv-accordion__trail") || this.button(step);
      if (!host) return;
      const note2 = document.createElement("span");
      note2.className = REQUIRED_NOTE_CLASS;
      if (lead) note2.classList.add(`${REQUIRED_NOTE_CLASS}--lead`);
      note2.textContent = REQUIRED_NOTE_TEXT;
      if (lead) {
        host.append(note2);
      } else {
        host.prepend(note2);
      }
      this._requiredStep = step;
      this._requiredTimer = setTimeout(
        () => this.clearRequired(),
        REQUIRED_NOTE_MS
      );
    },
    clearRequired() {
      if (this._requiredTimer) {
        clearTimeout(this._requiredTimer);
        this._requiredTimer = null;
      }
      const step = this._requiredStep;
      this._requiredStep = null;
      if (!step) return;
      step.querySelectorAll(`.${REQUIRED_NOTE_CLASS}`).forEach((note2) => note2.remove());
    },
    openStep(step, reveal = false) {
      const btn = this.button(step);
      if (btn && btn.getAttribute("aria-expanded") !== "true") btn.click();
      if (reveal) this.revealStep(step);
    },
    // Bring a step the coordinator opened into view: the information column is
    // sticky, so an auto-opened step can expand below the fold. Held until the
    // collapse transition finishes, otherwise the step is still at its collapsed
    // height when it's measured and it lands off-centre. Steps taller than the
    // viewport are aligned to the top instead, so their first option stays
    // reachable — .fv-accordion carries the scroll-margin that clears the header.
    revealStep(step) {
      this.cancelReveal();
      const content = step.querySelector(".fv-accordion__content");
      const centre = () => {
        this.cancelReveal();
        if (!document.contains(step)) return;
        const viewport = window.innerHeight || 0;
        const fits = step.getBoundingClientRect().height + 48 <= viewport;
        step.scrollIntoView({
          block: fits ? "center" : "start",
          behavior: this.prefersReducedMotion() ? "auto" : "smooth"
        });
      };
      const onEnd = (event) => {
        if (event.target === content && event.propertyName === "height") centre();
      };
      if (content) content.addEventListener("transitionend", onEnd);
      const timer = setTimeout(centre, content ? REVEAL_FALLBACK_MS : 0);
      this._cancelReveal = () => {
        clearTimeout(timer);
        if (content) content.removeEventListener("transitionend", onEnd);
        this._cancelReveal = null;
      };
    },
    cancelReveal() {
      this._cancelReveal?.();
    },
    prefersReducedMotion() {
      return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    },
    firstIncomplete() {
      return this.steps().find((step) => this.needsInput(step)) || null;
    },
    // The next step needing input *after* a given step. Advancing relative to the
    // completed step (rather than the global first-incomplete) means an optional
    // step left unanswered — e.g. Additional Mattress — is still opened as the next
    // step when the one before it completes, but interacting with a later step
    // never yanks focus back to it.
    nextIncompleteAfter(step) {
      const steps = this.steps();
      const start2 = steps.indexOf(step);
      if (start2 === -1) return null;
      for (let i = start2 + 1; i < steps.length; i += 1) {
        if (this.needsInput(steps[i])) return steps[i];
      }
      return null;
    },
    openFirstIncomplete() {
      const target = this.firstIncomplete();
      if (!target) return;
      if (this.groupOpen && this.inGroup(target)) {
        this.openGroup();
        return;
      }
      if (this.isOpen(target)) return;
      if (this.blockingStep(target)) return;
      this.openStep(target);
    },
    inGroup(step) {
      return GROUP_SELECTORS.some((selector) => step.matches(selector));
    },
    openGroup() {
      const steps = this.steps();
      const group = steps.filter((step) => this.inGroup(step));
      if (!group.length) return;
      this.startBatch();
      steps.forEach((step) => {
        if (!this.inGroup(step) && this.isOpen(step)) this.button(step)?.click();
      });
      group.forEach((step) => this.openStep(step));
      this.endBatch();
    },
    startBatch() {
      if (this._batchOpening) return;
      this._batchOpening = true;
      this._suppressNotify = (event) => event.stopPropagation();
      window.addEventListener("notify", this._suppressNotify, true);
    },
    endBatch() {
      if (this._releaseRaf) cancelAnimationFrame(this._releaseRaf);
      this._releaseRaf = requestAnimationFrame(
        () => requestAnimationFrame(() => {
          this._releaseRaf = null;
          this.releaseBatch();
        })
      );
    },
    releaseBatch() {
      if (this._suppressNotify) {
        window.removeEventListener("notify", this._suppressNotify, true);
        this._suppressNotify = null;
      }
      this._batchOpening = false;
    },
    firstBlockingStep() {
      const gated = this.steps().filter(
        (step) => step.hasAttribute(GATE_ATTRIBUTE)
      );
      for (const step of gated) {
        const blocker = this.blockingStep(step);
        if (blocker) return blocker;
      }
      return null;
    },
    requiredStep() {
      return this.firstBlockingStep() || this.steps().find(
        (step) => step.matches(COLOUR_SELECTOR) && this.needsInput(step)
      ) || null;
    },
    onValidateRequest(event) {
      if (!this.groupOpen) return;
      const step = this.requiredStep();
      if (!step) return;
      event.preventDefault();
      this.cancelAdvance();
      this.flagRequired(step, "lead");
      const colour = step.matches(COLOUR_SELECTOR);
      this.openStep(step, !colour);
      if (!colour) this.button(step)?.focus({ preventScroll: true });
    },
    onSelectionChange(event) {
      const input = event.target;
      if (!input || input.tagName !== "INPUT" && input.tagName !== "SELECT") {
        return;
      }
      const step = input.closest(".fv-accordion");
      if (!step) return;
      this.settle(() => {
        if (this._requiredStep && this.isComplete(this._requiredStep)) {
          this.clearRequired();
        }
        if (!this.isComplete(step)) {
          this.cancelAdvance();
          return;
        }
        if (this.groupOpen) {
          this.cancelAdvance();
          return;
        }
        const target = this.nextIncompleteAfter(step);
        if (!target || this.isOpen(target)) {
          this.cancelAdvance();
          return;
        }
        if (this.blockingStep(target)) {
          this.cancelAdvance();
          return;
        }
        this.scheduleAdvance(step, target);
      });
    },
    // Hold the answered step open for ADVANCE_DELAY_MS before moving on, marking it
    // .is-advancing so the CSS can run the countdown ring on its number. Called on
    // every selection, so re-picking inside the step restarts the wait rather than
    // stranding the customer in the step that follows.
    scheduleAdvance(step, target) {
      this.cancelAdvance();
      this._advanceStep = step;
      this._advanceTarget = target;
      step.style.setProperty("--fv-advance-ms", `${ADVANCE_DELAY_MS}ms`);
      void step.offsetWidth;
      step.classList.add("is-advancing");
      this._advanceTimer = setTimeout(
        () => this.commitAdvance(),
        ADVANCE_DELAY_MS
      );
    },
    commitAdvance() {
      const target = this._advanceTarget;
      this.cancelAdvance();
      if (!target || !document.contains(target)) return;
      if (!this.needsInput(target) || this.isOpen(target)) return;
      if (this.blockingStep(target)) return;
      this.openStep(target, true);
    },
    cancelAdvance() {
      if (this._advanceTimer) {
        clearTimeout(this._advanceTimer);
        this._advanceTimer = null;
      }
      if (this._advanceStep) {
        this._advanceStep.classList.remove("is-advancing");
        this._advanceStep.style.removeProperty("--fv-advance-ms");
      }
      this._advanceStep = null;
      this._advanceTarget = null;
    },
    settle(fn) {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(
        () => requestAnimationFrame(() => {
          this._raf = null;
          fn();
        })
      );
    }
  });
  var progressive_steps_default = progressiveSteps;

  // src/scripts/islands/product.js
  var product = (data2 = {
    productId: null,
    productHandle: null,
    sectionId: null,
    productVariants: null,
    selectedVariant: null,
    isQuickBuy: false,
    isProductCard: false,
    lastViewed: null,
    isLoading: false,
    productUrl: null
  }) => ({
    sectionPrefix: "shopify-section-",
    sectionId: data2.sectionId,
    isInitialLoad: true,
    isProductCard: data2.isProductCard,
    isQuickBuy: data2.isQuickBuy,
    isLoading: data2.isLoading,
    lastViewed: Alpine.store("lastViewed"),
    productId: data2.productId,
    productHandle: data2.productHandle,
    // Card / quickbuy only: the product's own URL, plus whether a swatch picker
    // is shown here. Together they drive selectedVariantUrl.
    productUrl: data2.productUrl || null,
    hasVariantPicker: false,
    // Init-only properties
    product: {},
    productVariants: [],
    currentVariant: {},
    variantSelector: null,
    variantId: null,
    addons: {},
    salePrices: {},
    quantityToAdd: 1,
    option1: null,
    option2: null,
    option3: null,
    available: null,
    price: null,
    compareAtPrice: null,
    // Product images zoom data
    zoomOpen: false,
    zoomSrc: "",
    zoomAlt: "",
    zoomScale: 1,
    eventController: null,
    isSwappingProduct: false,
    init() {
      const eventController = new AbortController();
      this.eventController = eventController;
      this.getProductData();
      this.getView();
      this.setEventListeners();
      this.setInitialOptions();
      this.setCurrentVariant(this.currentVariant.id);
      this.$watch("variantSelector", () => this.onVariantChange());
      this.$watch("option1", (_, newValue) => {
        this.onOptionChange(0, newValue);
      });
      this.$watch("option2", (_, newValue) => {
        this.onOptionChange(1, newValue);
      });
      this.$watch("option3", (_, newValue) => {
        this.onOptionChange(2, newValue);
      });
      this.$watch(
        "quantityToAdd",
        (value) => this.$dispatch(`swanky:product${this.productId}:quantity-changed`, {
          quantity: value,
          sectionId: this.sectionId
        })
      );
      this.onVariantChange();
      if (this.isProductCard || this.isQuickBuy) {
        this.hasVariantPicker = Boolean(
          this.$el.querySelector(".variant-selector")
        );
      }
      if (!this.isProductCard && !this.isQuickBuy) {
        const swapData = sessionStorage.getItem(`${getSessionId()}-swap-data`);
        const swap = swapData ? JSON.parse(swapData) : null;
        sessionStorage.removeItem(`${getSessionId()}-swap-data`);
        this.$el.addEventListener("fv-modal-trigger", (event) => {
          const detail = event.detail;
          const optionName = getStepName(event.target);
          if (!optionName) return;
          const linkName = detail.linkName || "";
          sendDataLayerEvent(
            new BedBuilderInfoClick(this, { optionName, linkName })
          );
        });
        this.$el.addEventListener("fake-variant:changed", (event) => {
          const detail = event.detail;
          if (detail.initial) {
            return;
          }
          const { optionName, fromProductId } = detail;
          this.onFakeVariantSwap(event);
          if (!optionName) return;
          const salePriceLoaded = detail.swapped ? this.loadSalePrice(this.currentVariant.id) : Promise.resolve();
          salePriceLoaded.then(() => {
            this.$nextTick(() => {
              sendDataLayerEvent(
                (previousDataLayerState) => new BedBuilderOptionSelected(this, {
                  optionName,
                  previousDataLayerState
                })
              );
              if (detail.swapped) {
                sendDataLayerEvent(
                  new BedBuilderProductSwap(this, {
                    triggerStep: optionName,
                    fromProductId
                  })
                );
              }
            });
          });
        });
        this.$el.addEventListener("swanky:mattress-changed", (event) => {
          const optionName = getStepName(event.target);
          if (!optionName) return;
          const detail = event.detail;
          const priceChildDataStack = event.target.parentElement.querySelector(
            ".fv-upgrade__price[x-data]"
          );
          const addonsPrice = priceChildDataStack?._x_dataStack?.[0].saleTrueCents || null;
          const isUpgradeSelected = detail.upgradeProductId !== "0";
          const currentAddon = new BedBuilderAddon({
            isUpgradeSelected,
            product: detail?.product,
            addonsPrice
          });
          this.addons[optionName] = isUpgradeSelected ? currentAddon : {};
          this.$nextTick(() => {
            sendDataLayerEvent(
              (previousDataLayerState) => new BedBuilderAddonSelected(this, {
                optionName,
                previousDataLayerState,
                currentAddon
              })
            );
          });
        });
        this.$el.addEventListener("swanky:upgrade-changed", (event) => {
          this.upgradeId = (event.detail?.upgradeProductId ?? "0").toString();
          const defaultVariantId = (event.detail?.variantId ?? "").toString();
          if (defaultVariantId && !this.upgradeVariantIds[this.upgradeId]) {
            this.upgradeVariantIds[this.upgradeId] = defaultVariantId;
          }
          if (this.isSingleImageGrid()) {
            this.showUpgradeGridImage(this.upgradeId);
          } else {
            this.showUpgradeFeatured(this.upgradeId);
          }
          const optionName = getStepName(event.target);
          if (!optionName) return;
          const detail = event.detail;
          const priceChildDataStack = event.target.parentElement.querySelector(
            ".fv-upgrade__price[x-data]"
          );
          const addonsPrice = priceChildDataStack?._x_dataStack?.[0].saleTrueCents || null;
          const isUpgradeSelected = detail.upgradeProductId !== "0";
          const currentAddon = new BedBuilderAddon({
            isUpgradeSelected,
            product: detail?.product,
            addonsPrice
          });
          this.addons[optionName] = isUpgradeSelected ? currentAddon : {};
          this.$nextTick(() => {
            sendDataLayerEvent(
              (previousDataLayerState) => new BedBuilderAddonSelected(this, {
                optionName,
                previousDataLayerState,
                currentAddon
              })
            );
          });
        });
        this.$el.addEventListener("swanky:upgrade-variant-changed", (event) => {
          const upgradeId = (event.detail?.upgradeProductId ?? "").toString();
          const variantId = (event.detail?.variantId ?? "").toString();
          if (!upgradeId || !variantId) return;
          this.upgradeVariantIds[upgradeId] = variantId;
          if (this.isSingleImageGrid()) {
            this.showUpgradeGridImage(upgradeId);
          } else {
            this.showUpgradeFeatured(upgradeId);
          }
        });
        this.$el.addEventListener(
          "swanky:upgrade-variant-changed:analytics",
          (event) => {
            const optionName = getStepName(event.target);
            if (!optionName) return;
            const previousAddon = this.addons[optionName];
            if (!previousAddon?.status) return;
            const currentAddon = {
              ...previousAddon,
              status: ADDON_STATUS.CHANGED
            };
            this.addons[optionName] = currentAddon;
            this.$nextTick(() => {
              sendDataLayerEvent(
                (previousDataLayerState) => new BedBuilderAddonSelected(this, {
                  optionName,
                  previousDataLayerState,
                  currentAddon
                })
              );
            });
          }
        );
        document.addEventListener("configurator:resolved-image", (event) => {
          if (!this.isSingleImageGrid()) return;
          this.showResolvedGridImage(event.detail);
          this.filterGridTrailing();
        });
        document.addEventListener("configurator:media-changed", () => {
          if (!this.isSingleImageGrid()) return;
          this.filterGridTrailing();
        });
        document.addEventListener("configurator:request-resolve", () => {
          if (!this.isSingleImageGrid()) return;
          this.filterGridTrailing();
        });
        this.$el.addEventListener("click", (event) => {
          if (!event.isTrusted) return;
          const button = event.target.closest(".fv-accordion__button");
          const step = button?.closest("[data-option-name]");
          if (!step) return;
          this.$nextTick(() => {
            if (button.getAttribute("aria-expanded") !== "true") return;
            const optionName = toSnakeCase(step.dataset.optionName);
            sendDataLayerEvent(new BedBuilderOptionOpened(this, { optionName }));
          });
        });
        this.$nextTick(() => this.filterGridTrailing());
        const sendExit = () => {
          if (this.isSwappingProduct) return;
          if (!canSendExit()) return;
          sendDataLayerEvent(
            (previousDataLayerState) => new BedBuilderExit(this, { previousDataLayerState })
          );
        };
        document.addEventListener(
          "visibilitychange",
          () => {
            if (document.visibilityState === "hidden") sendExit();
          },
          { signal: eventController.signal }
        );
        window.addEventListener("pagehide", sendExit, {
          signal: eventController.signal
        });
        window.addEventListener(
          "pageshow",
          (event) => {
            if (event.persisted) this.isSwappingProduct = false;
          },
          { signal: eventController.signal }
        );
        document.addEventListener(
          "bed-builder-complete",
          () => {
            sendDataLayerEvent(new BedBuilderComplete(this));
          },
          { signal: eventController.signal }
        );
        document.addEventListener(
          "swanky:product-swapped",
          (event) => {
            const detail = event.detail;
            const fromProductId = detail.fromProductId;
            const triggerStep = toSnakeCase(detail.optionName);
            this.isSwappingProduct = true;
            sendDataLayerEvent(
              (previousDataLayerState) => new BedBuilderOptionSelected(this, {
                optionName: triggerStep,
                value: detail.value,
                previousDataLayerState
              })
            );
            sessionStorage.setItem(
              `${getSessionId()}-swap-data`,
              JSON.stringify({ triggerStep, fromProductId })
            );
          },
          { signal: eventController.signal }
        );
        document.addEventListener(
          "regiosTilePrice:updated",
          (event) => {
            const detail = event.detail;
            const saleTrueCents = detail.saleTrueCents || null;
            const variantId = Number(detail.variantId);
            this.salePrices[variantId] = saleTrueCents;
          },
          { signal: eventController.signal }
        );
        this.loadSalePrice(this.currentVariant.id).then(() => {
          if (swap) {
            sendDataLayerEvent(new BedBuilderProductSwap(this, swap));
          }
          sendDataLayerEvent(new BedBuilderStartEvent(this));
        });
      }
      dispatchHydrationEvent(this);
    },
    destroy() {
      this.eventController?.abort();
    },
    async loadSalePrice(variantId) {
      try {
        const product2 = { ...this.product, productId: this.product.id };
        product2.variants = product2.variants.map((variant) => ({
          ...variant,
          priceInCents: variant.price,
          compareAtPriceInCents: variant.compare_at_price || void 0
        }));
        const discount = await discountFor(product2, String(variantId));
        this.salePrices[variantId] = discount?.saleTrueCents || null;
      } catch (error2) {
        console.error("Could not load sale price", error2);
        this.salePrices[variantId] = null;
      }
    },
    upgradeId: "0",
    upgradeVariantIds: {},
    gridFeaturedOriginal: null,
    // Non-single grid: swap only the featured image to the selected upgrade
    // variant's image and keep the parent product's trailing grid in place.
    showUpgradeFeatured(upgradeId) {
      const id = (upgradeId ?? "0").toString();
      if (id === "0") {
        this.restoreGridFeatured();
        return;
      }
      const conditional = window.configuratorMedia?.resolvedImage?.() || null;
      if (conditional) {
        this.setGridFeatured(conditional);
        return;
      }
      const variantId = this.upgradeVariantIds[id];
      const source = variantId ? this.findUpgradeVariantImg(id, variantId) : null;
      if (source) this.overwriteGridFeatured(source);
    },
    setGridFeatured(src, alt) {
      const img = this.gridFeaturedImg();
      if (!img || !src) return;
      if (!this.gridFeaturedOriginal) {
        this.gridFeaturedOriginal = {
          src: img.getAttribute("src"),
          srcset: img.getAttribute("srcset"),
          sizes: img.getAttribute("sizes"),
          alt: img.getAttribute("alt")
        };
      }
      ["srcset", "sizes"].forEach((attr) => img.removeAttribute(attr));
      img.setAttribute("src", src);
      if (alt != null) img.setAttribute("alt", alt);
    },
    gridFeaturedImg() {
      const grid = this.$el.querySelector("product-gallery-grid");
      if (!grid) return null;
      const base = grid.querySelector('[data-upgrade-grid="0"]') || grid;
      return base.querySelector("[data-color-hero] img") || base.querySelector(".grid__item img") || base.querySelector("img");
    },
    overwriteGridFeatured(source) {
      const img = this.gridFeaturedImg();
      if (!img || !source) return;
      if (!this.gridFeaturedOriginal) {
        this.gridFeaturedOriginal = {
          src: img.getAttribute("src"),
          srcset: img.getAttribute("srcset"),
          sizes: img.getAttribute("sizes"),
          alt: img.getAttribute("alt")
        };
      }
      ["src", "srcset", "sizes", "alt"].forEach((attr) => {
        const value = source.getAttribute(attr);
        if (value) img.setAttribute(attr, value);
        else img.removeAttribute(attr);
      });
    },
    restoreGridFeatured() {
      if (!this.gridFeaturedOriginal) return;
      const img = this.gridFeaturedImg();
      if (img) {
        ["src", "srcset", "sizes", "alt"].forEach((attr) => {
          const value = this.gridFeaturedOriginal[attr];
          if (value != null) img.setAttribute(attr, value);
          else img.removeAttribute(attr);
        });
      }
      this.gridFeaturedOriginal = null;
    },
    isSingleImageGrid() {
      return !!this.$el.querySelector("product-gallery-grid")?.hasAttribute("data-single-image");
    },
    gridTrailing: [],
    gridTrailingRoot: null,
    gridTrailingList() {
      const wrapper = this.$el.querySelector(
        "product-gallery-grid[data-single-image] [data-parent-trailing]"
      );
      return wrapper?.querySelector(".grid") || wrapper || null;
    },
    captureGridTrailing() {
      const list2 = this.gridTrailingList();
      this.gridTrailingRoot = list2;
      this.gridTrailing = list2 ? [...list2.children] : [];
    },
    filterGridTrailing() {
      const list2 = this.gridTrailingList();
      if (!list2) return;
      if (list2 !== this.gridTrailingRoot) this.captureGridTrailing();
      if (!this.gridTrailing.length) return;
      const filter = window.configuratorMedia?.selectionFilter?.(this.$el);
      if (!filter) return;
      const leadKey = filter.key(this.gridFeaturedImg());
      let next = null;
      let changed = false;
      for (let index = this.gridTrailing.length - 1; index >= 0; index -= 1) {
        const node = this.gridTrailing[index];
        const img = "mediaAlt" in node.dataset ? node : node.querySelector("img");
        const key = filter.key(img);
        const wanted = filter.matches(img) && (!key || key !== leadKey);
        if (wanted) {
          if (node.parentNode !== list2) {
            list2.insertBefore(node, next);
            changed = true;
          }
          next = node;
        } else if (node.parentNode === list2) {
          list2.removeChild(node);
          changed = true;
        }
      }
      if (changed && window.configuratorMedia?.logging) {
        console.log(
          `[product] grid filtered to ${list2.children.length} trailing image(s) for the current selection`
        );
      }
    },
    findUpgradeVariantImg(upgradeId, variantId) {
      const template = this.$el.querySelector(
        `template[data-upgrade-variant-gallery="${CSS.escape(upgradeId)}"]`
      );
      if (!template) return null;
      return [...template.content.querySelectorAll("img")].find(
        (img) => (img.dataset.variants || "").includes(variantId)
      ) || null;
    },
    showUpgradeGridImage(upgradeId) {
      let id = (upgradeId ?? "0").toString();
      const grid = this.$el.querySelector("product-gallery-grid");
      if (!grid) return;
      if (!grid.querySelector(`[data-upgrade-grid="${CSS.escape(id)}"]`)) {
        id = "0";
      }
      grid.querySelectorAll("[data-upgrade-grid]").forEach((el) => el.hidden = el.dataset.upgradeGrid !== id);
      if (id === "0") {
        if (window.configuratorMedia?.logging) {
          console.log(
            "[product] no upgrades selected \u2014 grid back on the base product image"
          );
        }
        return;
      }
      const container = grid.querySelector(
        `[data-upgrade-grid="${CSS.escape(id)}"]`
      );
      const img = container?.querySelector("img");
      if (img && img.dataset.upgradeOriginalSrc === void 0) {
        img.dataset.upgradeOriginalSrc = img.getAttribute("src") || "";
        img.dataset.upgradeOriginalSrcset = img.getAttribute("srcset") || "";
        img.dataset.upgradeOriginalSizes = img.getAttribute("sizes") || "";
      }
      const conditional = window.configuratorMedia?.resolvedImage?.() || null;
      if (img && conditional) {
        ["srcset", "sizes"].forEach((attr) => img.removeAttribute(attr));
        img.setAttribute("src", conditional);
        if (window.configuratorMedia?.logging) {
          console.log(
            `[product] upgrade ${id} \u2014 grid showing conditional image:`,
            conditional
          );
        }
        return;
      }
      const variantId = this.upgradeVariantIds[id];
      const source = variantId ? this.findUpgradeVariantImg(id, variantId) : null;
      if (img && source) {
        ["src", "srcset", "sizes", "alt", "width", "height"].forEach((attr) => {
          const value = source.getAttribute(attr);
          if (value) img.setAttribute(attr, value);
          else img.removeAttribute(attr);
        });
        if (window.configuratorMedia?.logging) {
          console.log(
            `[product] upgrade ${id} variant ${variantId} \u2014 grid showing:`,
            source.getAttribute("src")
          );
        }
        return;
      }
      if (img && img.dataset.upgradeOriginalSrc !== void 0) {
        const originalSrcset = img.dataset.upgradeOriginalSrcset;
        const originalSizes = img.dataset.upgradeOriginalSizes;
        if (originalSrcset) img.setAttribute("srcset", originalSrcset);
        else img.removeAttribute("srcset");
        if (originalSizes) img.setAttribute("sizes", originalSizes);
        else img.removeAttribute("sizes");
        img.setAttribute("src", img.dataset.upgradeOriginalSrc);
        if (window.configuratorMedia?.logging) {
          console.log(
            `[product] upgrade ${id}: no rule/variant image for this combo \u2014 reverted to the upgrade's default image`
          );
        }
      }
    },
    showResolvedGridImage({ src, alt } = {}) {
      if (!src) return;
      const grid = this.$el.querySelector("product-gallery-grid");
      if (!grid) return;
      const active = grid.querySelector("[data-upgrade-grid]:not([hidden])") || grid.querySelector('[data-upgrade-grid="0"]') || grid;
      const img = active.querySelector("img");
      if (!img) return;
      if (img.getAttribute("src") === src) return;
      ["srcset", "sizes"].forEach((attr) => img.removeAttribute(attr));
      img.setAttribute("src", src);
      if (alt != null) img.setAttribute("alt", alt);
      if (window.configuratorMedia?.logging) {
        console.log("[product] resolved conditional image \u2192 grid showing:", src);
      }
    },
    onFakeVariantSwap(event) {
      if (event.detail?.sectionId != this.sectionId) return;
      if (event.detail?.initial) return;
      if (event.detail?.swapped === false) return;
      this.getProductData();
      const productId = this.$el.querySelector('[data-fv-region="product-data"]')?.dataset.productId;
      if (productId) this.productId = productId;
      this.upgradeId = "0";
      this.upgradeVariantIds = {};
      this.gridFeaturedOriginal = null;
      this.isInitialLoad = true;
      this.option1 = this.currentVariant?.option1 ?? null;
      this.option2 = this.currentVariant?.option2 ?? null;
      this.option3 = this.currentVariant?.option3 ?? null;
      this.setCurrentVariant(this.currentVariant?.id);
    },
    setEventListeners() {
      window.addEventListener(
        `swanky:product${this.productId}:quantity-changed`,
        (event) => {
          if (event.detail.sectionId != this.sectionId) {
            this.quantityToAdd = event.detail.quantity;
          }
        }
      );
    },
    getProductData() {
      const variantsScript = this.$el.querySelector(
        '[data-product-json="variants"]'
      );
      const selectedScript = this.$el.querySelector(
        '[data-product-json="selected-variant"]'
      );
      if (variantsScript) {
        try {
          this.productVariants = JSON.parse(variantsScript.textContent);
        } catch (e) {
          console.error(
            `Error parsing product variants for product ${this.productId}`,
            e
          );
          this.productVariants = [];
        }
      } else {
        console.warn(`No variants JSON found for product ${this.productId}`);
        this.productVariants = [];
      }
      if (selectedScript) {
        try {
          this.currentVariant = JSON.parse(selectedScript.textContent);
        } catch (e) {
          console.error(
            `Error parsing selected variant for product ${this.productId}`,
            e
          );
          this.currentVariant = this.productVariants[0] || {};
        }
      } else {
        this.currentVariant = this.productVariants[0] || {};
      }
      this.selectedVariant = this.currentVariant;
      if (!this.currentVariant.id) {
        console.warn(`No current variant found for product ${this.productId}`);
      }
      this.updatePageSEODataWrapper({ title: this.currentVariant.name });
      const productScript = this.$el.querySelector(
        '[data-product-json="product"]'
      );
      if (productScript) {
        try {
          this.product = JSON.parse(productScript.textContent);
        } catch (e) {
          console.error(`Error parsing product data for ${this.productId}`, e);
          this.product = {};
        }
      } else {
        console.warn(`No product JSON found for ${this.productId}`);
        this.product = {};
      }
    },
    getView() {
      if (this.isProductCard) return "card";
      if (this.isQuickBuy) return "quickbuy";
      return null;
    },
    get sectionElement() {
      if (this.isProductCard) {
        return document.querySelector(
          `[data-product-card-id="${this.productId}"]`
        );
      } else {
        return document.getElementById(`${this.sectionPrefix}${this.sectionId}`);
      }
    },
    // Card / quickbuy product link. Where a swatch picker is shown, it carries
    // ?variant= for the variant the picker shows — picked or the default — so the
    // destination opens on that variant, which is what the configurator reads to
    // open its colour step already answered. Without a picker it stays plain.
    get selectedVariantUrl() {
      if (!this.productUrl) return null;
      if (!this.hasVariantPicker || !this.variantId) return this.productUrl;
      const separator = this.productUrl.includes("?") ? "&" : "?";
      return `${this.productUrl}${separator}variant=${this.variantId}`;
    },
    addToCart() {
      this.isLoading = true;
      let data3 = new FormData();
      data3.append("id", this.variantId);
      data3.append("quantity", this.quantityToAdd);
      Cart.addToCart(data3).then(() => {
        this.isLoading = false;
        sendDataLayerEvent(
          new BedBuilderAddToCart(this, [
            { id: this.variantId, quantity: this.quantityToAdd }
          ]),
          { isEcommerceEvent: true }
        );
        removeStorageSession();
      }).catch(() => {
        this.isLoading = false;
      });
    },
    optionUpdate(option, value) {
      this[option] = value;
    },
    onOptionChange(key, newValue) {
      this.setCurrentVariant(this.getMasterId());
      this.$nextTick(() => {
        const optionName = toSnakeCase(this.product.options[key]);
        sendDataLayerEvent(
          (previousDataLayerState) => new BedBuilderOptionSelected(this, {
            optionName,
            previousDataLayerState
          })
        );
      });
    },
    onVariantChange() {
      this.variantId = Number(this.variantSelector);
      this.currentVariant = this.getVariantById(this.variantId);
      if (this.currentVariant) {
        this.setAvailabilty();
        this.dispatchEvents();
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
          return false;
        }
        if (!this.isProductCard && !this.isQuickBuy) {
          this.loadSalePrice(this.currentVariant.id);
        }
        if (this.isProductCard) {
          this.renderProductCard();
        } else if (this.isQuickBuy) {
          this.renderProductQuickBuy();
        } else {
          if (window.configuratorMedia?.logging) {
            console.log(
              `[product] variant ${this.variantId} picked \u2014 gallery should show:`,
              this.currentVariant?.featured_image?.src || "(variant has no image \u2014 keeping the current image)"
            );
          }
          this.setURL();
          this.renderProductInfo();
        }
      } else {
        this.setUnavilable();
      }
      this.updatePageSEODataWrapper({ title: this.currentVariant.name });
      this.updateCheckedOptions();
    },
    updatePageSEODataWrapper(options) {
      if (this.sectionId?.toLowerCase().includes("main")) {
        updatePageSEOData(options);
      }
    },
    updateCheckedOptions() {
      const options = ["option1", "option2", "option3"];
      if (!options.some((option) => this.currentVariant[option])) {
        throw new Error("No variant options available");
      }
      options.forEach((option) => {
        if (this.currentVariant[option]) {
          this[option] = this.currentVariant[option];
        }
      });
    },
    dispatchEvents() {
      this.$dispatch(`swanky:product${this.productId}:variant-changed`, {
        productId: this.productId,
        variantId: this.variantId,
        initial: this.isInitialLoad
      });
    },
    getVariantById(id) {
      return this.productVariants.find((variant) => {
        return variant.id === id;
      });
    },
    getMasterId() {
      const variant = this.productVariants.find((variant2) => {
        return !variant2.options.map((option, index) => {
          return this[`option${index + 1}`] === option;
        }).includes(false);
      });
      if (variant) {
        return variant.id;
      } else {
        return null;
      }
    },
    setInitialOptions() {
      this.updateCheckedOptions();
    },
    setCurrentVariant(variantId) {
      this.variantId = variantId;
      this.variantSelector = this.variantId;
    },
    setQuantity(number) {
      this.quantityToAdd = Number(this.quantityToAdd) + Number(number);
    },
    setUnavilable() {
      this.available = false;
    },
    setAvailabilty() {
      this.available = this.currentVariant.available;
    },
    setURL() {
      const newURL = `${window.location.origin}${window.location.pathname}?variant=${this.variantId}`;
      window.history.replaceState({}, "", newURL);
    },
    // FUTUREFOCUS: This might be worth moving to a utility file
    getinformationHTML(sectionId, elementsToUpdate) {
      getSection({
        sectionId,
        uri: `?variant=${this.variantId}`,
        pageAware: true,
        view: this.getView()
      }).then((returnedSectionAsString) => {
        const informationHTML = new DOMParser().parseFromString(
          returnedSectionAsString,
          "text/html"
        );
        elementsToUpdate.map((element) => {
          reRenderSectionComponent({
            selector: element,
            section: this.sectionElement,
            newHTML: informationHTML
          });
        });
        document.dispatchEvent(new CustomEvent("configurator:request-resolve"));
      });
    },
    openQuickBuy() {
      product_quickbuy_default.loadQuickBuy(this.productHandle, this.variantId).then(() => {
        this.$dispatch("quickbuy-toggle");
      });
    },
    getQuickbuyHTML(elementsToUpdate) {
      const variantId = this.variantId;
      product_quickbuy_default.getProductQuickbuy(this.productHandle, variantId).then((productQuickbuyHTML) => {
        elementsToUpdate.map((element) => {
          reRenderSectionComponent({
            selector: element,
            section: this.sectionElement,
            newHTML: productQuickbuyHTML
          });
        });
        product_quickbuy_default.applyRegiosPrices(this.sectionElement, variantId);
      });
    },
    getCardHTML(cardElement, elementsToUpdate) {
      product_card_default.getProductCard(this.productHandle, this.variantId).then((productCardHTML) => {
        elementsToUpdate.map((element) => {
          reRenderSectionComponent({
            selector: element,
            section: cardElement,
            newHTML: productCardHTML
          });
        });
      });
    },
    renderProductInfo() {
      const elementsToUpdate = [
        "product-gallery-grid",
        ".product-badges",
        ".price",
        ".description",
        ".product-buy-buttons",
        ".pickup__availability",
        ".product-lead-time",
        "share-button"
      ];
      this.getinformationHTML(this.sectionId, elementsToUpdate);
    },
    renderProductCard() {
      const cardElement = this.$el;
      this.updateCardImage();
      const elementsToUpdate = [".card__title", ".card__reviews", ".card__price"];
      this.getCardHTML(cardElement, elementsToUpdate);
    },
    updateCardImage() {
      const image = this.currentVariant?.featured_image;
      if (!image?.src) return;
      const primary = this.$el.querySelector(".card__media-primary") || this.$el.querySelector(".card__media");
      if (!primary) return;
      const base = image.src;
      const widthUrl = (width) => `${base}${base.includes("?") ? "&" : "?"}width=${width}`;
      const widths = [240, 320, 480, 768, 1024, 1280];
      primary.srcset = widths.map((width) => `${widthUrl(width)} ${width}w`).join(", ");
      primary.src = widthUrl(800);
      if (image.alt) primary.alt = image.alt;
    },
    renderProductQuickBuy() {
      const elementsToUpdate = [
        ".quickbuy__content",
        ".quickbuy__buy",
        ".quickbuy__details"
      ];
      this.getQuickbuyHTML(elementsToUpdate);
    },
    openZoom({ zoomSrc, zoomAlt }) {
      if (!zoomSrc) return console.warn("No image src provided to zoom");
      const img = new Image();
      img.src = zoomSrc;
      this.$nextTick(() => {
        this.zoomSrc = zoomSrc;
        this.zoomAlt = zoomAlt || "";
        this.zoomOpen = true;
        document.body.style.overflow = "hidden";
      });
      img.onerror = () => {
        console.warn("Failed to load zoom image:", zoomSrc);
      };
    },
    closeZoom() {
      this.zoomOpen = false;
      this.zoomSrc = "";
      this.zoomAlt = "";
      document.body.style.overflow = "";
    },
    toggleScale() {
      this.zoomScale = this.zoomScale === 1 ? 2 : 1;
      const overlay = this.$refs.zoomOverlay;
      overlay.classList.toggle("image--zoomed", this.zoomScale === 2);
      this.initDrag();
    },
    initDrag() {
      const clamp3 = (v, min3, max3) => Math.min(Math.max(v, min3), max3);
      let startX, startY, currentX = 0, currentY = 0;
      const resistance = 0.5;
      const image = this.$refs.zoomImage;
      const maxDrag = 100;
      const minDrag = -maxDrag;
      const onTouchStart = (e) => {
        if (this.zoomScale !== 2) return;
        const touch = e.touches[0];
        startX = touch.clientX - currentX;
        startY = touch.clientY - currentY;
        image.style.transition = "none";
      };
      const onTouchMove = (e) => {
        if (this.zoomScale !== 2) return e.preventDefault();
        const touch = e.touches[0];
        currentX = clamp3((touch.clientX - startX) * resistance, minDrag, maxDrag);
        currentY = clamp3((touch.clientY - startY) * resistance, minDrag, maxDrag);
        image.style.transform = `scale(${this.zoomScale}) translate(${currentX}px, ${currentY}px)`;
      };
      const onTouchEnd = () => {
        if (this.zoomScale !== 2) return;
        image.style.transition = "transform 0.3s ease";
      };
      image.addEventListener("touchstart", onTouchStart);
      image.addEventListener("touchmove", onTouchMove);
      image.addEventListener("touchend", onTouchEnd);
    }
  });
  var product_default = product;
  function toSnakeCase(item) {
    if (typeof item !== "string") {
      return item;
    }
    const sanitizedItem = item?.split(" ").map((x) => x.trim()).filter((x) => !["-", "+", "/"].includes(x)).join(" ");
    return sanitizedItem?.toLowerCase()?.replaceAll(" ", "_");
  }
  function getStepName(element) {
    const step = element.closest("[data-option-name]");
    return step ? toSnakeCase(step.dataset.optionName) : null;
  }
  var sendDataLayerEvent = function dataLayerSender() {
    let previousDataLayerState = {
      lastStepName: "",
      lastStepSelected: "",
      data: {}
    };
    let previousOpenEvent = { time: 0, data: {} };
    const eventDebounceTime = 2 * 1e3;
    function classToObject(item) {
      if (!item) {
        console.error("Item missing");
        debugger;
        return {};
      }
      return Object.fromEntries(Object.entries(item));
    }
    return function send(initialEventOrEventCallback, options) {
      const finalEvent = typeof initialEventOrEventCallback == "function" ? initialEventOrEventCallback(previousDataLayerState) : initialEventOrEventCallback;
      if (finalEvent.bed_builder.options.totalLength < 4) {
        return;
      }
      if (finalEvent instanceof BedBuilderOptionOpened) {
        const now = performance.now();
        const debouncedNowTime = now - eventDebounceTime;
        if (previousOpenEvent.data?.event == finalEvent.event && previousOpenEvent.time > debouncedNowTime) {
          return;
        } else {
          previousOpenEvent.time = now;
          previousOpenEvent.data = finalEvent;
        }
      }
      if (finalEvent instanceof BedBuilderStartEvent && hasSessionFlag("started")) {
        return;
      }
      if (finalEvent instanceof BedBuilderExit && !canSendExit()) {
        return;
      }
      if (!(finalEvent instanceof BedBuilderStartEvent) && !hasSessionFlag("started")) {
        send(new BedBuilderStartEvent(finalEvent.state));
      }
      const initEvent = new BedBuilderInitTriggerEvent(
        previousDataLayerState.data,
        options
      );
      if (finalEvent instanceof BedBuilderComplete && hasSessionFlag("completed")) {
        return;
      }
      previousDataLayerState.data = finalEvent;
      if (finalEvent instanceof BedBuilderOptionSelected) {
        previousDataLayerState.lastStepName = finalEvent.optionName;
        previousDataLayerState.lastStepSelected = true;
        const finalEventOptions = finalEvent["bed_builder"].options;
        if (finalEventOptions.length == finalEventOptions.totalLength) {
          const customEvent = new CustomEvent("bed-builder-complete");
          setTimeout(() => {
            document.dispatchEvent(customEvent);
          }, 0);
        }
      }
      if (finalEvent instanceof BedBuilderOptionOpened) {
        previousDataLayerState.lastStepName = finalEvent.optionName;
        previousDataLayerState.lastStepSelected = false;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(classToObject(initEvent));
      window.dataLayer.push(classToObject(finalEvent));
      if (window.Shopify.theme.role === "development") {
        console.log("dataLayer Init Event", classToObject(initEvent));
        console.log("dataLayer Data Event", {
          finalEvent,
          finalObject: classToObject(finalEvent)
        });
      }
      setSessionFlag("started");
      if (finalEvent instanceof BedBuilderComplete) {
        setSessionFlag("completed");
      }
      if (finalEvent instanceof BedBuilderExit) {
        setSessionFlag("exit-sent");
      }
    };
  }();
  var SESSION_ID_STORAGE_KEY = "bed-builder-session-id";
  function getSessionId() {
    let sessionId = "";
    if (sessionStorage.hasOwnProperty(SESSION_ID_STORAGE_KEY)) {
      sessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    }
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
    }
    return sessionId;
  }
  function hasSessionFlag(name) {
    const sessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    return Boolean(sessionId) && sessionStorage.getItem(`${sessionId}-${name}`) === "true";
  }
  function canSendExit() {
    return hasSessionFlag("started") && !hasSessionFlag("completed") && !hasSessionFlag("exit-sent");
  }
  function setSessionFlag(name) {
    sessionStorage.setItem(`${getSessionId()}-${name}`, "true");
  }
  function removeStorageSession() {
    const sessionId = getSessionId();
    Object.keys(sessionStorage).filter((key) => key.startsWith(`${sessionId}-`)).forEach((key) => sessionStorage.removeItem(key));
    sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);
  }
  function getTimeInBuilder(sessionId) {
    const builderStartTimeStorageKey = `${sessionId}-time-in-builder`;
    let timeInBuilder = 0;
    if (sessionStorage.hasOwnProperty(builderStartTimeStorageKey)) {
      const startTimeMs = Number(
        sessionStorage.getItem(builderStartTimeStorageKey)
      );
      timeInBuilder = Math.floor((Date.now() - startTimeMs) / 1e3);
    } else {
      sessionStorage.setItem(builderStartTimeStorageKey, Date.now());
    }
    return timeInBuilder;
  }
  function getOptions(state) {
    let options = {
      get(key) {
        return options[key] || null;
      }
    };
    const productJSNode = state.$el.closest('[x-data*="product("]');
    const progressiveStepsNode = productJSNode.querySelector(
      '[x-ref="progressive-steps"]'
    );
    const progressiveSteps2 = progressiveStepsNode?._x_dataStack?.[0]?.steps() || [];
    const product2 = state.product;
    progressiveSteps2.map((x) => {
      if (!x?._x_dataStack) {
        return {
          optionName: toSnakeCase(x.dataset.optionName),
          value: x.querySelector("[data-fv-selected]").textContent
        };
      }
      const dataStack = x?._x_dataStack?.[0];
      const optionName = toSnakeCase(x?._x_dataStack?.[0].optionName);
      let value = dataStack.value || dataStack.selectedTitle || dataStack.selected;
      if (product2.options.map((x2) => toSnakeCase(x2)).includes(optionName) && !value) {
        value = Object.values(dataStack.touched) && Object.values(dataStack.touched)?.[0] || dataStack.touched;
      }
      if (["bed_upgrade", "mattress"].includes(optionName)) {
        if (!value) {
          if (typeof state.addons[optionName] === "undefined") {
            value = null;
          } else {
            value = "none";
          }
        }
      }
      return {
        optionName,
        value
      };
    }).forEach(({ optionName, value }) => {
      options[optionName] = toSnakeCase(value);
    });
    product2.options.forEach((optionName, optionIndex) => {
      let _optionName = toSnakeCase(optionName);
      if (options[_optionName]) {
        options[_optionName] = toSnakeCase(state[`option${optionIndex + 1}`]);
      }
    });
    const optionsWithoutFunctions = Object.entries(options).filter(
      ([_, value]) => typeof value !== "function"
    );
    options.length = optionsWithoutFunctions.filter(([_, value]) => value).length;
    options.totalLength = optionsWithoutFunctions.length;
    options.keys = optionsWithoutFunctions.map(([key]) => key);
    return options;
  }
  function toPrice(price) {
    return price / 100;
  }
  var BedBuilderEventData = class {
    constructor(state) {
      const product2 = state.product;
      const currentVariant = state.currentVariant;
      const options = getOptions(state);
      const addons = state?.addons || [];
      const sessionId = getSessionId();
      const timeInBuilder = getTimeInBuilder(sessionId);
      const currentVariantPrice = state.salePrices[currentVariant.id] || currentVariant.price;
      let addonsCount = 0;
      let addonsPrice = 0;
      Object.values(addons).forEach((addon) => {
        if (addon.status == ADDON_STATUS.SELECTED || addon.status == ADDON_STATUS.CHANGED) {
          addonsPrice += addon.price;
          addonsCount += addon.count;
        }
      });
      Object.defineProperty(this, "options", {
        value: options,
        enumerable: false,
        writable: false
      });
      this.builder_session_id = sessionId;
      this.product_id = product2.id;
      this.product_name = currentVariant?.name || product2.name;
      this.variant_id = currentVariant?.id || null;
      this.lifting_method = options.get("lifting_method");
      this.size = options.get("size");
      this.orientation = options.get("orientation");
      this.color = options.get("color");
      this.bed_upgrade = options.get("bed_upgrade");
      this.mattress = options.get("mattress");
      this.steps_completed = options.length;
      this.base_price = toPrice(currentVariantPrice);
      this.addons_count = addonsCount;
      this.addons_price = toPrice(addonsPrice);
      this.config_value = toPrice(currentVariantPrice + addonsPrice);
      this.currency = window.Shopify.currency.active;
      this.time_in_builder_sec = timeInBuilder;
    }
    builder_session_id = "";
    product_id = "";
    product_name = "";
    variant_id = null;
    lifting_method = null;
    size = null;
    orientation = null;
    color = null;
    bed_upgrade = null;
    mattress = null;
    steps_completed = "";
    base_price = "";
    addons_count = "";
    addons_price = "";
    config_value = "";
    currency = "";
    time_in_builder_sec = 0;
  };
  var ADDON_STATUS = Object.freeze({
    SELECTED: "selected",
    CHANGED: "changed",
    SKIPPED: "skipped"
  });
  var BedBuilderInitTriggerEvent = class {
    constructor(previousEvent, options = { isEcommerceEvent: false }) {
      if (Object.keys(previousEvent).length > 0) {
        Object.keys(previousEvent).forEach((key) => {
          if (key !== "event") {
            this[key] = null;
          }
        });
      }
      if (options.isEcommerceEvent) {
        this.ecommerce = null;
      }
    }
    bed_builder = null;
  };
  var BedBuilderBaseEvent = class {
    constructor(state, options) {
      this.bed_builder = new BedBuilderEventData(state, options);
      Object.defineProperty(this, "state", {
        value: state,
        enumerable: false,
        writable: false
      });
    }
    event = "";
    bed_builder = null;
  };
  var BedBuilderStartEvent = class extends BedBuilderBaseEvent {
    constructor(state) {
      super(state);
    }
    event = "bed_builder_start";
  };
  var BedBuilderOptionOpened = class extends BedBuilderBaseEvent {
    constructor(state, options = { optionName: "" }) {
      const optionName = toSnakeCase(options.optionName);
      super(state);
      this.event = `bed_builder_${optionName}_open`, this.step_name = optionName;
      Object.defineProperty(this, "optionName", {
        value: optionName,
        enumerable: false,
        writable: false
      });
    }
    event = "bed_builder_${optionName}_open";
    step_name = "";
  };
  var BedBuilderOptionSelected = class extends BedBuilderBaseEvent {
    constructor(state, options = { optionName: "", previousDataLayerState: {} }) {
      const optionName = toSnakeCase(options.optionName);
      super(state, options);
      if (options.value) {
        this.bed_builder[optionName] = toSnakeCase(options.value);
      }
      this.event = `bed_builder_${optionName}_select`;
      this.step_name = optionName;
      this.option_value = this.bed_builder[optionName];
      this.previous_value = options?.previousDataLayerState?.data?.bed_builder?.[optionName] || null;
      Object.defineProperty(this, "optionName", {
        value: optionName,
        enumerable: false,
        writable: false
      });
    }
    event = "bed_builder_${optionName}_select";
    step_name = "step_name";
    option_value = "option_value";
    previous_value = null;
  };
  var BedBuilderAddon = class {
    constructor({ isUpgradeSelected, product: product2, addonsPrice = null }) {
      this.status = isUpgradeSelected ? ADDON_STATUS.SELECTED : ADDON_STATUS.SKIPPED;
      this.count = isUpgradeSelected ? 1 : 0;
      this.option = product2?.title || "";
      this.price = addonsPrice || product2?.price || 0;
    }
    status = "";
    option = "";
    count = 0;
    price = 0;
  };
  var BedBuilderAddonSelected = class extends BedBuilderOptionSelected {
    constructor(state, options = { optionName: "", previousDataLayerState: {}, currentAddon: {} }) {
      super(state, options);
      const addon = options.currentAddon;
      if (addon) {
        this.addon_status = addon.status;
        this.addon_option = toSnakeCase(addon.option);
        this.addon_price = toPrice(addon.price);
      }
    }
  };
  var BedBuilderProductSwap = class extends BedBuilderBaseEvent {
    constructor(state, options = { triggerStep: "", fromProductId: "" }) {
      super(state);
      this.trigger_step = toSnakeCase(options.triggerStep);
      this.from_product_id = options.fromProductId;
    }
    event = "bed_builder_product_swap";
    trigger_step = "";
    from_product_id = "";
  };
  var BedBuilderAddToCart = class extends BedBuilderBaseEvent {
    constructor(state, items = []) {
      super(state);
      this.items = items;
    }
    event = "bed_builder_add_to_cart";
    items = [];
  };
  var BedBuilderComplete = class extends BedBuilderBaseEvent {
    constructor(state) {
      super(state);
    }
    event = "bed_builder_complete";
  };
  var BedBuilderInfoClick = class extends BedBuilderBaseEvent {
    constructor(state, options = { optionName: "", linkName: "" }) {
      super(state);
      this.step_name = options.optionName;
      this.link_name = options.linkName;
    }
    event = "bed_builder_info_click";
    step_name = "";
    link_name = "";
  };
  var BedBuilderExit = class extends BedBuilderBaseEvent {
    constructor(state, options = { previousDataLayerState: {} }) {
      super(state);
      this.last_step_name = options.previousDataLayerState.lastStepName;
      this.last_step_selected = options.previousDataLayerState.lastStepSelected;
    }
    event = "bed_builder_exit";
    last_step_name = "";
    last_step_selected = "";
  };

  // src/scripts/islands/buy-buttons.js
  var buyButtons = (data2 = {
    qty: 1,
    productId: null,
    isGiftCard: false,
    variantId: false,
    enablePopup: false
  }) => ({
    qty: data2.qty,
    productId: data2.productId,
    isGiftCard: data2.isGiftCard,
    variantId: data2.variantId,
    enablePopup: data2.enablePopup,
    buttonState: "",
    buttonNote: "",
    init() {
      window.addEventListener(
        `swanky:product${this.productId}:variant-changed`,
        function(event) {
          this.variantId = event.detail.variantId;
        }.bind(this)
      );
      this._upgradeHandler = () => this.refreshButton();
      window.addEventListener("swanky:upgrade-changed", this._upgradeHandler);
      window.addEventListener(
        "swanky:upgrade-variant-changed",
        this._upgradeHandler
      );
    },
    destroy() {
      window.removeEventListener("swanky:upgrade-changed", this._upgradeHandler);
      window.removeEventListener(
        "swanky:upgrade-variant-changed",
        this._upgradeHandler
      );
    },
    refreshButton() {
      this.buttonState = "";
      this.buttonNote = "";
      const run = () => this.applyButtonState();
      if (window.Alpine && window.Alpine.nextTick) window.Alpine.nextTick(run);
      else run();
    },
    applyButtonState() {
      const button = this.$root.querySelector("[data-buy-state]");
      if (!button) return;
      const result = configurationState(
        button.dataset.buyState,
        this.getUpgradeStates()
      );
      this.buttonState = result.state;
      this.buttonNote = result.blocker ? (button.dataset.upgradeSoldOut || "").replace("[title]", result.blocker) : "";
    },
    buttonLabel(el) {
      if (!this.buttonState) return el.textContent.trim();
      if (this.buttonState === "sold_out") return el.dataset.labelSoldOut;
      return el.dataset.labelAvailable;
    },
    getUpgradeStates() {
      const scope2 = this.$root.closest(".product") || document;
      const states = [];
      scope2.querySelectorAll(
        "[data-product-upgrades] input[data-upgrade-product-id]:checked"
      ).forEach((input) => {
        const productId = input.dataset.upgradeProductId;
        if (!productId || productId === "0") return;
        const variantRadio = scope2.querySelector(
          `[data-product-upgrades] input[data-upgrade-variant][data-upgrade-of="${productId}"]:checked`
        );
        const source = variantRadio || input;
        const variantName = [
          variantRadio?.dataset.upgradeColor,
          variantRadio?.dataset.upgradeSize
        ].filter(Boolean).join(", ");
        states.push({
          state: upgradeState(
            source.dataset.upgradePreorder,
            source.dataset.upgradeAvailable
          ),
          name: [input.dataset.upgradeTitle, variantName].filter(Boolean).join(": ")
        });
      });
      return states;
    },
    cartData() {
      const form = this.$refs.product_form;
      const formData = new FormData(form);
      const upgrades = this.getSelectedUpgrades();
      if (!upgrades.length) return formData;
      const components = upgrades.map((u) => ({
        id: `gid://shopify/ProductVariant/${u.variantId}`,
        quantity: u.quantity,
        price: u.price,
        sig: u.sig
      }));
      formData.append("properties[_bundle_upgrades]", JSON.stringify(components));
      const render = upgrades.filter((u) => u.handle).map((u) => `${u.variantId}:${u.handle}:${u.quantity}`).join(",");
      if (render) formData.append("properties[_bundle_render]", render);
      const images2 = upgrades.filter((u) => u.image).map((u) => `${u.variantId}|${u.image}`).join(",");
      if (images2) formData.append("properties[_bundle_upgrade_images]", images2);
      return formData;
    },
    getSelectedUpgrades() {
      const scope2 = this.$root.closest(".product") || document;
      const checked = scope2.querySelectorAll(
        "[data-product-upgrades] input[data-upgrade-product-id]:checked"
      );
      const upgrades = [];
      checked.forEach((input) => {
        const productId = input.dataset.upgradeProductId;
        if (!productId || productId === "0") return;
        const variantRadio = scope2.querySelector(
          `[data-product-upgrades] input[data-upgrade-variant][data-upgrade-of="${productId}"]:checked`
        );
        const variantId = variantRadio?.dataset.upgradeVariantId || input.dataset.upgradeVariantId;
        if (!variantId || variantId === "0") return;
        const price = variantRadio?.dataset.upgradePrice || input.dataset.upgradePrice || "0";
        const sig = variantRadio?.dataset.upgradePrice ? variantRadio.dataset.upgradeSig || "" : input.dataset.upgradeSig || "";
        const qtyInput = scope2.querySelector(
          `[data-product-upgrades] input[data-upgrade-qty][data-upgrade-of="${productId}"]`
        );
        let quantity = parseInt(qtyInput?.value, 10);
        if (!Number.isInteger(quantity) || quantity < 1) quantity = 1;
        const handle = input.dataset.upgradeHandle || "";
        const image = window.configuratorMedia?.conditionalImageForUpgrade?.({
          id: productId,
          handle,
          colour: variantRadio?.dataset.upgradeColor || "",
          size: variantRadio?.dataset.upgradeSize || "",
          variantId
        }) || "";
        upgrades.push({
          variantId,
          handle,
          price,
          sig,
          quantity,
          image
        });
      });
      return upgrades;
    },
    // Bundle & Save selections are held as on-page state by the bundleSave component
    // and surfaced on the DOM via data-selected. Read the selected variant ids here so
    // they can be added alongside the parent in a single request.
    getSelectedBundleProducts() {
      const scope2 = this.$root.closest(".product") || document;
      const selected = scope2.querySelectorAll(
        '[data-bundle-product][data-selected="true"]'
      );
      const bundles = [];
      selected.forEach((el) => {
        const variantId = el.dataset.variantId;
        if (variantId && variantId !== "0") bundles.push({ variantId });
      });
      return bundles;
    },
    // The Additional Mattress accordion holds its selection as on-page state and
    // surfaces the chosen variant on the DOM via data-selected (same pattern as
    // Bundle & Save). Read the selected variant id here so it can be added as a
    // separate line item alongside the parent in the same request. Returns [] when
    // nothing is selected (or the accordion isn't present).
    getSelectedAdditionalMattress() {
      const scope2 = this.$root.closest(".product") || document;
      const selected = scope2.querySelectorAll(
        '[data-additional-mattress][data-selected="true"]'
      );
      const items = [];
      selected.forEach((el) => {
        const variantId = el.dataset.variantId;
        if (variantId && variantId !== "0") items.push({ variantId });
      });
      return items;
    },
    // Re-express the parent's single-item form payload as a multi-item add and append
    // each extra line item (bundle products, additional mattress) at quantity 1. The
    // parent's properties/selling plan are carried over so the existing add-to-cart
    // behaviour is preserved.
    withBundleProducts(parentData, bundles) {
      const formData = new FormData();
      formData.append("items[0][id]", parentData.get("id"));
      formData.append("items[0][quantity]", parentData.get("quantity") || 1);
      for (const [key, value] of parentData.entries()) {
        if (key.startsWith("properties[")) {
          const inner = key.slice("properties[".length, -1);
          formData.append(`items[0][properties][${inner}]`, value);
        } else if (key === "selling_plan") {
          formData.append("items[0][selling_plan]", value);
        }
      }
      bundles.forEach((bundle, index) => {
        formData.append(`items[${index + 1}][id]`, bundle.variantId);
        formData.append(`items[${index + 1}][quantity]`, 1);
      });
      return formData;
    },
    validateGiftCardForm() {
      const form = this.$refs.product_form;
      const requiredFields = form.querySelectorAll("[required]");
      let isValid = true;
      requiredFields.forEach((field) => {
        const valid = field.checkValidity();
        field.classList.toggle("field--error", !valid);
        if (!valid) isValid = false;
      });
      const sendDateField = form.querySelector('[name="properties[Send on]"]');
      if (sendDateField && sendDateField.value) {
        const selected = new Date(sendDateField.value);
        const today = /* @__PURE__ */ new Date();
        const maxDate = /* @__PURE__ */ new Date();
        maxDate.setDate(maxDate.getDate() + 90);
        if (selected > maxDate) {
          isValid = false;
          sendDateField.classList.add("field--error");
          sendDateField.setCustomValidity(
            "Gift cards can only be scheduled up to 90 days in advance."
          );
        } else {
          sendDateField.setCustomValidity("");
        }
      }
      if (!isValid) {
        const firstInvalid = form.querySelector(":invalid");
        if (firstInvalid) {
          firstInvalid.focus();
          firstInvalid.reportValidity?.();
        }
      }
      return isValid;
    },
    configuratorState() {
      const scope2 = this.$root && this.$root.closest(".product") || document;
      const colour = scope2.querySelector(".fv-color-accordion");
      if (!colour) return null;
      const positions = Array.from(
        colour.querySelectorAll(".variant-selector__option[data-option-position]")
      ).map((el) => Number(el.dataset.optionPosition));
      const data3 = colour._x_dataStack?.[0] || null;
      const touched = data3 && data3.touched ? data3.touched : colour.classList.contains("is-selected") ? positions.reduce((acc, position) => (acc[position] = true, acc), {}) : {};
      const missing = positions.filter((position) => !touched[position]);
      return { colour, data: data3, positions, missing, complete: missing.length === 0 };
    },
    validateConfiguration() {
      const state = this.configuratorState();
      if (!state || state.complete) return true;
      this.revealConfiguratorSelection(state);
      return false;
    },
    revealConfiguratorSelection(state) {
      const { colour, data: data3, missing } = state;
      if (data3) {
        data3.open = true;
      } else {
        const button = colour.querySelector(".fv-accordion__button");
        if (button && button.getAttribute("aria-expanded") !== "true") {
          button.click();
        }
      }
      const targetPosition = missing[0];
      requestAnimationFrame(() => {
        const fieldset = targetPosition ? colour.querySelector(
          `.variant-selector__option[data-option-position="${targetPosition}"]`
        ) : colour;
        const target = fieldset || colour;
        const input = target.querySelector(".radio__input:not([disabled])") || target.querySelector(".radio__input");
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        input?.focus({ preventScroll: true });
      });
    },
    validateConfiguratorSteps() {
      const scope2 = this.$root && this.$root.closest(".product") || document;
      const configurator = scope2.querySelector(".fv-configurator");
      if (!configurator) return true;
      const request = new CustomEvent("swanky:configurator-validate", {
        cancelable: true
      });
      configurator.dispatchEvent(request);
      return !request.defaultPrevented;
    },
    addToCart() {
      this.isLoading = true;
      const stepsValid = this.validateConfiguratorSteps();
      if (!this.validateConfiguration()) {
        this.isLoading = false;
        return;
      }
      if (!stepsValid) {
        this.isLoading = false;
        return;
      }
      const valid = this.isGiftCard ? this.validateGiftCardForm() : true;
      if (valid) {
        const parentData = this.cartData();
        const extras = [
          ...this.getSelectedBundleProducts(),
          ...this.getSelectedAdditionalMattress()
        ];
        const payload = extras.length ? this.withBundleProducts(parentData, extras) : parentData;
        const addedImage = this.enablePopup ? this.resolveAddedImage() : null;
        Cart.addToCart(payload, { openDrawer: !this.enablePopup }).then((added) => {
          this.isLoading = false;
          if (added && this.enablePopup) this.triggerAddedPopup(addedImage);
          try {
            const items = Array.from(
              this.withBundleProducts(parentData, extras).entries()
            ).reduce((prev, [keyStr, value]) => {
              let keySplit = keyStr.split("[").map((x) => x.replaceAll("]", "")).slice(1);
              const [index, key, propertyName] = keySplit;
              if (!prev[index]) {
                prev[index] = {};
              }
              if (propertyName) {
                prev[index][key] = {
                  ...prev[index][key],
                  [propertyName]: value
                };
              } else {
                prev[index][key] = value;
              }
              return prev;
            }, []);
            sendDataLayerEvent(new BedBuilderAddToCart(this, items), {
              isEcommerceEvent: true
            });
            removeStorageSession();
          } catch (err) {
            console.error("Datalayer Error:", err.message, err);
          }
        }).catch(() => {
          this.isLoading = false;
        });
      } else {
        this.isLoading = false;
      }
    },
    resolveAddedImage() {
      const conditional = window.configuratorMedia?.resolvedImage?.();
      if (conditional) return conditional;
      const scope2 = this.$root && this.$root.closest(".product") || document;
      const gallery = scope2.querySelector(".product-gallery");
      const slide = gallery?.querySelector(
        ".splide__slide.is-active:not(.splide__slide--clone) img"
      ) || gallery?.querySelector(".splide__slide:not(.splide__slide--clone) img");
      return slide?.getAttribute("src") || slide?.dataset?.src || null;
    },
    triggerAddedPopup(image) {
      window.dispatchEvent(
        new CustomEvent("swanky:atc-popup-open", {
          detail: {
            productId: this.productId,
            variantId: this.variantId,
            image
          }
        })
      );
    }
  });
  function upgradeState(rawStatus, rawAvailable) {
    let status = null;
    try {
      status = JSON.parse(rawStatus || "null");
    } catch (_error) {
      status = null;
    }
    if (rawAvailable === "false" || status?.state === "sold_out") {
      return "sold_out";
    }
    if (status?.state === "preorder") return "preorder";
    return "available";
  }
  function configurationState(parentState, upgrades) {
    if (parentState === "sold_out") return { state: "sold_out", blocker: "" };
    const blocked = upgrades.find((upgrade) => upgrade.state === "sold_out");
    if (blocked) return { state: "sold_out", blocker: blocked.name };
    if (parentState === "preorder" || upgrades.some((upgrade) => upgrade.state === "preorder")) {
      return { state: "preorder", blocker: "" };
    }
    return { state: "available", blocker: "" };
  }
  var buy_buttons_default = buyButtons;

  // src/scripts/islands/custom-range.js
  var customRange = (data2 = {
    minLimit: null,
    min: null,
    max: null,
    maxLimit: null,
    step: null
  }) => ({
    minLimit: Number.parseInt(data2.minLimit),
    min: Number.parseInt(data2.min),
    max: Number.parseInt(data2.max),
    maxLimit: Number.parseInt(data2.maxLimit),
    step: Number.parseInt(data2.step),
    sendPriceRange() {
      this.$dispatch("price-range-changed", { min: this.min, max: this.max });
    },
    updateMin(value) {
      this.min = Math.min(value, this.max - this.step);
    },
    updateMax(value) {
      this.max = Math.max(value, this.min + this.step);
    }
  });
  var custom_range_default = customRange;

  // src/scripts/islands/note.js
  var debug3 = false;
  var note = (data2 = {
    noteAccordion: false,
    note: ""
  }) => ({
    noteAccordion: data2.noteAccordion,
    note: data2.note,
    toggleAccordion() {
      this.noteAccordion = !this.noteAccordion;
    },
    closeAccordion() {
      this.noteAccordion = false;
    },
    async saveNote() {
      return new Promise(async (resolve, reject) => {
        try {
          const result = await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify({
              note: this.note
            })
          });
          resolve(await result.json());
          if (!result.ok) {
            Alpine.store("errors").add(result.message);
          }
        } catch (error2) {
          reject(error2);
        }
        if (debug3) {
          console.log("Note Logic sent");
          console.log("Note:" + this.note);
        }
      });
    },
    saveNoteAndClose() {
      this.saveNote();
      this.closeAccordion();
    }
  });
  var note_default = note;

  // src/scripts/islands/purchase-options.js
  var purchaseOptions = (data2 = {
    sellingPlan: null,
    required: null,
    error: null,
    productId: null
  }) => ({
    productId: data2.productId,
    error: data2.error,
    required: data2.required,
    sellingPlan: data2.sellingPlan,
    init() {
      window.addEventListener(
        `swanky:product${this.productId}:variant-changed`,
        async (e) => {
          this.selectPlan(null);
        }
      );
    },
    disableForm() {
      const form = this.$refs.product_form;
      if (!form) return;
      const submitButton = form.querySelector('[type="submit"]');
      if (!submitButton) return;
      if (this.required && !this.sellingPlan) {
        submitButton.disabled = true;
        submitButton.classList.add("disabled");
      } else {
        submitButton.disabled = false;
        submitButton.classList.remove("disabled");
      }
    },
    selectPlan(planId) {
      this.sellingPlan = planId;
    }
  });
  var purchase_options_default = purchaseOptions;

  // src/scripts/islands/additional-mattress.js
  var carriedMattress = null;
  var carryAdditionalMattress = (section) => {
    const root = section?.querySelector(".fv-additional-mattress-accordion");
    const state = root?._x_dataStack?.[0];
    if (!state?.selected) return;
    const productId = state.selected;
    const panel = [...root.querySelectorAll(".fv-upgrade__variants")].map((el) => el._x_dataStack?.[0]).find((data2) => data2?.productId === productId);
    carriedMattress = {
      productId,
      variantId: state.variantFor(productId),
      values: panel?.choice?.slice(),
      label: { ...state.labels[productId] },
      variantLabel: state.variantLabels[productId],
      variantPending: state.variantPending,
      variantTouched: state.variantTouched[productId] === true
    };
    setTimeout(() => {
      carriedMattress = null;
    });
  };
  var additionalMattress = (data2 = {}) => ({
    optionName: data2.optionName,
    open: false,
    touched: false,
    selected: "",
    selectedTitle: "",
    variantPending: false,
    variantTouched: {},
    labels: {},
    // productId -> currently resolved variant id. Seeded from Liquid with each
    // product's default (first configured) variant so the card carries a usable
    // variant id before its panel has been touched.
    variantIds: Object.assign({}, data2.variants),
    // variantId -> money-formatted price, so a card's price follows its panel.
    prices: Object.assign({}, data2.prices),
    noneLabel: data2.noneLabel || "No mattress",
    // productId -> the variant label last resolved by that product's panel.
    variantLabels: Object.assign({}, data2.labels),
    carryPending: null,
    init() {
      const carry = carriedMattress;
      carriedMattress = null;
      if (!carry?.variantId) return;
      if (!(carry.productId in this.variantIds)) return;
      if (!(carry.variantId in this.prices)) {
        if (carry.values) this.carryPending = carry;
        return;
      }
      this.adoptCarry(carry, carry.variantId);
    },
    adoptCarry(carry, variantId) {
      this.variantIds[carry.productId] = variantId;
      if (carry.variantLabel) {
        this.variantLabels[carry.productId] = carry.variantLabel;
      }
      if (carry.variantTouched) this.variantTouched[carry.productId] = true;
      this.selected = carry.productId;
      this.variantPending = carry.variantPending;
      this.labels = { [carry.productId]: carry.label };
      this.touched = true;
    },
    get summary() {
      if (!this.selected) return this.noneLabel;
      const label = this.labels[this.selected] || {};
      return label.variant ? label.title + ": " + label.variant : label.title;
    },
    isSelected(productId) {
      return this.selected === String(productId);
    },
    variantFor(productId) {
      return this.variantIds[productId] || "";
    },
    priceFor(productId) {
      const price = this.prices[this.variantFor(productId)];
      return price ? "+ " + price : "";
    },
    selectNone() {
      this.selected = "";
      this.selectedTitle = "";
      this.variantPending = false;
      this.labels = {};
      this.emit(0);
    },
    selectProduct(productId, title, variantLabel, pendsVariant) {
      const key = String(productId);
      const variant = this.getVariant(productId);
      this.selected = key;
      this.selectedTitle = variant?.name || title || "";
      this.variantPending = pendsVariant === true && !this.variantTouched[key];
      this.labels = {
        [key]: { title, variant: this.variantLabels[key] || variantLabel || "" }
      };
      this.emit(productId);
    },
    emit(productId) {
      const variant = this.getVariant(productId);
      const product2 = variant ? { title: variant.name, price: variant.price } : {};
      this.$dispatch("swanky:mattress-changed", {
        upgradeProductId: String(productId),
        product: product2
      });
    },
    getVariant(productId) {
      const textContent = this.$root.querySelector(
        `[data-json="product-data"]`
      )?.textContent;
      const entries2 = [].concat(textContent && JSON.parse(textContent) || []);
      const variants = entries2.flatMap((entry) => entry.variants || [entry]);
      const variant = variants.find(
        (x) => String(x.id) == String(this.variantIds?.[productId])
      );
      return variant;
    },
    // Clicking an already-checked card fires no change event, so the header still
    // has to learn the step was answered. For the pre-checked "No Mattress" that
    // also means emitting the change, as no selectNone() runs.
    touch() {
      const isFirstNoneClick = this.selected === "" && !this.touched;
      this.touched = true;
      if (isFirstNoneClick) this.emit(0);
    }
  });
  var additionalMattressVariants = (data2 = {}) => ({
    productId: String(data2.productId || ""),
    options: data2.options || [],
    variants: data2.variants || [],
    choice: [],
    touchedOptions: {},
    init() {
      const carry = this.carryPending;
      if (carry?.productId === this.productId) {
        this.carryPending = null;
        const closest2 = this.closestTo(carry.values);
        if (closest2) {
          this.adoptCarry(carry, closest2.id);
          this.choice = closest2.values.slice();
          this.sync();
          return;
        }
      }
      const current = this.variants.find(
        (variant) => variant.id === this.variantIds[this.productId]
      );
      this.choice = ((current || this.variants[0])?.values || []).slice();
    },
    closestTo(values) {
      let best = null;
      let bestScore = 0;
      this.variants.forEach((variant) => {
        const score = variant.values.filter(
          (value, index) => value === values[index]
        ).length;
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      });
      return best;
    },
    valuesFor(index) {
      const values = [];
      this.variants.forEach((variant) => {
        const value = variant.values[index];
        if (value !== void 0 && !values.includes(value)) values.push(value);
      });
      return values;
    },
    isChosen(index, value) {
      return this.choice[index] === value;
    },
    currentVariant() {
      return this.variants.find(
        (variant) => variant.values.every((value, index) => value === this.choice[index])
      ) || null;
    },
    // Prefer the exact combination; when it isn't configured, adopt the whole
    // tuple of the first variant carrying the value that was just picked, so an
    // invalid combination can never be left resolved.
    pick(index, value) {
      const next = this.choice.slice();
      next[index] = value;
      const exact = this.variants.find(
        (variant2) => variant2.values.every((option, i) => option === next[i])
      );
      const variant = exact || this.variants.find((entry) => entry.values[index] === value);
      if (!variant) return;
      this.choice = variant.values.slice();
      this.touchedOptions[index] = true;
      this.sync();
    },
    // A default value is always pre-checked, so :checked can't tell a real choice
    // from the default — only these handlers run on interaction.
    review(index, value) {
      if (this.choice[index] !== value) return;
      this.touchedOptions[index] = true;
      this.sync();
    },
    sync() {
      const pending = this.options.some(
        (option, index) => this.valuesFor(index).length > 1 && !this.touchedOptions[index]
      );
      this.variantIds[this.productId] = this.currentVariant()?.id || "";
      this.variantLabels[this.productId] = this.choice.join(", ");
      const label = this.labels[this.productId];
      if (label) label.variant = this.variantLabels[this.productId];
      if (!pending) this.variantTouched[this.productId] = true;
      if (this.selected !== this.productId) return;
      this.variantPending = pending && !this.variantTouched[this.productId];
    }
  });
  var additional_mattress_default = additionalMattress;

  // src/scripts/islands/fake-variants.js
  var sectionCache = /* @__PURE__ */ new Map();
  var selectionBySection = /* @__PURE__ */ new Map();
  var ACCORDION_GROUP = "product-information-accordion";
  function updateSectionURL(handle, historyKey, variantId) {
    const root = window.Shopify?.routes?.root || "/";
    const url = `${root}products/${handle}`;
    const query2 = variantId ? `?variant=${variantId}` : "";
    window.history.replaceState({ [historyKey]: handle }, "", `${url}${query2}`);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", `${window.location.origin}${url}`);
    }
  }
  function setSectionLoading(ctx, state) {
    ctx.isLoading = state;
    const region = document.getElementById(`shopify-section-${ctx.sectionId}`);
    region?.toggleAttribute("data-fv-loading", state);
    region?.setAttribute("aria-busy", String(state));
  }
  async function fetchProductSection(handle, sectionId, signal, variantId) {
    const cacheKey = variantId ? `${handle}:${sectionId}:${variantId}` : `${handle}:${sectionId}`;
    if (sectionCache.has(cacheKey)) {
      return sectionCache.get(cacheKey);
    }
    const root = window.Shopify?.routes?.root || "/";
    const variantQuery = variantId ? `variant=${variantId}&` : "";
    const url = `${root}products/${handle}?${variantQuery}section_id=${encodeURIComponent(
      sectionId
    )}`;
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(
        `Failed to load section "${sectionId}" for product "${handle}" (${response.status})`
      );
    }
    const html = await response.text();
    sectionCache.set(cacheKey, html);
    return html;
  }
  var REGIOS_CONFIG_ID = "regios-dopp-product-page-config-json";
  async function fetchRegiosProduct(handle, signal) {
    if (!window.RegiosDOPP_ProductPage) return null;
    const section = document.getElementById(REGIOS_CONFIG_ID)?.closest(".shopify-section[id^='shopify-section-']");
    if (!section) return null;
    try {
      const html = await fetchProductSection(
        handle,
        section.id.replace("shopify-section-", ""),
        signal
      );
      const doc = new DOMParser().parseFromString(html, "text/html");
      const script = doc.getElementById(REGIOS_CONFIG_ID);
      return script ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  }
  async function handOffRegiosProduct(product2) {
    const variantId = Number(product2?.variantId);
    if (!product2?.variants?.some((variant) => variant.id === variantId)) {
      return () => {
      };
    }
    const announce = () => window.dispatchEvent(
      new CustomEvent("regios-dopp:variant-change", {
        detail: { selectedVariant: { id: variantId }, quantity: 1 }
      })
    );
    window.RegiosDOPP_ProductPage = product2;
    announce();
    await new Promise((resolve) => setTimeout(resolve));
    return announce;
  }
  var fakeVariants = (data2 = {
    sectionId: null,
    currentHandle: null,
    pickerType: "button",
    numberOffset: 0,
    preselect: false,
    hideUnavailable: false
  }) => ({
    sectionId: data2.sectionId,
    currentHandle: data2.currentHandle,
    pickerType: data2.pickerType || "button",
    numberOffset: data2.numberOffset || 0,
    preselect: data2.preselect || false,
    hideUnavailable: data2.hideUnavailable || false,
    config: null,
    optionNames: [],
    optionValues: {},
    products: [],
    handleByKey: /* @__PURE__ */ new Map(),
    images: {},
    optionValueImages: {},
    titles: {},
    selected: {},
    touched: {},
    isLoading: false,
    enabled: false,
    _listeners: null,
    _request: null,
    init() {
      if (!this.parseConfig()) {
        return;
      }
      this.buildMatrix();
      const listed = this.products.some((p) => p.handle === this.currentHandle);
      this.resolveCurrentSelection();
      this.adoptPersistedSelection();
      this.enabled = true;
      this.renderSelectors();
      this.dispatchSwapEvent(this.currentHandle, { initial: true });
      this.$nextTick(() => this.prefetchSiblings());
      const answered = this.optionNames.some((n) => this.selected[n] != null);
      if (answered) {
        const handle = this.resolveHandle();
        if (handle !== this.currentHandle || !listed) this.load(handle);
      }
    },
    destroy() {
      this._listeners?.abort();
      this._request?.abort();
    },
    parseConfig() {
      const script = this.$el.querySelector('[data-fake-variants-json="config"]');
      if (!script) {
        console.warn("[fake-variants] No configuration JSON found. Skipping.");
        return false;
      }
      let parsed;
      try {
        parsed = JSON.parse(script.textContent);
      } catch (error2) {
        console.warn("[fake-variants] Malformed configuration JSON.", error2);
        return false;
      }
      if (!parsed || !Array.isArray(parsed.option_names) || parsed.option_names.length === 0 || !Array.isArray(parsed.products) || parsed.products.length === 0) {
        console.warn(
          "[fake-variants] Configuration is missing option_names or products.",
          parsed
        );
        return false;
      }
      const products = parsed.products.filter(
        (p) => p && typeof p.handle === "string" && p.options
      );
      if (products.length === 0) {
        console.warn("[fake-variants] No valid sibling products in config.");
        return false;
      }
      this.config = { option_names: parsed.option_names, products };
      this.parseImages();
      this.parseOptionValueImages();
      this.parseTitles();
      return true;
    },
    parseTitles() {
      this.titles = {};
      const script = this.$el.querySelector('[data-fake-variants-json="titles"]');
      if (!script) return;
      try {
        const parsed = JSON.parse(script.textContent);
        if (parsed && typeof parsed === "object") this.titles = parsed;
      } catch (error2) {
        console.warn("[fake-variants] Malformed titles JSON.", error2);
      }
    },
    // Optional per-step title override. Exact match first, then a trimmed,
    // case-insensitive fallback; otherwise the option name is used unchanged.
    titleFor(name) {
      if (this.titles[name]) return this.titles[name];
      const wanted = String(name).trim().toLowerCase();
      for (const key in this.titles) {
        if (String(key).trim().toLowerCase() === wanted && this.titles[key]) {
          return this.titles[key];
        }
      }
      return name;
    },
    parseImages() {
      this.images = {};
      const script = this.$el.querySelector('[data-fake-variants-json="images"]');
      if (!script) return;
      try {
        const parsed = JSON.parse(script.textContent);
        if (parsed && typeof parsed === "object") this.images = parsed;
      } catch (error2) {
        console.warn("[fake-variants] Malformed image map JSON.", error2);
      }
    },
    parseOptionValueImages() {
      this.optionValueImages = {};
      const script = this.$el.querySelector(
        '[data-fake-variants-json="option-value-images"]'
      );
      if (!script) return;
      try {
        const parsed = JSON.parse(script.textContent);
        if (!parsed || typeof parsed !== "object") return;
        Object.entries(parsed).forEach(([name, values]) => {
          if (!values || typeof values !== "object") return;
          const map = {};
          Object.entries(values).forEach(([value, url]) => {
            if (typeof url === "string" && url) {
              map[String(value).trim().toLowerCase()] = url;
            }
          });
          this.optionValueImages[String(name).trim().toLowerCase()] = map;
        });
      } catch (error2) {
        console.warn("[fake-variants] Malformed option-value image JSON.", error2);
      }
    },
    valueImageFor(name, value) {
      return this.optionValueImages[String(name).trim().toLowerCase()]?.[String(value).trim().toLowerCase()] || null;
    },
    imageForOption(name, value) {
      let best = null;
      let bestScore = -1;
      for (const product2 of this.products) {
        if (product2.options?.[name] !== value) continue;
        let score = 0;
        for (const other of this.optionNames) {
          if (other === name) continue;
          if (this.selected[other] != null && product2.options?.[other] === this.selected[other]) {
            score += 1;
          }
        }
        if (score > bestScore) {
          bestScore = score;
          best = product2;
        }
      }
      return best ? this.images[best.handle] || null : null;
    },
    buildMatrix() {
      this.optionNames = this.config.option_names;
      this.products = this.config.products;
      this.optionValues = {};
      this.handleByKey = /* @__PURE__ */ new Map();
      this.optionNames.forEach((name) => this.optionValues[name] = []);
      this.products.forEach((product2) => {
        this.optionNames.forEach((name) => {
          const value = product2.options?.[name];
          if (value != null && !this.optionValues[name].includes(value)) {
            this.optionValues[name].push(value);
          }
        });
        const key = this.keyFor(product2.options);
        if (this.handleByKey.has(key)) {
          console.warn(
            `[fake-variants] Duplicate option combination "${key}" ("${this.handleByKey.get(key)}" vs "${product2.handle}"). Each product family needs its own fake_variants config.`
          );
        }
        this.handleByKey.set(key, product2.handle);
      });
    },
    keyFor(options) {
      return this.optionNames.map((name) => options?.[name] ?? "").join("|");
    },
    resolveCurrentSelection() {
      const current = this.products.find((p) => p.handle === this.currentHandle) || this.products[0];
      this.currentHandle = current.handle;
      this.selected = {};
      this.optionNames.forEach((name) => {
        this.selected[name] = null;
      });
      if (this.preselect) {
        this.optionNames.forEach((name) => {
          const value = current.options?.[name];
          if (value == null) return;
          if (!this.optionValues[name]?.includes(value)) return;
          this.selected[name] = value;
          this.touched[name] = true;
        });
      }
    },
    adoptPersistedSelection() {
      const persisted = selectionBySection.get(this.sectionId);
      if (!persisted) return;
      this.optionNames.forEach((name) => {
        const value = persisted.selected?.[name];
        if (persisted.touched?.[name] !== true || value == null) return;
        if (!this.optionValues[name]?.includes(value)) return;
        this.selected[name] = value;
        this.touched[name] = true;
      });
    },
    persistSelection() {
      selectionBySection.set(this.sectionId, {
        selected: { ...this.selected },
        touched: { ...this.touched }
      });
    },
    resolveHandle() {
      return this.resolveFor(this.selected);
    },
    resolveFor(selection) {
      const exact = this.handleByKey.get(this.keyFor(selection));
      if (exact) return exact;
      let best = null;
      let bestScore = 0;
      for (const product2 of this.products) {
        let score = 0;
        for (const name of this.optionNames) {
          if (selection[name] != null && product2.options?.[name] === selection[name]) {
            score += 1;
          }
        }
        if (score > bestScore) {
          bestScore = score;
          best = product2;
        }
      }
      if (best) return best.handle;
      return this.products[0].handle;
    },
    renderSelectors() {
      const root = this.$refs.selectors;
      if (!root) return;
      this._listeners?.abort();
      this._listeners = new AbortController();
      const { signal } = this._listeners;
      root.innerHTML = "";
      this.optionNames.forEach((name, index) => {
        const group = this.pickerType === "select" ? this.buildSelectGroup(name, signal) : this.buildRadioGroup(name, signal);
        root.appendChild(this.buildAccordion(name, group, signal, index));
      });
      this.syncActiveState();
      this.updateAvailability();
    },
    buildAccordion(name, group, signal, index) {
      const accordion = document.createElement("div");
      accordion.className = "accordion fv-accordion";
      accordion.dataset.optionName = name;
      const open = this.optionNames.length === 1;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "button button--collapsible accordion__button fv-accordion__button";
      if (!open) button.classList.add("button--collapsed");
      button.setAttribute("aria-expanded", String(open));
      const lead = document.createElement("span");
      lead.className = "fv-accordion__lead";
      const status = document.createElement("span");
      status.className = "fv-accordion__status";
      const number = document.createElement("span");
      number.className = "fv-accordion__number";
      number.textContent = String((index ?? 0) + 1 + this.numberOffset);
      const tick = document.createElement("span");
      tick.className = "fv-accordion__tick";
      const tickIcon = this.getIcon("tick");
      if (tickIcon) tick.appendChild(tickIcon);
      status.appendChild(number);
      status.appendChild(tick);
      lead.appendChild(status);
      const heading = document.createElement("span");
      heading.className = "heading h6";
      heading.textContent = this.titleFor(name);
      lead.appendChild(heading);
      const titleLink = this.getOptionLink(name);
      if (titleLink) {
        titleLink.hidden = !open;
        if (open) titleLink.classList.add("is-visible");
        titleLink.addEventListener("click", (event) => event.stopPropagation(), {
          signal
        });
        this.wireDrawerTrigger(titleLink, signal);
        lead.appendChild(titleLink);
      }
      button.appendChild(lead);
      const trail = document.createElement("span");
      trail.className = "fv-accordion__trail";
      const selected = document.createElement("span");
      selected.className = "fv-accordion__selected";
      selected.dataset.fvSelected = "";
      trail.appendChild(selected);
      const icon = document.createElement("span");
      icon.className = "fv-accordion__icon";
      const up = this.getIcon("chevron-up");
      const down = this.getIcon("chevron-down");
      if (up) icon.appendChild(up);
      if (down) icon.appendChild(down);
      trail.appendChild(icon);
      button.appendChild(trail);
      const content = document.createElement("section");
      content.className = "accordion-content fv-accordion__content";
      content.hidden = !open;
      const inner = document.createElement("div");
      inner.className = "content";
      const contentLink = this.getOptionLink(name);
      if (contentLink) {
        contentLink.classList.add(
          "fv-accordion__title-link--content",
          "is-visible"
        );
        this.wireDrawerTrigger(contentLink, signal);
        inner.appendChild(contentLink);
      }
      inner.appendChild(group);
      content.appendChild(inner);
      const accId = `fv-acc-${this.sectionId}-${name}`.replace(/\s+/g, "-");
      let pendingOpen = null;
      content.addEventListener(
        "transitionend",
        (event) => {
          if (event.target !== content || event.propertyName !== "height") return;
          if (pendingOpen === null) return;
          content.style.transition = "";
          content.style.overflow = "";
          content.style.height = "";
          if (!pendingOpen) content.hidden = true;
          pendingOpen = null;
        },
        { signal }
      );
      const slideContent = (next) => {
        const reduceMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        if (reduceMotion) {
          pendingOpen = null;
          content.style.transition = "";
          content.style.overflow = "";
          content.style.height = "";
          content.hidden = !next;
          return;
        }
        const from = content.hidden ? 0 : content.getBoundingClientRect().height;
        content.hidden = false;
        content.style.transition = "none";
        content.style.height = "auto";
        const to = next ? content.getBoundingClientRect().height : 0;
        content.style.height = `${from}px`;
        content.style.overflow = "hidden";
        content.getBoundingClientRect();
        content.style.transition = "height 0.25s ease";
        content.style.height = `${to}px`;
        pendingOpen = next;
      };
      const setOpen = (next) => {
        button.setAttribute("aria-expanded", String(next));
        button.classList.toggle("button--collapsed", !next);
        slideContent(next);
        if (titleLink) {
          if (next) {
            titleLink.hidden = false;
            requestAnimationFrame(() => titleLink.classList.add("is-visible"));
          } else {
            titleLink.classList.remove("is-visible");
            titleLink.addEventListener(
              "transitionend",
              () => {
                if (!titleLink.classList.contains("is-visible")) {
                  titleLink.hidden = true;
                }
              },
              { once: true }
            );
          }
        }
      };
      button.addEventListener(
        "click",
        () => {
          const next = button.getAttribute("aria-expanded") !== "true";
          setOpen(next);
          if (next) {
            button.dispatchEvent(
              new CustomEvent("notify", {
                bubbles: true,
                detail: { group: ACCORDION_GROUP, id: accId }
              })
            );
          }
        },
        { signal }
      );
      window.addEventListener(
        "notify",
        (event) => {
          if (event.detail?.group === ACCORDION_GROUP && event.detail?.id !== accId && button.getAttribute("aria-expanded") === "true") {
            setOpen(false);
          }
        },
        { signal }
      );
      accordion.appendChild(button);
      accordion.appendChild(content);
      return accordion;
    },
    getIcon(name) {
      const tpl = this.$el.querySelector(`template[data-fv-icon="${name}"]`);
      if (!tpl) return null;
      return tpl.content.cloneNode(true);
    },
    // The cloned option link stops propagation on click, so its drawer trigger
    // must be bound directly here rather than via delegation.
    wireDrawerTrigger(link, signal) {
      const id = link?.dataset?.drawerTrigger;
      if (!id) return;
      link.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          window.dispatchEvent(new CustomEvent(`modal-trigger-${id}`));
        },
        { signal }
      );
    },
    getOptionLink(name) {
      const wanted = String(name).trim().toLowerCase();
      const templates = this.$el.querySelectorAll(
        "template[data-fv-option-link]"
      );
      for (const tpl of templates) {
        if (tpl.dataset.fvOptionLink.trim().toLowerCase() !== wanted) continue;
        const link = tpl.content.firstElementChild;
        return link ? link.cloneNode(true) : null;
      }
      return null;
    },
    buildRadioGroup(name, signal) {
      const fieldset = document.createElement("fieldset");
      fieldset.className = "fv-option";
      fieldset.dataset.optionName = name;
      const legend = document.createElement("legend");
      legend.className = "fv-option__label sr-only";
      legend.textContent = name;
      fieldset.appendChild(legend);
      const list2 = document.createElement("div");
      list2.className = this.pickerType === "swatch" ? "fv-option__swatches" : "fv-option__radios";
      list2.setAttribute("role", "radiogroup");
      list2.setAttribute("aria-label", name);
      this.optionValues[name].forEach((value, index) => {
        const id = `fv-${this.sectionId}-${name}-${index}`.replace(/\s+/g, "-");
        const label = document.createElement("label");
        label.className = "fv-option__item";
        const input = document.createElement("input");
        input.type = "radio";
        input.className = "fv-option__input sr-only";
        input.name = `fv-${this.sectionId}-${name}`;
        input.id = id;
        input.value = value;
        input.checked = this.selected[name] === value;
        const text = document.createElement("div");
        text.className = this.pickerType === "swatch" ? "fv-option__swatch" : "fv-option__value";
        if (this.pickerType === "swatch") {
          text.title = value;
          const imageUrl = this.imageForOption(name, value);
          if (imageUrl) {
            text.classList.add("fv-option__swatch--image");
            const img = document.createElement("img");
            img.className = "fv-option__swatch-img";
            img.src = imageUrl;
            img.alt = value;
            img.loading = "lazy";
            img.decoding = "async";
            img.dataset.optionValue = value;
            text.appendChild(img);
          } else {
            text.style.setProperty("--fv-swatch", value.toLowerCase());
          }
          const sr = document.createElement("span");
          sr.className = "sr-only";
          sr.textContent = value;
          text.appendChild(sr);
        } else {
          const valueImage = this.valueImageFor(name, value);
          if (valueImage) {
            text.classList.add("fv-option__value--with-image");
            const img = document.createElement("img");
            img.className = "fv-option__value-img";
            img.src = valueImage;
            img.alt = "";
            img.loading = "lazy";
            img.decoding = "async";
            text.appendChild(img);
            const valueLabel = document.createElement("span");
            valueLabel.textContent = value;
            text.appendChild(valueLabel);
          } else {
            text.textContent = value;
          }
        }
        input.addEventListener("change", () => this.onSelect(name, value), {
          signal
        });
        label.addEventListener(
          "pointerenter",
          () => this.prefetchFor(name, value),
          { signal }
        );
        input.addEventListener("focus", () => this.prefetchFor(name, value), {
          signal
        });
        label.appendChild(input);
        label.appendChild(text);
        list2.appendChild(label);
      });
      fieldset.appendChild(list2);
      return fieldset;
    },
    buildSelectGroup(name, signal) {
      const wrapper = document.createElement("div");
      wrapper.className = "fv-option fv-option--select";
      wrapper.dataset.optionName = name;
      const id = `fv-select-${this.sectionId}-${name}`.replace(/\s+/g, "-");
      const label = document.createElement("label");
      label.className = "fv-option__label sr-only";
      label.setAttribute("for", id);
      label.textContent = name;
      const select = document.createElement("select");
      select.className = "fv-option__select";
      select.id = id;
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = `Select ${name}`;
      placeholder.disabled = true;
      placeholder.selected = this.selected[name] == null;
      select.appendChild(placeholder);
      this.optionValues[name].forEach((value) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        option.selected = this.selected[name] === value;
        select.appendChild(option);
      });
      select.addEventListener(
        "change",
        (event) => this.onSelect(name, event.target.value),
        { signal }
      );
      wrapper.appendChild(label);
      wrapper.appendChild(select);
      return wrapper;
    },
    syncActiveState() {
      const root = this.$refs.selectors;
      if (!root) return;
      root.querySelectorAll(".fv-option__input").forEach((input) => {
        const name = input.closest("[data-option-name]")?.dataset.optionName;
        const active = name && this.selected[name] === input.value;
        input.checked = active;
        input.closest(".fv-option__item")?.classList.toggle("is-active", active);
      });
      root.querySelectorAll(".fv-option__select").forEach((select) => {
        const name = select.closest("[data-option-name]")?.dataset.optionName;
        if (!name) return;
        select.value = this.selected[name] ?? "";
      });
      root.querySelectorAll(".fv-option__swatch-img").forEach((img) => {
        const name = img.closest("[data-option-name]")?.dataset.optionName;
        const value = img.dataset.optionValue;
        if (!name || value == null) return;
        const url = this.imageForOption(name, value);
        if (url && img.getAttribute("src") !== url) img.setAttribute("src", url);
      });
      this.updateAccordionMeta();
    },
    updateAccordionMeta() {
      const root = this.$refs.selectors;
      if (!root) return;
      this.optionNames.forEach((name) => {
        const accordion = root.querySelector(
          `.fv-accordion[data-option-name="${name}"]`
        );
        if (!accordion) return;
        const touched = this.touched[name] === true;
        const value = touched ? this.selected[name] : null;
        accordion.classList.toggle("is-selected", touched);
        const selectedEl = accordion.querySelector("[data-fv-selected]");
        if (selectedEl) {
          selectedEl.textContent = value != null ? value : "Select Now";
        }
      });
    },
    onSelect(name, value) {
      this.selected[name] = value;
      this.touched[name] = true;
      this.reconcileSelection(name);
      this.persistSelection();
      this.updateAvailability();
      this.syncActiveState();
      const handle = this.resolveHandle();
      if (handle === this.currentHandle) {
        this.dispatchSwapEvent(handle, {
          swapped: false,
          optionName: name,
          optionValue: value
        });
        return;
      }
      this.load(handle, { optionName: name });
    },
    isAvailable(name, value) {
      const index = this.optionNames.indexOf(name);
      return this.products.some((product2) => {
        if (product2.options?.[name] !== value) return false;
        for (let i = 0; i < index; i += 1) {
          const prior = this.optionNames[i];
          if (this.selected[prior] != null && product2.options?.[prior] !== this.selected[prior]) {
            return false;
          }
        }
        return true;
      });
    },
    reconcileSelection(changedName) {
      const changedIndex = this.optionNames.indexOf(changedName);
      this.optionNames.forEach((name, index) => {
        if (index <= changedIndex) return;
        if (this.selected[name] == null) return;
        if (this.isAvailable(name, this.selected[name])) return;
        this.selected[name] = null;
        this.touched[name] = false;
      });
    },
    updateAvailability() {
      const root = this.$refs.selectors;
      if (!root) return;
      root.querySelectorAll(".fv-option").forEach((group) => {
        const name = group.dataset.optionName;
        if (!name) return;
        group.querySelectorAll(".fv-option__input").forEach((input) => {
          const available = this.isAvailable(name, input.value);
          input.disabled = !available;
          const item = input.closest(".fv-option__item");
          item?.classList.toggle("is-disabled", !available);
          if (item) item.hidden = this.hideUnavailable && !available;
        });
        group.querySelectorAll(".fv-option__select option").forEach((option) => {
          option.disabled = !this.isAvailable(name, option.value);
        });
      });
    },
    async load(handle, { optionName = "" } = {}) {
      const previousProduct = this?.product;
      this._request?.abort();
      this._request = new AbortController();
      const { signal } = this._request;
      this.setLoading(true);
      try {
        const [html, regiosProduct] = await Promise.all([
          fetchProductSection(handle, this.sectionId, signal),
          fetchRegiosProduct(handle, signal)
        ]);
        let doc = new DOMParser().parseFromString(html, "text/html");
        const variantId = this.carriedVariantId(doc);
        if (variantId) {
          const variantHtml = await fetchProductSection(
            handle,
            this.sectionId,
            signal,
            variantId
          );
          doc = new DOMParser().parseFromString(variantHtml, "text/html");
        }
        const announceRegios = await handOffRegiosProduct(
          variantId && regiosProduct ? { ...regiosProduct, variantId } : regiosProduct
        );
        if (signal.aborted) return;
        this.swapRegions(doc);
        this.afterSwap(handle, variantId, {
          optionName,
          fromProductId: previousProduct?.id || ""
        });
        announceRegios();
      } catch (error2) {
        if (error2.name === "AbortError") return;
        console.warn(`[fake-variants] Could not load "${handle}".`, error2);
        Alpine.store("errors")?.add?.(
          window.theme?.strings?.fakeVariantError || "Sorry, that option couldn't be loaded. Please try again."
        );
      } finally {
        this.setLoading(false);
      }
    },
    carriedVariantId(doc) {
      const picked = new URLSearchParams(window.location.search).get("variant");
      if (!picked) return null;
      const readVariants = (root) => {
        try {
          return JSON.parse(
            root?.querySelector('[data-product-json="variants"]')?.textContent || "[]"
          );
        } catch {
          return [];
        }
      };
      const liveSection = document.getElementById(
        `shopify-section-${this.sectionId}`
      );
      const current = readVariants(liveSection).find(
        (variant) => String(variant.id) === picked
      );
      if (!current?.options) return null;
      const match = readVariants(doc).find(
        (variant) => variant.options?.length === current.options.length && variant.options.every(
          (value, index) => value === current.options[index]
        )
      );
      return match?.id || null;
    },
    swapRegions(doc) {
      const liveSection = document.getElementById(
        `shopify-section-${this.sectionId}`
      );
      if (!liveSection) return;
      carryAdditionalMattress(liveSection);
      const incoming = doc.querySelectorAll("[data-fv-region]");
      incoming.forEach((source) => {
        const region = source.getAttribute("data-fv-region");
        const target = liveSection.querySelector(`[data-fv-region="${region}"]`);
        if (target) {
          target.replaceWith(document.importNode(source, true));
        }
      });
      this.renumberSteps();
      document.dispatchEvent(new CustomEvent("swanky:section-swapped"));
    },
    renumberSteps() {
      const configurator = this.$el.closest(".fv-configurator");
      if (!configurator) return;
      configurator.querySelectorAll(".fv-accordion__number").forEach((number, index) => {
        number.textContent = String(index + 1);
      });
    },
    afterSwap(handle, variantId, { optionName, fromProductId }) {
      this.currentHandle = handle;
      const product2 = this.products.find((p) => p.handle === handle);
      if (product2) {
        this.optionNames.forEach((name) => {
          if (this.touched[name] !== true) return;
          if (product2.options?.[name] != null) {
            this.selected[name] = product2.options[name];
          }
        });
      }
      this.persistSelection();
      this.syncActiveState();
      this.updateAvailability();
      this.updateURL(handle, variantId);
      this.dispatchSwapEvent(handle, { handle, optionName, fromProductId });
    },
    updateURL(handle, variantId) {
      updateSectionURL(handle, "fakeVariantHandle", variantId);
    },
    dispatchSwapEvent(handle, options = { initial: false, swapped: true }) {
      const media = {};
      this.optionNames.forEach((name) => {
        if (this.touched[name] !== true || this.selected[name] == null) return;
        media[name] = this.imageForOption(name, this.selected[name]);
      });
      this.$dispatch("fake-variant:changed", {
        handle,
        sectionId: this.sectionId,
        selected: { ...this.selected },
        media,
        ...options,
        initial: options.initial ?? false,
        swapped: options.swapped ?? true
      });
    },
    setLoading(state) {
      setSectionLoading(this, state);
    },
    prefetchFor(name, value) {
      const probe = { ...this.selected, [name]: value };
      const handle = this.resolveFor(probe);
      if (handle && handle !== this.currentHandle) this.prefetch(handle);
    },
    prefetchSiblings() {
      const warm = () => {
        this.optionNames.forEach((name) => {
          this.optionValues[name].forEach((value) => {
            if (value !== this.selected[name]) this.prefetchFor(name, value);
          });
        });
      };
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(warm, { timeout: 2e3 });
      } else {
        setTimeout(warm, 1200);
      }
    },
    prefetch(handle) {
      const cacheKey = `${handle}:${this.sectionId}`;
      if (sectionCache.has(cacheKey)) return;
      fetchProductSection(handle, this.sectionId).catch(() => {
      });
      fetchRegiosProduct(handle);
    }
  });
  var fake_variants_default = fakeVariants;

  // src/scripts/islands/lead-time.js
  var ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  var DEFAULT_PREORDER_TEXT = "Ships in {weeks} weeks";
  var MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ];
  var lead_time_default = (config = {}) => ({
    parentDate: config.parentDate || "",
    // Global lead-time buffer (whole days) added to every ship date before the
    // week maths; mirrors the `leadtime_buffer_days` theme setting. Default 0.
    bufferDays: Number(config.bufferDays) || 0,
    rangeWeeks: Math.max(0, Number(config.rangeWeeks) || 0),
    minWeeks: 0,
    maxWeeks: 0,
    description: "",
    hasLeadTime: false,
    usesPreorderText: false,
    preorderText: "",
    init() {
      this._handler = () => this.recompute();
      window.addEventListener("swanky:upgrade-changed", this._handler);
      window.addEventListener("swanky:upgrade-variant-changed", this._handler);
      this.recompute();
    },
    destroy() {
      window.removeEventListener("swanky:upgrade-changed", this._handler);
      window.removeEventListener("swanky:upgrade-variant-changed", this._handler);
    },
    get valueText() {
      if (!this.hasLeadTime) return "";
      if (this.usesPreorderText) return this.preorderText;
      if (this.rangeWeeks > 0) {
        return `${this.maxWeeks}-${this.maxWeeks + this.rangeWeeks} weeks`;
      }
      return this.maxWeeks === 1 ? "1 week" : `${this.maxWeeks} weeks`;
    },
    recompute() {
      const run = () => this._compute();
      if (window.Alpine && window.Alpine.nextTick) window.Alpine.nextTick(run);
      else run();
    },
    _compute() {
      const items = [];
      const windows = [];
      const root = this.$root || document;
      const parentEl = root.querySelector("[data-parent-date]");
      if (parentEl) {
        addItem(
          items,
          windows,
          parentEl.dataset.parentPreorder,
          parentEl.dataset.parentDate,
          parentEl.dataset.parentMessage,
          this.bufferDays
        );
      }
      document.querySelectorAll(".fv-upgrade__input:checked").forEach((checkbox) => {
        if (checkbox.value === "0") return;
        const variant = document.querySelector(
          `[data-upgrade-of="${checkbox.value}"]:checked`
        );
        const date = variant ? variant.dataset.upgradeLeadtime : checkbox.dataset.upgradeLeadtime;
        const message = variant ? variant.dataset.upgradeMessage : checkbox.dataset.upgradeMessage;
        const status = variant ? variant.dataset.upgradePreorder : checkbox.dataset.upgradePreorder;
        addItem(items, windows, status, date, message, this.bufferDays);
      });
      this.usesPreorderText = false;
      this.preorderText = "";
      if (windows.length) {
        const result = describeWindow(windows, items, {
          bufferDays: this.bufferDays,
          rangeWeeks: this.rangeWeeks,
          template: parentEl?.dataset.preorderText || DEFAULT_PREORDER_TEXT,
          today: parentEl?.dataset.today || localToday()
        });
        if (result) {
          this.usesPreorderText = true;
          this.preorderText = result.text;
          this.description = result.message;
          this.hasLeadTime = true;
          return;
        }
      }
      const parentStatus = parseStatus(parentEl?.dataset.parentPreorder);
      if (!items.length && !windows.length && parentStatus?.state === "in_stock" && parentEl.dataset.instockText) {
        this.usesPreorderText = true;
        this.preorderText = parentEl.dataset.instockText;
        this.description = parentEl.dataset.instockMessage || "";
        this.hasLeadTime = true;
        return;
      }
      if (!items.length) {
        this.hasLeadTime = false;
        this.minWeeks = 0;
        this.maxWeeks = 0;
        this.description = "";
        return;
      }
      const weeks = items.map((i) => i.weeks);
      this.minWeeks = Math.min(...weeks);
      this.maxWeeks = Math.max(...weeks);
      this.description = items.reduce((a, b) => b.weeks > a.weeks ? b : a, items[0]).message || "";
      this.hasLeadTime = true;
    }
  });
  function pushItem(list2, iso, message, bufferDays) {
    const w = weeksUntil(iso, bufferDays);
    if (w > 0) list2.push({ weeks: w, message: message || "", iso });
  }
  function addItem(items, windows, rawStatus, iso, message, bufferDays) {
    const status = parseStatus(rawStatus);
    if (!status) {
      pushItem(items, iso, message, bufferDays);
      return;
    }
    if (status.state === "preorder" && ISO_DATE.test(status.shipFrom) && ISO_DATE.test(status.shipTo)) {
      windows.push({
        from: status.shipFrom,
        to: status.shipTo,
        message: message || ""
      });
    }
  }
  function parseStatus(raw2) {
    if (!raw2) return null;
    try {
      const value = JSON.parse(raw2);
      return value && typeof value.state === "string" ? value : null;
    } catch (_error) {
      return null;
    }
  }
  function describeWindow(windows, items, options) {
    const all = [
      ...windows,
      ...items.map((item) => {
        const from2 = addDays(item.iso, options.bufferDays);
        return {
          from: from2,
          to: addDays(from2, options.rangeWeeks * 7),
          message: item.message
        };
      })
    ];
    const latest = all.reduce((a, b) => b.from > a.from ? b : a, all[0]);
    const shift = Math.max(
      0,
      1 - (toEpochDays(latest.from) - toEpochDays(options.today))
    );
    const from = addDays(latest.from, shift);
    const to = addDays(
      all.reduce((max3, w) => w.to > max3 ? w.to : max3, all[0].to),
      shift
    );
    const days = toEpochDays(from) - toEpochDays(options.today);
    const weeks = Math.ceil(days / 7);
    const lastWeek = Math.ceil(
      (toEpochDays(to) - toEpochDays(options.today)) / 7
    );
    const weeksText = lastWeek > weeks ? `${weeks}\u2013${lastWeek}` : String(weeks);
    return {
      text: options.template.replaceAll("{weeks}", weeksText).replaceAll("{from}", formatShortDate(from)).replaceAll("{to}", formatShortDate(to)),
      message: latest.message || ""
    };
  }
  function toEpochDays(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    return Date.UTC(y, m - 1, d) / 864e5;
  }
  function addDays(iso, days) {
    return new Date((toEpochDays(iso) + days) * 864e5).toISOString().slice(0, 10);
  }
  function formatShortDate(iso) {
    const [, m, d] = iso.split("-").map(Number);
    return `${MONTHS[m - 1]} ${d}`;
  }
  function localToday() {
    const now = /* @__PURE__ */ new Date();
    return [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0")
    ].join("-");
  }
  function weeksUntil(iso, bufferDays = 0) {
    if (!iso) return 0;
    const parts = iso.split("-").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return 0;
    const target = new Date(parts[0], parts[1] - 1, parts[2]);
    const days = Math.floor((target - /* @__PURE__ */ new Date()) / 864e5) + 1 + (Number(bufferDays) || 0);
    if (days <= 0) return 0;
    return Math.max(1, Math.floor((days + 3) / 7));
  }

  // src/scripts/islands/lifting-methods.js
  var touchedSections = /* @__PURE__ */ new Set();
  var prefetchedUrls = /* @__PURE__ */ new Set();
  var productUrl = (handle) => `${window.Shopify?.routes?.root || "/"}products/${handle}`;
  var liftingMethods = (data2 = { sectionId: null, optionName: "", value: "" }) => ({
    optionName: data2.optionName?.toLowerCase(),
    value: data2.value || "",
    sectionId: data2.sectionId,
    preselected: data2.preselected || false,
    open: true,
    selected: false,
    isLoading: false,
    init() {
      this.selected = this.preselected || touchedSections.has(this.sectionId);
      if (this.preselected) this.open = false;
      this.$watch("open", (value) => {
        if (value) {
          this.$dispatch("notify", {
            group: ACCORDION_GROUP,
            id: this.$el.id
          });
        }
      });
      this._onPageShow = (event) => {
        if (event.persisted) this.setLoading(false);
      };
      window.addEventListener("pageshow", this._onPageShow);
    },
    destroy() {
      window.removeEventListener("pageshow", this._onPageShow);
    },
    choose(handle, event) {
      this.selected = true;
      touchedSections.add(this.sectionId);
      this.announce(event?.currentTarget, handle);
    },
    announce(link, handle) {
      document.dispatchEvent(
        new CustomEvent("lifting-method:changed", {
          detail: {
            sectionId: this.sectionId,
            step: this.$el?.dataset?.optionName || "Lifting Method",
            handle: handle || null,
            title: link?.dataset?.methodTitle || null,
            image: link?.dataset?.methodImage || null
          }
        })
      );
    },
    prefetch(handle) {
      if (!handle) return;
      const url = productUrl(handle);
      if (prefetchedUrls.has(url)) return;
      prefetchedUrls.add(url);
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "document";
      link.href = url;
      document.head.append(link);
    },
    navigate(handle, event) {
      if (event) event.preventDefault();
      if (!handle) return;
      this.setLoading(true);
      document.dispatchEvent(
        new CustomEvent("swanky:product-swapped", {
          detail: {
            fromProductId: this?.product?.id,
            optionName: this.optionName,
            value: event?.currentTarget?.dataset?.methodTitle
          }
        })
      );
      window.location.assign(productUrl(handle));
    },
    setLoading(state) {
      setSectionLoading(this, state);
    },
    selectedText() {
      return this.selected ? this.value : this.$el.dataset.selectNowTranslation;
    }
  });
  var lifting_methods_default = liftingMethods;

  // src/scripts/islands/progressive-steps.js
  var SELECTABLE = 'input:not([disabled]):not([type="hidden"]), select:not([disabled])';
  var GATE_ATTRIBUTE = "data-step-requires-previous";
  var REQUIRED_NOTE_CLASS = "fv-accordion__required-note";
  var REQUIRED_NOTE_TEXT = "Required selection";
  var REQUIRED_NOTE_MS = 5e3;
  var ADVANCE_DELAY_MS = 3e3;
  var REVEAL_FALLBACK_MS = 700;
  var COLOUR_SELECTOR = ".fv-color-accordion";
  var GROUP_SELECTORS = [
    COLOUR_SELECTOR,
    ".fv-upgrades-accordion",
    ".fv-additional-mattress-accordion"
  ];
  var VALIDATE_EVENT = "swanky:configurator-validate";
  var progressiveSteps = (data2 = {}) => ({
    openOnLoad: data2.openOnLoad === true,
    autoAdvance: data2.autoAdvance === true,
    groupOpen: data2.groupOpen === true,
    _batchOpening: false,
    _suppressNotify: null,
    _releaseRaf: null,
    _onValidate: null,
    _raf: null,
    _onSwapped: null,
    _onChange: null,
    _onHeaderClick: null,
    _requiredStep: null,
    _requiredTimer: null,
    _advanceStep: null,
    _advanceTarget: null,
    _advanceTimer: null,
    _cancelReveal: null,
    init() {
      this._onChange = (event) => this.onSelectionChange(event);
      this.$el.addEventListener("change", this._onChange);
      this._onHeaderClick = (event) => this.onHeaderClick(event);
      this.$el.addEventListener("click", this._onHeaderClick, true);
      this._onSwapped = () => {
        if (!document.contains(this.$el)) return;
        this.cancelAdvance();
        this.settle(() => this.openFirstIncomplete(this.autoAdvance));
      };
      document.addEventListener("swanky:section-swapped", this._onSwapped);
      this._onValidate = (event) => this.onValidateRequest(event);
      this.$el.addEventListener(VALIDATE_EVENT, this._onValidate);
      this.settle(() => this.openFirstIncomplete(this.openOnLoad));
    },
    destroy() {
      if (this._onChange) this.$el.removeEventListener("change", this._onChange);
      if (this._onHeaderClick) {
        this.$el.removeEventListener("click", this._onHeaderClick, true);
      }
      if (this._onSwapped) {
        document.removeEventListener("swanky:section-swapped", this._onSwapped);
      }
      if (this._onValidate) {
        this.$el.removeEventListener(VALIDATE_EVENT, this._onValidate);
      }
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._releaseRaf) cancelAnimationFrame(this._releaseRaf);
      this.releaseBatch();
      this.clearRequired();
      this.cancelAdvance();
      this.cancelReveal();
    },
    steps() {
      return Array.from(this.$el.querySelectorAll(".fv-accordion")).filter(
        (el) => el.closest(".fv-accordion") === el
      );
    },
    button(step) {
      return step.querySelector(".fv-accordion__button");
    },
    isComplete(step) {
      return step.classList.contains("is-selected");
    },
    isOpen(step) {
      return this.button(step)?.getAttribute("aria-expanded") === "true";
    },
    needsInput(step) {
      return !this.isComplete(step) && step.querySelector(SELECTABLE) != null;
    },
    // The step above a gated one (data-step-requires-previous, i.e. Upgrades) that
    // still wants an answer, so upgrades can't be configured against a half-built
    // bed. Reusing needsInput means steps with no form controls of their own —
    // Lifting Method — never hold the gate closed.
    blockingStep(step) {
      if (!step.hasAttribute(GATE_ATTRIBUTE)) return null;
      const steps = this.steps();
      const index = steps.indexOf(step);
      if (index < 1) return null;
      return steps.slice(0, index).find((earlier) => this.needsInput(earlier)) || null;
    },
    // Clicking a gated step sends the customer to the selection it depends on
    // instead of opening it. The header itself stays live and unstyled — the
    // redirect plus the flagged step is the whole explanation.
    onHeaderClick(event) {
      const btn = event.target?.closest?.(".fv-accordion__button");
      if (!btn || !this.$el.contains(btn)) return;
      const step = btn.closest(".fv-accordion");
      if (!step) return;
      this.cancelAdvance();
      if (this.isOpen(step)) return;
      if (this._batchOpening || this.groupOpen || !this.autoAdvance) return;
      const blocker = this.blockingStep(step);
      if (!blocker) return;
      event.preventDefault();
      event.stopPropagation();
      this.flagRequired(blocker);
      this.openStep(blocker, true);
      this.button(blocker)?.focus({ preventScroll: true });
    },
    // Transient "Required selection" flag on the step being asked for. Injected
    // rather than authored per step because every step type — Liquid-rendered and
    // the JS-built fake-variant ones — shares the same trail container.
    flagRequired(step, placement = "trail") {
      this.clearRequired();
      const lead = placement === "lead" ? step.querySelector(".fv-accordion__lead") : null;
      const host = lead || step.querySelector(".fv-accordion__trail") || this.button(step);
      if (!host) return;
      const note2 = document.createElement("span");
      note2.className = REQUIRED_NOTE_CLASS;
      if (lead) note2.classList.add(`${REQUIRED_NOTE_CLASS}--lead`);
      note2.textContent = REQUIRED_NOTE_TEXT;
      if (lead) {
        host.append(note2);
      } else {
        host.prepend(note2);
      }
      this._requiredStep = step;
      this._requiredTimer = setTimeout(
        () => this.clearRequired(),
        REQUIRED_NOTE_MS
      );
    },
    clearRequired() {
      if (this._requiredTimer) {
        clearTimeout(this._requiredTimer);
        this._requiredTimer = null;
      }
      const step = this._requiredStep;
      this._requiredStep = null;
      if (!step) return;
      step.querySelectorAll(`.${REQUIRED_NOTE_CLASS}`).forEach((note2) => note2.remove());
    },
    openStep(step, reveal = false) {
      const btn = this.button(step);
      if (btn && btn.getAttribute("aria-expanded") !== "true") btn.click();
      if (reveal) this.revealStep(step);
    },
    // Bring a step the coordinator opened into view: the information column is
    // sticky, so an auto-opened step can expand below the fold. Held until the
    // collapse transition finishes, otherwise the step is still at its collapsed
    // height when it's measured and it lands off-centre. Steps taller than the
    // viewport are aligned to the top instead, so their first option stays
    // reachable — .fv-accordion carries the scroll-margin that clears the header.
    revealStep(step) {
      this.cancelReveal();
      const content = step.querySelector(".fv-accordion__content");
      const centre = () => {
        this.cancelReveal();
        if (!document.contains(step)) return;
        const viewport = window.innerHeight || 0;
        const fits = step.getBoundingClientRect().height + 48 <= viewport;
        step.scrollIntoView({
          block: fits ? "center" : "start",
          behavior: this.prefersReducedMotion() ? "auto" : "smooth"
        });
      };
      const onEnd = (event) => {
        if (event.target === content && event.propertyName === "height") centre();
      };
      if (content) content.addEventListener("transitionend", onEnd);
      const timer = setTimeout(centre, content ? REVEAL_FALLBACK_MS : 0);
      this._cancelReveal = () => {
        clearTimeout(timer);
        if (content) content.removeEventListener("transitionend", onEnd);
        this._cancelReveal = null;
      };
    },
    cancelReveal() {
      this._cancelReveal?.();
    },
    prefersReducedMotion() {
      return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    },
    firstIncomplete() {
      return this.steps().find((step) => this.needsInput(step)) || null;
    },
    // The next step needing input *after* a given step. Advancing relative to the
    // completed step (rather than the global first-incomplete) means an optional
    // step left unanswered — e.g. Additional Mattress — is still opened as the next
    // step when the one before it completes, but interacting with a later step
    // never yanks focus back to it.
    nextIncompleteAfter(step) {
      const steps = this.steps();
      const start2 = steps.indexOf(step);
      if (start2 === -1) return null;
      for (let i = start2 + 1; i < steps.length; i += 1) {
        if (this.needsInput(steps[i])) return steps[i];
      }
      return null;
    },
    openFirstIncomplete(includeTarget) {
      const target = includeTarget ? this.firstIncomplete() : null;
      if (this.groupOpen) {
        this.openGroup();
        if (target && !this.inGroup(target)) this.openStep(target);
        return;
      }
      if (!target) return;
      if (this.isOpen(target)) return;
      if (this.blockingStep(target)) return;
      this.openStep(target);
    },
    inGroup(step) {
      return GROUP_SELECTORS.some((selector) => step.matches(selector));
    },
    openGroup() {
      const steps = this.steps();
      const group = steps.filter((step) => this.inGroup(step));
      if (!group.length) return;
      this.startBatch();
      steps.forEach((step) => {
        if (!this.inGroup(step) && this.isOpen(step)) this.button(step)?.click();
      });
      group.forEach((step) => this.openStep(step));
      this.endBatch();
    },
    startBatch() {
      if (this._batchOpening) return;
      this._batchOpening = true;
      this._suppressNotify = (event) => event.stopPropagation();
      window.addEventListener("notify", this._suppressNotify, true);
    },
    endBatch() {
      if (this._releaseRaf) cancelAnimationFrame(this._releaseRaf);
      this._releaseRaf = requestAnimationFrame(
        () => requestAnimationFrame(() => {
          this._releaseRaf = null;
          this.releaseBatch();
        })
      );
    },
    releaseBatch() {
      if (this._suppressNotify) {
        window.removeEventListener("notify", this._suppressNotify, true);
        this._suppressNotify = null;
      }
      this._batchOpening = false;
    },
    firstBlockingStep() {
      const gated = this.steps().filter(
        (step) => step.hasAttribute(GATE_ATTRIBUTE)
      );
      for (const step of gated) {
        const blocker = this.blockingStep(step);
        if (blocker) return blocker;
      }
      return null;
    },
    requiredStep() {
      return this.firstBlockingStep() || this.steps().find(
        (step) => step.matches(COLOUR_SELECTOR) && this.needsInput(step)
      ) || null;
    },
    onValidateRequest(event) {
      if (!this.groupOpen) return;
      const step = this.requiredStep();
      if (!step) return;
      event.preventDefault();
      this.cancelAdvance();
      this.flagRequired(step, "lead");
      const colour = step.matches(COLOUR_SELECTOR);
      this.openStep(step, !colour);
      if (!colour) this.button(step)?.focus({ preventScroll: true });
    },
    onSelectionChange(event) {
      const input = event.target;
      if (!input || input.tagName !== "INPUT" && input.tagName !== "SELECT") {
        return;
      }
      const step = input.closest(".fv-accordion");
      if (!step) return;
      this.settle(() => {
        if (this._requiredStep && this.isComplete(this._requiredStep)) {
          this.clearRequired();
        }
        if (!this.isComplete(step)) {
          this.cancelAdvance();
          return;
        }
        if (this.groupOpen || !this.autoAdvance) {
          this.cancelAdvance();
          return;
        }
        const target = this.nextIncompleteAfter(step);
        if (!target || this.isOpen(target)) {
          this.cancelAdvance();
          return;
        }
        if (this.blockingStep(target)) {
          this.cancelAdvance();
          return;
        }
        this.scheduleAdvance(step, target);
      });
    },
    // Hold the answered step open for ADVANCE_DELAY_MS before moving on, marking it
    // .is-advancing so the CSS can run the countdown ring on its number. Called on
    // every selection, so re-picking inside the step restarts the wait rather than
    // stranding the customer in the step that follows.
    scheduleAdvance(step, target) {
      this.cancelAdvance();
      this._advanceStep = step;
      this._advanceTarget = target;
      step.style.setProperty("--fv-advance-ms", `${ADVANCE_DELAY_MS}ms`);
      void step.offsetWidth;
      step.classList.add("is-advancing");
      this._advanceTimer = setTimeout(
        () => this.commitAdvance(),
        ADVANCE_DELAY_MS
      );
    },
    commitAdvance() {
      const target = this._advanceTarget;
      this.cancelAdvance();
      if (!target || !document.contains(target)) return;
      if (!this.needsInput(target) || this.isOpen(target)) return;
      if (this.blockingStep(target)) return;
      this.openStep(target, true);
    },
    cancelAdvance() {
      if (this._advanceTimer) {
        clearTimeout(this._advanceTimer);
        this._advanceTimer = null;
      }
      if (this._advanceStep) {
        this._advanceStep.classList.remove("is-advancing");
        this._advanceStep.style.removeProperty("--fv-advance-ms");
      }
      this._advanceStep = null;
      this._advanceTarget = null;
    },
    settle(fn) {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(
        () => requestAnimationFrame(() => {
          this._raf = null;
          fn();
        })
      );
    }
  });
  var progressive_steps_default = progressiveSteps;

  // src/scripts/islands/feature-comparison.js
  var pageCache = /* @__PURE__ */ new Map();
  var featureComparison = (data2 = { url: "", uniqueId: "" }) => ({
    url: data2.url,
    uniqueId: data2.uniqueId,
    html: "",
    loading: false,
    error: false,
    _loaded: false,
    // Fired by the same `modal-trigger-<uniqueId>` window event that opens the
    // modal. Guarded so the page is only ever fetched once.
    async load() {
      if (this._loaded || this.loading || !this.url) return;
      if (pageCache.has(this.url)) {
        this.html = pageCache.get(this.url);
        this._loaded = true;
        return;
      }
      this.loading = true;
      this.error = false;
      try {
        const response = await fetch(this.url, {
          headers: { "X-Requested-With": "XMLHttpRequest" }
        });
        if (!response.ok) {
          throw new Error(`Failed to load "${this.url}" (${response.status})`);
        }
        const doc = new DOMParser().parseFromString(
          await response.text(),
          "text/html"
        );
        const markers = doc.querySelectorAll("[data-drawer-content]");
        const main = doc.querySelector("#Main");
        const sections = main ? main.querySelectorAll(":scope > .shopify-section") : [];
        let html = "";
        if (markers.length) {
          html = Array.from(markers, (el) => el.outerHTML).join("");
        } else if (sections.length) {
          html = Array.from(sections, (el) => el.outerHTML).join("");
        } else {
          const fallback = doc.querySelector(".shopify-section--main-page") || main || doc.querySelector("main");
          html = fallback ? fallback.innerHTML : "";
        }
        if (!html) throw new Error("No content region found in fetched page.");
        this.html = html;
        pageCache.set(this.url, this.html);
        this._loaded = true;
      } catch (error2) {
        console.warn("[feature-comparison]", error2);
        this.error = true;
      } finally {
        this.loading = false;
      }
    }
  });
  var feature_comparison_default = featureComparison;

  // src/scripts/islands/bundle-save.js
  var bundleSave = () => ({
    selected: [],
    isSelected(variantId) {
      return this.selected.includes(String(variantId));
    },
    toggle(variantId) {
      const id = String(variantId);
      if (this.isSelected(id)) {
        this.selected = this.selected.filter((value) => value !== id);
      } else {
        this.selected = [...this.selected, id];
      }
    }
  });
  var bundle_save_default = bundleSave;

  // src/scripts/islands/atc-popup.js
  var atcPopup = (data2 = {
    productId: null
  }) => ({
    open: false,
    productId: data2.productId,
    fallbackImage: "",
    image: "",
    variantTitle: "",
    init() {
      this.fallbackImage = this.$root?.dataset?.fallbackImage || "";
      this.image = this.fallbackImage;
    },
    variants() {
      const el = document.querySelector('[data-product-json="variants"]');
      if (!el) return [];
      try {
        return JSON.parse(el.textContent) || [];
      } catch {
        return [];
      }
    },
    show(detail = {}) {
      if (detail.productId != null && this.productId != null && String(detail.productId) !== String(this.productId)) {
        return;
      }
      this.image = detail.image || this.fallbackImage;
      const variant = this.variants().find(
        (candidate) => String(candidate.id) === String(detail.variantId)
      );
      const subtitle = variant ? variant.public_title || variant.title || "" : "";
      this.variantTitle = subtitle && subtitle !== "Default Title" ? subtitle : "";
      this.open = true;
    },
    close() {
      this.open = false;
    }
  });
  var atcBundle = () => ({
    added: [],
    pending: [],
    init() {
      this.syncFromCart();
      window.addEventListener("cart-updated", () => this.syncFromCart());
    },
    async syncFromCart() {
      try {
        const cart = await fetch(window.Shopify.routes.root + "cart.js").then(
          (response) => response.json()
        );
        const ids2 = (cart.items || []).map(
          (item) => String(item.variant_id ?? item.id)
        );
        this.added = [...new Set(ids2)];
      } catch {
      }
    },
    isAdded(variantId) {
      return this.added.includes(String(variantId));
    },
    isPending(variantId) {
      return this.pending.includes(String(variantId));
    },
    async add(variantId) {
      const id = String(variantId);
      if (!id || id === "0" || this.isAdded(id) || this.isPending(id)) return;
      this.pending = [...this.pending, id];
      const formData = new FormData();
      formData.append("id", id);
      formData.append("quantity", 1);
      let added = false;
      try {
        added = await window.Cart?.addToCart(formData, { openDrawer: false });
      } catch {
        added = false;
      }
      this.pending = this.pending.filter((value) => value !== id);
      if (added) this.added = [...this.added, id];
    }
  });

  // src/scripts/islands/product-image-preview.js
  var MOBILE_QUERY = "(max-width: 767px)";
  var LOADING_DELAY = 500;
  var BUSY_RETRY = 100;
  var DEFAULT_HEIGHT = 50;
  var DEFAULT_GUTTER = 24;
  var GALLERY_SCOPE = ".product__gallery";
  var HEADER_SELECTOR = ".header__wrapper";
  var ANNOUNCEMENT_SELECTOR = ".announcement-bar";
  var LEAD_IMAGE = `${GALLERY_SCOPE} .product__media .splide__slide:not(.splide__slide--clone) img`;
  var OPEN_CLASS = "image-preview-open";
  var carried = null;
  var imageKey2 = (url) => window.configuratorMedia?.imageKey ? window.configuratorMedia.imageKey(url) : String(url).split("?")[0];
  var preload = (url) => new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = url;
  });
  var productImagePreview = () => ({
    visible: false,
    expanded: false,
    loading: false,
    src: "",
    alt: "",
    caption: "",
    top: 0,
    height: DEFAULT_HEIGHT,
    gutter: DEFAULT_GUTTER,
    shown: "",
    loadTimer: null,
    loadToken: 0,
    frame: null,
    init() {
      this.defaultAlt = this.$root?.dataset?.defaultAlt || "";
      this.mobile = window.matchMedia(MOBILE_QUERY);
      this.onSelection = (event) => {
        const current = event.detail?.current;
        if (!current?.value) return;
        this.caption = String(current.value);
        this.refresh();
      };
      this.onImage = (event) => {
        const src = event.detail?.src;
        if (!src || this.loadTimer) return;
        if (imageKey2(src) === this.shown) return;
        this.refresh();
      };
      this.onViewport = () => this.schedulePosition();
      this.onSectionSwapped = () => {
        if (this.$root?.isConnected) return;
        window.Alpine?.destroyTree?.(this.$root);
      };
      document.addEventListener("configurator:media-changed", this.onSelection);
      document.addEventListener(
        "configurator:primary-image-changed",
        this.onImage
      );
      window.addEventListener("scroll", this.onViewport, { passive: true });
      window.addEventListener("resize", this.onViewport);
      document.addEventListener("swanky:section-swapped", this.onSectionSwapped);
      this.$watch(
        "visible",
        (value) => document.documentElement.classList.toggle(OPEN_CLASS, value)
      );
      if (carried) {
        Object.assign(this, carried);
        carried = null;
        this.refresh();
      }
      if (!this.caption) this.caption = this.defaultAlt;
      this.schedulePosition();
    },
    destroy() {
      document.removeEventListener(
        "configurator:media-changed",
        this.onSelection
      );
      document.removeEventListener(
        "configurator:primary-image-changed",
        this.onImage
      );
      window.removeEventListener("scroll", this.onViewport);
      window.removeEventListener("resize", this.onViewport);
      document.removeEventListener(
        "swanky:section-swapped",
        this.onSectionSwapped
      );
      clearTimeout(this.loadTimer);
      cancelAnimationFrame(this.frame);
      carried = {
        visible: this.visible,
        expanded: this.expanded,
        src: this.src,
        alt: this.alt,
        caption: this.caption === this.defaultAlt ? "" : this.caption,
        shown: this.shown
      };
      if (!this.visible) document.documentElement.classList.remove(OPEN_CLASS);
    },
    refresh() {
      this.loading = true;
      this.loadToken += 1;
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.settle(), LOADING_DELAY);
    },
    sync() {
      if (this.loadTimer) return;
      this.loading = true;
      this.loadToken += 1;
      this.settle();
    },
    async settle() {
      if (this.$root?.closest("[data-fv-loading]")) {
        this.loadTimer = setTimeout(() => this.settle(), BUSY_RETRY);
        return;
      }
      this.loadTimer = null;
      const token = this.loadToken;
      const lead = this.leadImage();
      const next = lead?.getAttribute("src") || lead?.dataset?.src;
      if (!next || imageKey2(next) === this.shown) {
        this.loading = false;
        return;
      }
      const src = lead.currentSrc || next;
      await preload(src);
      if (token !== this.loadToken) return;
      this.shown = imageKey2(next);
      this.src = src;
      this.alt = lead.getAttribute("alt") || this.defaultAlt;
      this.loading = false;
    },
    leadImage() {
      const scope2 = this.$root?.closest(".section") || document;
      return scope2.querySelector(LEAD_IMAGE);
    },
    galleryOutOfView() {
      const scope2 = this.$root?.closest(".section") || document;
      const gallery = scope2.querySelector(GALLERY_SCOPE);
      if (!gallery) return false;
      const range = document.createRange();
      range.selectNodeContents(gallery);
      const rect2 = range.getBoundingClientRect();
      if (!rect2.height) return false;
      return rect2.bottom <= this.top;
    },
    toggle() {
      this.expanded = !this.expanded;
    },
    schedulePosition() {
      if (this.frame || !this.mobile.matches && !this.visible) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.position();
      });
    },
    position() {
      const header2 = document.querySelector(HEADER_SELECTOR);
      const bottom = header2 ? header2.getBoundingClientRect().bottom : 0;
      const gutter = header2 ? parseFloat(getComputedStyle(header2).paddingInlineStart) : NaN;
      this.top = Math.max(0, Math.round(bottom));
      this.gutter = Number.isFinite(gutter) ? gutter : DEFAULT_GUTTER;
      this.height = document.querySelector(ANNOUNCEMENT_SELECTOR)?.offsetHeight || DEFAULT_HEIGHT;
      const show = this.mobile.matches && this.galleryOutOfView();
      if (show && !this.visible) this.sync();
      if (!show) this.expanded = false;
      this.visible = show;
    }
  });
  var product_image_preview_default = productImagePreview;

  // src/scripts/components/regios-cart-total.js
  async function upgradeSavings(upgrades = []) {
    const discounts = await Promise.all(
      upgrades.map(
        (upgrade) => discountFor(upgrade.product, String(upgrade.variantId), upgrade.quantity)
      )
    );
    return discounts.reduce((sum, discount) => sum + (discount?.saving || 0), 0);
  }
  var regiosCartTotal = ({
    cents = 0,
    display: display2 = "",
    format: format2 = "",
    upgrades = []
  } = {}) => ({
    display: display2,
    async init() {
      const saving = await upgradeSavings(upgrades);
      if (saving > 0) {
        this.display = formatMoney(cents - saving, format2, "cart-money__cents");
      }
    }
  });
  var regios_cart_total_default = regiosCartTotal;

  // src/scripts/components/regios-cart-discount.js
  var regiosCartDiscount = ({
    cents = 0,
    display: display2 = "",
    format: format2 = "",
    upgrades = []
  } = {}) => ({
    display: display2,
    async init() {
      const saving = await upgradeSavings(upgrades);
      if (saving > 0) {
        this.display = formatMoney(cents + saving, format2, "cart-money__cents");
      }
    }
  });
  var regios_cart_discount_default = regiosCartDiscount;

  // src/scripts/components/regios-card-badge.js
  var regiosCardBadge = () => ({
    init() {
      const source = this.$el.closest(".card")?.querySelector(".badges .regios-dopp-generic-badge");
      if (!source) return;
      const sync = () => {
        const html = source.hasAttribute("hidden") || source.style.display === "none" ? "" : source.innerHTML;
        if (this.$el.innerHTML !== html) this.$el.innerHTML = html;
      };
      sync();
      new MutationObserver(sync).observe(source, {
        attributes: true,
        attributeFilter: ["hidden", "style"],
        characterData: true,
        childList: true,
        subtree: true
      });
    }
  });
  var regios_card_badge_default = regiosCardBadge;

  // src/scripts/utilities/alpine-defer.js
  function alpineDefer(selector = "[data-alpine-defer]") {
    const deferredElements = document.querySelectorAll(selector);
    deferredElements.forEach((element) => {
      Alpine.destroyTree(element);
      element.removeAttribute("x-ignore");
      Alpine.initTree(element);
    });
  }
  var alpine_defer_default = alpineDefer;

  // src/scripts/utilities/anchor-accordion.js
  var HOLD_MS = 700;
  var DRIFT_PX = 0.5;
  var anchorAccordion = (el) => {
    const header2 = el?.querySelector(".accordion__button");
    if (!header2) return;
    const top = header2.getBoundingClientRect().top;
    const deadline = performance.now() + HOLD_MS;
    let frame = null;
    const release2 = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
      window.removeEventListener("wheel", release2);
      window.removeEventListener("touchstart", release2);
      window.removeEventListener("keydown", release2);
    };
    const hold = (now) => {
      if (!document.contains(header2)) return release2();
      const drift = header2.getBoundingClientRect().top - top;
      if (Math.abs(drift) > DRIFT_PX) {
        window.scrollBy({ top: drift, behavior: "instant" });
      }
      if (now >= deadline) return release2();
      frame = requestAnimationFrame(hold);
    };
    window.addEventListener("wheel", release2, { passive: true });
    window.addEventListener("touchstart", release2, { passive: true });
    window.addEventListener("keydown", release2);
    frame = requestAnimationFrame(hold);
  };
  var anchor_accordion_default = anchorAccordion;

  // src/scripts/theme.js
  window.DefaultSplide = DefaultSplide;
  window.alpineDefer = alpine_defer_default;
  window.Alpine = module_default;
  window.leadTime = lead_time_default;
  window.bundleSave = bundle_save_default;
  window.additionalMattress = additional_mattress_default;
  window.additionalMattressVariants = additionalMattressVariants;
  window.progressiveSteps = progressive_steps_default;
  window.atcPopup = atcPopup;
  window.atcBundle = atcBundle;
  window.productImagePreview = product_image_preview_default;
  module_default.magic("anchorAccordion", () => (el) => anchor_accordion_default(el));
  module_default.plugin(module_default3);
  module_default.plugin(module_default4);
  module_default.plugin(module_default6);
  module_default.plugin(module_default5);
  module_default.plugin(src_default6);
  module_default.data("navigation", navigation_default);
  module_default.data("leadTime", lead_time_default);
  module_default.data("bundleSave", bundle_save_default);
  module_default.data("additionalMattress", additional_mattress_default);
  module_default.data("additionalMattressVariants", additionalMattressVariants);
  module_default.data("atcPopup", atcPopup);
  module_default.data("atcBundle", atcBundle);
  module_default.data("productImagePreview", product_image_preview_default);
  module_default.data("regiosTilePrice", regios_tile_price_default);
  module_default.data("regiosCartTotal", regios_cart_total_default);
  module_default.data("regiosCartDiscount", regios_cart_discount_default);
  module_default.data("regiosCardBadge", regios_card_badge_default);
  module_default.data("header", header_default);
  module_default.data("product", product_default);
  module_default.data("buyButtons", buy_buttons_default);
  module_default.data("range", custom_range_default);
  module_default.data("purchaseOptions", purchase_options_default);
  module_default.data("note", note_default);
  module_default.data("fakeVariants", fake_variants_default);
  module_default.data("liftingMethods", lifting_methods_default);
  module_default.data("progressiveSteps", progressive_steps_default);
  module_default.data("featureComparison", feature_comparison_default);
  module_default.start();
})();
/*! Bundled license information:

alpinejs/dist/module.esm.js:
  (*! Bundled license information:
  
  @vue/shared/dist/shared.esm-bundler.js:
    (**
    * @vue/shared v3.5.41
    * (c) 2018-present Yuxi (Evan) You and Vue contributors
    * @license MIT
    **)
  
  @vue/reactivity/dist/reactivity.esm-bundler.js:
    (**
    * @vue/reactivity v3.5.41
    * (c) 2018-present Yuxi (Evan) You and Vue contributors
    * @license MIT
    **)
  *)

@splidejs/splide/dist/js/splide.esm.js:
  (*!
   * Splide.js
   * Version  : 4.1.4
   * License  : MIT
   * Copyright: 2022 Naotoshi Fujita
   *)

@splidejs/splide-extension-auto-scroll/dist/js/splide-extension-auto-scroll.esm.js:
  (*!
   * @splidejs/splide-extension-auto-scroll
   * Version  : 0.5.3
   * License  : MIT
   * Copyright: 2022 Naotoshi Fujita
   *)
*/
